import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Cloud, Server, Database, Network, Shield, Activity, GitBranch, Box,
  Code, Terminal, Layers, Lock, Globe, Zap, ChevronRight, ChevronLeft,
  Check, Copy, Download, ArrowRight, Settings, FileText, Folder, File,
  AlertCircle, Sparkles, Container, HardDrive, Workflow, Eye, Edit3,
  RefreshCw, CircleDot, Hexagon, Cpu, Boxes, FileCode2, Map as MapIcon, GaugeCircle,
  KeyRound, FolderTree, Wrench, Rocket, ShieldCheck, Radio, X
} from 'lucide-react';
import { firebaseAuth, googleProvider, firestoreDb } from './firebase';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  doc, getDoc, setDoc, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import {
  uploadDesignBuild,
  getCloudUsage,
  listStoredDesigns,
  deleteStoredDesign,
  getDesignDownloadURL,
} from './cloudStorage';

/* =========================================================================
 * STAX — Production Stack Generator
 * Generates Terraform + CI/CD + K8s + ops dashboard from declarative input.
 * ========================================================================= */

// ---------- catalog ----------
const PROVIDERS = [
  { id: 'aws',   name: 'Amazon Web Services', short: 'AWS',   regions: ['us-east-1','us-west-2','eu-west-1','ap-south-1','ap-southeast-2'] },
  { id: 'gcp',   name: 'Google Cloud Platform', short: 'GCP', regions: ['us-central1','us-east1','europe-west1','asia-southeast1'] },
  { id: 'azure', name: 'Microsoft Azure',     short: 'Azure', regions: ['eastus','westus2','westeurope','southeastasia'] },
];

const COMPUTE_BY_PROVIDER = {
  aws: [
    { id: 'eks',    name: 'EKS',         tag: 'Kubernetes',        desc: 'Managed Kubernetes with autoscaling node groups, IRSA, ALB ingress.', icon: Boxes },
    { id: 'ecs',    name: 'ECS Fargate', tag: 'Serverless containers', desc: 'Fargate task runtime — no node management, per-second billing.', icon: Container },
    { id: 'lambda', name: 'Lambda',      tag: 'Functions',         desc: 'Event-driven functions behind API Gateway, IAM least-priv.', icon: Zap },
    { id: 'ec2',    name: 'EC2 ASG',     tag: 'Virtual machines',  desc: 'Auto-scaling EC2 group behind ALB, launch templates, SSM.', icon: Server },
  ],
  gcp: [
    { id: 'gke',    name: 'GKE Autopilot', tag: 'Kubernetes',  desc: 'Fully managed K8s, workload identity, regional control plane.', icon: Boxes },
    { id: 'run',    name: 'Cloud Run',     tag: 'Serverless containers', desc: 'Container HTTP services, scale-to-zero, IAP-friendly.', icon: Container },
    { id: 'gcf',    name: 'Cloud Functions', tag: 'Functions', desc: 'Gen2 functions on Cloud Run substrate, EventArc triggers.', icon: Zap },
    { id: 'gce',    name: 'GCE MIG',       tag: 'Virtual machines', desc: 'Managed instance group with regional load balancer.', icon: Server },
  ],
  azure: [
    { id: 'aks',    name: 'AKS',           tag: 'Kubernetes',  desc: 'Managed Kubernetes, AAD-integrated, virtual node scaling.', icon: Boxes },
    { id: 'aca',    name: 'Container Apps', tag: 'Serverless containers', desc: 'KEDA-based scaling, Dapr sidecars, revisions.', icon: Container },
    { id: 'func',   name: 'Functions',     tag: 'Functions',   desc: 'Serverless functions on consumption or premium plan.', icon: Zap },
    { id: 'vmss',   name: 'VM Scale Set',  tag: 'Virtual machines', desc: 'Auto-scaling VMs behind Application Gateway.', icon: Server },
  ],
};

const DATABASE_OPTIONS = {
  aws:   [
    { id: 'rds-postgres', name: 'RDS PostgreSQL', desc: 'Multi-AZ, encrypted at rest, automated backups, IAM auth.', icon: Database },
    { id: 'rds-mysql',    name: 'RDS MySQL',      desc: 'Multi-AZ, performance insights, IAM auth.', icon: Database },
    { id: 'aurora',       name: 'Aurora Serverless v2', desc: 'Auto-scaling, fault-tolerant, sub-second failover.', icon: Database },
    { id: 'dynamo',       name: 'DynamoDB',       desc: 'On-demand NoSQL, point-in-time recovery, global tables.', icon: HardDrive },
    { id: 'none',         name: 'No database',    desc: 'Skip the data layer.', icon: AlertCircle },
  ],
  gcp:   [
    { id: 'cloudsql-pg',  name: 'Cloud SQL Postgres', desc: 'HA regional, private IP, automated backups.', icon: Database },
    { id: 'cloudsql-mysql', name: 'Cloud SQL MySQL', desc: 'HA, IAM auth, private connectivity.', icon: Database },
    { id: 'firestore',    name: 'Firestore',      desc: 'Native mode, multi-region, strong consistency.', icon: HardDrive },
    { id: 'spanner',      name: 'Spanner',        desc: 'Globally distributed, strongly consistent SQL.', icon: Database },
    { id: 'none',         name: 'No database',    desc: 'Skip the data layer.', icon: AlertCircle },
  ],
  azure: [
    { id: 'pg-flex',      name: 'Postgres Flexible', desc: 'Zone-redundant HA, private endpoint, AAD auth.', icon: Database },
    { id: 'sql',          name: 'Azure SQL',      desc: 'Managed SQL with elastic pools and geo-replication.', icon: Database },
    { id: 'cosmos',       name: 'Cosmos DB',      desc: 'Multi-model, multi-region writes, sub-10ms reads.', icon: HardDrive },
    { id: 'none',         name: 'No database',    desc: 'Skip the data layer.', icon: AlertCircle },
  ],
};

const CACHE_OPTIONS = [
  { id: 'redis',     name: 'Redis',     desc: 'In-memory KV, pub/sub, Lua scripting.', icon: Zap },
  { id: 'memcached', name: 'Memcached', desc: 'Simple distributed object cache.',     icon: Zap },
  { id: 'none',      name: 'None',      desc: 'No cache layer.',                      icon: AlertCircle },
];

// ── Data Engineering catalogs ──────────────────────────────────
// All open-source first. Managed alternatives noted in `managed` field.
const DE_STREAMING = [
  { id: 'kafka',     name: 'Apache Kafka',  tag: 'OSS',     managed: 'MSK / Confluent / Aiven',
    desc: 'De-facto streaming standard. KRaft mode, no ZooKeeper. Tiered storage.', icon: Activity },
  { id: 'redpanda',  name: 'Redpanda',      tag: 'OSS',     managed: 'Redpanda Cloud',
    desc: 'Kafka-API-compatible, single-binary, C++. 10x lower latency, 6x cheaper.', icon: Activity },
  { id: 'pulsar',    name: 'Apache Pulsar', tag: 'OSS',     managed: 'StreamNative',
    desc: 'Tiered storage built-in, geo-replication, multi-tenancy.', icon: Activity },
  { id: 'kinesis',   name: 'AWS Kinesis',   tag: 'managed', managed: 'AWS-only',
    desc: 'Managed stream. Tight AWS IAM integration. On-demand or provisioned.', icon: Activity },
  { id: 'pubsub',    name: 'GCP Pub/Sub',   tag: 'managed', managed: 'GCP-only',
    desc: 'Global low-latency messaging. Push or pull. Schema registry built-in.', icon: Activity },
  { id: 'eventhubs', name: 'Azure Event Hubs', tag: 'managed', managed: 'Azure-only',
    desc: 'Kafka-compatible endpoint. Capture to ADLS / Blob.', icon: Activity },
  { id: 'none',      name: 'No streaming',  tag: '—',       managed: '—',
    desc: 'Skip the streaming ingest layer.', icon: AlertCircle },
];

const DE_OLAP = [
  { id: 'pinot',      name: 'Apache Pinot',     tag: 'OSS',     managed: 'StarTree',
    desc: 'Real-time + batch ingest. Sub-second on billions of rows. Star-tree indexes.', icon: Database },
  { id: 'druid',      name: 'Apache Druid',     tag: 'OSS',     managed: 'Imply',
    desc: 'Time-series OLAP. Sub-second slice/dice on streaming data.', icon: Database },
  { id: 'clickhouse', name: 'ClickHouse',       tag: 'OSS',     managed: 'ClickHouse Cloud',
    desc: 'Columnar SQL warehouse. Single-node performance is unreal.', icon: Database },
  { id: 'starrocks',  name: 'StarRocks',        tag: 'OSS',     managed: 'CelerData',
    desc: 'MPP analytical DB, MySQL protocol, materialized views.', icon: Database },
  { id: 'doris',      name: 'Apache Doris',     tag: 'OSS',     managed: 'SelectDB',
    desc: 'Real-time analytical database. MySQL-compatible.', icon: Database },
  { id: 'redshift',   name: 'AWS Redshift',     tag: 'managed', managed: 'AWS-only',
    desc: 'Columnar warehouse. RA3 nodes with managed storage.', icon: Database },
  { id: 'bigquery',   name: 'BigQuery',         tag: 'managed', managed: 'GCP-only',
    desc: 'Serverless warehouse. Pay-per-query or flat-rate slots.', icon: Database },
  { id: 'synapse',    name: 'Azure Synapse',    tag: 'managed', managed: 'Azure-only',
    desc: 'Dedicated SQL pools + Spark + serverless.', icon: Database },
  { id: 'none',       name: 'No OLAP layer',    tag: '—',       managed: '—',
    desc: 'Skip the analytical / serving layer.', icon: AlertCircle },
];

const DE_STREAM_PROC = [
  { id: 'flink',      name: 'Apache Flink',           tag: 'OSS',     managed: 'AWS Kinesis Analytics / Aiven',
    desc: 'Exactly-once stream processing. SQL + DataStream API. Stateful operators.', icon: Workflow },
  { id: 'spark-ss',   name: 'Spark Structured Streaming', tag: 'OSS', managed: 'Databricks / EMR / Dataproc',
    desc: 'Micro-batch streaming on Spark. Same API as batch jobs.', icon: Workflow },
  { id: 'beam',       name: 'Apache Beam',            tag: 'OSS',     managed: 'GCP Dataflow',
    desc: 'Unified batch + stream model. Runs on Flink, Spark, or Dataflow.', icon: Workflow },
  { id: 'materialize',name: 'Materialize',            tag: 'OSS',     managed: 'Materialize Cloud',
    desc: 'Streaming SQL with maintained materialized views. Postgres wire protocol.', icon: Workflow },
  { id: 'risingwave', name: 'RisingWave',             tag: 'OSS',     managed: 'RisingWave Cloud',
    desc: 'Cloud-native streaming DB. Postgres-compatible. State on S3.', icon: Workflow },
  { id: 'none',       name: 'No stream processing',   tag: '—',       managed: '—',
    desc: 'Skip the stream processing layer.', icon: AlertCircle },
];

const DE_WIDE_COL = [
  { id: 'cassandra',  name: 'Apache Cassandra',  tag: 'OSS',     managed: 'AstraDB / Aiven / Instaclustr',
    desc: 'Wide-column, masterless, linear scale. Eventually consistent.', icon: HardDrive },
  { id: 'scylla',     name: 'ScyllaDB',          tag: 'OSS',     managed: 'ScyllaDB Cloud',
    desc: 'Cassandra-compatible, C++, shard-per-core. 10x throughput, lower latency.', icon: HardDrive },
  { id: 'hbase',      name: 'Apache HBase',      tag: 'OSS',     managed: 'EMR / Dataproc',
    desc: 'Hadoop-native wide-column. Strong consistency. HDFS-backed.', icon: HardDrive },
  { id: 'timescale',  name: 'TimescaleDB',       tag: 'OSS',     managed: 'Timescale Cloud',
    desc: 'PostgreSQL extension for time-series. Continuous aggregates.', icon: HardDrive },
  { id: 'questdb',    name: 'QuestDB',           tag: 'OSS',     managed: 'QuestDB Cloud',
    desc: 'High-performance time-series DB. Postgres wire + InfluxDB line protocol.', icon: HardDrive },
  { id: 'none',       name: 'No wide-column / TS', tag: '—',     managed: '—',
    desc: 'Skip wide-column / time-series storage.', icon: AlertCircle },
];

const DE_LAKE_TABLE = [
  { id: 'iceberg',    name: 'Apache Iceberg',  tag: 'OSS',
    desc: 'Open table format. ACID, schema evolution, time travel. Glue / Nessie / REST catalog.', icon: Layers },
  { id: 'delta',      name: 'Delta Lake',      tag: 'OSS',
    desc: 'ACID over Parquet. Time travel, schema enforcement. Databricks-led.', icon: Layers },
  { id: 'hudi',       name: 'Apache Hudi',     tag: 'OSS',
    desc: 'Streaming upserts on data lakes. CoW + MoR table types.', icon: Layers },
  { id: 'parquet',    name: 'Plain Parquet',   tag: 'OSS',
    desc: 'Just columnar files on object storage. No table format. External catalog optional.', icon: Layers },
  { id: 'none',       name: 'No data lake',    tag: '—',
    desc: 'Skip the lake / lakehouse layer.', icon: AlertCircle },
];

const DE_QUERY_ENGINE = [
  { id: 'trino',      name: 'Trino',           tag: 'OSS',     managed: 'Starburst',
    desc: 'Federated SQL across Iceberg / Hive / Postgres / Kafka / etc.', icon: Terminal },
  { id: 'presto',     name: 'PrestoDB',        tag: 'OSS',     managed: 'Ahana',
    desc: 'Original distributed SQL engine. Facebook fork upstream of Trino.', icon: Terminal },
  { id: 'duckdb',     name: 'DuckDB',          tag: 'OSS',     managed: 'MotherDuck',
    desc: 'In-process OLAP. Lambda-friendly, single binary. Reads Iceberg / Parquet directly.', icon: Terminal },
  { id: 'athena',     name: 'AWS Athena',      tag: 'managed', managed: 'AWS-only',
    desc: 'Serverless Trino on S3. Glue catalog. Pay per TB scanned.', icon: Terminal },
  { id: 'none',       name: 'No query engine', tag: '—',       managed: '—',
    desc: 'Skip the federated query layer.', icon: AlertCircle },
];

const DE_ORCHESTRATION = [
  { id: 'airflow',    name: 'Apache Airflow',  tag: 'OSS',     managed: 'MWAA / Astronomer',
    desc: 'The boring choice. Python DAGs. Battle-tested operators for everything.', icon: GitBranch },
  { id: 'prefect',    name: 'Prefect',         tag: 'OSS',     managed: 'Prefect Cloud',
    desc: 'Modern Python-native. Dynamic DAGs, hybrid execution model.', icon: GitBranch },
  { id: 'dagster',    name: 'Dagster',         tag: 'OSS',     managed: 'Dagster Cloud',
    desc: 'Asset-centric. Software-defined data products. Type-safe IO managers.', icon: GitBranch },
  { id: 'temporal',   name: 'Temporal',        tag: 'OSS',     managed: 'Temporal Cloud',
    desc: 'Durable workflows. Code-first, retries + state are automatic.', icon: GitBranch },
  { id: 'none',       name: 'No orchestrator', tag: '—',       managed: '—',
    desc: 'Skip workflow orchestration.', icon: AlertCircle },
];

const CICD_OPTIONS = [
  { id: 'github',  name: 'GitHub Actions', desc: 'OIDC federation, environments, required reviewers.', icon: GitBranch },
  { id: 'gitlab',  name: 'GitLab CI',      desc: 'Merge train, manual prod gate, OIDC to cloud.', icon: GitBranch },
  { id: 'circle',  name: 'CircleCI',       desc: 'Workflows with manual approval jobs, OIDC.', icon: GitBranch },
];

const OBSERVABILITY_OPTIONS = [
  { id: 'cloudnative', name: 'Cloud-native', desc: 'CloudWatch / Cloud Logging / Azure Monitor.', icon: GaugeCircle },
  { id: 'datadog',     name: 'Datadog',      desc: 'APM + Logs + Metrics, agent DaemonSet.',     icon: GaugeCircle },
  { id: 'grafana',     name: 'Grafana Stack', desc: 'Prometheus, Loki, Tempo, Grafana.',         icon: GaugeCircle },
  { id: 'newrelic',    name: 'New Relic',    desc: 'Full-stack observability with NRDB.',        icon: GaugeCircle },
];

// ── AI / ML / LLM / Agents ──────────────────────────────────────
const AI_INFERENCE_OPTIONS = {
  aws: [
    { id: 'bedrock',   name: 'AWS Bedrock',         tag: 'Managed LLM API',       desc: 'Claude, Llama, Titan, Mistral via single IAM-scoped endpoint. No infra to run.' },
    { id: 'sagemaker', name: 'SageMaker Endpoint',  tag: 'Custom hosted models',  desc: 'Deploy custom or fine-tuned weights on managed real-time inference endpoints.' },
    { id: 'vllm-eks',  name: 'vLLM on EKS (GPU)',   tag: 'Self-hosted',           desc: 'Open-weights models on g5/g6/p5 GPU nodes with vLLM serving + PagedAttention.' },
    { id: 'external',  name: 'External provider',   tag: 'OpenAI / Anthropic / etc', desc: 'Egress proxy to 3rd-party API with secret rotation and budget alarms.' },
  ],
  gcp: [
    { id: 'vertex',    name: 'Vertex AI',           tag: 'Managed',               desc: 'Gemini + 3P models with unified endpoint, IAM, and prompt versioning.' },
    { id: 'vllm-gke',  name: 'vLLM on GKE (GPU)',   tag: 'Self-hosted',           desc: 'Open-weights models on a2/a3 GPU node pools with vLLM.' },
    { id: 'external',  name: 'External provider',   tag: 'OpenAI / Anthropic / etc', desc: 'Egress to 3rd-party API with Secret Manager and budget alerts.' },
  ],
  azure: [
    { id: 'aoai',      name: 'Azure OpenAI',        tag: 'Managed',               desc: 'GPT-4o / o-series with private endpoints, content filters, AAD auth.' },
    { id: 'vllm-aks',  name: 'vLLM on AKS (GPU)',   tag: 'Self-hosted',           desc: 'Open-weights models on NC/ND GPU node pools with vLLM.' },
    { id: 'external',  name: 'External provider',   tag: 'OpenAI / Anthropic / etc', desc: 'Egress to 3rd-party with Key Vault rotation and cost caps.' },
  ],
};

const VECTOR_DB_OPTIONS = [
  { id: 'pgvector',   name: 'pgvector',     tag: 'Postgres extension', desc: 'Vectors in your existing managed Postgres — simplest ops story, transactional joins.' },
  { id: 'opensearch', name: 'OpenSearch k-NN', tag: 'AWS managed',     desc: 'Hybrid lexical + dense retrieval, hot/warm tiers, IAM auth.' },
  { id: 'qdrant',     name: 'Qdrant',       tag: 'Self-hosted',        desc: 'High-perf vector DB on K8s, payload filtering, scalar quantisation.' },
  { id: 'pinecone',   name: 'Pinecone',     tag: 'Managed SaaS',       desc: 'Serverless vectors — zero infra. API key in Secrets Manager.' },
  { id: 'weaviate',   name: 'Weaviate',     tag: 'Self-hosted',        desc: 'GraphQL vector DB, modular vectorisers, hybrid BM25 + vector.' },
  { id: 'none',       name: 'No vector DB', tag: 'Skip retrieval',     desc: 'Inference only — no RAG, no semantic search.' },
];

const AGENT_FRAMEWORK_OPTIONS = [
  { id: 'langgraph',  name: 'LangGraph',     tag: 'Stateful graphs',     desc: 'Cyclic agent flows with persistent state, human-in-the-loop, replay.' },
  { id: 'llamaindex', name: 'LlamaIndex',    tag: 'RAG-first',           desc: 'Deep retrieval pipelines, query engines, doc-aware agents.' },
  { id: 'crewai',     name: 'CrewAI',        tag: 'Multi-agent',         desc: 'Role-based collaborative crews with task delegation.' },
  { id: 'pydanticai', name: 'PydanticAI',    tag: 'Type-safe',           desc: 'Type-safe agents with structured outputs and dependency injection.' },
  { id: 'custom',     name: 'No framework',  tag: 'Direct API',          desc: 'Direct LLM calls with your own orchestration. Maximum control.' },
];

const LLM_OBSERVABILITY = [
  { id: 'langsmith', name: 'LangSmith',      desc: 'Hosted tracing, evals, prompt versioning, dataset replay.' },
  { id: 'langfuse',  name: 'Langfuse',       desc: 'OSS LLM observability — self-hostable on your cluster.' },
  { id: 'phoenix',   name: 'Arize Phoenix',  desc: 'OTLP-native LLM tracing, fully open source, self-hosted.' },
  { id: 'none',      name: 'None',           desc: 'Skip LLM-specific tracing — rely on existing APM only.' },
];

const GPU_TYPES = {
  aws:   ['g5.xlarge (A10G · 24GB)', 'g6.xlarge (L4 · 24GB)', 'p4d.24xlarge (A100×8 · 320GB)', 'p5.48xlarge (H100×8 · 640GB)', 'inf2.xlarge (Inferentia2)'],
  gcp:   ['n1-standard-4 + T4 (16GB)', 'a2-highgpu-1g (A100 · 40GB)', 'a3-highgpu-8g (H100×8 · 640GB)', 'tpu-v5e-1', 'tpu-v5p-8'],
  azure: ['NC6s_v3 (V100 · 16GB)', 'NC24ads_A100_v4 (A100 · 80GB)', 'ND96isr_H100_v5 (H100×8)'],
};

const OPEN_MODELS = [
  'meta-llama/Llama-3.3-70B-Instruct',
  'meta-llama/Llama-3.1-8B-Instruct',
  'mistralai/Mixtral-8x7B-Instruct-v0.1',
  'Qwen/Qwen2.5-72B-Instruct',
  'google/gemma-2-27b-it',
];

// ── AI / LLM / MCP Security & Guardrails ──────────────────────────
const PROMPT_FIREWALLS = [
  { id: 'bedrock-guardrails',  name: 'Bedrock Guardrails',     tag: 'AWS managed',  desc: 'Built-in for Bedrock — content filters, PII redaction, denied topics, grounding checks, prompt-attack filter.' },
  { id: 'azure-content-safety',name: 'Azure AI Content Safety',tag: 'Azure managed',desc: 'Multimodal moderation, prompt shields for jailbreak detection, groundedness API.' },
  { id: 'lakera',              name: 'Lakera Guard',           tag: 'SaaS',         desc: 'Real-time prompt injection + DLP. Sub-100ms latency, language-agnostic.' },
  { id: 'llm-guard',           name: 'LLM Guard',              tag: 'OSS',          desc: 'Self-hosted — prompt injection, anonymization, ban substrings, toxicity, secret detection.' },
  { id: 'prompt-armor',        name: 'Prompt Armor',           tag: 'SaaS',         desc: 'Real-time scanning for jailbreaks, PII, secrets, brand-safety violations.' },
  { id: 'none',                name: 'None (not recommended)', tag: '⚠ unsafe',     desc: 'No prompt firewall. Acceptable only with layered controls + manual review of every output.' },
];

const PII_DETECTORS = [
  { id: 'presidio',   name: 'Microsoft Presidio',   tag: 'OSS',       desc: 'Configurable PII detection + anonymization. Self-hosted.' },
  { id: 'comprehend', name: 'AWS Comprehend PII',   tag: 'AWS',       desc: 'Managed PII detection + redaction. Pay-per-use.' },
  { id: 'cloud-dlp',  name: 'Google Cloud DLP',     tag: 'GCP',       desc: 'DLP API with deep PII inspection + de-identification templates.' },
  { id: 'piiranha',   name: 'Piiranha (HF model)',  tag: 'OSS local', desc: 'Open-weights NER model for offline PII detection.' },
  { id: 'none',       name: 'None',                 tag: '⚠ unsafe',  desc: 'No PII redaction. Risk: PII leaked to LLM provider + persisted in logs.' },
];

// Compliance frameworks with tier requirement.
// `tier` controls who can select the framework:
//   'core'     — Free, Pro, Team
//   'advanced' — Pro, Team only
//   (no 'team' tier exists for built-in frameworks; "custom" frameworks
//    on Team are uploaded separately, not in this catalog.)
const COMPLIANCE_FRAMEWORKS = [
  { id: 'soc2',       name: 'SOC 2 Type II',  short: 'SOC 2',  tier: 'core',
    desc: 'Security · Availability · Confidentiality controls.' },
  { id: 'iso27001',   name: 'ISO/IEC 27001',  short: 'ISO 27k', tier: 'core',
    desc: 'Information security management system.' },
  { id: 'gdpr',       name: 'GDPR',           short: 'GDPR',   tier: 'core',
    desc: 'EU data protection. Right to erasure, lawful basis, DPO.' },
  { id: 'iso42001',   name: 'ISO/IEC 42001',  short: 'ISO 42k', tier: 'advanced',
    desc: 'AI management system. New 2023 standard for AI governance.' },
  { id: 'hipaa',      name: 'HIPAA',          short: 'HIPAA',  tier: 'advanced',
    desc: 'US healthcare PHI handling. Requires BAA with cloud provider.' },
  { id: 'pci-dss',    name: 'PCI-DSS',        short: 'PCI',    tier: 'advanced',
    desc: 'Payment card data. Tokenisation, network segmentation.' },
  { id: 'eu-ai-act',  name: 'EU AI Act',      short: 'EU AI',  tier: 'advanced',
    desc: 'High-risk AI: risk assessment, transparency, human oversight, post-market monitoring.' },
  { id: 'nist-ai-rmf',name: 'NIST AI RMF',    short: 'NIST',   tier: 'advanced',
    desc: 'Govern · Map · Measure · Manage AI risks. Voluntary US framework.' },
  { id: 'fedramp',    name: 'FedRAMP Mod',    short: 'FedRAMP', tier: 'advanced',
    desc: 'US federal cloud authorization. Strict baseline of NIST 800-53 controls.' },
];

const SCAN_TOOLS = [
  { id: 'trivy',     name: 'Trivy',     cat: 'container', desc: 'Container image + filesystem CVE scanner. Aquasec, OSS.' },
  { id: 'grype',     name: 'Grype',     cat: 'container', desc: 'Image/SBOM CVE scanner. Anchore, OSS.' },
  { id: 'checkov',   name: 'Checkov',   cat: 'iac',       desc: 'IaC misconfig scanner — Terraform, K8s, CloudFormation, Helm.' },
  { id: 'tfsec',     name: 'tfsec',     cat: 'iac',       desc: 'Terraform-specific static analysis.' },
  { id: 'gitleaks',  name: 'Gitleaks',  cat: 'secrets',   desc: 'Secret detection in git history + working tree.' },
  { id: 'trufflehog',name: 'TruffleHog',cat: 'secrets',   desc: 'High-entropy + verifiable-credential secret scanner.' },
  { id: 'syft',      name: 'Syft',      cat: 'sbom',      desc: 'SBOM generation in CycloneDX / SPDX format.' },
  { id: 'semgrep',   name: 'Semgrep',   cat: 'sast',      desc: 'Pattern-based static analysis. OSS + paid.' },
  { id: 'falco',     name: 'Falco',     cat: 'runtime',   desc: 'Runtime threat detection in K8s. Syscall-based.' },
  { id: 'garak',     name: 'garak',     cat: 'ai-redteam',desc: 'LLM vulnerability scanner — prompt injection, jailbreaks, leakage probes.' },
  { id: 'pyrit',     name: 'PyRIT',     cat: 'ai-redteam',desc: 'Microsoft AI red-team automation.' },
  { id: 'promptfoo', name: 'Promptfoo', cat: 'ai-eval',   desc: 'Prompt regression + adversarial test runner.' },
  { id: 'modelscan', name: 'ModelScan', cat: 'ai-supply', desc: 'Pickle / safetensors scanning for malicious model weights.' },
];

const ALERT_CHANNELS = [
  { id: 'pagerduty', name: 'PagerDuty', desc: 'On-call paging' },
  { id: 'opsgenie',  name: 'Opsgenie',  desc: 'Atlassian on-call' },
  { id: 'slack',     name: 'Slack',     desc: 'Channel notifications' },
  { id: 'webhook',   name: 'Webhook',   desc: 'Generic HTTP POST' },
  { id: 'email',     name: 'Email',     desc: 'SMTP digest' },
];

// Compliance → control mapping (which artifact satisfies which control)
const COMPLIANCE_CONTROLS = {
  soc2: [
    { id: 'CC6.1', desc: 'Logical access controls',                        artifact: 'IAM least-priv + IRSA / Workload Identity (terraform/main.tf)' },
    { id: 'CC6.6', desc: 'Encryption in transit + at rest',                artifact: 'KMS keys (terraform/ai.tf, terraform/database.tf), TLS 1.3' },
    { id: 'CC7.1', desc: 'Vulnerability monitoring',                       artifact: 'Trivy + Grype CI scan (.github/workflows/security.yml)' },
    { id: 'CC7.2', desc: 'Detection of anomalies',                         artifact: 'Falco + alert rules (k8s/falco-rules.yaml, monitoring/alerts.yaml)' },
    { id: 'CC8.1', desc: 'Change management',                              artifact: 'CI/CD env promotion + approval gate' },
  ],
  iso42001: [
    { id: 'A.6.2.6', desc: 'AI system impact assessment',                  artifact: 'compliance/ai-impact-assessment.md' },
    { id: 'A.7.4',   desc: 'Resources for AI system development',          artifact: 'agent/ + agent/guardrails.py + evals/' },
    { id: 'A.8.2',   desc: 'AI risk management throughout lifecycle',      artifact: '.github/workflows/ai-redteam.yml + evals.yml' },
    { id: 'A.8.4',   desc: 'Verification & validation of AI',              artifact: 'evals/golden.jsonl + promptfoo regression' },
  ],
  gdpr: [
    { id: 'Art.5',  desc: 'Data minimisation + purpose limitation',        artifact: 'agent/guardrails.py PII scrubber pre-LLM' },
    { id: 'Art.17', desc: 'Right to erasure',                              artifact: 'compliance/data-subject-procedures.md' },
    { id: 'Art.25', desc: 'Privacy by design + default',                   artifact: 'PII redaction enabled by default in guardrails' },
    { id: 'Art.32', desc: 'Security of processing',                        artifact: 'KMS, TLS, NetworkPolicies, audit logs' },
    { id: 'Art.35', desc: 'DPIA for high-risk processing',                 artifact: 'compliance/dpia-template.md' },
  ],
  hipaa: [
    { id: '164.308', desc: 'Administrative safeguards',                    artifact: 'IAM RBAC + audit log retention 6yr' },
    { id: '164.310', desc: 'Physical safeguards',                          artifact: 'Cloud provider BAA (AWS/GCP/Azure)' },
    { id: '164.312', desc: 'Technical safeguards — encryption + audit',    artifact: 'KMS encryption, CloudTrail/Audit logs' },
    { id: '164.502', desc: 'Minimum necessary',                            artifact: 'PII redaction + denied-topics in guardrails' },
  ],
  'eu-ai-act': [
    { id: 'Art.9',  desc: 'Risk management system',                        artifact: 'compliance/ai-impact-assessment.md' },
    { id: 'Art.10', desc: 'Data + data governance',                        artifact: 'PII detection + retention policies + lineage' },
    { id: 'Art.12', desc: 'Logging — automatic recording of events',       artifact: 'agent/audit_log.py + structured tracing' },
    { id: 'Art.13', desc: 'Transparency — instructions for use',           artifact: 'directory/agent.json + README' },
    { id: 'Art.14', desc: 'Human oversight',                               artifact: 'COPILOT/SEMI autonomy + escalation paths' },
    { id: 'Art.15', desc: 'Accuracy + robustness + cybersecurity',         artifact: 'evals + AI red-team workflow + guardrails' },
    { id: 'Art.61', desc: 'Post-market monitoring',                        artifact: 'LLM observability + alert rules + drift detection' },
  ],
  'nist-ai-rmf': [
    { id: 'GOVERN.1', desc: 'AI risk management policies',                 artifact: 'compliance/ai-policy.md' },
    { id: 'MAP.5',    desc: 'Impacts to individuals + groups',             artifact: 'compliance/ai-impact-assessment.md' },
    { id: 'MEASURE.2',desc: 'Trustworthy characteristics measurement',     artifact: 'evals + LLM observability dashboards' },
    { id: 'MANAGE.1', desc: 'AI risks based on assessments',               artifact: 'AI red-team workflow + alert rules' },
  ],
  iso27001: [
    { id: 'A.5',   desc: 'Information security policies',                  artifact: 'compliance/security-policy.md' },
    { id: 'A.8',   desc: 'Asset management',                               artifact: 'SBOM (Syft) + tag taxonomy in Terraform' },
    { id: 'A.12',  desc: 'Operations security',                            artifact: 'CI/CD scans + Falco runtime detection' },
    { id: 'A.14',  desc: 'System acquisition & development security',      artifact: 'IaC scanning + SAST + secret scanning' },
  ],
  'pci-dss': [
    { id: 'Req.3', desc: 'Protect stored card data',                       artifact: 'KMS encryption + tokenisation guardrail' },
    { id: 'Req.4', desc: 'Encrypt transmission of card data',              artifact: 'TLS 1.3 + cert-manager' },
    { id: 'Req.6', desc: 'Develop + maintain secure systems',              artifact: 'Trivy + Checkov + Semgrep CI gates' },
    { id: 'Req.10',desc: 'Track + monitor access',                         artifact: 'CloudTrail + audit_log.py + Falco' },
  ],
  fedramp: [
    { id: 'AC-2',  desc: 'Account management',                             artifact: 'IAM + IRSA + Secrets Manager' },
    { id: 'AU-2',  desc: 'Audit events',                                   artifact: 'CloudTrail + structured agent audit logs' },
    { id: 'SI-4',  desc: 'System monitoring',                              artifact: 'Falco + alert rules + LLM observability' },
    { id: 'CM-7',  desc: 'Least functionality',                            artifact: 'PSS restricted + NetworkPolicies default-deny' },
  ],
};

const ENVIRONMENTS = ['dev', 'staging', 'prod'];

// ---------- step definitions ----------
const STEPS = [
  { id: 'project',   label: 'Project',       icon: Settings },
  { id: 'cloud',     label: 'Cloud',         icon: Cloud },
  { id: 'compute',   label: 'Compute',       icon: Cpu },
  { id: 'data',      label: 'Data',          icon: Database },
  { id: 'data-eng',  label: 'Data Eng',      icon: HardDrive },
  { id: 'ai',        label: 'AI / Agents',   icon: Sparkles },
  { id: 'security',  label: 'Security',      icon: ShieldCheck },
  { id: 'network',   label: 'Networking',    icon: Network },
  { id: 'obs',       label: 'Observability', icon: Activity },
  { id: 'cicd',      label: 'CI/CD',         icon: Workflow },
  { id: 'rollout',   label: 'Rollout',       icon: GitBranch },
  { id: 'directory', label: 'Directory',     icon: Globe },
  { id: 'output',    label: 'Generate',      icon: Rocket },
];

// ── Rollout teams (issue tracker categories) ────────────────────
const ROLLOUT_TEAMS = [
  { id: 'platform-net',     name: 'Platform · Networking', color: '#60a5fa', icon: Network,    desc: 'VPC, subnets, routing, DNS, edge.' },
  { id: 'security',         name: 'Security',              color: '#be123c', icon: Shield,     desc: 'KMS, secrets, IAM, WAF, compliance.' },
  { id: 'platform-compute', name: 'Platform · Compute',    color: '#bef264', icon: Cpu,        desc: 'Cluster, node groups, runtime.' },
  { id: 'data',             name: 'Data Platform',         color: '#a78bfa', icon: Database,   desc: 'DB, cache, backups, schema.' },
  { id: 'ml',               name: 'ML Platform',           color: '#f0abfc', icon: Sparkles,   desc: 'Inference, vector DB, agents, evals.' },
  { id: 'sre',              name: 'SRE · Observability',   color: '#b45309', icon: Activity,   desc: 'Metrics, logs, traces, SLOs, paging.' },
  { id: 'devops',           name: 'DevOps · CI/CD',        color: '#2dd4bf', icon: Workflow,   desc: 'Pipelines, OIDC, environments.' },
  { id: 'release',          name: 'Release Engineering',   color: '#52524d', icon: Rocket,     desc: 'Cutover, smoke tests, runbooks.' },
];

const ISSUE_TRACKERS = [
  { id: 'jira',   name: 'Jira',          desc: 'REST v3 · API token · curl-based bulk creation script.' },
  { id: 'linear', name: 'Linear',        desc: 'GraphQL API · API key · bulk import via CLI.' },
  { id: 'github', name: 'GitHub Issues', desc: 'Issues API via gh CLI · per-team labels.' },
  { id: 'csv',    name: 'CSV export',    desc: 'Universal — import into any tool that takes CSV.' },
];

// ── agentic.ai directory integration ──────────────────────────────
// 8 dimensions × 0–4 = 32-point agenticness scale
const AGENTIC_DIMENSIONS = [
  { id: 'goal',     name: 'Goal handling',  desc: 'Takes high-level goals and decomposes into actions.' },
  { id: 'tools',    name: 'Tool use',       desc: 'Number and breadth of integrations available.' },
  { id: 'planning', name: 'Planning',       desc: 'Multi-step decision graphs with conditional flow.' },
  { id: 'memory',   name: 'Memory',         desc: 'Persistent state and context retrieval.' },
  { id: 'autonomy', name: 'Autonomy',       desc: 'Operates without per-step approval.' },
  { id: 'observe',  name: 'Observability',  desc: 'Tracing, evals, and audit trail.' },
  { id: 'adapt',    name: 'Adaptation',     desc: 'Self-correction and replanning on failure.' },
  { id: 'reach',    name: 'Domain reach',   desc: 'Narrow specialist vs broad generalist.' },
];

const AGENTIC_LEVELS = [
  { min: 0,  max: 5,  level: 'L0', name: 'Reactive Tool',         desc: 'Responds to prompts but takes no autonomous action.' },
  { min: 6,  max: 11, level: 'L1', name: 'Guided Assistant',      desc: 'Executes tasks you assign, one step at a time.' },
  { min: 12, max: 17, level: 'L2', name: 'Adaptive Collaborator', desc: 'Proposes and executes multi-step plans with approval.' },
  { min: 18, max: 23, level: 'L3', name: 'Domain Specialist',     desc: 'Handles domain-specific workflows independently.' },
  { min: 24, max: 28, level: 'L4', name: 'Autonomous Operator',   desc: 'Manages cross-domain workflows with self-correction.' },
  { min: 29, max: 32, level: 'L5', name: 'Strategic Agent',       desc: 'Fully self-directed — sets own goals, operates continuously.' },
];

// Curated subset of agentic.ai categories — those most likely for STAX-generated agents
const AGENTIC_CATEGORIES = [
  { id: 'engineering-devtools',       name: 'Engineering & DevTools' },
  { id: 'data-analytics',             name: 'Data & Analytics' },
  { id: 'customer-support',           name: 'Customer Support' },
  { id: 'sales-marketing',            name: 'Sales & Marketing' },
  { id: 'research-intelligence',      name: 'Research & Intelligence' },
  { id: 'workflow-automation',        name: 'Workflow Automation' },
  { id: 'agent-frameworks',           name: 'Agent Frameworks' },
  { id: 'agent-infrastructure',       name: 'Agent Infrastructure' },
  { id: 'enterprise-agent-platforms', name: 'Enterprise Agent Platforms' },
  { id: 'voice-phone-agents',         name: 'Voice & Phone Agents' },
  { id: 'browser-automation',         name: 'Browser Automation Agents' },
  { id: 'multi-agent-orchestration',  name: 'Multi-Agent Orchestration' },
  { id: 'cybersecurity-ai',           name: 'Cybersecurity AI' },
  { id: 'legal-ai',                   name: 'Legal AI' },
  { id: 'health-wellness',            name: 'Health & Wellness' },
  { id: 'finance-planning',           name: 'Finance & Planning' },
  { id: 'hr-operations',              name: 'HR & Operations' },
  { id: 'general-purpose-agents',     name: 'General-Purpose AI Agents' },
];

const AUTONOMY_LEVELS = [
  { id: 'copilot', name: 'Copilot',          tag: 'COPILOT',           desc: 'User approves every action. Highest safety, lowest velocity.' },
  { id: 'semi',    name: 'Semi-autonomous',  tag: 'SEMI_AUTONOMOUS',   desc: 'Auto-runs within guardrails, escalates on uncertainty.' },
  { id: 'fully',   name: 'Fully autonomous', tag: 'FULLY_AUTONOMOUS',  desc: 'Runs without supervision. Requires evals + observability.' },
];

const PRICING_MODELS = [
  { id: 'FREE_OSS',     name: 'Free / OSS' },
  { id: 'FREEMIUM',     name: 'Freemium' },
  { id: 'SUBSCRIPTION', name: 'Subscription' },
  { id: 'PER_SEAT',     name: 'Per seat' },
  { id: 'USAGE_BASED',  name: 'Usage-based' },
  { id: 'HYBRID',       name: 'Hybrid' },
];

// ---------- helpers ----------
const fmt = (s) => s.replace(/[^a-z0-9-]/gi, '-').toLowerCase();
const isK8s = (c) => ['eks','gke','aks'].includes(c);
const isContainer = (c) => ['eks','gke','aks','ecs','run','aca'].includes(c);
const isFunctions = (c) => ['lambda','gcf','func'].includes(c);
const isVM = (c) => ['ec2','gce','vmss'].includes(c);

const computeName = (cfg) => {
  const all = [...COMPUTE_BY_PROVIDER.aws, ...COMPUTE_BY_PROVIDER.gcp, ...COMPUTE_BY_PROVIDER.azure];
  return all.find(x => x.id === cfg.compute)?.name || cfg.compute;
};
const dbName = (cfg) => {
  const all = [...DATABASE_OPTIONS.aws, ...DATABASE_OPTIONS.gcp, ...DATABASE_OPTIONS.azure];
  return all.find(x => x.id === cfg.database)?.name || cfg.database;
};

// AI helpers
const isAIEnabled       = (cfg) => cfg.aiEnabled === true;
const isSelfHostedLLM   = (cfg) => isAIEnabled(cfg) && ['vllm-eks','vllm-gke','vllm-aks'].includes(cfg.aiInference);
const isManagedLLM      = (cfg) => isAIEnabled(cfg) && ['bedrock','sagemaker','vertex','aoai'].includes(cfg.aiInference);
const isExternalLLM     = (cfg) => isAIEnabled(cfg) && cfg.aiInference === 'external';
const needsGPU          = (cfg) => isSelfHostedLLM(cfg);
const aiInferenceName   = (cfg) => {
  const all = [...AI_INFERENCE_OPTIONS.aws, ...AI_INFERENCE_OPTIONS.gcp, ...AI_INFERENCE_OPTIONS.azure];
  return all.find(x => x.id === cfg.aiInference)?.name || cfg.aiInference;
};
const vectorDBName      = (cfg) => VECTOR_DB_OPTIONS.find(x => x.id === cfg.vectorDB)?.name || cfg.vectorDB;
const agentFwName       = (cfg) => AGENT_FRAMEWORK_OPTIONS.find(x => x.id === cfg.agentFramework)?.name || cfg.agentFramework;

// ── agentic.ai scoring + structured-field inference ─────────────
function inferDeploymentModel(cfg) {
  if (isSelfHostedLLM(cfg)) return 'SELF_HOSTED';
  if (isExternalLLM(cfg))   return 'HYBRID';
  return 'CLOUD';
}
function inferModelAgnosticism(cfg) {
  if (isSelfHostedLLM(cfg)) return 'SUPPORTS_LOCAL';
  if (cfg.aiInference === 'bedrock' || cfg.aiInference === 'vertex') return 'MULTI_MODEL';
  return 'SINGLE_MODEL';
}
function inferAutonomyTag(cfg) {
  return AUTONOMY_LEVELS.find(a => a.id === cfg.directoryAutonomy)?.tag || 'COPILOT';
}
function inferPrimaryInterface(cfg) {
  // STAX-generated agents expose HTTP /chat by default
  return 'api';
}

function computeAgenticness(cfg) {
  if (!isAIEnabled(cfg)) {
    return { dimensions: {}, total: 0, level: AGENTIC_LEVELS[0], enabled: false, reasons: {} };
  }
  const fw = cfg.agentFramework;
  const reasons = {};
  const dims = {};

  // ── Goal handling ────────────────────────────────────────
  if (fw === 'langgraph' || fw === 'crewai')        { dims.goal = 4; reasons.goal = 'Stateful framework with goal-state machines.'; }
  else if (fw === 'pydanticai' || fw === 'llamaindex') { dims.goal = 3; reasons.goal = 'Framework supports structured goals + tool calls.'; }
  else                                               { dims.goal = 2; reasons.goal = 'Direct LLM calls — single-shot goal handling.'; }

  // ── Tool use ──────────────────────────────────────────────
  let toolCount = 1; // LLM call counts as 1
  if (cfg.vectorDB !== 'none')      toolCount += 1;
  if (cfg.directoryMcpEnabled)      toolCount += 1;
  if (cfg.directoryWebTool)         toolCount += 1;
  if (cfg.directoryCodeExecution)   toolCount += 1;
  dims.tools = Math.min(4, toolCount);
  reasons.tools = `${toolCount} tool surface${toolCount !== 1 ? 's' : ''} (LLM${cfg.vectorDB !== 'none' ? ' + vector retrieval' : ''}${cfg.directoryMcpEnabled ? ' + MCP' : ''}${cfg.directoryWebTool ? ' + web' : ''}${cfg.directoryCodeExecution ? ' + code exec' : ''}).`;

  // ── Planning ──────────────────────────────────────────────
  if (fw === 'langgraph')      { dims.planning = 4; reasons.planning = 'Cyclic graph with conditional edges + state.'; }
  else if (fw === 'crewai')    { dims.planning = 4; reasons.planning = 'Multi-agent task decomposition with role specialisation.'; }
  else if (fw === 'pydanticai'){ dims.planning = 3; reasons.planning = 'Type-safe tool selection with structured outputs.'; }
  else if (fw === 'llamaindex'){ dims.planning = 3; reasons.planning = 'ReAct loop with retrieval-augmented planning.'; }
  else                         { dims.planning = 1; reasons.planning = 'No framework — single-step planning only.'; }

  // ── Memory ────────────────────────────────────────────────
  if (cfg.vectorDB === 'none')                   { dims.memory = 0; reasons.memory = 'No vector store — no persistent retrieval.'; }
  else if (cfg.vectorDB === 'opensearch')        { dims.memory = 4; reasons.memory = 'Hybrid lexical + dense retrieval at scale.'; }
  else if (cfg.vectorDB === 'pgvector')          { dims.memory = 3; reasons.memory = 'pgvector — transactional joins with retrieval.'; }
  else                                            { dims.memory = 3; reasons.memory = `${vectorDBName(cfg)} — dedicated vector store.`; }

  // ── Autonomy ──────────────────────────────────────────────
  if (cfg.directoryAutonomy === 'fully')      { dims.autonomy = 4; reasons.autonomy = 'Runs without per-step approval.'; }
  else if (cfg.directoryAutonomy === 'semi')  { dims.autonomy = 3; reasons.autonomy = 'Auto-runs within guardrails, escalates on uncertainty.'; }
  else                                         { dims.autonomy = 2; reasons.autonomy = 'Copilot mode — user approves each action.'; }

  // ── Observability ────────────────────────────────────────
  let obs = 0;
  if (cfg.llmObservability !== 'none')   obs += 2;
  if (cfg.observability !== 'none')      obs += 1;
  if (cfg.enableEvals)                   obs += 1;
  dims.observe = Math.min(4, obs);
  reasons.observe = `${cfg.llmObservability !== 'none' ? `LLM tracing via ${LLM_OBSERVABILITY.find(o => o.id === cfg.llmObservability)?.name}` : 'no LLM tracing'}${cfg.enableEvals ? ' + eval gate' : ''}.`;

  // ── Adaptation ───────────────────────────────────────────
  let adapt = 0;
  if (fw === 'langgraph')                                            adapt += 2;
  else if (fw === 'crewai' || fw === 'pydanticai')                   adapt += 1;
  if (cfg.llmObservability !== 'none' && cfg.enableEvals)            adapt += 2;
  dims.adapt = Math.min(4, adapt);
  reasons.adapt = adapt >= 3 ? 'Cyclic replanning + eval-driven self-correction.'
                : adapt >= 1 ? 'Some replanning capability.'
                : 'No self-correction — single-pass execution.';

  // ── Domain reach ─────────────────────────────────────────
  dims.reach = Math.max(1, Math.min(4, cfg.directoryReach || 2));
  reasons.reach = dims.reach >= 4 ? 'Broad generalist — multi-domain.'
                : dims.reach >= 3 ? 'Multi-skill within domain.'
                : dims.reach >= 2 ? 'Domain-specialised.'
                : 'Narrow single-task agent.';

  const total = Object.values(dims).reduce((a, b) => a + b, 0);
  const level = AGENTIC_LEVELS.find(l => total >= l.min && total <= l.max) || AGENTIC_LEVELS[0];
  return { dimensions: dims, total, level, enabled: true, reasons };
}

// ── Security posture scoring (100-point scale) ──────────────────
// Each control has a weight reflecting its real-world impact.
// The total reflects defense-in-depth coverage, not absolute safety.
const SECURITY_CONTROLS = [
  // ── AI/LLM defense (44 pts) ─────────────────────────────────
  { id: 'prompt-firewall',    name: 'Prompt firewall',       weight: 12, layer: 'ai',
    check: (c) => isAIEnabled(c) && c.promptFirewall && c.promptFirewall !== 'none' },
  { id: 'pii-redaction',      name: 'PII redaction',         weight: 8,  layer: 'ai',
    check: (c) => isAIEnabled(c) && c.piiDetector && c.piiDetector !== 'none' },
  { id: 'output-moderation',  name: 'Output moderation',     weight: 6,  layer: 'ai',
    check: (c) => isAIEnabled(c) && c.outputModeration === true },
  { id: 'denied-topics',      name: 'Denied-topic filter',   weight: 4,  layer: 'ai',
    check: (c) => isAIEnabled(c) && c.deniedTopics === true },
  { id: 'audit-log',          name: 'LLM audit log',         weight: 6,  layer: 'ai',
    check: (c) => isAIEnabled(c) && c.llmAuditLog === true },
  { id: 'ai-redteam',         name: 'AI red-team scans',     weight: 8,  layer: 'ai',
    check: (c) => isAIEnabled(c) && (c.scanTools || []).some(s => ['garak','pyrit','promptfoo'].includes(s)) },

  // ── MCP security (12 pts) ───────────────────────────────────
  { id: 'mcp-allowlist',      name: 'MCP server allowlist',  weight: 6,  layer: 'mcp',
    check: (c) => !c.directoryMcpEnabled || c.mcpAllowlist === true },
  { id: 'mcp-signing',        name: 'MCP tool signature verify', weight: 6, layer: 'mcp',
    check: (c) => !c.directoryMcpEnabled || c.mcpSignatureVerify === true },

  // ── Infra hardening (24 pts) ────────────────────────────────
  { id: 'kms',                name: 'KMS encryption',        weight: 4,  layer: 'infra',
    check: (c) => true /* always generated */ },
  { id: 'secrets-mgr',        name: 'Secrets manager',       weight: 4,  layer: 'infra',
    check: (c) => c.secretsManager === true },
  { id: 'waf',                name: 'WAF',                   weight: 4,  layer: 'infra',
    check: (c) => c.waf === true },
  { id: 'netpol',             name: 'NetworkPolicies (default-deny)', weight: 4, layer: 'infra',
    check: (c) => isK8s(c.compute) && c.networkPoliciesDefault === true },
  { id: 'pss',                name: 'Pod Security Standards (restricted)', weight: 4, layer: 'infra',
    check: (c) => isK8s(c.compute) && c.podSecurityRestricted === true },
  { id: 'falco',              name: 'Runtime threat detection', weight: 4, layer: 'infra',
    check: (c) => isK8s(c.compute) && (c.scanTools || []).includes('falco') },

  // ── Continuous scans (14 pts) ───────────────────────────────
  { id: 'cve',                name: 'Container CVE scan',    weight: 4,  layer: 'scan',
    check: (c) => (c.scanTools || []).some(s => ['trivy','grype'].includes(s)) },
  { id: 'iac-scan',           name: 'IaC scan',              weight: 3,  layer: 'scan',
    check: (c) => (c.scanTools || []).some(s => ['checkov','tfsec'].includes(s)) },
  { id: 'secret-scan',        name: 'Secret scan',           weight: 3,  layer: 'scan',
    check: (c) => (c.scanTools || []).some(s => ['gitleaks','trufflehog'].includes(s)) },
  { id: 'sbom',               name: 'SBOM',                  weight: 2,  layer: 'scan',
    check: (c) => (c.scanTools || []).includes('syft') },
  { id: 'sast',               name: 'SAST',                  weight: 2,  layer: 'scan',
    check: (c) => (c.scanTools || []).includes('semgrep') },

  // ── Compliance + monitoring (6 pts) ─────────────────────────
  { id: 'compliance',         name: 'Compliance frameworks chosen', weight: 3, layer: 'compliance',
    check: (c) => (c.complianceFrameworks || []).length > 0 },
  { id: 'alerting',           name: 'Security alert routing', weight: 3, layer: 'compliance',
    check: (c) => (c.alertChannels || []).length > 0 },
];

function computeSecurityPosture(cfg) {
  const breakdown = SECURITY_CONTROLS.map(c => ({
    ...c,
    passing: c.check(cfg),
  }));
  const total = breakdown.reduce((sum, c) => sum + (c.passing ? c.weight : 0), 0);
  const max = SECURITY_CONTROLS.reduce((sum, c) => sum + c.weight, 0);
  const grade =
    total >= 90 ? { letter: 'A',  color: '#bef264', label: 'Strong defense-in-depth' } :
    total >= 75 ? { letter: 'B',  color: '#86efac', label: 'Solid baseline, gaps exist' } :
    total >= 60 ? { letter: 'C',  color: '#b45309', label: 'Functional, missing AI-specific controls' } :
    total >= 40 ? { letter: 'D',  color: '#fb923c', label: 'Significant gaps — not production-ready' } :
                  { letter: 'F',  color: '#be123c', label: 'Critical risk. Do not ship to prod.' };
  return { breakdown, total, max, grade };
}

const promptFirewallName = (cfg) => PROMPT_FIREWALLS.find(x => x.id === cfg.promptFirewall)?.name || cfg.promptFirewall;
const piiDetectorName    = (cfg) => PII_DETECTORS.find(x => x.id === cfg.piiDetector)?.name || cfg.piiDetector;

// ── pure-JS ZIP writer (store, no compression) ─────────────────────
// Implements PKZIP minimal subset: local file header + central dir + EOCD.
// CRC32 via Sarwate-style table.
const _crcTable = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[n] = c >>> 0;
  }
  return t;
})();
function _crc32(bytes) {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = _crcTable[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}
function _dosTime(d = new Date()) {
  const time = ((d.getHours() & 0x1F) << 11) | ((d.getMinutes() & 0x3F) << 5) | ((Math.floor(d.getSeconds() / 2)) & 0x1F);
  const date = (((d.getFullYear() - 1980) & 0x7F) << 9) | (((d.getMonth() + 1) & 0x0F) << 5) | (d.getDate() & 0x1F);
  return { time, date };
}
function makeZipBlob(files) {
  // files = [{ path: string, content: string }]
  const enc = new TextEncoder();
  const { time, date } = _dosTime();
  const localChunks = [];
  const centralChunks = [];
  let offset = 0;

  for (const f of files) {
    const nameBytes = enc.encode(f.path);
    const dataBytes = enc.encode(f.content);
    const crc = _crc32(dataBytes);
    const size = dataBytes.length;

    // Local file header (30 bytes + name + data)
    const local = new Uint8Array(30 + nameBytes.length + dataBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);     // version needed
    lv.setUint16(6, 0, true);      // flags
    lv.setUint16(8, 0, true);      // method = store
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);
    lv.setUint32(22, size, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    local.set(nameBytes, 30);
    local.set(dataBytes, 30 + nameBytes.length);
    localChunks.push(local);

    // Central directory header (46 bytes + name)
    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);     // version made by
    cv.setUint16(6, 20, true);     // version needed
    cv.setUint16(8, 0, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, offset, true);
    central.set(nameBytes, 46);
    centralChunks.push(central);

    offset += local.length;
  }

  const centralSize = centralChunks.reduce((s, c) => s + c.length, 0);
  const centralOffset = offset;

  // End of central directory (22 bytes)
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, centralOffset, true);
  ev.setUint16(20, 0, true);

  return new Blob([...localChunks, ...centralChunks, eocd], { type: 'application/zip' });
}
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Rollout DAG builder ─────────────────────────────────────────
// Produces an ordered list of tickets with team assignment + blocked_by deps.
// Tickets are grouped into "waves" via topological levels — wave N can only start
// after every ticket in wave < N is done. This is the order ops actually executes.
function buildRolloutPlan(cfg) {
  if (!cfg.rolloutEnabled) return { tickets: [], waves: [], teams: ROLLOUT_TEAMS };

  const prefix = (cfg.ticketPrefix || 'INFRA').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const tickets = [];
  let n = 1;
  const add = (team, summary, deps = [], extras = {}) => {
    const tk = {
      key: `${prefix}-${n++}`,
      team,
      summary,
      blocked_by: deps.filter(Boolean),
      effort: extras.effort ?? 2,
      labels: ['stax-generated', `env-${cfg.environment}`, ...(extras.labels || [])],
      description: extras.description || '',
      component: extras.component || team,
    };
    tickets.push(tk);
    return tk.key;
  };

  // ── Networking ───────────────────────────────────────────
  const VPC = add('platform-net',
    `Provision VPC + multi-AZ subnets (${cfg.provider === 'aws' ? 'aws_vpc + 6 subnets' : cfg.provider === 'gcp' ? 'google_compute_network' : 'azurerm_virtual_network'})`,
    [], { effort: 3, labels: ['terraform','networking'], description: 'Apply terraform/networking.tf — creates VPC 10.0.0.0/16 with 3 public + 3 private subnets and NAT egress.' });
  const SG = add('platform-net',
    'Configure security groups (app, db, cache tiers)',
    [VPC], { effort: 2, labels: ['security-groups'] });
  const ROUTING = add('platform-net',
    'Validate routing tables, NAT, IGW + flow logs',
    [VPC], { effort: 2, labels: ['routing','flow-logs'] });

  // ── Security foundation ─────────────────────────────────
  const KMS = add('security',
    'Provision KMS / CMEK keys for encryption at rest',
    [VPC], { effort: 2, labels: ['kms','encryption'] });
  const SECRETS = cfg.secretsManager
    ? add('security', `Configure ${cfg.provider === 'aws' ? 'Secrets Manager' : cfg.provider === 'gcp' ? 'Secret Manager' : 'Key Vault'} + rotation policies`,
        [KMS], { effort: 2, labels: ['secrets','rotation'] })
    : null;
  const WAF = cfg.waf
    ? add('security', 'Configure WAF rules (OWASP top 10 + rate limit)',
        [VPC], { effort: 3, labels: ['waf','owasp'] })
    : null;
  const IAM_BASE = add('security',
    'Bootstrap IAM roles + OIDC provider for CI/CD',
    [VPC], { effort: 3, labels: ['iam','oidc'] });

  // ── Compute platform ────────────────────────────────────
  const CLUSTER = isContainer(cfg.compute) || isFunctions(cfg.compute) || isVM(cfg.compute)
    ? add('platform-compute',
        `Provision ${computeName(cfg)} (control plane / runtime)`,
        [SG, KMS, IAM_BASE], { effort: isK8s(cfg.compute) ? 5 : 3, labels: ['compute', cfg.compute] })
    : null;
  const NODE_GROUPS = isK8s(cfg.compute)
    ? add('platform-compute',
        'Configure node groups + autoscaling + pod identity (IRSA / Workload Identity)',
        [CLUSTER], { effort: 3, labels: ['nodes','autoscaling'] })
    : null;
  const ADDONS = isK8s(cfg.compute)
    ? add('platform-compute',
        'Install cluster add-ons (CNI, CSI, ingress controller, cert-manager)',
        [NODE_GROUPS], { effort: 3, labels: ['addons','helm'] })
    : null;
  const COMPUTE_READY = ADDONS || NODE_GROUPS || CLUSTER;

  // ── Data platform ───────────────────────────────────────
  const DB = cfg.database !== 'none'
    ? add('data',
        `Provision ${dbName(cfg)} (multi-AZ, encrypted)`,
        [SG, KMS, SECRETS].filter(Boolean), { effort: 3, labels: ['database', cfg.database] })
    : null;
  const DB_BACKUP = DB
    ? add('data',
        'Configure automated backups + point-in-time recovery + retention policy',
        [DB], { effort: 2, labels: ['backups','dr'] })
    : null;
  const DB_USERS = DB
    ? add('data',
        'Create application DB users via IAM auth + grant minimum privileges',
        [DB], { effort: 2, labels: ['iam-auth','schema'] })
    : null;
  const CACHE = cfg.cache === 'redis'
    ? add('data', 'Provision Redis cluster + configure connection pooling',
        [SG, KMS], { effort: 2, labels: ['cache','redis'] })
    : null;

  // ── ML platform (only if AI enabled) ────────────────────
  let ML_TICKETS = [];
  if (isAIEnabled(cfg)) {
    const GPU_NODES = needsGPU(cfg)
      ? add('ml',
          `Provision GPU node group (${(cfg.gpuType || '').split(' ')[0]}) + NVIDIA device plugin`,
          [COMPUTE_READY], { effort: 5, labels: ['gpu','nvidia'] })
      : null;
    const INFERENCE = isManagedLLM(cfg)
      ? add('ml',
          `Request foundation model access · ${aiInferenceName(cfg)}`,
          [IAM_BASE], { effort: 2, labels: ['inference','model-access'] })
      : isSelfHostedLLM(cfg)
        ? add('ml',
            `Deploy vLLM on GPU nodes + load ${OPEN_MODELS[0].split('/')[1]}`,
            [GPU_NODES, SECRETS].filter(Boolean), { effort: 5, labels: ['vllm','self-hosted'] })
        : add('ml',
            'Configure external LLM provider proxy + Secrets Manager + budget alarms',
            [SECRETS, IAM_BASE].filter(Boolean), { effort: 3, labels: ['external-llm','cost-controls'] });
    const VECTOR = cfg.vectorDB === 'pgvector'
      ? add('ml', 'Install pgvector extension + create embeddings schema + ivfflat index',
          [DB].filter(Boolean), { effort: 2, labels: ['pgvector','rag'] })
      : cfg.vectorDB === 'opensearch'
        ? add('ml', 'Provision OpenSearch domain + configure k-NN index mappings',
            [SG, KMS], { effort: 4, labels: ['opensearch','knn'] })
        : cfg.vectorDB === 'qdrant' || cfg.vectorDB === 'weaviate'
          ? add('ml', `Deploy ${cfg.vectorDB} on K8s + configure persistent volumes`,
              [COMPUTE_READY], { effort: 3, labels: [cfg.vectorDB,'self-hosted'] })
          : cfg.vectorDB === 'pinecone'
            ? add('ml', 'Configure Pinecone index + store API key in Secrets Manager',
                [SECRETS].filter(Boolean), { effort: 1, labels: ['pinecone','saas'] })
            : null;
    const AGENT = add('ml',
      `Build + deploy agent service (${agentFwName(cfg)})`,
      [INFERENCE, VECTOR, COMPUTE_READY].filter(Boolean), { effort: 5, labels: ['agent','fastapi'] });
    const LLM_OBS = cfg.llmObservability !== 'none'
      ? add('ml', `Wire LLM observability — ${LLM_OBSERVABILITY.find(o => o.id === cfg.llmObservability)?.name}`,
          [AGENT], { effort: 2, labels: ['llm-obs','tracing'] })
      : null;
    const EVALS = cfg.llmObservability !== 'none'
      ? add('ml', 'Configure eval pipeline (golden set + thresholds + PR gate)',
          [LLM_OBS, AGENT].filter(Boolean), { effort: 3, labels: ['evals','quality'] })
      : null;
    ML_TICKETS = [GPU_NODES, INFERENCE, VECTOR, AGENT, LLM_OBS, EVALS].filter(Boolean);
  }

  // ── Observability ───────────────────────────────────────
  const APP_OBS = COMPUTE_READY
    ? add('sre',
        `Install ${OBSERVABILITY_OPTIONS.find(x=>x.id===cfg.observability)?.name} agent / stack`,
        [COMPUTE_READY], { effort: 3, labels: ['observability', cfg.observability] })
    : null;
  const DASHBOARDS = APP_OBS
    ? add('sre', 'Build SLO dashboards (latency, error rate, saturation, requests/min)',
        [APP_OBS, DB].filter(Boolean), { effort: 3, labels: ['slo','dashboards'] })
    : null;
  const ALERTING = APP_OBS
    ? add('sre', 'Configure paging + on-call rotation + alert routing',
        [DASHBOARDS], { effort: 2, labels: ['paging','alerts'] })
    : null;

  // ── DevOps / CI/CD ──────────────────────────────────────
  const CICD_PIPE = add('devops',
    `Configure ${CICD_OPTIONS.find(x=>x.id===cfg.cicd)?.name} pipeline + OIDC federation`,
    [IAM_BASE], { effort: 3, labels: ['cicd', cfg.cicd] });
  const CICD_ENV = add('devops',
    'Set up environment promotion (dev → staging → prod) + manual approval gate',
    [CICD_PIPE], { effort: 2, labels: ['environments','approvals'] });
  const CICD_FIRST = add('devops',
    'First successful deploy to dev — image build + push + apply',
    [CICD_ENV, COMPUTE_READY].filter(Boolean), { effort: 2, labels: ['first-deploy'] });

  // ── Release / cutover (final wave) ──────────────────────
  const RELEASE_BLOCKERS = [CICD_FIRST, ALERTING, DB_USERS, ...ML_TICKETS, WAF].filter(Boolean);
  const SMOKE = add('release',
    'Run smoke tests against deployed stack + verify all probes green',
    RELEASE_BLOCKERS, { effort: 2, labels: ['smoke','verification'] });
  const DNS = cfg.cdn
    ? add('release', 'DNS cutover · update Route53 / Cloud DNS to new edge endpoint',
        [SMOKE], { effort: 2, labels: ['dns','cutover'] })
    : null;
  const RUNBOOK = add('release',
    'Publish runbook + on-call handoff + tag release v1.0.0',
    [DNS || SMOKE], { effort: 2, labels: ['runbook','handoff'] });

  // ── Compute waves via topological levels ────────────────
  const byKey = Object.fromEntries(tickets.map(t => [t.key, t]));
  const waveOf = {};
  const computeWave = (key) => {
    if (waveOf[key] != null) return waveOf[key];
    const t = byKey[key];
    if (!t.blocked_by.length) return waveOf[key] = 0;
    return waveOf[key] = 1 + Math.max(...t.blocked_by.map(computeWave));
  };
  tickets.forEach(t => { t.wave = computeWave(t.key); });

  // Group by wave for rendering
  const maxWave = Math.max(...tickets.map(t => t.wave));
  const waves = Array.from({ length: maxWave + 1 }, (_, i) => tickets.filter(t => t.wave === i));

  return { tickets, waves, teams: ROLLOUT_TEAMS };
}

// =============================================================
// CODE GENERATORS
// =============================================================

const tfHeader = (cfg) => `# ─────────────────────────────────────────────────────────────────
# ${cfg.projectName} · ${cfg.environment}
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
# Provider: ${cfg.provider.toUpperCase()} · Region: ${cfg.region}${cfg._byoi ? `
# BYOI: ${cfg._byoi.tfBackend} backend · ${cfg._byoi.registry} registry${cfg._byoi.ssoProvider !== 'none' ? ' · ' + cfg._byoi.ssoProvider + ' SSO' : ''}` : ''}
# ─────────────────────────────────────────────────────────────────
`;

function genTerraformMain(cfg) {
  const providerBlocks = {
    aws: `provider "aws" {
  region = var.region

  default_tags {
    tags = local.common_tags
  }
}`,
    gcp: `provider "google" {
  project = var.gcp_project_id
  region  = var.region
}

provider "google-beta" {
  project = var.gcp_project_id
  region  = var.region
}`,
    azure: `provider "azurerm" {
  features {
    key_vault {
      purge_soft_delete_on_destroy    = false
      recover_soft_deleted_key_vaults = true
    }
  }
}`,
  };

  const requiredProviders = {
    aws: `    aws = {
      source  = "hashicorp/aws"
      version = "~> 5.40"
    }${isK8s(cfg.compute) ? `
    kubernetes = {
      source  = "hashicorp/kubernetes"
      version = "~> 2.27"
    }
    helm = {
      source  = "hashicorp/helm"
      version = "~> 2.13"
    }` : ''}`,
    gcp: `    google = {
      source  = "hashicorp/google"
      version = "~> 5.20"
    }
    google-beta = {
      source  = "hashicorp/google-beta"
      version = "~> 5.20"
    }`,
    azure: `    azurerm = {
      source  = "hashicorp/azurerm"
      version = "~> 3.95"
    }`,
  };

  // BYOI override: when team plan supplies custom backend config, use it
  const byoi = cfg._byoi || null;
  const byoiBucket = byoi?.tfBackendBucket?.trim();
  const byoiRegion = byoi?.tfBackendRegion?.trim() || cfg.region;

  let backendBlock;
  if (byoi && byoi.tfBackend === 'terraform-cloud' && byoiBucket) {
    // byoiBucket = workspace name in this case
    backendBlock = `  backend "remote" {
    organization = "${byoiBucket.split('/')[0] || 'your-org'}"
    workspaces { name = "${byoiBucket.split('/')[1] || byoiBucket}" }
  }`;
  } else if (byoi && byoi.tfBackend === 'atlantis') {
    backendBlock = `  # Atlantis (self-hosted) — backend block managed by Atlantis runner
  # See atlantis.yaml. State lives in your configured backend.`;
  } else {
    const backend = {
      aws: `  backend "s3" {
    bucket         = "${byoiBucket || fmt(cfg.projectName) + '-tfstate'}"
    key            = "${cfg.environment}/terraform.tfstate"
    region         = "${byoiRegion}"
    encrypt        = true
    dynamodb_table = "${byoiBucket || fmt(cfg.projectName)}-tfstate-locks"
  }`,
      gcp: `  backend "gcs" {
    bucket = "${byoiBucket || fmt(cfg.projectName) + '-tfstate'}"
    prefix = "${cfg.environment}"
  }`,
      azure: `  backend "azurerm" {
    resource_group_name  = "${byoiBucket || fmt(cfg.projectName) + '-tfstate-rg'}"
    storage_account_name = "${(byoiBucket || fmt(cfg.projectName)).replace(/-/g,'')}tfstate"
    container_name       = "tfstate"
    key                  = "${cfg.environment}.terraform.tfstate"
  }`,
    };
    backendBlock = backend[cfg.provider];
  }

  return `${tfHeader(cfg)}
terraform {
  required_version = ">= 1.6.0"

  required_providers {
${requiredProviders[cfg.provider]}
  }

${backendBlock}
}

${providerBlocks[cfg.provider]}

locals {
  name_prefix = "${fmt(cfg.projectName)}-${cfg.environment}"

  common_tags = {
    Project     = "${cfg.projectName}"
    Environment = "${cfg.environment}"
    ManagedBy   = "terraform"
    Owner       = "${cfg.owner || 'platform-team'}"
    CostCenter  = "${cfg.costCenter || 'engineering'}"
  }
}
`;
}

function genTerraformVariables(cfg) {
  return `${tfHeader(cfg)}
variable "project_name" {
  description = "Short project identifier used in resource names."
  type        = string
  default     = "${fmt(cfg.projectName)}"
}

variable "environment" {
  description = "Deployment environment (dev | staging | prod)."
  type        = string
  default     = "${cfg.environment}"

  validation {
    condition     = contains(["dev", "staging", "prod"], var.environment)
    error_message = "environment must be one of: dev, staging, prod."
  }
}

variable "region" {
  description = "Cloud region for primary resources."
  type        = string
  default     = "${cfg.region}"
}

${cfg.provider === 'gcp' ? `variable "gcp_project_id" {
  description = "GCP project ID."
  type        = string
}

` : ''}variable "vpc_cidr" {
  description = "Primary VPC/VNet CIDR block."
  type        = string
  default     = "10.${cfg.environment === 'prod' ? 0 : cfg.environment === 'staging' ? 10 : 20}.0.0/16"
}

variable "availability_zone_count" {
  description = "Number of AZs to span. Min 2 for HA, 3 for prod."
  type        = number
  default     = ${cfg.environment === 'prod' ? 3 : 2}
}
${isK8s(cfg.compute) ? `
variable "kubernetes_version" {
  description = "Kubernetes control-plane version."
  type        = string
  default     = "1.30"
}

variable "node_instance_types" {
  description = "Instance types for default node pool."
  type        = list(string)
  default     = ${cfg.environment === 'prod' ? '["m6i.large", "m6i.xlarge"]' : '["t3.medium"]'}
}

variable "node_pool_min" {
  description = "Minimum number of nodes."
  type        = number
  default     = ${cfg.environment === 'prod' ? 3 : 1}
}

variable "node_pool_max" {
  description = "Maximum number of nodes."
  type        = number
  default     = ${cfg.environment === 'prod' ? 12 : 4}
}
` : ''}${cfg.database !== 'none' ? `
variable "db_instance_class" {
  description = "Database instance class / tier."
  type        = string
  default     = "${cfg.environment === 'prod' ? 'db.r6g.large' : 'db.t4g.medium'}"
}

variable "db_allocated_storage_gb" {
  description = "Initial allocated storage in GB."
  type        = number
  default     = ${cfg.environment === 'prod' ? 200 : 50}
}

variable "db_backup_retention_days" {
  description = "Number of days to retain backups."
  type        = number
  default     = ${cfg.environment === 'prod' ? 30 : 7}
}
` : ''}
variable "owner" {
  description = "Team or individual responsible."
  type        = string
  default     = "${cfg.owner || 'platform-team'}"
}

variable "cost_center" {
  description = "Cost center tag for chargeback."
  type        = string
  default     = "${cfg.costCenter || 'engineering'}"
}
`;
}

function genTerraformNetworking(cfg) {
  if (cfg.provider === 'aws') {
    return `${tfHeader(cfg)}
# ── Networking · VPC, subnets, NAT, flow logs ────────────────────

data "aws_availability_zones" "available" {
  state = "available"
}

locals {
  azs            = slice(data.aws_availability_zones.available.names, 0, var.availability_zone_count)
  public_subnets  = [for i, _ in local.azs : cidrsubnet(var.vpc_cidr, 4, i)]
  private_subnets = [for i, _ in local.azs : cidrsubnet(var.vpc_cidr, 4, i + 8)]
}

resource "aws_vpc" "main" {
  cidr_block           = var.vpc_cidr
  enable_dns_hostnames = true
  enable_dns_support   = true

  tags = merge(local.common_tags, {
    Name = "\${local.name_prefix}-vpc"
  })
}

resource "aws_internet_gateway" "main" {
  vpc_id = aws_vpc.main.id
  tags   = merge(local.common_tags, { Name = "\${local.name_prefix}-igw" })
}

resource "aws_subnet" "public" {
  count                   = length(local.azs)
  vpc_id                  = aws_vpc.main.id
  cidr_block              = local.public_subnets[count.index]
  availability_zone       = local.azs[count.index]
  map_public_ip_on_launch = false

  tags = merge(local.common_tags, {
    Name                                          = "\${local.name_prefix}-public-\${local.azs[count.index]}"
    "kubernetes.io/role/elb"                      = "1"
    "kubernetes.io/cluster/\${local.name_prefix}" = "shared"
  })
}

resource "aws_subnet" "private" {
  count             = length(local.azs)
  vpc_id            = aws_vpc.main.id
  cidr_block        = local.private_subnets[count.index]
  availability_zone = local.azs[count.index]

  tags = merge(local.common_tags, {
    Name                                          = "\${local.name_prefix}-private-\${local.azs[count.index]}"
    "kubernetes.io/role/internal-elb"             = "1"
    "kubernetes.io/cluster/\${local.name_prefix}" = "shared"
  })
}

resource "aws_eip" "nat" {
  count  = ${cfg.environment === 'prod' ? 'length(local.azs)' : '1'}
  domain = "vpc"
  tags   = merge(local.common_tags, { Name = "\${local.name_prefix}-nat-eip-\${count.index}" })
}

resource "aws_nat_gateway" "main" {
  count         = ${cfg.environment === 'prod' ? 'length(local.azs)' : '1'}
  allocation_id = aws_eip.nat[count.index].id
  subnet_id     = aws_subnet.public[count.index].id

  tags = merge(local.common_tags, { Name = "\${local.name_prefix}-nat-\${count.index}" })

  depends_on = [aws_internet_gateway.main]
}

# ── Flow logs to CloudWatch ──────────────────────────────────────
resource "aws_cloudwatch_log_group" "vpc_flow" {
  name              = "/aws/vpc/\${local.name_prefix}/flow-logs"
  retention_in_days = ${cfg.environment === 'prod' ? 90 : 14}
  tags              = local.common_tags
}

resource "aws_iam_role" "flow_logs" {
  name = "\${local.name_prefix}-flow-logs"

  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Action = "sts:AssumeRole"
      Principal = { Service = "vpc-flow-logs.amazonaws.com" }
    }]
  })

  tags = local.common_tags
}

resource "aws_flow_log" "main" {
  iam_role_arn    = aws_iam_role.flow_logs.arn
  log_destination = aws_cloudwatch_log_group.vpc_flow.arn
  traffic_type    = "ALL"
  vpc_id          = aws_vpc.main.id
}
`;
  }
  if (cfg.provider === 'gcp') {
    return `${tfHeader(cfg)}
resource "google_compute_network" "main" {
  name                    = "\${local.name_prefix}-vpc"
  auto_create_subnetworks = false
  routing_mode            = "REGIONAL"
}

resource "google_compute_subnetwork" "primary" {
  name          = "\${local.name_prefix}-primary"
  network       = google_compute_network.main.id
  ip_cidr_range = var.vpc_cidr
  region        = var.region

  private_ip_google_access = true

  log_config {
    aggregation_interval = "INTERVAL_5_SEC"
    flow_sampling        = 0.5
    metadata             = "INCLUDE_ALL_METADATA"
  }

  secondary_ip_range {
    range_name    = "pods"
    ip_cidr_range = "10.100.0.0/14"
  }

  secondary_ip_range {
    range_name    = "services"
    ip_cidr_range = "10.200.0.0/20"
  }
}

resource "google_compute_router" "router" {
  name    = "\${local.name_prefix}-router"
  network = google_compute_network.main.id
  region  = var.region
}

resource "google_compute_router_nat" "nat" {
  name                               = "\${local.name_prefix}-nat"
  router                             = google_compute_router.router.name
  region                             = var.region
  nat_ip_allocate_option             = "AUTO_ONLY"
  source_subnetwork_ip_ranges_to_nat = "ALL_SUBNETWORKS_ALL_IP_RANGES"

  log_config {
    enable = true
    filter = "ERRORS_ONLY"
  }
}
`;
  }
  // azure
  return `${tfHeader(cfg)}
resource "azurerm_resource_group" "main" {
  name     = "\${local.name_prefix}-rg"
  location = var.region
  tags     = local.common_tags
}

resource "azurerm_virtual_network" "main" {
  name                = "\${local.name_prefix}-vnet"
  address_space       = [var.vpc_cidr]
  location            = azurerm_resource_group.main.location
  resource_group_name = azurerm_resource_group.main.name
  tags                = local.common_tags
}

resource "azurerm_subnet" "compute" {
  name                 = "\${local.name_prefix}-compute"
  resource_group_name  = azurerm_resource_group.main.name
  virtual_network_name = azurerm_virtual_network.main.name
  address_prefixes     = [cidrsubnet(var.vpc_cidr, 4, 0)]
}

resource "azurerm_subnet" "data" {
  name                 = "\${local.name_prefix}-data"
  resource_group_name  = azurerm_resource_group.main.name
  virtual_network_name = azurerm_virtual_network.main.name
  address_prefixes     = [cidrsubnet(var.vpc_cidr, 4, 1)]

  service_endpoints = ["Microsoft.Sql", "Microsoft.Storage"]
}

resource "azurerm_network_security_group" "compute" {
  name                = "\${local.name_prefix}-compute-nsg"
  location            = azurerm_resource_group.main.location
  resource_group_name = azurerm_resource_group.main.name
  tags                = local.common_tags
}

resource "azurerm_subnet_network_security_group_association" "compute" {
  subnet_id                 = azurerm_subnet.compute.id
  network_security_group_id = azurerm_network_security_group.compute.id
}
`;
}

function genTerraformCompute(cfg) {
  if (cfg.compute === 'eks') {
    return `${tfHeader(cfg)}
# ── EKS cluster · IRSA · managed node group ──────────────────────

resource "aws_iam_role" "cluster" {
  name = "\${local.name_prefix}-eks-cluster"

  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Action = "sts:AssumeRole"
      Principal = { Service = "eks.amazonaws.com" }
    }]
  })

  tags = local.common_tags
}

resource "aws_iam_role_policy_attachment" "cluster_policy" {
  role       = aws_iam_role.cluster.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonEKSClusterPolicy"
}

resource "aws_kms_key" "eks" {
  description             = "EKS secrets envelope encryption"
  deletion_window_in_days = 30
  enable_key_rotation     = true
  tags                    = local.common_tags
}

resource "aws_eks_cluster" "main" {
  name     = local.name_prefix
  version  = var.kubernetes_version
  role_arn = aws_iam_role.cluster.arn

  vpc_config {
    subnet_ids              = concat(aws_subnet.private[*].id, aws_subnet.public[*].id)
    endpoint_private_access = true
    endpoint_public_access  = ${cfg.environment === 'prod' ? 'false' : 'true'}
  }

  encryption_config {
    provider {
      key_arn = aws_kms_key.eks.arn
    }
    resources = ["secrets"]
  }

  enabled_cluster_log_types = ["api", "audit", "authenticator", "controllerManager", "scheduler"]

  tags = local.common_tags

  depends_on = [aws_iam_role_policy_attachment.cluster_policy]
}

# ── Node group ──
resource "aws_iam_role" "node" {
  name = "\${local.name_prefix}-eks-node"

  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Action = "sts:AssumeRole"
      Principal = { Service = "ec2.amazonaws.com" }
    }]
  })

  tags = local.common_tags
}

resource "aws_iam_role_policy_attachment" "node_policies" {
  for_each = toset([
    "arn:aws:iam::aws:policy/AmazonEKSWorkerNodePolicy",
    "arn:aws:iam::aws:policy/AmazonEKS_CNI_Policy",
    "arn:aws:iam::aws:policy/AmazonEC2ContainerRegistryReadOnly",
    "arn:aws:iam::aws:policy/AmazonSSMManagedInstanceCore",
  ])
  role       = aws_iam_role.node.name
  policy_arn = each.value
}

resource "aws_eks_node_group" "default" {
  cluster_name    = aws_eks_cluster.main.name
  node_group_name = "default"
  node_role_arn   = aws_iam_role.node.arn
  subnet_ids      = aws_subnet.private[*].id

  instance_types = var.node_instance_types
  capacity_type  = "${cfg.environment === 'prod' ? 'ON_DEMAND' : 'SPOT'}"

  scaling_config {
    desired_size = ${cfg.environment === 'prod' ? 3 : 2}
    min_size     = var.node_pool_min
    max_size     = var.node_pool_max
  }

  update_config {
    max_unavailable_percentage = 33
  }

  labels = {
    role = "default"
  }

  tags = local.common_tags

  depends_on = [aws_iam_role_policy_attachment.node_policies]
}

# ── OIDC provider for IRSA ──
data "tls_certificate" "eks" {
  url = aws_eks_cluster.main.identity[0].oidc[0].issuer
}

resource "aws_iam_openid_connect_provider" "eks" {
  client_id_list  = ["sts.amazonaws.com"]
  thumbprint_list = [data.tls_certificate.eks.certificates[0].sha1_fingerprint]
  url             = aws_eks_cluster.main.identity[0].oidc[0].issuer
  tags            = local.common_tags
}
`;
  }
  if (cfg.compute === 'ecs') {
    return `${tfHeader(cfg)}
# ── ECS Fargate cluster · service · ALB ──────────────────────────

resource "aws_ecs_cluster" "main" {
  name = local.name_prefix

  setting {
    name  = "containerInsights"
    value = "enabled"
  }

  tags = local.common_tags
}

resource "aws_ecs_cluster_capacity_providers" "main" {
  cluster_name       = aws_ecs_cluster.main.name
  capacity_providers = ["FARGATE", "FARGATE_SPOT"]

  default_capacity_provider_strategy {
    base              = 1
    weight            = 100
    capacity_provider = "${cfg.environment === 'prod' ? 'FARGATE' : 'FARGATE_SPOT'}"
  }
}

resource "aws_cloudwatch_log_group" "app" {
  name              = "/ecs/\${local.name_prefix}/app"
  retention_in_days = ${cfg.environment === 'prod' ? 30 : 7}
  tags              = local.common_tags
}

resource "aws_iam_role" "task_execution" {
  name = "\${local.name_prefix}-task-execution"

  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Action    = "sts:AssumeRole"
      Principal = { Service = "ecs-tasks.amazonaws.com" }
    }]
  })

  tags = local.common_tags
}

resource "aws_iam_role_policy_attachment" "task_execution" {
  role       = aws_iam_role.task_execution.name
  policy_arn = "arn:aws:iam::aws:policy/service-role/AmazonECSTaskExecutionRolePolicy"
}

resource "aws_ecs_task_definition" "app" {
  family                   = "\${local.name_prefix}-app"
  network_mode             = "awsvpc"
  requires_compatibilities = ["FARGATE"]
  cpu                      = "${cfg.environment === 'prod' ? 1024 : 512}"
  memory                   = "${cfg.environment === 'prod' ? 2048 : 1024}"
  execution_role_arn       = aws_iam_role.task_execution.arn

  container_definitions = jsonencode([{
    name      = "app"
    image     = "\${aws_ecr_repository.app.repository_url}:latest"
    essential = true
    portMappings = [{ containerPort = 8080, protocol = "tcp" }]
    logConfiguration = {
      logDriver = "awslogs"
      options = {
        awslogs-group         = aws_cloudwatch_log_group.app.name
        awslogs-region        = var.region
        awslogs-stream-prefix = "app"
      }
    }
  }])

  tags = local.common_tags
}

resource "aws_ecr_repository" "app" {
  name                 = "\${local.name_prefix}-app"
  image_tag_mutability = "IMMUTABLE"

  image_scanning_configuration {
    scan_on_push = true
  }

  encryption_configuration {
    encryption_type = "KMS"
  }

  tags = local.common_tags
}
`;
  }
  if (cfg.compute === 'lambda') {
    return `${tfHeader(cfg)}
# ── Lambda + API Gateway ─────────────────────────────────────────

resource "aws_iam_role" "lambda" {
  name = "\${local.name_prefix}-lambda"

  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Action    = "sts:AssumeRole"
      Principal = { Service = "lambda.amazonaws.com" }
    }]
  })

  tags = local.common_tags
}

resource "aws_iam_role_policy_attachment" "lambda_basic" {
  role       = aws_iam_role.lambda.name
  policy_arn = "arn:aws:iam::aws:policy/service-role/AWSLambdaVPCAccessExecutionRole"
}

resource "aws_lambda_function" "api" {
  function_name = "\${local.name_prefix}-api"
  role          = aws_iam_role.lambda.arn
  handler       = "index.handler"
  runtime       = "nodejs20.x"
  timeout       = 30
  memory_size   = ${cfg.environment === 'prod' ? 1024 : 512}

  filename         = data.archive_file.lambda.output_path
  source_code_hash = data.archive_file.lambda.output_base64sha256

  environment {
    variables = {
      ENVIRONMENT = var.environment
    }
  }

  vpc_config {
    subnet_ids         = aws_subnet.private[*].id
    security_group_ids = [aws_security_group.lambda.id]
  }

  tracing_config {
    mode = "Active"
  }

  tags = local.common_tags
}

data "archive_file" "lambda" {
  type        = "zip"
  source_dir  = "\${path.module}/../app"
  output_path = "\${path.module}/.build/lambda.zip"
}

resource "aws_security_group" "lambda" {
  name        = "\${local.name_prefix}-lambda"
  description = "Lambda egress"
  vpc_id      = aws_vpc.main.id

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = local.common_tags
}

resource "aws_apigatewayv2_api" "main" {
  name          = local.name_prefix
  protocol_type = "HTTP"

  cors_configuration {
    allow_origins = ["*"]
    allow_methods = ["GET", "POST", "PUT", "DELETE", "OPTIONS"]
    allow_headers = ["content-type", "authorization"]
  }

  tags = local.common_tags
}

resource "aws_apigatewayv2_integration" "lambda" {
  api_id                 = aws_apigatewayv2_api.main.id
  integration_type       = "AWS_PROXY"
  integration_uri        = aws_lambda_function.api.invoke_arn
  payload_format_version = "2.0"
}

resource "aws_apigatewayv2_route" "default" {
  api_id    = aws_apigatewayv2_api.main.id
  route_key = "ANY /{proxy+}"
  target    = "integrations/\${aws_apigatewayv2_integration.lambda.id}"
}

resource "aws_apigatewayv2_stage" "main" {
  api_id      = aws_apigatewayv2_api.main.id
  name        = var.environment
  auto_deploy = true

  default_route_settings {
    throttling_burst_limit = ${cfg.environment === 'prod' ? 5000 : 500}
    throttling_rate_limit  = ${cfg.environment === 'prod' ? 10000 : 1000}
  }
}

resource "aws_lambda_permission" "apigw" {
  statement_id  = "AllowAPIGatewayInvoke"
  action        = "lambda:InvokeFunction"
  function_name = aws_lambda_function.api.function_name
  principal     = "apigateway.amazonaws.com"
  source_arn    = "\${aws_apigatewayv2_api.main.execution_arn}/*/*"
}
`;
  }
  if (cfg.compute === 'gke') {
    return `${tfHeader(cfg)}
resource "google_container_cluster" "main" {
  provider = google-beta

  name     = local.name_prefix
  location = var.region

  enable_autopilot = true

  network    = google_compute_network.main.id
  subnetwork = google_compute_subnetwork.primary.id

  ip_allocation_policy {
    cluster_secondary_range_name  = "pods"
    services_secondary_range_name = "services"
  }

  release_channel {
    channel = "${cfg.environment === 'prod' ? 'STABLE' : 'REGULAR'}"
  }

  workload_identity_config {
    workload_pool = "\${var.gcp_project_id}.svc.id.goog"
  }

  private_cluster_config {
    enable_private_nodes    = true
    enable_private_endpoint = ${cfg.environment === 'prod' ? 'true' : 'false'}
    master_ipv4_cidr_block  = "172.16.0.0/28"
  }

  master_authorized_networks_config {
    cidr_blocks {
      cidr_block   = var.vpc_cidr
      display_name = "vpc-internal"
    }
  }

  resource_labels = local.common_tags
}
`;
  }
  if (cfg.compute === 'aks') {
    return `${tfHeader(cfg)}
resource "azurerm_kubernetes_cluster" "main" {
  name                = local.name_prefix
  location            = azurerm_resource_group.main.location
  resource_group_name = azurerm_resource_group.main.name
  dns_prefix          = local.name_prefix

  kubernetes_version  = "1.30"

  default_node_pool {
    name                = "system"
    node_count          = ${cfg.environment === 'prod' ? 3 : 1}
    vm_size             = "${cfg.environment === 'prod' ? 'Standard_D4s_v5' : 'Standard_D2s_v5'}"
    vnet_subnet_id      = azurerm_subnet.compute.id
    type                = "VirtualMachineScaleSets"
    enable_auto_scaling = true
    min_count           = ${cfg.environment === 'prod' ? 3 : 1}
    max_count           = ${cfg.environment === 'prod' ? 10 : 4}
    only_critical_addons_enabled = true
  }

  identity {
    type = "SystemAssigned"
  }

  network_profile {
    network_plugin    = "azure"
    network_policy    = "calico"
    load_balancer_sku = "standard"
  }

  azure_active_directory_role_based_access_control {
    managed                = true
    azure_rbac_enabled     = true
  }

  tags = local.common_tags
}

resource "azurerm_kubernetes_cluster_node_pool" "user" {
  name                  = "user"
  kubernetes_cluster_id = azurerm_kubernetes_cluster.main.id
  vm_size               = "${cfg.environment === 'prod' ? 'Standard_D4s_v5' : 'Standard_D2s_v5'}"
  vnet_subnet_id        = azurerm_subnet.compute.id

  enable_auto_scaling = true
  min_count           = ${cfg.environment === 'prod' ? 2 : 1}
  max_count           = ${cfg.environment === 'prod' ? 10 : 4}
  mode                = "User"

  tags = local.common_tags
}
`;
  }
  return `${tfHeader(cfg)}
# Compute: ${cfg.compute} — module scaffold below
# (Generator covers EKS / ECS / Lambda / GKE / AKS in detail; extend as needed.)
`;
}

function genTerraformDatabase(cfg) {
  if (cfg.database === 'none') return `${tfHeader(cfg)}# (No database selected)\n`;

  if (cfg.database.startsWith('rds')) {
    const engine = cfg.database === 'rds-postgres' ? 'postgres' : 'mysql';
    const port   = engine === 'postgres' ? 5432 : 3306;
    return `${tfHeader(cfg)}
# ── RDS ${engine === 'postgres' ? 'PostgreSQL' : 'MySQL'} · multi-AZ · encrypted ─────────────

resource "aws_db_subnet_group" "main" {
  name       = "\${local.name_prefix}-db"
  subnet_ids = aws_subnet.private[*].id
  tags       = local.common_tags
}

resource "aws_security_group" "db" {
  name        = "\${local.name_prefix}-db"
  description = "Database ingress from compute layer only"
  vpc_id      = aws_vpc.main.id

  ingress {
    from_port       = ${port}
    to_port         = ${port}
    protocol        = "tcp"
    security_groups = [aws_security_group.compute.id]
    description     = "Compute layer"
  }

  tags = local.common_tags
}

resource "aws_security_group" "compute" {
  name        = "\${local.name_prefix}-compute"
  description = "Compute SG referenced by DB ingress"
  vpc_id      = aws_vpc.main.id

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = local.common_tags
}

resource "aws_kms_key" "db" {
  description             = "Encryption key for RDS \${local.name_prefix}"
  deletion_window_in_days = 30
  enable_key_rotation     = true
  tags                    = local.common_tags
}

resource "random_password" "db_master" {
  length           = 32
  special          = true
  override_special = "!#$%&*()-_=+[]{}<>:?"
}

resource "aws_secretsmanager_secret" "db" {
  name                    = "\${local.name_prefix}/db/master"
  kms_key_id              = aws_kms_key.db.arn
  recovery_window_in_days = ${cfg.environment === 'prod' ? 30 : 0}
  tags                    = local.common_tags
}

resource "aws_secretsmanager_secret_version" "db" {
  secret_id = aws_secretsmanager_secret.db.id
  secret_string = jsonencode({
    username = "appadmin"
    password = random_password.db_master.result
    engine   = "${engine}"
    host     = aws_db_instance.main.address
    port     = ${port}
    dbname   = "appdb"
  })
}

resource "aws_db_instance" "main" {
  identifier     = "\${local.name_prefix}"
  engine         = "${engine}"
  engine_version = "${engine === 'postgres' ? '16.3' : '8.0.36'}"
  instance_class = var.db_instance_class

  allocated_storage     = var.db_allocated_storage_gb
  max_allocated_storage = var.db_allocated_storage_gb * 4
  storage_type          = "gp3"
  storage_encrypted     = true
  kms_key_id            = aws_kms_key.db.arn

  username = "appadmin"
  password = random_password.db_master.result
  db_name  = "appdb"
  port     = ${port}

  multi_az               = ${cfg.environment === 'prod' ? 'true' : 'false'}
  db_subnet_group_name   = aws_db_subnet_group.main.name
  vpc_security_group_ids = [aws_security_group.db.id]
  publicly_accessible    = false

  backup_retention_period   = var.db_backup_retention_days
  backup_window             = "03:00-04:00"
  maintenance_window        = "sun:04:00-sun:05:00"
  copy_tags_to_snapshot     = true
  deletion_protection       = ${cfg.environment === 'prod' ? 'true' : 'false'}
  skip_final_snapshot       = ${cfg.environment === 'prod' ? 'false' : 'true'}
  final_snapshot_identifier = "\${local.name_prefix}-final"

  performance_insights_enabled          = true
  performance_insights_retention_period = ${cfg.environment === 'prod' ? 731 : 7}
  monitoring_interval                   = 60
  monitoring_role_arn                   = aws_iam_role.rds_monitoring.arn

  enabled_cloudwatch_logs_exports = ${engine === 'postgres' ? '["postgresql", "upgrade"]' : '["error", "general", "slowquery"]'}

  iam_database_authentication_enabled = true
  auto_minor_version_upgrade          = true

  tags = local.common_tags
}

resource "aws_iam_role" "rds_monitoring" {
  name = "\${local.name_prefix}-rds-monitoring"

  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Action    = "sts:AssumeRole"
      Principal = { Service = "monitoring.rds.amazonaws.com" }
    }]
  })

  tags = local.common_tags
}

resource "aws_iam_role_policy_attachment" "rds_monitoring" {
  role       = aws_iam_role.rds_monitoring.name
  policy_arn = "arn:aws:iam::aws:policy/service-role/AmazonRDSEnhancedMonitoringRole"
}
`;
  }
  if (cfg.database === 'dynamo') {
    return `${tfHeader(cfg)}
resource "aws_dynamodb_table" "app" {
  name         = "\${local.name_prefix}-app"
  billing_mode = "PAY_PER_REQUEST"
  hash_key     = "pk"
  range_key    = "sk"

  attribute {
    name = "pk"
    type = "S"
  }

  attribute {
    name = "sk"
    type = "S"
  }

  point_in_time_recovery {
    enabled = ${cfg.environment === 'prod' ? 'true' : 'false'}
  }

  server_side_encryption {
    enabled = true
  }

  ${cfg.environment === 'prod' ? 'deletion_protection_enabled = true' : ''}

  tags = local.common_tags
}
`;
  }
  return `${tfHeader(cfg)}# Database scaffold for ${cfg.database}\n`;
}

function genTerraformOutputs(cfg) {
  const outs = [];
  if (cfg.provider === 'aws') {
    outs.push(`output "vpc_id" {
  description = "Primary VPC ID."
  value       = aws_vpc.main.id
}

output "private_subnet_ids" {
  description = "Private subnet IDs."
  value       = aws_subnet.private[*].id
}`);
  }
  if (cfg.compute === 'eks') {
    outs.push(`output "cluster_name" {
  description = "EKS cluster name."
  value       = aws_eks_cluster.main.name
}

output "cluster_endpoint" {
  description = "EKS cluster endpoint."
  value       = aws_eks_cluster.main.endpoint
  sensitive   = true
}

output "cluster_oidc_issuer" {
  description = "OIDC issuer for IRSA."
  value       = aws_iam_openid_connect_provider.eks.url
}`);
  }
  if (cfg.compute === 'ecs') {
    outs.push(`output "ecs_cluster_arn" {
  description = "ECS cluster ARN."
  value       = aws_ecs_cluster.main.arn
}

output "ecr_repository_url" {
  description = "ECR repository URL."
  value       = aws_ecr_repository.app.repository_url
}`);
  }
  if (cfg.compute === 'lambda') {
    outs.push(`output "api_endpoint" {
  description = "API Gateway invoke URL."
  value       = aws_apigatewayv2_stage.main.invoke_url
}`);
  }
  if (cfg.database !== 'none' && cfg.database.startsWith('rds')) {
    outs.push(`output "db_endpoint" {
  description = "RDS endpoint hostname."
  value       = aws_db_instance.main.address
  sensitive   = true
}

output "db_secret_arn" {
  description = "Secrets Manager ARN holding DB credentials."
  value       = aws_secretsmanager_secret.db.arn
}`);
  }
  return `${tfHeader(cfg)}\n${outs.join('\n\n')}\n`;
}

// =============================================================
// DATA ENGINEERING GENERATORS
// =============================================================
// All output is opt-in (cfg.deEnabled). Each section conditionally
// emits Terraform + K8s manifests + Helm values for the chosen tech.
// Sizing scales by cfg.deDataVolume: small / medium / large.

const isDEOn = (cfg) => cfg.deEnabled === true;

// Sizing presets for cluster shapes
function deSize(cfg) {
  const v = cfg.deDataVolume || 'medium';
  return {
    kafka:    { brokers: v==='small'?3:v==='medium'?5:9,  storage: v==='small'?'200Gi':v==='medium'?'1Ti':'4Ti', vol: v==='small'?'gp3':'gp3' },
    pinot:    { servers: v==='small'?3:v==='medium'?5:9,  brokers: v==='small'?2:3, controllers: 3, storage: v==='small'?'500Gi':v==='medium'?'2Ti':'8Ti' },
    flink:    { tm: v==='small'?2:v==='medium'?6:12,      slots: 4, mem: v==='small'?'4Gi':v==='medium'?'8Gi':'16Gi' },
    cass:     { nodes: v==='small'?3:v==='medium'?6:12,   storage: v==='small'?'500Gi':v==='medium'?'2Ti':'8Ti' },
    trino:    { workers: v==='small'?3:v==='medium'?8:20, mem: v==='small'?'8Gi':v==='medium'?'16Gi':'32Gi' },
    airflow:  { schedulers: v==='small'?1:2, workers: v==='small'?2:v==='medium'?6:12 },
  };
}

// ── Names for README + diagram labels ──────────────────────────
const _deName = (list, id) => list.find(x => x.id === id)?.name || id;
const deStreamingName    = (cfg) => _deName(DE_STREAMING, cfg.deStreaming);
const deOlapName         = (cfg) => _deName(DE_OLAP, cfg.deOlap);
const deStreamProcName   = (cfg) => _deName(DE_STREAM_PROC, cfg.deStreamProc);
const deWideColName      = (cfg) => _deName(DE_WIDE_COL, cfg.deWideCol);
const deLakeTableName    = (cfg) => _deName(DE_LAKE_TABLE, cfg.deLakeTable);
const deQueryEngineName  = (cfg) => _deName(DE_QUERY_ENGINE, cfg.deQueryEngine);
const deOrchestrationName = (cfg) => _deName(DE_ORCHESTRATION, cfg.deOrchestration);

// =============================================================
// genTerraformDataEng — bucket + IAM + DNS plumbing for DE layer
// =============================================================
function genTerraformDataEng(cfg) {
  if (!isDEOn(cfg)) return '';
  const name = fmt(cfg.projectName);
  const sz = deSize(cfg);

  const lakeBucketBlock = (cfg.deLakeTable !== 'none') ? `
# ── Lake table object storage (${deLakeTableName(cfg)}) ─────────
${cfg.provider === 'aws' ? `resource "aws_s3_bucket" "lake" {
  bucket = "${name}-${cfg.environment}-lake"
}
resource "aws_s3_bucket_versioning" "lake" {
  bucket = aws_s3_bucket.lake.id
  versioning_configuration { status = "Enabled" }
}
resource "aws_s3_bucket_server_side_encryption_configuration" "lake" {
  bucket = aws_s3_bucket.lake.id
  rule {
    apply_server_side_encryption_by_default {
      sse_algorithm     = "aws:kms"
      kms_master_key_id = aws_kms_key.data.arn
    }
  }
}
resource "aws_s3_bucket_lifecycle_configuration" "lake" {
  bucket = aws_s3_bucket.lake.id
  rule {
    id     = "transition-cold"
    status = "Enabled"
    transition { days = 90  storage_class = "STANDARD_IA" }
    transition { days = 365 storage_class = "GLACIER" }
  }
}
${cfg.deLakeTable === 'iceberg' ? `
# Glue Catalog as Iceberg REST catalog backend
resource "aws_glue_catalog_database" "lake" {
  name = "${name}_lake"
}` : ''}` : cfg.provider === 'gcp' ? `resource "google_storage_bucket" "lake" {
  name          = "${name}-${cfg.environment}-lake"
  location      = "${cfg.region}"
  storage_class = "STANDARD"
  uniform_bucket_level_access = true
  versioning { enabled = true }
  lifecycle_rule {
    condition { age = 90 }
    action { type = "SetStorageClass" storage_class = "NEARLINE" }
  }
  lifecycle_rule {
    condition { age = 365 }
    action { type = "SetStorageClass" storage_class = "COLDLINE" }
  }
}` : `resource "azurerm_storage_account" "lake" {
  name                     = "${name.replace(/-/g,'')}${cfg.environment}lake"
  resource_group_name      = azurerm_resource_group.main.name
  location                 = azurerm_resource_group.main.location
  account_tier             = "Standard"
  account_replication_type = "${cfg.environment === 'prod' ? 'GRS' : 'LRS'}"
  is_hns_enabled           = true   # ADLS Gen2
  blob_properties { versioning_enabled = true }
}
resource "azurerm_storage_container" "lake" {
  name                  = "${name}-lake"
  storage_account_name  = azurerm_storage_account.lake.name
  container_access_type = "private"
}`}` : '';

  const cdcBucketBlock = (cfg.deStreaming !== 'none' && cfg.provider === 'aws' && cfg.deStreaming === 'kinesis') ? `
# ── Kinesis stream (managed alternative to Kafka) ──────────────
resource "aws_kinesis_stream" "events" {
  name             = "${name}-events"
  shard_count      = ${cfg.environment === 'prod' ? 8 : 2}
  retention_period = ${cfg.environment === 'prod' ? 168 : 24}  # hours
  stream_mode_details { stream_mode = "ON_DEMAND" }
  encryption_type = "KMS"
  kms_key_id      = aws_kms_key.data.arn
}` : '';

  // Worker IAM for Flink/Spark/Trino to access lake
  const workerIamBlock = cfg.provider === 'aws' ? `
# ── IAM role for DE workers (Flink TM, Trino workers, Airflow) ─
resource "aws_iam_role" "de_worker" {
  name = "${name}-${cfg.environment}-de-worker"
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Action = "sts:AssumeRoleWithWebIdentity"
      Principal = { Federated = aws_iam_openid_connect_provider.eks.arn }
      Condition = {
        StringEquals = {
          "$\{trimprefix(aws_iam_openid_connect_provider.eks.url, "https://")}:sub" = "system:serviceaccount:${name}-de:de-worker"
        }
      }
    }]
  })
}

resource "aws_iam_role_policy" "de_worker_lake" {
  role   = aws_iam_role.de_worker.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [
      {
        Effect = "Allow"
        Action = [
          "s3:GetObject", "s3:PutObject", "s3:DeleteObject",
          "s3:ListBucket", "s3:GetBucketLocation"
        ]
        Resource = [
          aws_s3_bucket.lake.arn,
          "$\{aws_s3_bucket.lake.arn}/*"
        ]
      },
      {
        Effect = "Allow"
        Action = ["kms:Decrypt", "kms:GenerateDataKey"]
        Resource = aws_kms_key.data.arn
      }${cfg.deLakeTable === 'iceberg' ? `,
      {
        Effect = "Allow"
        Action = ["glue:*"]
        Resource = "*"
      }` : ''}
    ]
  })
}` : '';

  return `${tfHeader(cfg)}
# ─────────────────────────────────────────────────────────────────
# Data Engineering layer
# ${cfg.deStreaming !== 'none' ? `Streaming:    ${deStreamingName(cfg)}\n# ` : ''}${cfg.deOlap !== 'none' ? `OLAP:         ${deOlapName(cfg)}\n# ` : ''}${cfg.deStreamProc !== 'none' ? `Stream proc:  ${deStreamProcName(cfg)}\n# ` : ''}${cfg.deWideCol !== 'none' ? `Wide-column:  ${deWideColName(cfg)}\n# ` : ''}${cfg.deLakeTable !== 'none' ? `Lake table:   ${deLakeTableName(cfg)}\n# ` : ''}${cfg.deQueryEngine !== 'none' ? `Query engine: ${deQueryEngineName(cfg)}\n# ` : ''}${cfg.deOrchestration !== 'none' ? `Orchestrator: ${deOrchestrationName(cfg)}` : ''}
# Volume tier:  ${cfg.deDataVolume}
# ─────────────────────────────────────────────────────────────────
${lakeBucketBlock}
${cdcBucketBlock}
${workerIamBlock}

# OSS clusters (Kafka, Pinot, Flink, Cassandra, Trino, Airflow) deploy
# via Helm charts under k8s/de/. See README.md for chart versions.
`;
}

// =============================================================
// genHelmKafka — Kafka via Strimzi operator (KRaft mode, no ZK)
// =============================================================
function genHelmKafka(cfg) {
  if (!isDEOn(cfg) || cfg.deStreaming !== 'kafka') return '';
  const sz = deSize(cfg).kafka;
  return `# ─────────────────────────────────────────────────────────────────
# Apache Kafka (Strimzi operator, KRaft mode)
# helm install strimzi-kafka-operator strimzi/strimzi-kafka-operator -n kafka --create-namespace
# kubectl apply -f k8s/de/kafka.yaml
# ─────────────────────────────────────────────────────────────────
apiVersion: kafka.strimzi.io/v1beta2
kind: KafkaNodePool
metadata:
  name: brokers
  namespace: ${fmt(cfg.projectName)}-de
  labels:
    strimzi.io/cluster: ${fmt(cfg.projectName)}
spec:
  replicas: ${sz.brokers}
  roles: [controller, broker]
  storage:
    type: jbod
    volumes:
      - id: 0
        type: persistent-claim
        size: ${sz.storage}
        deleteClaim: false
        kraftMetadata: shared
        class: ${cfg.provider === 'aws' ? 'gp3' : cfg.provider === 'gcp' ? 'pd-ssd' : 'managed-csi'}
  resources:
    requests: { memory: 4Gi, cpu: "1" }
    limits:   { memory: 8Gi, cpu: "2" }
---
apiVersion: kafka.strimzi.io/v1beta2
kind: Kafka
metadata:
  name: ${fmt(cfg.projectName)}
  namespace: ${fmt(cfg.projectName)}-de
  annotations:
    strimzi.io/node-pools: enabled
    strimzi.io/kraft: enabled
spec:
  kafka:
    version: 3.8.0
    metadataVersion: 3.8-IV0
    listeners:
      - name: tls
        port: 9093
        type: internal
        tls: true
        authentication: { type: tls }
      - name: external
        port: 9094
        type: ${cfg.provider === 'aws' ? 'loadbalancer' : 'route'}
        tls: true
        authentication: { type: tls }
    config:
      offsets.topic.replication.factor: ${cfg.environment === 'prod' ? 3 : 1}
      transaction.state.log.replication.factor: ${cfg.environment === 'prod' ? 3 : 1}
      transaction.state.log.min.isr: ${cfg.environment === 'prod' ? 2 : 1}
      default.replication.factor: ${cfg.environment === 'prod' ? 3 : 1}
      min.insync.replicas: ${cfg.environment === 'prod' ? 2 : 1}
      log.retention.hours: ${cfg.environment === 'prod' ? 168 : 24}
      compression.type: zstd
  entityOperator:
    topicOperator: {}
    userOperator: {}
---
# CDC source topic (apps publish here)
apiVersion: kafka.strimzi.io/v1beta2
kind: KafkaTopic
metadata:
  name: events.${fmt(cfg.projectName)}.raw
  namespace: ${fmt(cfg.projectName)}-de
  labels: { strimzi.io/cluster: ${fmt(cfg.projectName)} }
spec:
  partitions: ${sz.brokers * 4}
  replicas: ${cfg.environment === 'prod' ? 3 : 1}
  config:
    retention.ms: ${cfg.environment === 'prod' ? 604800000 : 86400000}
    cleanup.policy: delete
    compression.type: zstd
`;
}

// =============================================================
// genHelmRedpanda — Redpanda alternative
// =============================================================
function genHelmRedpanda(cfg) {
  if (!isDEOn(cfg) || cfg.deStreaming !== 'redpanda') return '';
  const sz = deSize(cfg).kafka;
  return `# Redpanda — Kafka-compatible, single-binary
# helm install redpanda redpanda/redpanda -n ${fmt(cfg.projectName)}-de -f redpanda-values.yaml
statefulset:
  replicas: ${sz.brokers}
storage:
  persistentVolume:
    enabled: true
    size: ${sz.storage}
    storageClass: ${cfg.provider === 'aws' ? 'gp3' : cfg.provider === 'gcp' ? 'pd-ssd' : 'managed-csi'}
resources:
  cpu:    { cores: 2 }
  memory: { container: { max: 8Gi } }
config:
  cluster:
    auto_create_topics_enabled: false
    default_topic_replications: ${cfg.environment === 'prod' ? 3 : 1}
tls:
  enabled: true
auth:
  sasl: { enabled: true, mechanism: SCRAM-SHA-512 }
console:
  enabled: true
`;
}

// =============================================================
// genHelmPinot — Apache Pinot
// =============================================================
function genHelmPinot(cfg) {
  if (!isDEOn(cfg) || cfg.deOlap !== 'pinot') return '';
  const sz = deSize(cfg).pinot;
  return `# Apache Pinot — real-time OLAP
# helm repo add pinot https://raw.githubusercontent.com/apache/pinot/master/helm
# helm install pinot pinot/pinot -n ${fmt(cfg.projectName)}-de -f pinot-values.yaml
cluster:
  name: ${fmt(cfg.projectName)}-pinot

controller:
  replicaCount: ${sz.controllers}
  persistence:
    enabled: true
    size: 50Gi
  data:
    dir: /var/pinot/controller/data
  resources:
    requests: { cpu: 500m, memory: 2Gi }
    limits:   { cpu: "2",  memory: 4Gi }

broker:
  replicaCount: ${sz.brokers}
  resources:
    requests: { cpu: "1",  memory: 4Gi }
    limits:   { cpu: "2",  memory: 8Gi }

server:
  replicaCount: ${sz.servers}
  persistence:
    enabled: true
    size: ${sz.storage}
    storageClass: ${cfg.provider === 'aws' ? 'gp3' : cfg.provider === 'gcp' ? 'pd-ssd' : 'managed-csi'}
  resources:
    requests: { cpu: "2",  memory: 8Gi }
    limits:   { cpu: "4",  memory: 16Gi }
  jvmOpts: "-Xms6G -Xmx12G"

minion:
  enabled: ${cfg.environment === 'prod' ? 'true' : 'false'}
  replicaCount: 2

zookeeper:
  enabled: true
  replicaCount: 3
  persistence:
    size: 20Gi

# Realtime ingestion from Kafka (auto-wired when both are enabled)
${cfg.deStreaming === 'kafka' || cfg.deStreaming === 'redpanda' ? `extraConfigs:
  controller:
    pinot.controller.realtime.segment.relocator.enabled: "true"
    pinot.controller.segment.relocator.frequencyPeriod: "1h"
` : ''}
# Deep store on lake bucket
${cfg.deLakeTable !== 'none' && cfg.provider === 'aws' ? `deepStorage:
  type: s3
  s3:
    bucket: ${fmt(cfg.projectName)}-${cfg.environment}-lake
    prefix: pinot/segments
    region: ${cfg.region}
` : ''}`;
}

// =============================================================
// genPinotTableSchema — example schema + table for Kafka ingest
// =============================================================
function genPinotTableSchema(cfg) {
  if (!isDEOn(cfg) || cfg.deOlap !== 'pinot') return '';
  return `// ─────────────────────────────────────────────────────────────────
// Example Pinot schema + realtime table — events from Kafka
// Apply via: pinot-admin AddTable -schemaFile schema.json -tableConfigFile table.json
// ─────────────────────────────────────────────────────────────────
{
  "schemaName": "events",
  "dimensionFieldSpecs": [
    { "name": "user_id",    "dataType": "STRING" },
    { "name": "session_id", "dataType": "STRING" },
    { "name": "event_type", "dataType": "STRING" },
    { "name": "country",    "dataType": "STRING" },
    { "name": "device",     "dataType": "STRING" }
  ],
  "metricFieldSpecs": [
    { "name": "value",      "dataType": "DOUBLE", "defaultNullValue": 0 },
    { "name": "duration_ms","dataType": "LONG",   "defaultNullValue": 0 }
  ],
  "dateTimeFieldSpecs": [
    {
      "name": "ts",
      "dataType": "TIMESTAMP",
      "format": "1:MILLISECONDS:EPOCH",
      "granularity": "1:MILLISECONDS"
    }
  ]
}
`;
}

// =============================================================
// genHelmFlink — Apache Flink
// =============================================================
function genHelmFlink(cfg) {
  if (!isDEOn(cfg) || cfg.deStreamProc !== 'flink') return '';
  const sz = deSize(cfg).flink;
  return `# Apache Flink — operator-based (apache/flink-kubernetes-operator)
apiVersion: flink.apache.org/v1beta1
kind: FlinkDeployment
metadata:
  name: ${fmt(cfg.projectName)}-flink
  namespace: ${fmt(cfg.projectName)}-de
spec:
  image: flink:1.20-scala_2.12-java17
  flinkVersion: v1_20
  flinkConfiguration:
    taskmanager.numberOfTaskSlots: "${sz.slots}"
    state.backend: rocksdb
    state.backend.incremental: "true"
    execution.checkpointing.interval: "30000"
    execution.checkpointing.mode: EXACTLY_ONCE
    execution.checkpointing.unaligned: "true"
    state.checkpoints.dir: ${cfg.provider === 'aws' ? `s3://${fmt(cfg.projectName)}-${cfg.environment}-lake/flink-checkpoints` : cfg.provider === 'gcp' ? `gs://${fmt(cfg.projectName)}-${cfg.environment}-lake/flink-checkpoints` : 'abfs://flink-checkpoints@...'}
    state.savepoints.dir: ${cfg.provider === 'aws' ? `s3://${fmt(cfg.projectName)}-${cfg.environment}-lake/flink-savepoints` : cfg.provider === 'gcp' ? `gs://${fmt(cfg.projectName)}-${cfg.environment}-lake/flink-savepoints` : 'abfs://flink-savepoints@...'}
    high-availability: kubernetes
    high-availability.storageDir: ${cfg.provider === 'aws' ? `s3://${fmt(cfg.projectName)}-${cfg.environment}-lake/flink-ha` : cfg.provider === 'gcp' ? `gs://${fmt(cfg.projectName)}-${cfg.environment}-lake/flink-ha` : 'abfs://flink-ha@...'}
  serviceAccount: de-worker
  jobManager:
    resource:
      memory: "2048m"
      cpu: 1
    replicas: ${cfg.environment === 'prod' ? 2 : 1}
  taskManager:
    resource:
      memory: "${sz.mem}"
      cpu: 2
    replicas: ${sz.tm}
  podTemplate:
    spec:
      containers:
        - name: flink-main-container
          env:
            - name: ENABLE_BUILT_IN_PLUGINS
              value: flink-s3-fs-presto-1.20.0.jar;flink-s3-fs-hadoop-1.20.0.jar
`;
}

// =============================================================
// genFlinkExampleJob — sample job: Kafka → enrich → Pinot/Iceberg
// =============================================================
function genFlinkExampleJob(cfg) {
  if (!isDEOn(cfg) || cfg.deStreamProc !== 'flink') return '';
  const sinkPinot = cfg.deOlap === 'pinot';
  const sinkIceberg = cfg.deLakeTable === 'iceberg';
  return `# ─────────────────────────────────────────────────────────────────
# Example Flink SQL job — Kafka → enrich → ${sinkPinot ? 'Pinot' : ''}${sinkPinot && sinkIceberg ? ' + ' : ''}${sinkIceberg ? 'Iceberg' : 'sink'}
#
# Submit with:
#   ./bin/sql-client.sh -f flink_jobs/events_enrich.sql
# ─────────────────────────────────────────────────────────────────
SET 'execution.checkpointing.interval' = '30s';
SET 'pipeline.name' = '${fmt(cfg.projectName)}-events-enrich';

-- ── Source: raw events from Kafka ──────────────────────────────
CREATE TABLE events_raw (
  user_id     STRING,
  session_id  STRING,
  event_type  STRING,
  payload     STRING,
  ts          TIMESTAMP_LTZ(3),
  WATERMARK FOR ts AS ts - INTERVAL '5' SECOND
) WITH (
  'connector' = 'kafka',
  'topic'     = 'events.${fmt(cfg.projectName)}.raw',
  'properties.bootstrap.servers' = '${fmt(cfg.projectName)}-kafka-bootstrap.${fmt(cfg.projectName)}-de.svc:9092',
  'properties.group.id' = 'flink-${fmt(cfg.projectName)}-enrich',
  'scan.startup.mode' = 'group-offsets',
  'format' = 'json',
  'json.fail-on-missing-field' = 'false',
  'json.ignore-parse-errors'   = 'true'
);

-- ── Dimension: user lookup (CDC from OLTP) ─────────────────────
CREATE TABLE users_dim (
  user_id STRING PRIMARY KEY NOT ENFORCED,
  country STRING,
  device  STRING,
  plan    STRING
) WITH (
  'connector' = 'upsert-kafka',
  'topic'     = 'cdc.users',
  'properties.bootstrap.servers' = '${fmt(cfg.projectName)}-kafka-bootstrap.${fmt(cfg.projectName)}-de.svc:9092',
  'key.format'   = 'json',
  'value.format' = 'json'
);

${sinkPinot ? `-- ── Sink: Pinot realtime table ─────────────────────────────────
-- Pinot ingests from Kafka directly; this Flink job lands the
-- enriched topic, Pinot tails it via realtime ingestion config.
CREATE TABLE events_enriched_kafka (
  user_id    STRING,
  session_id STRING,
  event_type STRING,
  country    STRING,
  device     STRING,
  plan       STRING,
  ts         TIMESTAMP_LTZ(3)
) WITH (
  'connector' = 'kafka',
  'topic'     = 'events.${fmt(cfg.projectName)}.enriched',
  'properties.bootstrap.servers' = '${fmt(cfg.projectName)}-kafka-bootstrap.${fmt(cfg.projectName)}-de.svc:9092',
  'format'    = 'json'
);

INSERT INTO events_enriched_kafka
SELECT
  e.user_id,
  e.session_id,
  e.event_type,
  COALESCE(u.country, 'unknown') AS country,
  COALESCE(u.device,  'unknown') AS device,
  COALESCE(u.plan,    'free')    AS plan,
  e.ts
FROM events_raw e
LEFT JOIN users_dim FOR SYSTEM_TIME AS OF e.ts AS u
  ON e.user_id = u.user_id;
` : ''}
${sinkIceberg ? `-- ── Sink: Iceberg (durable analytical record) ──────────────────
CREATE CATALOG iceberg WITH (
  'type'                   = 'iceberg',
  'catalog-type'           = 'glue',
  'warehouse'              = '${cfg.provider === 'aws' ? `s3://${fmt(cfg.projectName)}-${cfg.environment}-lake/warehouse` : cfg.provider === 'gcp' ? `gs://${fmt(cfg.projectName)}-${cfg.environment}-lake/warehouse` : 'abfs://...'}',
  'glue.skip-archive'      = 'true'
);
USE CATALOG iceberg;
CREATE TABLE IF NOT EXISTS lake.events_enriched (
  user_id    STRING,
  session_id STRING,
  event_type STRING,
  country    STRING,
  device     STRING,
  plan       STRING,
  ts         TIMESTAMP_LTZ(3)
) PARTITIONED BY (days(ts));
` : ''}`;
}

// =============================================================
// genHelmCassandra — Cassandra via K8ssandra
// =============================================================
function genHelmCassandra(cfg) {
  if (!isDEOn(cfg) || cfg.deWideCol !== 'cassandra') return '';
  const sz = deSize(cfg).cass;
  return `# K8ssandra — Cassandra operator on Kubernetes
# helm install k8ssandra-operator k8ssandra/k8ssandra-operator -n k8ssandra-operator
apiVersion: k8ssandra.io/v1alpha1
kind: K8ssandraCluster
metadata:
  name: ${fmt(cfg.projectName)}-cassandra
  namespace: ${fmt(cfg.projectName)}-de
spec:
  cassandra:
    serverVersion: 4.1.6
    datacenters:
      - metadata:
          name: dc1
        size: ${sz.nodes}
        storageConfig:
          cassandraDataVolumeClaimSpec:
            storageClassName: ${cfg.provider === 'aws' ? 'gp3' : cfg.provider === 'gcp' ? 'pd-ssd' : 'managed-csi'}
            accessModes: [ReadWriteOnce]
            resources:
              requests: { storage: ${sz.storage} }
        resources:
          requests: { cpu: "2",  memory: 8Gi }
          limits:   { cpu: "4",  memory: 16Gi }
        racks:
          - name: rack1
          - name: rack2
          - name: rack3
        config:
          jvmOptions:
            heapSize: 8G
          cassandraYaml:
            authenticator: PasswordAuthenticator
            authorizer:    CassandraAuthorizer
  stargate:
    size: 1
    heapSize: 384M
  reaper:
    enabled: true
  medusa:
    storageProperties:
      storageProvider: ${cfg.provider === 'aws' ? 's3' : cfg.provider === 'gcp' ? 'google_storage' : 'azure_blobs'}
      bucketName: ${fmt(cfg.projectName)}-${cfg.environment}-lake
      prefix: cassandra-backups
`;
}

// =============================================================
// genHelmTrino — Trino federated query
// =============================================================
function genHelmTrino(cfg) {
  if (!isDEOn(cfg) || cfg.deQueryEngine !== 'trino') return '';
  const sz = deSize(cfg).trino;
  return `# Trino — federated SQL
# helm install trino trino/trino -n ${fmt(cfg.projectName)}-de -f trino-values.yaml
image:
  tag: "461"

server:
  workers: ${sz.workers}
  config:
    query:
      maxMemoryPerNode: ${sz.mem}
      maxMemory: ${parseInt(sz.mem) * sz.workers}Gi
  coordinator:
    jvm:
      maxHeapSize: 8G

# Catalogs — federate every store you have
additionalCatalogs:
${cfg.deLakeTable === 'iceberg' ? `  iceberg: |
    connector.name=iceberg
    iceberg.catalog.type=glue
    iceberg.file-format=PARQUET
    hive.s3.aws-access-key-id=
    hive.s3.aws-secret-key=
    hive.s3.region=${cfg.region}
` : ''}${cfg.deLakeTable === 'delta' ? `  delta: |
    connector.name=delta_lake
    hive.metastore=glue
    delta.enable-non-concurrent-writes=true
` : ''}${cfg.deOlap === 'pinot' ? `  pinot: |
    connector.name=pinot
    pinot.controller-urls=${fmt(cfg.projectName)}-pinot-controller.${fmt(cfg.projectName)}-de.svc:9000
` : ''}${cfg.deWideCol === 'cassandra' ? `  cassandra: |
    connector.name=cassandra
    cassandra.contact-points=${fmt(cfg.projectName)}-cassandra-dc1-service.${fmt(cfg.projectName)}-de.svc
    cassandra.protocol-version=V4
` : ''}${cfg.database !== 'none' ? `  postgres: |
    connector.name=postgresql
    connection-url=jdbc:postgresql://${fmt(cfg.projectName)}-rds.${cfg.region}.rds.amazonaws.com:5432/app
` : ''}${cfg.deStreaming === 'kafka' ? `  kafka: |
    connector.name=kafka
    kafka.nodes=${fmt(cfg.projectName)}-kafka-bootstrap.${fmt(cfg.projectName)}-de.svc:9092
    kafka.default-schema=${fmt(cfg.projectName)}
` : ''}
serviceAccount:
  create: true
  name: de-worker
`;
}

// =============================================================
// genHelmAirflow — Airflow / Prefect / Dagster orchestration
// =============================================================
function genHelmAirflow(cfg) {
  if (!isDEOn(cfg) || cfg.deOrchestration !== 'airflow') return '';
  const sz = deSize(cfg).airflow;
  return `# Apache Airflow — official Helm chart
# helm install airflow apache-airflow/airflow -n ${fmt(cfg.projectName)}-de -f airflow-values.yaml
executor: KubernetesExecutor

webserver:
  replicas: 2
  resources:
    requests: { cpu: 500m, memory: 1Gi }

scheduler:
  replicas: ${sz.schedulers}
  resources:
    requests: { cpu: "1", memory: 2Gi }

workers:
  replicas: ${sz.workers}

triggerer:
  replicas: 2
  resources:
    requests: { cpu: 500m, memory: 1Gi }

postgresql:
  enabled: false
externalDatabase:
  type: postgres
  host: ${fmt(cfg.projectName)}-rds.${cfg.region}.rds.amazonaws.com
  database: airflow

dags:
  gitSync:
    enabled: true
    repo: git@github.com:${fmt(cfg.projectName)}/airflow-dags.git
    branch: main
    subPath: dags

logs:
  persistence:
    enabled: false
  remoteLogging:
    enabled: true
    remoteLogConn: ${cfg.provider === 'aws' ? 'aws_default' : cfg.provider === 'gcp' ? 'google_cloud_default' : 'wasb_default'}
    remoteBaseLog: ${cfg.provider === 'aws' ? `s3://${fmt(cfg.projectName)}-${cfg.environment}-lake/airflow/logs` : cfg.provider === 'gcp' ? `gs://${fmt(cfg.projectName)}-${cfg.environment}-lake/airflow/logs` : 'wasb-airflow-logs'}

config:
  core:
    load_examples: 'False'
    parallelism: 64
    dag_concurrency: 32
  scheduler:
    catchup_by_default: 'False'
  webserver:
    expose_config: 'False'
    rbac: 'True'
`;
}

function genAirflowExampleDag(cfg) {
  if (!isDEOn(cfg) || cfg.deOrchestration !== 'airflow') return '';
  return `"""
${cfg.projectName} · daily lake-to-OLAP DAG
Generated by STAX. Reads ${deLakeTableName(cfg)} → writes to ${deOlapName(cfg)}.
"""
from datetime import datetime, timedelta
from airflow import DAG
from airflow.providers.cncf.kubernetes.operators.pod import KubernetesPodOperator

default_args = {
    "owner": "data-platform",
    "retries": 2,
    "retry_delay": timedelta(minutes=5),
    "email_on_failure": True,
}

with DAG(
    dag_id="${fmt(cfg.projectName)}_lake_to_olap",
    description="Compact yesterday's events from Iceberg into Pinot offline segments",
    schedule="0 2 * * *",      # 02:00 UTC daily
    start_date=datetime(2026, 1, 1),
    catchup=False,
    default_args=default_args,
    tags=["${fmt(cfg.projectName)}", "data-eng"],
) as dag:

    compact = KubernetesPodOperator(
        task_id="iceberg_compact",
        name="iceberg-compact-{{ ds_nodash }}",
        namespace="${fmt(cfg.projectName)}-de",
        image="apache/spark:3.5-scala2.12",
        cmds=["spark-submit"],
        arguments=[
            "--class", "org.apache.iceberg.spark.actions.RewriteDataFilesAction",
            "--conf", "spark.sql.catalog.iceberg=org.apache.iceberg.spark.SparkCatalog",
            "/opt/jobs/compact.py",
            "--table", "lake.events_enriched",
            "--date",  "{{ ds }}",
        ],
        service_account_name="de-worker",
    )

    publish = KubernetesPodOperator(
        task_id="pinot_offline_publish",
        name="pinot-publish-{{ ds_nodash }}",
        namespace="${fmt(cfg.projectName)}-de",
        image="apachepinot/pinot:1.2.0",
        cmds=["pinot-admin.sh"],
        arguments=[
            "LaunchDataIngestionJob",
            "-jobSpecFile", "/etc/pinot/spec.yaml",
            "-values", "executionDate={{ ds }}",
        ],
        service_account_name="de-worker",
    )

    compact >> publish
`;
}

// =============================================================
// genDataEngReadme — humanised guide to what STAX deployed
// =============================================================
function genDataEngReadme(cfg) {
  if (!isDEOn(cfg)) return '';
  return `# Data Engineering layer

This stack ships a complete data engineering platform alongside your application.

## Components

| Layer              | Tech                              | Where                          |
|--------------------|-----------------------------------|--------------------------------|
| Streaming ingest   | ${deStreamingName(cfg)}           | \`k8s/de/kafka.yaml\`          |
| OLAP / serving     | ${deOlapName(cfg)}                | \`k8s/de/pinot-values.yaml\`   |
| Stream processing  | ${deStreamProcName(cfg)}          | \`k8s/de/flink.yaml\`          |
| Wide-column / TS   | ${deWideColName(cfg)}             | \`k8s/de/cassandra.yaml\`      |
| Lake table format  | ${deLakeTableName(cfg)}           | object storage (lake bucket)   |
| Federated query    | ${deQueryEngineName(cfg)}         | \`k8s/de/trino-values.yaml\`   |
| Orchestration      | ${deOrchestrationName(cfg)}       | \`k8s/de/airflow-values.yaml\` |

## Data flow

\`\`\`
  apps + CDC ──▶ ${deStreamingName(cfg)} ──▶ ${deStreamProcName(cfg)} ──┬──▶ ${deOlapName(cfg)}  (sub-second analytics)
                                                                         │
                                                                         └──▶ ${deLakeTableName(cfg)}  (durable record)
                                                                                       │
                                                                                       └──▶ ${deQueryEngineName(cfg)} ──▶ analysts
\`\`\`

## Deploy order

\`\`\`bash
# 1. Provision lake bucket + IAM (terraform handles this)
terraform -chdir=terraform apply

# 2. Create namespace
kubectl create namespace ${fmt(cfg.projectName)}-de

# 3. Operators first (Strimzi for Kafka, K8ssandra for Cassandra, Flink-K8s)
helm install strimzi-kafka-operator strimzi/strimzi-kafka-operator -n strimzi-system --create-namespace
helm install k8ssandra-operator k8ssandra/k8ssandra-operator -n k8ssandra-operator --create-namespace
helm install flink-kubernetes-operator flink/flink-kubernetes-operator -n flink-system --create-namespace

# 4. Workloads
kubectl apply -f k8s/de/kafka.yaml
helm install pinot pinot/pinot -n ${fmt(cfg.projectName)}-de -f k8s/de/pinot-values.yaml
kubectl apply -f k8s/de/flink.yaml
kubectl apply -f k8s/de/cassandra.yaml
helm install trino trino/trino -n ${fmt(cfg.projectName)}-de -f k8s/de/trino-values.yaml
helm install airflow apache-airflow/airflow -n ${fmt(cfg.projectName)}-de -f k8s/de/airflow-values.yaml
\`\`\`

## Capacity (volume tier: ${cfg.deDataVolume})

Sized for:
- ${cfg.deDataVolume === 'small' ? '<100 MB/s ingest · ~10 TB/yr · single-AZ' : cfg.deDataVolume === 'medium' ? '<1 GB/s ingest · ~1 PB/yr · multi-AZ' : '>1 GB/s ingest · 10+ PB/yr · multi-region'}

Resize knobs in \`k8s/de/*-values.yaml\`. Re-running STAX with a different volume tier regenerates these.

## What's wired together

- **Apps publish to Kafka** topic \`events.${fmt(cfg.projectName)}.raw\`
- **Flink** subscribes to that topic, joins with CDC user dims, writes to:
  - \`events.${fmt(cfg.projectName)}.enriched\` (Pinot tails this for sub-second queries)
  - \`lake.events_enriched\` Iceberg table (durable record)
- **Trino** federates across Pinot + Iceberg + Cassandra + Postgres for analyst queries
- **Airflow** runs nightly compaction (Iceberg) + offline segment publish (Pinot)

## Cost approximation

| Tier | Compute | Storage | Total |
|------|---------|---------|-------|
| Small  | ~$1,200/mo  | ~$200/mo  | ~$1,400/mo |
| Medium | ~$4,800/mo  | ~$1,500/mo | ~$6,300/mo |
| Large  | ~$18,000/mo | ~$8,000/mo | ~$26,000/mo |

These are open-source self-hosted approximations. Managed equivalents
(Confluent Cloud, StarTree, Imply, AstraDB) typically run 1.5–3× higher.
`;
}

function genGitHubActions(cfg) {
  const cloudAuth = {
    aws: `      - name: Configure AWS credentials (OIDC)
        uses: aws-actions/configure-aws-credentials@v4
        with:
          role-to-assume: \${{ secrets.AWS_DEPLOY_ROLE_ARN }}
          aws-region: \${{ env.AWS_REGION }}`,
    gcp: `      - name: Authenticate to Google Cloud (OIDC)
        uses: google-github-actions/auth@v2
        with:
          workload_identity_provider: \${{ secrets.GCP_WORKLOAD_IDENTITY_PROVIDER }}
          service_account: \${{ secrets.GCP_DEPLOY_SA }}`,
    azure: `      - name: Azure login (OIDC)
        uses: azure/login@v2
        with:
          client-id: \${{ secrets.AZURE_CLIENT_ID }}
          tenant-id: \${{ secrets.AZURE_TENANT_ID }}
          subscription-id: \${{ secrets.AZURE_SUBSCRIPTION_ID }}`,
  };

  return `# .github/workflows/deploy.yml
name: deploy

on:
  push:
    branches: [main]
  pull_request:
    branches: [main]
  workflow_dispatch:
    inputs:
      environment:
        description: 'Target environment'
        required: true
        default: 'dev'
        type: choice
        options: [dev, staging, prod]

permissions:
  id-token: write   # OIDC federation
  contents: read
  pull-requests: write  # PR plan comments

env:
  TF_VERSION: 1.7.5
  AWS_REGION: ${cfg.region}
  TF_IN_AUTOMATION: true

concurrency:
  group: deploy-\${{ github.ref }}-\${{ github.event.inputs.environment || 'dev' }}
  cancel-in-progress: false

jobs:
  # ── Lint and validate ────────────────────────────────────────────
  validate:
    name: validate
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - uses: hashicorp/setup-terraform@v3
        with:
          terraform_version: \${{ env.TF_VERSION }}

      - name: terraform fmt
        run: terraform -chdir=terraform fmt -check -recursive

      - name: terraform init (no backend)
        run: terraform -chdir=terraform init -backend=false

      - name: terraform validate
        run: terraform -chdir=terraform validate

      - name: tflint
        uses: terraform-linters/setup-tflint@v4

      - name: trivy IaC scan
        uses: aquasecurity/trivy-action@master
        with:
          scan-type: config
          scan-ref: terraform/
          exit-code: '1'
          severity: HIGH,CRITICAL
${isContainer(cfg.compute) ? `
  # ── Build container image ────────────────────────────────────────
  build:
    name: build
    needs: validate
    runs-on: ubuntu-latest
    outputs:
      image_tag: \${{ steps.meta.outputs.tag }}
    steps:
      - uses: actions/checkout@v4

${cloudAuth[cfg.provider]}

      - name: Image metadata
        id: meta
        run: echo "tag=\${{ github.sha }}" >> $GITHUB_OUTPUT

${cfg.provider === 'aws' ? `      - name: ECR login
        id: ecr
        uses: aws-actions/amazon-ecr-login@v2

      - name: Build and push
        uses: docker/build-push-action@v5
        with:
          context: ./app
          push: true
          tags: \${{ steps.ecr.outputs.registry }}/${fmt(cfg.projectName)}-${cfg.environment}-app:\${{ steps.meta.outputs.tag }}
          cache-from: type=gha
          cache-to: type=gha,mode=max` : ''}` : ''}

  # ── Plan ─────────────────────────────────────────────────────────
  plan:
    name: plan
    needs: validate
    runs-on: ubuntu-latest
    environment: ${cfg.environment}
    steps:
      - uses: actions/checkout@v4

${cloudAuth[cfg.provider]}

      - uses: hashicorp/setup-terraform@v3
        with:
          terraform_version: \${{ env.TF_VERSION }}

      - name: terraform init
        run: terraform -chdir=terraform init

      - name: terraform plan
        id: plan
        run: |
          terraform -chdir=terraform plan -no-color -out=tfplan \\
            -var="environment=\${{ github.event.inputs.environment || '${cfg.environment}' }}"

      - name: Upload plan
        uses: actions/upload-artifact@v4
        with:
          name: tfplan
          path: terraform/tfplan
          retention-days: 7

      - name: Plan summary on PR
        if: github.event_name == 'pull_request'
        uses: actions/github-script@v7
        with:
          script: |
            const { data: { id } } = await github.rest.issues.createComment({
              issue_number: context.issue.number,
              owner: context.repo.owner,
              repo: context.repo.repo,
              body: '✅ Plan generated. See artifact \`tfplan\` for details.'
            });

  # ── Apply (gated on prod) ────────────────────────────────────────
  apply:
    name: apply
    needs: [plan${isContainer(cfg.compute) ? ', build' : ''}]
    if: github.event_name != 'pull_request' && (github.ref == 'refs/heads/main' || github.event_name == 'workflow_dispatch')
    runs-on: ubuntu-latest
    environment:
      name: ${cfg.environment}
      url: \${{ steps.outputs.outputs.endpoint }}
    steps:
      - uses: actions/checkout@v4

${cloudAuth[cfg.provider]}

      - uses: hashicorp/setup-terraform@v3
        with:
          terraform_version: \${{ env.TF_VERSION }}

      - name: Download plan
        uses: actions/download-artifact@v4
        with:
          name: tfplan
          path: terraform/

      - name: terraform init
        run: terraform -chdir=terraform init

      - name: terraform apply
        run: terraform -chdir=terraform apply -auto-approve tfplan

      - name: Capture outputs
        id: outputs
        run: |
          ENDPOINT=$(terraform -chdir=terraform output -raw ${cfg.compute === 'lambda' ? 'api_endpoint' : 'cluster_endpoint'} 2>/dev/null || echo "n/a")
          echo "endpoint=$ENDPOINT" >> $GITHUB_OUTPUT
${isK8s(cfg.compute) ? `
      - name: Deploy to Kubernetes
        run: |
          aws eks update-kubeconfig --name \${{ env.PROJECT }}-${cfg.environment} --region \${{ env.AWS_REGION }}
          kubectl apply -f k8s/ --record
          kubectl rollout status deployment/app -n default --timeout=10m
        env:
          PROJECT: ${fmt(cfg.projectName)}` : ''}

  # ── Smoke tests ──────────────────────────────────────────────────
  smoke:
    name: smoke
    needs: apply
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - name: Health check
        run: |
          for i in 1 2 3 4 5; do
            curl -fsSL "\${{ needs.apply.outputs.endpoint || 'https://example.com' }}/healthz" && break
            sleep 10
          done
`;
}

function genK8sManifests(cfg) {
  if (!isK8s(cfg.compute)) return null;
  return `# k8s/deployment.yaml
apiVersion: apps/v1
kind: Deployment
metadata:
  name: app
  namespace: default
  labels:
    app: ${fmt(cfg.projectName)}
    environment: ${cfg.environment}
spec:
  replicas: ${cfg.environment === 'prod' ? 3 : 2}
  strategy:
    type: RollingUpdate
    rollingUpdate:
      maxSurge: 25%
      maxUnavailable: 0
  selector:
    matchLabels:
      app: ${fmt(cfg.projectName)}
  template:
    metadata:
      labels:
        app: ${fmt(cfg.projectName)}
        environment: ${cfg.environment}
      annotations:
        prometheus.io/scrape: "true"
        prometheus.io/port: "9090"
    spec:
      serviceAccountName: app
      securityContext:
        runAsNonRoot: true
        runAsUser: 65532
        fsGroup: 65532
        seccompProfile:
          type: RuntimeDefault
      containers:
        - name: app
          image: ${fmt(cfg.projectName)}-${cfg.environment}-app:latest
          imagePullPolicy: IfNotPresent
          ports:
            - name: http
              containerPort: 8080
            - name: metrics
              containerPort: 9090
          env:
            - name: ENVIRONMENT
              value: ${cfg.environment}
            - name: POD_NAMESPACE
              valueFrom:
                fieldRef:
                  fieldPath: metadata.namespace
          resources:
            requests:
              cpu: ${cfg.environment === 'prod' ? '500m' : '100m'}
              memory: ${cfg.environment === 'prod' ? '512Mi' : '128Mi'}
            limits:
              cpu: ${cfg.environment === 'prod' ? '2000m' : '500m'}
              memory: ${cfg.environment === 'prod' ? '2Gi' : '512Mi'}
          livenessProbe:
            httpGet:
              path: /healthz
              port: http
            initialDelaySeconds: 15
            periodSeconds: 10
          readinessProbe:
            httpGet:
              path: /readyz
              port: http
            initialDelaySeconds: 5
            periodSeconds: 5
          securityContext:
            allowPrivilegeEscalation: false
            readOnlyRootFilesystem: true
            capabilities:
              drop: [ALL]
      topologySpreadConstraints:
        - maxSkew: 1
          topologyKey: topology.kubernetes.io/zone
          whenUnsatisfiable: ScheduleAnyway
          labelSelector:
            matchLabels:
              app: ${fmt(cfg.projectName)}
---
apiVersion: v1
kind: Service
metadata:
  name: app
  namespace: default
spec:
  type: ClusterIP
  selector:
    app: ${fmt(cfg.projectName)}
  ports:
    - name: http
      port: 80
      targetPort: http
---
apiVersion: autoscaling/v2
kind: HorizontalPodAutoscaler
metadata:
  name: app
  namespace: default
spec:
  scaleTargetRef:
    apiVersion: apps/v1
    kind: Deployment
    name: app
  minReplicas: ${cfg.environment === 'prod' ? 3 : 2}
  maxReplicas: ${cfg.environment === 'prod' ? 30 : 6}
  metrics:
    - type: Resource
      resource:
        name: cpu
        target:
          type: Utilization
          averageUtilization: 70
    - type: Resource
      resource:
        name: memory
        target:
          type: Utilization
          averageUtilization: 80
---
apiVersion: policy/v1
kind: PodDisruptionBudget
metadata:
  name: app
  namespace: default
spec:
  minAvailable: ${cfg.environment === 'prod' ? '50%' : 1}
  selector:
    matchLabels:
      app: ${fmt(cfg.projectName)}
`;
}

function genDockerfile(cfg) {
  if (!isContainer(cfg.compute)) return null;
  return `# Dockerfile · multi-stage · distroless · non-root
FROM node:20-alpine AS deps
WORKDIR /build
COPY package*.json ./
RUN npm ci --omit=dev --ignore-scripts

FROM node:20-alpine AS build
WORKDIR /build
COPY package*.json ./
RUN npm ci --ignore-scripts
COPY . .
RUN npm run build

FROM gcr.io/distroless/nodejs20-debian12:nonroot AS runtime
WORKDIR /app
COPY --from=deps   /build/node_modules ./node_modules
COPY --from=build  /build/dist          ./dist
COPY --from=build  /build/package.json  ./

ENV NODE_ENV=production
EXPOSE 8080

USER nonroot
CMD ["dist/server.js"]
`;
}

function genReadme(cfg) {
  return `# ${cfg.projectName}

Infrastructure-as-code stack generated by **STAX**.

| | |
|---|---|
| Cloud      | ${cfg.provider.toUpperCase()} |
| Region     | \`${cfg.region}\` |
| Environment| \`${cfg.environment}\` |
| Compute    | ${computeName(cfg)} |
| Database   | ${dbName(cfg)} |
| CI/CD      | ${CICD_OPTIONS.find(x=>x.id===cfg.cicd)?.name} |
| Observ.    | ${OBSERVABILITY_OPTIONS.find(x=>x.id===cfg.observability)?.name} |

---

## Layout

\`\`\`
.
├── terraform/
│   ├── main.tf            # providers, backend, locals
│   ├── variables.tf       # input variables
│   ├── networking.tf      # VPC, subnets, NAT, flow logs
│   ├── compute.tf         # ${computeName(cfg)}
│   ├── database.tf        # ${dbName(cfg)}
│   └── outputs.tf         # exported values
├── .github/workflows/
│   └── deploy.yml         # validate → plan → apply pipeline
${isK8s(cfg.compute) ? '├── k8s/\n│   └── deployment.yaml    # Deployment, Service, HPA, PDB\n' : ''}${isContainer(cfg.compute) ? '├── Dockerfile             # multi-stage, distroless, non-root\n' : ''}└── README.md
\`\`\`

## Bootstrap

1. **Backend.** Pre-create the state bucket and lock table once per project:
   \`\`\`
   aws s3api create-bucket --bucket ${fmt(cfg.projectName)}-tfstate --region ${cfg.region}
   aws s3api put-bucket-versioning --bucket ${fmt(cfg.projectName)}-tfstate --versioning-configuration Status=Enabled
   aws dynamodb create-table --table-name ${fmt(cfg.projectName)}-tfstate-locks \\
     --attribute-definitions AttributeName=LockID,AttributeType=S \\
     --key-schema AttributeName=LockID,KeyType=HASH \\
     --billing-mode PAY_PER_REQUEST
   \`\`\`

2. **OIDC role.** Create a deployment role assumable from your CI provider. Store the ARN as the \`AWS_DEPLOY_ROLE_ARN\` GitHub secret.

3. **Plan and apply.**
   \`\`\`
   cd terraform
   terraform init
   terraform plan -out=tfplan
   terraform apply tfplan
   \`\`\`

## Operating

- All state changes flow through the **deploy** workflow. Direct \`apply\` against prod is disabled by environment protection rules.
- Database credentials live in AWS Secrets Manager; applications read them via IAM, never via env vars.
${isK8s(cfg.compute) ? '- Cluster access is via IAM-mapped \`aws-auth\` ConfigMap. Add team roles via the \`mapRoles\` block.\n' : ''}- VPC flow logs ship to CloudWatch with ${cfg.environment === 'prod' ? '90' : '14'}-day retention.
- Cost alerting: configure a Cost Anomaly monitor scoped to the \`Project=${cfg.projectName}\` tag.
${isAIEnabled(cfg) ? `
## AI / Agent layer

- **Inference**: ${aiInferenceName(cfg)}${isSelfHostedLLM(cfg) ? ` on \`${(cfg.gpuType || '').split(' ')[0]}\` GPU nodes` : ''}
- **Vector store**: ${vectorDBName(cfg)}
- **Agent framework**: ${agentFwName(cfg)}
- **Observability**: ${LLM_OBSERVABILITY.find(o => o.id === cfg.llmObservability)?.name}

The agent service lives in \`agent/\` and is built into a distroless container. It exposes:
- \`GET /healthz\` — liveness
- \`GET /readyz\`  — readiness
- \`POST /chat\`   — RAG-backed Q&A endpoint

${isSelfHostedLLM(cfg) ? `### GPU node group

The vLLM Deployment in \`k8s/vllm.yaml\` schedules onto GPU nodes via:
- \`nvidia.com/gpu\` toleration
- \`workload=inference\` nodeSelector

Install the NVIDIA device plugin DaemonSet before first apply:
\`\`\`
helm install nvdp nvidia-device-plugin --namespace kube-system \\
  --create-namespace --set tolerations[0].operator=Exists
\`\`\`
` : ''}${cfg.aiInference === 'bedrock' ? `### Bedrock model access

Foundation model access is region-specific and must be requested in the AWS console (Bedrock → Model access). The IAM policy generated here scopes access to Anthropic, Llama, Mistral, and Titan families.

A guardrail (\`aws_bedrock_guardrail.main\`) applies content filters and PII redaction by default.
` : ''}${cfg.vectorDB === 'pgvector' ? `### pgvector setup

After RDS is provisioned, run as DB superuser:
\`\`\`sql
CREATE EXTENSION IF NOT EXISTS vector;
CREATE TABLE embeddings (
  id BIGSERIAL PRIMARY KEY,
  document_id TEXT NOT NULL,
  chunk_text TEXT NOT NULL,
  embedding VECTOR(1536),
  metadata JSONB,
  created_at TIMESTAMPTZ DEFAULT NOW()
);
CREATE INDEX ON embeddings USING ivfflat (embedding vector_cosine_ops) WITH (lists = 100);
\`\`\`
` : ''}### Eval pipeline

Pull requests touching \`agent/\` or \`evals/\` trigger \`.github/workflows/evals.yml\`, which runs the golden-set in \`evals/golden.jsonl\` against the configured LLM and posts a per-metric report to the PR. The job fails on regression below thresholds (faithfulness ≥ 0.85, answer-relevancy ≥ 0.80).
` : ''}

## Destroy

\`\`\`
terraform destroy -var="environment=${cfg.environment}"
\`\`\`

> ⚠ Production has \`deletion_protection = true\` on RDS and \`deletion_protection_enabled = true\` on DynamoDB. Disable in code, plan, apply, *then* destroy.

## Security

This stack treats AI/LLM/MCP security as the **spine**, not a feature bolted on at the end.${isAIEnabled(cfg) ? `

- **\`agent/guardrails.py\`** wraps every LLM call: PII redaction (${piiDetectorName(cfg)}) → prompt firewall (${promptFirewallName(cfg)}) → topic check → LLM → output moderation${cfg.llmAuditLog ? ' → audit log' : ''}. Use \`guarded_llm()\` as a drop-in replacement for \`call_llm()\` in your handlers.${cfg.directoryMcpEnabled ? `
- **\`agent/mcp_policy.yaml\`** defines the MCP server allowlist, signature verification, and schema pinning to defend against tool poisoning + rug-pull attacks.` : ''}` : ''}
- **\`.github/workflows/security.yml\`** runs on every PR + nightly: ${(cfg.scanTools || []).filter(s => !['garak','pyrit','promptfoo','modelscan'].includes(s)).map(s => SCAN_TOOLS.find(t => t.id === s)?.name).filter(Boolean).join(', ') || '— no scans configured —'}.${(cfg.scanTools || []).some(s => ['garak','pyrit','promptfoo','modelscan'].includes(s)) ? `
- **\`.github/workflows/ai-redteam.yml\`** runs daily + on agent changes: ${(cfg.scanTools || []).filter(s => ['garak','pyrit','promptfoo','modelscan'].includes(s)).map(s => SCAN_TOOLS.find(t => t.id === s)?.name).filter(Boolean).join(', ')}.` : ''}${isK8s(cfg.compute) ? `
- **Runtime**: ${[cfg.podSecurityRestricted && 'PodSecurityStandards (restricted)', cfg.networkPoliciesDefault && 'NetworkPolicies (default-deny)', (cfg.scanTools || []).includes('falco') && 'Falco syscall-level threat detection'].filter(Boolean).join(' · ') || 'minimal hardening — review before prod'}.` : ''}
- **Compliance**: ${(cfg.complianceFrameworks || []).map(f => COMPLIANCE_FRAMEWORKS.find(x => x.id === f)?.short).filter(Boolean).join(', ') || 'no frameworks selected'} — control-to-artifact mapping in \`compliance/control-matrix.md\`.
- **Alerting**: routes via ${(cfg.alertChannels || []).map(c => ALERT_CHANNELS.find(x => x.id === c)?.name).filter(Boolean).join(', ') || 'no channels configured'} (\`monitoring/alertmanager.yaml\`).

See **\`SECURITY.md\`** for the full posture breakdown and defense-in-depth diagram, **\`compliance/incident-response.md\`** for the IR runbook with kill-switch commands.

---
_Generated ${new Date().toISOString().slice(0,10)} · regenerate with STAX to refresh._${cfg._watermark ? `

> ⓘ Generated with **STAX** (Free plan). Upgrade to Pro to remove this footer and unlock advanced compliance frameworks (HIPAA, PCI-DSS, EU AI Act, NIST AI RMF, ISO 42001, FedRAMP). https://stax.dev/pricing` : ''}
`;
}

// =============================================================
// AI GENERATORS
// =============================================================

function genTerraformAI(cfg) {
  if (!isAIEnabled(cfg)) return '';
  const name = fmt(cfg.projectName);
  const env = cfg.environment;
  const isProd = env === 'prod';

  let body = tfHeader(cfg) + `
# ─────────────────────────────────────────────────────────────────
# AI / ML LAYER
#   inference: ${aiInferenceName(cfg)}
#   vector DB: ${vectorDBName(cfg)}
#   agents:    ${agentFwName(cfg)}
# ─────────────────────────────────────────────────────────────────
`;

  // ---- Inference layer ----
  if (cfg.aiInference === 'bedrock') {
    body += `
# ── AWS Bedrock model access (region-scoped IAM) ────────────────
data "aws_caller_identity" "current" {}

resource "aws_iam_policy" "bedrock_invoke" {
  name        = "\${local.name_prefix}-bedrock-invoke"
  description = "Allow application to invoke Bedrock foundation models"

  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [
      {
        Effect = "Allow"
        Action = [
          "bedrock:InvokeModel",
          "bedrock:InvokeModelWithResponseStream",
          "bedrock:Converse",
          "bedrock:ConverseStream",
        ]
        Resource = [
          "arn:aws:bedrock:\${var.region}::foundation-model/anthropic.claude-3-5-sonnet-*",
          "arn:aws:bedrock:\${var.region}::foundation-model/anthropic.claude-3-haiku-*",
          "arn:aws:bedrock:\${var.region}::foundation-model/meta.llama3-*",
          "arn:aws:bedrock:\${var.region}::foundation-model/mistral.*",
          "arn:aws:bedrock:\${var.region}::foundation-model/amazon.titan-embed-*",
        ]
      },
      {
        Effect = "Allow"
        Action = ["bedrock:GetFoundationModel", "bedrock:ListFoundationModels"]
        Resource = "*"
      }
    ]
  })

  tags = local.common_tags
}

# Bedrock guardrail for content filtering / PII redaction
resource "aws_bedrock_guardrail" "main" {
  name                      = "\${local.name_prefix}-guardrail"
  description               = "Default content + PII filters for ${name}"
  blocked_input_messaging   = "Your request was blocked by content policy."
  blocked_outputs_messaging = "I cannot provide that response."

  content_policy_config {
    filters_config {
      input_strength  = "HIGH"
      output_strength = "HIGH"
      type            = "SEXUAL"
    }
    filters_config {
      input_strength  = "HIGH"
      output_strength = "HIGH"
      type            = "VIOLENCE"
    }
    filters_config {
      input_strength  = "HIGH"
      output_strength = "HIGH"
      type            = "HATE"
    }
    filters_config {
      input_strength  = "MEDIUM"
      output_strength = "MEDIUM"
      type            = "INSULTS"
    }
    filters_config {
      input_strength  = "HIGH"
      output_strength = "HIGH"
      type            = "PROMPT_ATTACK"
    }
  }

  sensitive_information_policy_config {
    pii_entities_config {
      action = "ANONYMIZE"
      type   = "EMAIL"
    }
    pii_entities_config {
      action = "ANONYMIZE"
      type   = "PHONE"
    }
    pii_entities_config {
      action = "BLOCK"
      type   = "CREDIT_DEBIT_CARD_NUMBER"
    }
  }

  tags = local.common_tags
}
`;
  }

  if (cfg.aiInference === 'sagemaker') {
    body += `
# ── SageMaker real-time endpoint (custom/fine-tuned model) ──────
resource "aws_sagemaker_model" "main" {
  name               = "\${local.name_prefix}-model"
  execution_role_arn = aws_iam_role.sagemaker_exec.arn

  primary_container {
    image          = var.sagemaker_image_uri
    model_data_url = var.sagemaker_model_artifact
  }

  tags = local.common_tags
}

resource "aws_sagemaker_endpoint_configuration" "main" {
  name = "\${local.name_prefix}-ep-config"

  production_variants {
    variant_name           = "primary"
    model_name             = aws_sagemaker_model.main.name
    initial_instance_count = ${isProd ? 2 : 1}
    instance_type          = "ml.g5.xlarge"
    initial_variant_weight = 1
  }

  data_capture_config {
    enable_capture              = true
    initial_sampling_percentage = 100
    destination_s3_uri          = "s3://\${aws_s3_bucket.ml_artifacts.id}/capture"

    capture_options { capture_mode = "Input" }
    capture_options { capture_mode = "Output" }
  }

  tags = local.common_tags
}

resource "aws_sagemaker_endpoint" "main" {
  name                 = "\${local.name_prefix}-endpoint"
  endpoint_config_name = aws_sagemaker_endpoint_configuration.main.name
  tags                 = local.common_tags
}

resource "aws_iam_role" "sagemaker_exec" {
  name = "\${local.name_prefix}-sagemaker-exec"
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Principal = { Service = "sagemaker.amazonaws.com" }
      Action = "sts:AssumeRole"
    }]
  })
  tags = local.common_tags
}

resource "aws_iam_role_policy_attachment" "sagemaker_full" {
  role       = aws_iam_role.sagemaker_exec.name
  policy_arn = "arn:aws:iam::aws:policy/AmazonSageMakerFullAccess"
}

resource "aws_s3_bucket" "ml_artifacts" {
  bucket = "\${local.name_prefix}-ml-artifacts"
  tags   = local.common_tags
}
`;
  }

  if (cfg.aiInference === 'vllm-eks') {
    body += `
# ── GPU node group for self-hosted vLLM inference ───────────────
resource "aws_eks_node_group" "gpu" {
  cluster_name    = aws_eks_cluster.main.name
  node_group_name = "gpu-inference"
  node_role_arn   = aws_iam_role.eks_nodes.arn
  subnet_ids      = aws_subnet.private[*].id

  ami_type        = "AL2_x86_64_GPU"
  instance_types  = ["${cfg.gpuType?.startsWith('p5') ? 'p5.48xlarge' : cfg.gpuType?.startsWith('p4d') ? 'p4d.24xlarge' : cfg.gpuType?.startsWith('g6') ? 'g6.xlarge' : 'g5.xlarge'}"]
  capacity_type   = "${isProd ? 'ON_DEMAND' : 'SPOT'}"

  scaling_config {
    desired_size = ${isProd ? 2 : 1}
    min_size     = ${isProd ? 1 : 0}
    max_size     = ${isProd ? 8 : 3}
  }

  taint {
    key    = "nvidia.com/gpu"
    value  = "true"
    effect = "NO_SCHEDULE"
  }

  labels = {
    "workload" = "inference"
    "gpu"      = "true"
  }

  tags = local.common_tags

  depends_on = [aws_eks_cluster.main]
}

# NVIDIA device plugin DaemonSet (managed via helm in CI/CD)
resource "aws_iam_policy" "vllm_s3_models" {
  name = "\${local.name_prefix}-vllm-models"
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Action = ["s3:GetObject", "s3:ListBucket"]
      Resource = [
        aws_s3_bucket.ml_artifacts.arn,
        "\${aws_s3_bucket.ml_artifacts.arn}/*",
      ]
    }]
  })
}

resource "aws_s3_bucket" "ml_artifacts" {
  bucket = "\${local.name_prefix}-ml-artifacts"
  tags   = local.common_tags
}
`;
  }

  if (cfg.aiInference === 'external') {
    body += `
# ── External LLM provider (OpenAI / Anthropic / etc) ────────────
# API keys live in Secrets Manager with rotation alarms.

resource "aws_secretsmanager_secret" "llm_api_key" {
  name                    = "\${local.name_prefix}/llm/api-key"
  description             = "API key for external LLM provider"
  recovery_window_in_days = 7
  kms_key_id              = aws_kms_key.app.arn
  tags                    = local.common_tags
}

resource "aws_secretsmanager_secret_version" "llm_api_key" {
  secret_id     = aws_secretsmanager_secret.llm_api_key.id
  secret_string = jsonencode({ api_key = var.llm_api_key, provider = var.llm_provider })

  lifecycle { ignore_changes = [secret_string] }
}

# Cost guardrail — alarm if monthly LLM spend exceeds budget
resource "aws_budgets_budget" "llm_spend" {
  name              = "\${local.name_prefix}-llm-spend"
  budget_type       = "COST"
  limit_amount      = "${isProd ? 2000 : 200}"
  limit_unit        = "USD"
  time_unit         = "MONTHLY"
  cost_filter {
    name   = "TagKeyValue"
    values = ["user:Component$llm"]
  }

  notification {
    comparison_operator        = "GREATER_THAN"
    threshold                  = 80
    threshold_type             = "PERCENTAGE"
    notification_type          = "FORECASTED"
    subscriber_email_addresses = [var.alarm_email]
  }
}
`;
  }

  // ---- Vector DB ----
  if (cfg.vectorDB === 'pgvector') {
    body += `
# ── pgvector extension on existing Postgres ─────────────────────
# Run after RDS provisioning:
#   CREATE EXTENSION IF NOT EXISTS vector;
# Application schema for embeddings:
resource "null_resource" "pgvector_setup" {
  depends_on = [aws_db_instance.main]

  provisioner "local-exec" {
    when    = create
    command = <<-EOT
      echo "Provisioning pgvector — run as DB superuser:"
      echo "  CREATE EXTENSION IF NOT EXISTS vector;"
      echo "  CREATE TABLE IF NOT EXISTS embeddings ("
      echo "    id BIGSERIAL PRIMARY KEY,"
      echo "    document_id TEXT NOT NULL,"
      echo "    chunk_text TEXT NOT NULL,"
      echo "    embedding VECTOR(1536),"
      echo "    metadata JSONB,"
      echo "    created_at TIMESTAMPTZ DEFAULT NOW()"
      echo "  );"
      echo "  CREATE INDEX ON embeddings USING ivfflat (embedding vector_cosine_ops) WITH (lists = 100);"
    EOT
  }
}
`;
  }

  if (cfg.vectorDB === 'opensearch') {
    body += `
# ── OpenSearch with k-NN for hybrid retrieval ───────────────────
resource "aws_opensearch_domain" "vectors" {
  domain_name    = "\${local.name_prefix}-vectors"
  engine_version = "OpenSearch_2.13"

  cluster_config {
    instance_type            = "${isProd ? 'r6g.large.search' : 't3.small.search'}"
    instance_count           = ${isProd ? 3 : 1}
    zone_awareness_enabled   = ${isProd ? 'true' : 'false'}
    ${isProd ? `zone_awareness_config { availability_zone_count = 3 }` : ''}
  }

  ebs_options {
    ebs_enabled = true
    volume_size = ${isProd ? 100 : 20}
    volume_type = "gp3"
  }

  node_to_node_encryption { enabled = true }
  encrypt_at_rest         { enabled = true; kms_key_id = aws_kms_key.app.arn }
  domain_endpoint_options { enforce_https = true; tls_security_policy = "Policy-Min-TLS-1-2-2019-07" }

  vpc_options {
    subnet_ids         = ${isProd ? 'aws_subnet.private[*].id' : '[aws_subnet.private[0].id]'}
    security_group_ids = [aws_security_group.opensearch.id]
  }

  advanced_options = {
    "rest.action.multi.allow_explicit_index" = "true"
    "indices.knn.memory.circuit_breaker.limit" = "70%"
  }

  tags = local.common_tags
}

resource "aws_security_group" "opensearch" {
  name   = "\${local.name_prefix}-opensearch-sg"
  vpc_id = aws_vpc.main.id

  ingress {
    from_port       = 443
    to_port         = 443
    protocol        = "tcp"
    security_groups = [aws_security_group.app.id]
  }

  tags = local.common_tags
}
`;
  }

  if (cfg.vectorDB === 'qdrant' || cfg.vectorDB === 'weaviate') {
    body += `
# ── ${cfg.vectorDB === 'qdrant' ? 'Qdrant' : 'Weaviate'} on Kubernetes (deployed via Helm in CI/CD) ─────
# This module reserves storage; Helm chart deploys runtime.
resource "aws_ebs_volume" "vector_data" {
  availability_zone = "\${var.region}a"
  size              = ${isProd ? 200 : 50}
  type              = "gp3"
  encrypted         = true
  kms_key_id        = aws_kms_key.app.arn
  tags = merge(local.common_tags, { Name = "\${local.name_prefix}-${cfg.vectorDB}-data" })
}
`;
  }

  if (cfg.vectorDB === 'pinecone') {
    body += `
# ── Pinecone API key (managed SaaS, no infra) ───────────────────
resource "aws_secretsmanager_secret" "pinecone" {
  name        = "\${local.name_prefix}/pinecone/api-key"
  kms_key_id  = aws_kms_key.app.arn
  tags        = local.common_tags
}

resource "aws_secretsmanager_secret_version" "pinecone" {
  secret_id     = aws_secretsmanager_secret.pinecone.id
  secret_string = jsonencode({
    api_key      = var.pinecone_api_key
    environment  = var.pinecone_environment
    index_name   = "\${local.name_prefix}-rag"
  })
  lifecycle { ignore_changes = [secret_string] }
}
`;
  }

  // ---- Agent service IAM (IRSA) ----
  if (isK8s(cfg.compute)) {
    body += `
# ── IRSA role for agent service pod ─────────────────────────────
resource "aws_iam_role" "agent_service" {
  name = "\${local.name_prefix}-agent-svc"

  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Principal = { Federated = aws_iam_openid_connect_provider.eks.arn }
      Action = "sts:AssumeRoleWithWebIdentity"
      Condition = {
        StringEquals = {
          "\${replace(aws_iam_openid_connect_provider.eks.url, "https://", "")}:sub" = "system:serviceaccount:${name}:agent-svc"
        }
      }
    }]
  })

  tags = local.common_tags
}
${cfg.aiInference === 'bedrock' ? `
resource "aws_iam_role_policy_attachment" "agent_bedrock" {
  role       = aws_iam_role.agent_service.name
  policy_arn = aws_iam_policy.bedrock_invoke.arn
}` : ''}
${cfg.aiInference === 'external' ? `
resource "aws_iam_role_policy" "agent_secrets" {
  role = aws_iam_role.agent_service.name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = ["secretsmanager:GetSecretValue"]
      Resource = aws_secretsmanager_secret.llm_api_key.arn
    }]
  })
}` : ''}
`;
  }

  // ---- Outputs ----
  body += `
# ── outputs ─────────────────────────────────────────────────────
${cfg.aiInference === 'bedrock' ? `
output "bedrock_guardrail_id" { value = aws_bedrock_guardrail.main.id }
output "bedrock_policy_arn"   { value = aws_iam_policy.bedrock_invoke.arn }
` : ''}${cfg.aiInference === 'sagemaker' ? `
output "sagemaker_endpoint" { value = aws_sagemaker_endpoint.main.name }
` : ''}${cfg.vectorDB === 'opensearch' ? `
output "opensearch_endpoint" { value = aws_opensearch_domain.vectors.endpoint; sensitive = true }
` : ''}${cfg.vectorDB === 'pinecone' ? `
output "pinecone_secret_arn" { value = aws_secretsmanager_secret.pinecone.arn }
` : ''}`;

  return body;
}

function genAgentService(cfg) {
  if (!isAIEnabled(cfg)) return '';
  const fw = cfg.agentFramework;
  const inf = cfg.aiInference;
  const vdb = cfg.vectorDB;

  // ---- LLM client snippet ----
  let llmClient = '';
  if (inf === 'bedrock') {
    llmClient = `import boto3
from botocore.config import Config

bedrock = boto3.client(
    "bedrock-runtime",
    region_name=os.environ["AWS_REGION"],
    config=Config(retries={"max_attempts": 3, "mode": "adaptive"}),
)

def call_llm(messages: list[dict], model: str = "anthropic.claude-3-5-sonnet-20241022-v2:0") -> str:
    """Call Bedrock Converse API with guardrail."""
    response = bedrock.converse(
        modelId=model,
        messages=messages,
        inferenceConfig={"maxTokens": 4096, "temperature": 0.7},
        guardrailConfig={
            "guardrailIdentifier": os.environ["BEDROCK_GUARDRAIL_ID"],
            "guardrailVersion": "DRAFT",
            "trace": "enabled",
        },
    )
    return response["output"]["message"]["content"][0]["text"]`;
  } else if (inf === 'vertex') {
    llmClient = `from google.cloud import aiplatform
from vertexai.generative_models import GenerativeModel

aiplatform.init(project=os.environ["GCP_PROJECT"], location=os.environ["GCP_REGION"])
model = GenerativeModel("gemini-1.5-pro-002")

def call_llm(messages: list[dict], **kw) -> str:
    response = model.generate_content([m["content"] for m in messages])
    return response.text`;
  } else if (inf === 'aoai') {
    llmClient = `from openai import AzureOpenAI

client = AzureOpenAI(
    azure_endpoint=os.environ["AZURE_OPENAI_ENDPOINT"],
    api_key=os.environ["AZURE_OPENAI_KEY"],
    api_version="2024-08-01-preview",
)

def call_llm(messages: list[dict], model: str = "gpt-4o") -> str:
    response = client.chat.completions.create(
        model=model,
        messages=messages,
        temperature=0.7,
        max_tokens=4096,
    )
    return response.choices[0].message.content`;
  } else if (inf?.startsWith('vllm-')) {
    llmClient = `import httpx

VLLM_URL = os.environ.get("VLLM_URL", "http://vllm-svc.${fmt(cfg.projectName)}.svc.cluster.local:8000/v1")

async def call_llm(messages: list[dict], model: str = "${OPEN_MODELS[0]}") -> str:
    """Call self-hosted vLLM via OpenAI-compatible API."""
    async with httpx.AsyncClient(timeout=60.0) as client:
        response = await client.post(
            f"{VLLM_URL}/chat/completions",
            json={"model": model, "messages": messages, "max_tokens": 4096, "temperature": 0.7},
        )
        response.raise_for_status()
        return response.json()["choices"][0]["message"]["content"]`;
  } else if (inf === 'external') {
    llmClient = `from openai import OpenAI
import boto3, json

# Fetch API key from Secrets Manager at boot
_secrets = boto3.client("secretsmanager")
_creds = json.loads(_secrets.get_secret_value(SecretId=os.environ["LLM_SECRET_ARN"])["SecretString"])

client = OpenAI(api_key=_creds["api_key"])

def call_llm(messages: list[dict], model: str = "gpt-4o") -> str:
    response = client.chat.completions.create(
        model=model,
        messages=messages,
        temperature=0.7,
        max_tokens=4096,
    )
    return response.choices[0].message.content`;
  } else {
    llmClient = `# SageMaker custom endpoint
import boto3, json
sm = boto3.client("sagemaker-runtime")

def call_llm(messages: list[dict], **kw) -> str:
    response = sm.invoke_endpoint(
        EndpointName=os.environ["SAGEMAKER_ENDPOINT"],
        ContentType="application/json",
        Body=json.dumps({"inputs": messages}),
    )
    return json.loads(response["Body"].read())["generated_text"]`;
  }

  // ---- Vector DB client ----
  let vectorClient = '';
  if (vdb === 'pgvector') {
    vectorClient = `import asyncpg
import numpy as np

async def get_pg_pool():
    return await asyncpg.create_pool(os.environ["DATABASE_URL"], min_size=2, max_size=10)

async def vector_search(pool, query_embedding: list[float], k: int = 5) -> list[dict]:
    """Cosine similarity search in pgvector."""
    async with pool.acquire() as conn:
        rows = await conn.fetch(
            """
            SELECT document_id, chunk_text, metadata,
                   1 - (embedding <=> $1::vector) AS similarity
            FROM embeddings
            ORDER BY embedding <=> $1::vector
            LIMIT $2
            """,
            query_embedding, k,
        )
        return [dict(r) for r in rows]`;
  } else if (vdb === 'opensearch') {
    vectorClient = `from opensearchpy import AsyncOpenSearch, RequestsHttpConnection
from requests_aws4auth import AWS4Auth
import boto3

_creds = boto3.Session().get_credentials()
_auth = AWS4Auth(_creds.access_key, _creds.secret_key, os.environ["AWS_REGION"], "es", session_token=_creds.token)

os_client = AsyncOpenSearch(
    hosts=[{"host": os.environ["OPENSEARCH_HOST"], "port": 443}],
    http_auth=_auth,
    use_ssl=True,
    verify_certs=True,
    connection_class=RequestsHttpConnection,
)

async def vector_search(query_embedding: list[float], k: int = 5) -> list[dict]:
    body = {
        "size": k,
        "query": {"knn": {"embedding": {"vector": query_embedding, "k": k}}},
    }
    res = await os_client.search(index="embeddings", body=body)
    return [hit["_source"] for hit in res["hits"]["hits"]]`;
  } else if (vdb === 'qdrant') {
    vectorClient = `from qdrant_client import AsyncQdrantClient
from qdrant_client.models import Distance, VectorParams

qdrant = AsyncQdrantClient(url=os.environ.get("QDRANT_URL", "http://qdrant-svc:6333"))

async def vector_search(query_embedding: list[float], k: int = 5) -> list[dict]:
    results = await qdrant.search(
        collection_name="embeddings",
        query_vector=query_embedding,
        limit=k,
        with_payload=True,
    )
    return [{"chunk_text": r.payload.get("text"), "metadata": r.payload, "similarity": r.score} for r in results]`;
  } else if (vdb === 'pinecone') {
    vectorClient = `from pinecone import Pinecone
import boto3, json

_creds = json.loads(boto3.client("secretsmanager").get_secret_value(
    SecretId=os.environ["PINECONE_SECRET_ARN"]
)["SecretString"])
pc = Pinecone(api_key=_creds["api_key"])
index = pc.Index(_creds["index_name"])

def vector_search(query_embedding: list[float], k: int = 5) -> list[dict]:
    res = index.query(vector=query_embedding, top_k=k, include_metadata=True)
    return [{"chunk_text": m.metadata.get("text"), "metadata": m.metadata, "similarity": m.score}
            for m in res.matches]`;
  } else if (vdb === 'weaviate') {
    vectorClient = `import weaviate
from weaviate.classes.init import Auth

client = weaviate.connect_to_custom(
    http_host=os.environ["WEAVIATE_HOST"],
    http_port=8080,
    http_secure=False,
    grpc_host=os.environ["WEAVIATE_HOST"],
    grpc_port=50051,
    grpc_secure=False,
)

def vector_search(query_embedding: list[float], k: int = 5) -> list[dict]:
    coll = client.collections.get("Embeddings")
    res = coll.query.near_vector(near_vector=query_embedding, limit=k)
    return [{"chunk_text": o.properties.get("text"), "metadata": o.properties, "similarity": 1 - o.metadata.distance}
            for o in res.objects]`;
  } else {
    vectorClient = '# No vector DB configured — skipping retrieval module';
  }

  // ---- Tracing ----
  let tracing = '';
  if (cfg.llmObservability === 'langsmith') {
    tracing = `from langsmith import Client as LangSmithClient
ls_client = LangSmithClient()
# All langchain calls auto-traced when LANGCHAIN_TRACING_V2=true`;
  } else if (cfg.llmObservability === 'langfuse') {
    tracing = `from langfuse import Langfuse
lf = Langfuse(
    public_key=os.environ["LANGFUSE_PUBLIC_KEY"],
    secret_key=os.environ["LANGFUSE_SECRET_KEY"],
    host=os.environ.get("LANGFUSE_HOST", "https://cloud.langfuse.com"),
)`;
  } else if (cfg.llmObservability === 'phoenix') {
    tracing = `from openinference.instrumentation.openai import OpenAIInstrumentor
from opentelemetry.exporter.otlp.proto.http.trace_exporter import OTLPSpanExporter
from opentelemetry.sdk.trace import TracerProvider
from opentelemetry.sdk.trace.export import BatchSpanProcessor

provider = TracerProvider()
provider.add_span_processor(BatchSpanProcessor(OTLPSpanExporter(
    endpoint=os.environ["PHOENIX_ENDPOINT"]
)))
OpenAIInstrumentor().instrument(tracer_provider=provider)`;
  }

  // ---- Agent framework ----
  let agentImpl = '';
  if (fw === 'langgraph') {
    agentImpl = `from langgraph.graph import StateGraph, END
from typing import TypedDict, Annotated, Sequence
from langchain_core.messages import BaseMessage, HumanMessage, AIMessage
import operator

class AgentState(TypedDict):
    messages: Annotated[Sequence[BaseMessage], operator.add]
    context: list[dict]
    iterations: int

async def retrieve_node(state: AgentState) -> AgentState:
    query = state["messages"][-1].content
    embedding = await embed_text(query)
    docs = await vector_search(embedding, k=5)
    return {"context": docs, "iterations": state.get("iterations", 0) + 1}

async def generate_node(state: AgentState) -> AgentState:
    context = "\\n\\n".join(d["chunk_text"] for d in state["context"])
    system = f"Answer using ONLY this context:\\n\\n{context}"
    msgs = [{"role": "system", "content": system}] + [
        {"role": "user" if isinstance(m, HumanMessage) else "assistant", "content": m.content}
        for m in state["messages"]
    ]
    answer = await call_llm(msgs) if asyncio.iscoroutinefunction(call_llm) else call_llm(msgs)
    return {"messages": [AIMessage(content=answer)]}

def should_continue(state: AgentState) -> str:
    return END if state["iterations"] >= 1 else "retrieve"

graph = StateGraph(AgentState)
graph.add_node("retrieve", retrieve_node)
graph.add_node("generate", generate_node)
graph.set_entry_point("retrieve")
graph.add_edge("retrieve", "generate")
graph.add_conditional_edges("generate", should_continue)
agent = graph.compile()`;
  } else if (fw === 'llamaindex') {
    agentImpl = `from llama_index.core import VectorStoreIndex, Settings
from llama_index.core.agent import ReActAgent
from llama_index.core.tools import FunctionTool

# Custom retriever wrapping our vector_search()
class CustomRetriever:
    async def aretrieve(self, query: str):
        embedding = await embed_text(query)
        return await vector_search(embedding, k=5)

retriever = CustomRetriever()

def search_kb(query: str) -> str:
    """Search the knowledge base for relevant context."""
    import asyncio
    docs = asyncio.run(retriever.aretrieve(query))
    return "\\n\\n".join(d["chunk_text"] for d in docs)

tools = [FunctionTool.from_defaults(fn=search_kb)]
agent = ReActAgent.from_tools(tools, verbose=True)`;
  } else if (fw === 'crewai') {
    agentImpl = `from crewai import Agent, Task, Crew, Process
from crewai.tools import BaseTool

class KnowledgeBaseTool(BaseTool):
    name: str = "knowledge_base_search"
    description: str = "Search internal knowledge base for relevant context."

    def _run(self, query: str) -> str:
        import asyncio
        embedding = asyncio.run(embed_text(query))
        docs = asyncio.run(vector_search(embedding, k=5))
        return "\\n\\n".join(d["chunk_text"] for d in docs)

researcher = Agent(
    role="Senior Researcher",
    goal="Surface accurate context from the knowledge base",
    backstory="An expert at retrieval and synthesis.",
    tools=[KnowledgeBaseTool()],
    verbose=True,
)

writer = Agent(
    role="Technical Writer",
    goal="Produce concise, accurate answers grounded in retrieved context",
    backstory="Writes clearly. Cites sources.",
    verbose=True,
)

def run_crew(question: str) -> str:
    research = Task(description=f"Research: {question}", agent=researcher, expected_output="Bullet list of facts with source IDs")
    writing  = Task(description=f"Answer: {question}", agent=writer, expected_output="Final answer with citations", context=[research])
    crew = Crew(agents=[researcher, writer], tasks=[research, writing], process=Process.sequential)
    return crew.kickoff()`;
  } else if (fw === 'pydanticai') {
    agentImpl = `from pydantic_ai import Agent, RunContext
from pydantic import BaseModel
from dataclasses import dataclass

class Answer(BaseModel):
    text: str
    citations: list[str]
    confidence: float

@dataclass
class Deps:
    user_id: str

agent = Agent(
    "claude-3-5-sonnet-latest",
    deps_type=Deps,
    result_type=Answer,
    system_prompt="Answer using retrieved context. Cite document_ids.",
)

@agent.tool
async def search_kb(ctx: RunContext[Deps], query: str) -> list[dict]:
    """Search the knowledge base."""
    embedding = await embed_text(query)
    return await vector_search(embedding, k=5)`;
  } else {
    agentImpl = `# No framework — direct LLM + retrieval orchestration
async def answer_question(question: str) -> dict:
    embedding = await embed_text(question) if asyncio.iscoroutinefunction(embed_text) else embed_text(question)
    docs = await vector_search(embedding, k=5) if asyncio.iscoroutinefunction(vector_search) else vector_search(embedding, k=5)
    context = "\\n\\n".join(d["chunk_text"] for d in docs)

    answer = call_llm([
        {"role": "system", "content": f"Answer using this context:\\n\\n{context}"},
        {"role": "user", "content": question},
    ])

    return {"answer": answer, "sources": [d.get("document_id") for d in docs]}`;
  }

  return `"""
${cfg.projectName} agent service · ${aiInferenceName(cfg)} · ${vectorDBName(cfg)} · ${agentFwName(cfg)}
Generated by STAX. FastAPI service exposing /chat for retrieval-augmented agents.
"""
import os
import asyncio
from contextlib import asynccontextmanager

from fastapi import FastAPI, HTTPException
from pydantic import BaseModel
${cfg.llmObservability === 'phoenix' ? 'from opentelemetry import trace' : ''}

# ── tracing ─────────────────────────────────────────────────────
${tracing || '# (no LLM observability configured)'}

# ── LLM client ──────────────────────────────────────────────────
${llmClient}

# ── embedding ───────────────────────────────────────────────────
${inf === 'bedrock' ? `def embed_text(text: str) -> list[float]:
    """Bedrock Titan embeddings."""
    import json
    resp = bedrock.invoke_model(
        modelId="amazon.titan-embed-text-v2:0",
        body=json.dumps({"inputText": text, "dimensions": 1024}),
    )
    return json.loads(resp["body"].read())["embedding"]` : `async def embed_text(text: str) -> list[float]:
    # TODO: wire embedding provider (OpenAI text-embedding-3-small, Cohere, sentence-transformers, ...)
    raise NotImplementedError("Configure embedding provider")`}

# ── retrieval ───────────────────────────────────────────────────
${vectorClient}

# ── agent ───────────────────────────────────────────────────────
${agentImpl}

# ── HTTP layer ──────────────────────────────────────────────────
class ChatRequest(BaseModel):
    question: str
    user_id: str = "anonymous"
    session_id: str | None = None

class ChatResponse(BaseModel):
    answer: str
    sources: list[str] = []
    trace_id: str | None = None

${vdb === 'pgvector' ? `_pool = None

@asynccontextmanager
async def lifespan(app: FastAPI):
    global _pool
    _pool = await get_pg_pool()
    yield
    await _pool.close()

app = FastAPI(title="${cfg.projectName}-agent", lifespan=lifespan)` : `app = FastAPI(title="${cfg.projectName}-agent")`}

@app.get("/healthz")
async def healthz():
    return {"status": "ok"}

@app.get("/readyz")
async def readyz():
    # TODO: validate downstream connections
    return {"status": "ready"}

@app.post("/chat", response_model=ChatResponse)
async def chat(req: ChatRequest):
    try:
        ${fw === 'langgraph' ? `result = await agent.ainvoke({
            "messages": [HumanMessage(content=req.question)],
            "context": [],
            "iterations": 0,
        })
        return ChatResponse(
            answer=result["messages"][-1].content,
            sources=[d.get("document_id", "") for d in result["context"]],
        )` : fw === 'crewai' ? `answer = run_crew(req.question)
        return ChatResponse(answer=str(answer), sources=[])` : fw === 'pydanticai' ? `result = await agent.run(req.question, deps=Deps(user_id=req.user_id))
        return ChatResponse(
            answer=result.data.text,
            sources=result.data.citations,
        )` : `result = await answer_question(req.question)
        return ChatResponse(answer=result["answer"], sources=result.get("sources", []))`}
    except Exception as e:
        raise HTTPException(status_code=500, detail=str(e))

if __name__ == "__main__":
    import uvicorn
    uvicorn.run(app, host="0.0.0.0", port=8080)
`;
}

function genAgentRequirements(cfg) {
  if (!isAIEnabled(cfg)) return '';
  const lines = [
    'fastapi==0.115.0',
    'uvicorn[standard]==0.32.0',
    'pydantic==2.9.2',
    'httpx==0.27.2',
  ];
  if (cfg.aiInference === 'bedrock' || cfg.aiInference === 'sagemaker' || cfg.aiInference === 'external') lines.push('boto3==1.35.50');
  if (cfg.aiInference === 'vertex')   lines.push('google-cloud-aiplatform==1.71.0');
  if (cfg.aiInference === 'aoai')     lines.push('openai==1.54.3');
  if (cfg.aiInference === 'external') lines.push('openai==1.54.3');
  if (cfg.vectorDB === 'pgvector')    lines.push('asyncpg==0.30.0', 'pgvector==0.3.6');
  if (cfg.vectorDB === 'opensearch')  lines.push('opensearch-py==2.7.1', 'requests-aws4auth==1.3.1');
  if (cfg.vectorDB === 'qdrant')      lines.push('qdrant-client==1.12.1');
  if (cfg.vectorDB === 'pinecone')    lines.push('pinecone-client==5.0.1');
  if (cfg.vectorDB === 'weaviate')    lines.push('weaviate-client==4.9.4');
  if (cfg.agentFramework === 'langgraph')  lines.push('langgraph==0.2.45', 'langchain-core==0.3.15');
  if (cfg.agentFramework === 'llamaindex') lines.push('llama-index-core==0.11.20');
  if (cfg.agentFramework === 'crewai')     lines.push('crewai==0.80.0', 'crewai-tools==0.14.0');
  if (cfg.agentFramework === 'pydanticai') lines.push('pydantic-ai==0.0.13');
  if (cfg.llmObservability === 'langsmith') lines.push('langsmith==0.1.142');
  if (cfg.llmObservability === 'langfuse')  lines.push('langfuse==2.53.9');
  if (cfg.llmObservability === 'phoenix')   lines.push('arize-phoenix-otel==0.6.1', 'openinference-instrumentation-openai==0.1.18');
  if (cfg.directoryEnabled && cfg.directoryMcpEnabled) lines.push('mcp==1.0.0');
  // ── security / guardrails deps ──
  if (isAIEnabled(cfg)) lines.push('prometheus-client==0.21.0');
  if (cfg.piiDetector === 'presidio')          lines.push('presidio-analyzer==2.2.355', 'presidio-anonymizer==2.2.355');
  if (cfg.piiDetector === 'piiranha')          lines.push('transformers==4.46.2', 'torch==2.5.1');
  if (cfg.piiDetector === 'cloud-dlp')         lines.push('google-cloud-dlp==3.24.0');
  if (cfg.promptFirewall === 'llm-guard')      lines.push('llm-guard==0.3.15');
  if (cfg.promptFirewall === 'azure-content-safety') lines.push('azure-ai-contentsafety==1.0.0');
  return lines.join('\n') + '\n';
}

function genAgentDockerfile(cfg) {
  if (!isAIEnabled(cfg)) return '';
  return `# ── ${cfg.projectName} agent service ─────────────────────────────
# Multi-stage build · distroless runtime · non-root
FROM python:3.12-slim AS builder

WORKDIR /build
RUN pip install --no-cache-dir --upgrade pip

COPY agent/requirements.txt ./
RUN pip install --no-cache-dir --target=/install -r requirements.txt

# ── runtime ─────────────────────────────────────────────────────
FROM gcr.io/distroless/python3-debian12:nonroot

COPY --from=builder /install /usr/lib/python3.12/site-packages
ENV PYTHONPATH=/usr/lib/python3.12/site-packages

WORKDIR /app
COPY agent/ /app/

USER nonroot:nonroot
EXPOSE 8080

ENTRYPOINT ["python", "-m", "uvicorn", "main:app", "--host", "0.0.0.0", "--port", "8080"]
`;
}

function genVllmManifest(cfg) {
  if (!isSelfHostedLLM(cfg)) return '';
  const ns = fmt(cfg.projectName);
  const isProd = cfg.environment === 'prod';
  const model = OPEN_MODELS[0];
  return `# ─────────────────────────────────────────────────────────────────
# vLLM inference service · ${model}
# Schedules onto GPU node group with NVIDIA device plugin
# ─────────────────────────────────────────────────────────────────
apiVersion: v1
kind: ConfigMap
metadata:
  name: vllm-config
  namespace: ${ns}
data:
  MODEL_NAME: "${model}"
  MAX_MODEL_LEN: "8192"
  DTYPE: "bfloat16"
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: vllm
  namespace: ${ns}
  labels:
    app: vllm
    component: inference
spec:
  replicas: ${isProd ? 2 : 1}
  selector:
    matchLabels:
      app: vllm
  strategy:
    type: RollingUpdate
    rollingUpdate:
      maxSurge: 1
      maxUnavailable: 0
  template:
    metadata:
      labels:
        app: vllm
        component: inference
      annotations:
        prometheus.io/scrape: "true"
        prometheus.io/port: "8000"
        prometheus.io/path: "/metrics"
    spec:
      serviceAccountName: vllm
      tolerations:
        - key: nvidia.com/gpu
          operator: Equal
          value: "true"
          effect: NoSchedule
      nodeSelector:
        workload: inference
        gpu: "true"
      containers:
        - name: vllm
          image: vllm/vllm-openai:v0.6.3.post1
          imagePullPolicy: IfNotPresent
          args:
            - "--model=$(MODEL_NAME)"
            - "--max-model-len=$(MAX_MODEL_LEN)"
            - "--dtype=$(DTYPE)"
            - "--tensor-parallel-size=1"
            - "--gpu-memory-utilization=0.92"
            - "--enable-prefix-caching"
          envFrom:
            - configMapRef:
                name: vllm-config
          env:
            - name: HUGGING_FACE_HUB_TOKEN
              valueFrom:
                secretKeyRef:
                  name: hf-token
                  key: token
          ports:
            - containerPort: 8000
              name: http
          resources:
            requests:
              cpu: "4"
              memory: "16Gi"
              nvidia.com/gpu: "1"
            limits:
              cpu: "8"
              memory: "64Gi"
              nvidia.com/gpu: "1"
          readinessProbe:
            httpGet: { path: /health, port: 8000 }
            initialDelaySeconds: 120
            periodSeconds: 10
            timeoutSeconds: 5
          livenessProbe:
            httpGet: { path: /health, port: 8000 }
            initialDelaySeconds: 300
            periodSeconds: 30
          volumeMounts:
            - name: model-cache
              mountPath: /root/.cache/huggingface
            - name: shm
              mountPath: /dev/shm
      volumes:
        - name: model-cache
          persistentVolumeClaim:
            claimName: vllm-model-cache
        - name: shm
          emptyDir:
            medium: Memory
            sizeLimit: 16Gi
---
apiVersion: v1
kind: PersistentVolumeClaim
metadata:
  name: vllm-model-cache
  namespace: ${ns}
spec:
  accessModes: [ReadWriteOnce]
  storageClassName: gp3
  resources:
    requests:
      storage: ${isProd ? '500Gi' : '200Gi'}
---
apiVersion: v1
kind: Service
metadata:
  name: vllm-svc
  namespace: ${ns}
spec:
  type: ClusterIP
  selector:
    app: vllm
  ports:
    - port: 8000
      targetPort: 8000
      name: http
---
apiVersion: autoscaling/v2
kind: HorizontalPodAutoscaler
metadata:
  name: vllm-hpa
  namespace: ${ns}
spec:
  scaleTargetRef:
    apiVersion: apps/v1
    kind: Deployment
    name: vllm
  minReplicas: ${isProd ? 2 : 1}
  maxReplicas: ${isProd ? 8 : 3}
  metrics:
    - type: Pods
      pods:
        metric:
          name: vllm_running_requests
        target:
          type: AverageValue
          averageValue: "30"
  behavior:
    scaleDown:
      stabilizationWindowSeconds: 600
    scaleUp:
      stabilizationWindowSeconds: 60
`;
}

function genAgentManifest(cfg) {
  if (!isAIEnabled(cfg)) return '';
  const ns = fmt(cfg.projectName);
  const isProd = cfg.environment === 'prod';
  return `# ─────────────────────────────────────────────────────────────────
# agent service · ${agentFwName(cfg)} · retrieves from ${vectorDBName(cfg)}
# ─────────────────────────────────────────────────────────────────
apiVersion: v1
kind: ServiceAccount
metadata:
  name: agent-svc
  namespace: ${ns}
  ${cfg.provider === 'aws' && cfg.aiInference === 'bedrock' ? `annotations:
    eks.amazonaws.com/role-arn: arn:aws:iam::ACCOUNT_ID:role/${fmt(cfg.projectName)}-${cfg.environment}-agent-svc` : ''}
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: agent
  namespace: ${ns}
  labels:
    app: agent
    component: ai
spec:
  replicas: ${isProd ? 3 : 1}
  selector:
    matchLabels: { app: agent }
  template:
    metadata:
      labels:
        app: agent
        component: ai
      annotations:
        prometheus.io/scrape: "true"
        prometheus.io/port: "8080"
    spec:
      serviceAccountName: agent-svc
      securityContext:
        runAsNonRoot: true
        runAsUser: 65532
        seccompProfile: { type: RuntimeDefault }
      containers:
        - name: agent
          image: ${fmt(cfg.projectName)}/agent:latest
          ports:
            - { containerPort: 8080, name: http }
          env:
            - { name: AWS_REGION, value: "${cfg.region}" }
            ${cfg.aiInference === 'bedrock' ? `- { name: BEDROCK_GUARDRAIL_ID, valueFrom: { configMapKeyRef: { name: ai-config, key: guardrail_id } } }` : ''}
            ${isSelfHostedLLM(cfg) ? `- { name: VLLM_URL, value: "http://vllm-svc.${ns}.svc.cluster.local:8000/v1" }` : ''}
            ${cfg.vectorDB === 'pgvector' ? `- name: DATABASE_URL
              valueFrom: { secretKeyRef: { name: db-credentials, key: url } }` : ''}
            ${cfg.vectorDB === 'opensearch' ? `- { name: OPENSEARCH_HOST, valueFrom: { configMapKeyRef: { name: ai-config, key: opensearch_host } } }` : ''}
            ${cfg.vectorDB === 'pinecone' ? `- { name: PINECONE_SECRET_ARN, valueFrom: { configMapKeyRef: { name: ai-config, key: pinecone_arn } } }` : ''}
            ${cfg.llmObservability === 'langsmith' ? `- { name: LANGCHAIN_TRACING_V2, value: "true" }
            - name: LANGCHAIN_API_KEY
              valueFrom: { secretKeyRef: { name: llm-obs, key: langsmith_key } }` : ''}
            ${cfg.llmObservability === 'phoenix' ? `- { name: PHOENIX_ENDPOINT, value: "http://phoenix-svc:6006/v1/traces" }` : ''}
          resources:
            requests: { cpu: "200m", memory: "512Mi" }
            limits:   { cpu: "2", memory: "2Gi" }
          readinessProbe:
            httpGet: { path: /readyz, port: 8080 }
            initialDelaySeconds: 5
          livenessProbe:
            httpGet: { path: /healthz, port: 8080 }
            initialDelaySeconds: 30
---
apiVersion: v1
kind: Service
metadata:
  name: agent-svc
  namespace: ${ns}
spec:
  type: ClusterIP
  selector: { app: agent }
  ports:
    - { port: 80, targetPort: 8080 }
---
apiVersion: autoscaling/v2
kind: HorizontalPodAutoscaler
metadata:
  name: agent-hpa
  namespace: ${ns}
spec:
  scaleTargetRef: { apiVersion: apps/v1, kind: Deployment, name: agent }
  minReplicas: ${isProd ? 3 : 1}
  maxReplicas: ${isProd ? 20 : 5}
  metrics:
    - type: Resource
      resource: { name: cpu, target: { type: Utilization, averageUtilization: 70 } }
`;
}

function genEvalsWorkflow(cfg) {
  if (!isAIEnabled(cfg) || cfg.llmObservability === 'none') return '';
  return `# ─────────────────────────────────────────────────────────────────
# LLM regression eval pipeline
# Runs golden-set evaluation on every PR touching agent/
# ─────────────────────────────────────────────────────────────────
name: llm-evals

on:
  pull_request:
    paths: ['agent/**', 'evals/**']
  workflow_dispatch:

permissions:
  id-token: write
  contents: read
  pull-requests: write

jobs:
  evaluate:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - uses: actions/setup-python@v5
        with: { python-version: '3.12' }

      - name: Install deps
        run: |
          pip install -r agent/requirements.txt
          pip install pytest deepeval ragas

      - uses: aws-actions/configure-aws-credentials@v4
        with:
          role-to-assume: \${{ secrets.AWS_ROLE_ARN }}
          aws-region: ${cfg.region}

      - name: Run eval suite
        env:
          ${cfg.llmObservability === 'langfuse' ? `LANGFUSE_PUBLIC_KEY: \${{ secrets.LANGFUSE_PUBLIC_KEY }}
          LANGFUSE_SECRET_KEY: \${{ secrets.LANGFUSE_SECRET_KEY }}` : cfg.llmObservability === 'langsmith' ? `LANGCHAIN_API_KEY: \${{ secrets.LANGSMITH_KEY }}
          LANGCHAIN_TRACING_V2: "true"
          LANGCHAIN_PROJECT: ${fmt(cfg.projectName)}-eval` : 'PHOENIX_ENDPOINT: http://phoenix.internal:6006/v1/traces'}
        run: |
          python -m pytest evals/ \\
            --golden-set evals/golden.jsonl \\
            --metrics faithfulness,answer_relevancy,context_precision \\
            --threshold-faithfulness 0.85 \\
            --threshold-answer-relevancy 0.80 \\
            --report-out evals/report.json

      - name: Comment PR with eval delta
        uses: actions/github-script@v7
        with:
          script: |
            const fs = require('fs');
            const report = JSON.parse(fs.readFileSync('evals/report.json'));
            const body = \`### LLM Eval Results
| metric | score | threshold | status |
|--------|-------|-----------|--------|
\${Object.entries(report.scores).map(([k,v]) => \`| \${k} | \${v.score.toFixed(3)} | \${v.threshold} | \${v.passed ? '✅' : '❌'} |\`).join('\\n')}
\`;
            github.rest.issues.createComment({
              owner: context.repo.owner, repo: context.repo.repo,
              issue_number: context.issue.number, body
            });

      - name: Fail if regressions
        run: |
          python -c "import json; r=json.load(open('evals/report.json')); exit(0 if all(s['passed'] for s in r['scores'].values()) else 1)"
`;
}

function genGoldenSet(cfg) {
  if (!isAIEnabled(cfg)) return '';
  return `{"id": "q1", "question": "What is the company's refund policy?", "expected_keywords": ["30 days", "original payment"], "ground_truth": "Refunds are issued within 30 days to the original payment method."}
{"id": "q2", "question": "How do I reset my password?", "expected_keywords": ["email", "reset link"], "ground_truth": "Use the 'Forgot password' link on the login page; a reset email will be sent."}
{"id": "q3", "question": "What integrations are supported?", "expected_keywords": ["webhook", "REST"], "ground_truth": "Both REST API and webhook integrations are available; see the integration docs."}
{"id": "q4", "question": "Is there a free tier?", "expected_keywords": ["free", "limit"], "ground_truth": "Free tier includes 1,000 requests per month; upgrade for higher limits."}
{"id": "q5", "question": "How is data encrypted?", "expected_keywords": ["AES", "TLS", "rest", "transit"], "ground_truth": "AES-256 at rest and TLS 1.3 in transit with KMS-managed keys."}
`;
}

// =============================================================
// AGENTIC.AI DIRECTORY GENERATORS
// =============================================================

function genAgentDotJson(cfg) {
  if (!cfg.directoryEnabled || !isAIEnabled(cfg)) return '';
  const score = computeAgenticness(cfg);
  const slug = `${fmt(cfg.projectName)}-agent`;
  const manifest = {
    schema_version: '1.0',
    name:        slug,
    display_name: `${cfg.projectName} agent`,
    description: cfg.directoryDescription || `${agentFwName(cfg)} agent with ${vectorDBName(cfg)} retrieval.`,
    version:     '1.0.0',
    homepage:    cfg.directoryHomepage || `https://${fmt(cfg.projectName)}.example.com`,
    contact:     cfg.owner || 'platform-team',

    // Structured fields per agentic.ai schema
    deployment_model:    inferDeploymentModel(cfg),
    autonomy_level:      inferAutonomyTag(cfg),
    model_agnosticism:   inferModelAgnosticism(cfg),
    team_support:        cfg.environment === 'prod' ? 'ENTERPRISE' : 'SMALL_TEAM',
    pricing_model:       cfg.directoryPricing || 'SUBSCRIPTION',
    primary_interface:   inferPrimaryInterface(cfg),
    open_source:         !!cfg.directoryOpenSource,
    mcp_support:         !!cfg.directoryMcpEnabled,

    // Capabilities (agentic.ai filterable tags)
    capabilities: {
      voice:          false,
      vision:         false,
      code_execution: !!cfg.directoryCodeExecution,
      web_browsing:   !!cfg.directoryWebTool,
      file_access:    false,
      memory:         cfg.vectorDB !== 'none',
      integrations:   !!cfg.directoryMcpEnabled,
    },

    // Endpoints exposed by the agent service
    endpoints: {
      health: '/healthz',
      ready:  '/readyz',
      chat:   '/chat',
      mcp:    cfg.directoryMcpEnabled ? '/mcp' : null,
    },

    // Tech stack (informational)
    stack: {
      inference:     aiInferenceName(cfg),
      vector_store:  vectorDBName(cfg),
      framework:     agentFwName(cfg),
      observability: LLM_OBSERVABILITY.find(o => o.id === cfg.llmObservability)?.name,
      cloud:         cfg.provider.toUpperCase(),
      region:        cfg.region,
      ...(needsGPU(cfg) ? { gpu: (cfg.gpuType || '').split(' ')[0] } : {}),
    },

    // Predicted agenticness (must be re-verified by directory editors)
    agenticness: {
      total:      score.total,
      level:      score.level.level,
      level_name: score.level.name,
      dimensions: score.dimensions,
      computed_at: new Date().toISOString(),
      computed_by: 'STAX v1.0.0 (self-reported)',
    },

    // Discovery hints
    category:    cfg.directoryCategory || 'enterprise-agent-platforms',
    tags:        ['stax-generated', cfg.provider, cfg.aiInference, cfg.agentFramework, cfg.vectorDB].filter(t => t && t !== 'none'),
  };
  return JSON.stringify(manifest, null, 2) + '\n';
}

function genAgenticSubmission(cfg) {
  if (!cfg.directoryEnabled || !isAIEnabled(cfg)) return '';
  const score = computeAgenticness(cfg);
  const slug = `${fmt(cfg.projectName)}-agent`;
  const submission = {
    // POST to https://agentic.ai/submit (or via published API once available)
    name:        cfg.projectName + ' agent',
    slug,
    tagline:     cfg.directoryTagline || `${agentFwName(cfg)} + ${vectorDBName(cfg)} retrieval agent`,
    description: cfg.directoryDescription || `Internal agent built with STAX. Inference via ${aiInferenceName(cfg)}, retrieval-augmented with ${vectorDBName(cfg)}, orchestrated by ${agentFwName(cfg)}.`,
    homepage:    cfg.directoryHomepage || `https://${fmt(cfg.projectName)}.example.com`,
    manifest_url: `${cfg.directoryHomepage || `https://${fmt(cfg.projectName)}.example.com`}/.well-known/agent.json`,
    category:    cfg.directoryCategory || 'enterprise-agent-platforms',

    // Structured fields (mapped to directory schema)
    deployment_model:  inferDeploymentModel(cfg),
    autonomy_level:    inferAutonomyTag(cfg),
    model_agnosticism: inferModelAgnosticism(cfg),
    team_support:      cfg.environment === 'prod' ? 'ENTERPRISE' : 'SMALL_TEAM',
    pricing_model:     cfg.directoryPricing || 'SUBSCRIPTION',
    primary_interface: inferPrimaryInterface(cfg),
    open_source:       !!cfg.directoryOpenSource,
    mcp_support:       !!cfg.directoryMcpEnabled,

    // Self-reported agenticness (directory will re-score)
    agenticness_proposed: {
      total:      score.total,
      level:      score.level.level,
      dimensions: score.dimensions,
      reasoning:  score.reasons,
    },

    // Tags & filters
    tags: [
      cfg.directoryPricing === 'FREE_OSS' ? 'free-tier' : 'paid',
      'api',
      ...(cfg.directoryWebTool ? ['web-browsing'] : []),
      ...(cfg.directoryCodeExecution ? ['code-execution'] : []),
      ...(cfg.vectorDB !== 'none' ? ['memory'] : []),
      ...(cfg.directoryMcpEnabled ? ['integrations'] : []),
    ],

    // Submission metadata
    submitted_by:   cfg.owner || 'platform-team',
    submitter_org:  cfg.costCenter || '',
    submitted_at:   new Date().toISOString(),
    generated_by:   'STAX v1.0.0',
  };
  return JSON.stringify(submission, null, 2) + '\n';
}

function genDirectorySubmitScript(cfg) {
  if (!cfg.directoryEnabled || !isAIEnabled(cfg)) return '';
  return `#!/usr/bin/env bash
# ─────────────────────────────────────────────────────────────────
# Submit ${cfg.projectName}-agent to agentic.ai directory
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
#
# Prerequisites:
#   1. Your agent must serve agent.json at /.well-known/agent.json
#   2. Your service must be publicly reachable (the directory verifies the manifest URL)
#
# Usage:
#   ./submit-directory.sh
# ─────────────────────────────────────────────────────────────────
set -euo pipefail

ENDPOINT="\${AGENTIC_SUBMIT_URL:-https://agentic.ai/submit}"
PAYLOAD="$(dirname "$0")/submission.json"

[ -f "$PAYLOAD" ] || { echo "missing submission.json next to this script"; exit 1; }
command -v curl >/dev/null || { echo "curl required"; exit 1; }
command -v jq   >/dev/null || { echo "jq required (for response parsing)";   exit 1; }

# Verify the manifest is reachable before submitting
MANIFEST_URL=$(jq -r '.manifest_url' "$PAYLOAD")
echo "→ verifying manifest at $MANIFEST_URL"
HTTP_CODE=$(curl -s -o /dev/null -w "%{http_code}" "$MANIFEST_URL" || echo "000")
if [ "$HTTP_CODE" != "200" ]; then
  echo "  ⚠ manifest returned HTTP $HTTP_CODE — directory verification will fail."
  echo "  Deploy your agent + serve agent.json first, then re-run."
  exit 1
fi
echo "  ✓ manifest reachable"

echo "→ submitting to $ENDPOINT"
RESP=$(curl -s -X POST "$ENDPOINT" \\
  -H "Content-Type: application/json" \\
  -H "User-Agent: STAX/1.0.0 (+${cfg.directoryHomepage || 'https://example.com'})" \\
  --data-binary "@$PAYLOAD")

echo "$RESP" | jq .
echo ""
echo "✓ submission queued. The agentic.ai team will review and may re-score."
echo "  Track status: https://agentic.ai/t/${fmt(cfg.projectName)}-agent"
`;
}

function genMCPServerStub(cfg) {
  if (!cfg.directoryEnabled || !cfg.directoryMcpEnabled || !isAIEnabled(cfg)) return '';
  return `"""
${cfg.projectName}-agent · MCP server adapter

Exposes the agent as an MCP-compatible tool so it can be discovered and used
by Claude Desktop, Cursor, Cline, agentic.ai's MCP client, etc.

Mount this on the same pod as the agent service or run as a sidecar.

Run as STDIO server:
    python mcp_server.py

Run as HTTP server (for agentic.ai):
    uvicorn mcp_server:app --host 0.0.0.0 --port 8090
"""
import os
import asyncio
from typing import Any
from contextlib import asynccontextmanager

from mcp.server.fastmcp import FastMCP

# Re-use the agent's runtime — no duplicated logic.
from main import call_llm, embed_text, vector_search

mcp = FastMCP("${cfg.projectName}-agent")

@mcp.tool()
async def chat(question: str, user_id: str = "anonymous") -> dict[str, Any]:
    """
    Ask the agent a question. The agent will retrieve relevant context from
    the knowledge base and answer using ${aiInferenceName(cfg)}.

    Returns: { "answer": str, "sources": list[str] }
    """
    embedding = await embed_text(question) if asyncio.iscoroutinefunction(embed_text) else embed_text(question)
    docs = await vector_search(embedding, k=5) if asyncio.iscoroutinefunction(vector_search) else vector_search(embedding, k=5)

    context = "\\n\\n".join(d.get("chunk_text", "") for d in docs)
    answer = call_llm([
        {"role": "system", "content": f"Answer using only this context:\\n\\n{context}"},
        {"role": "user", "content": question},
    ])
    return {
        "answer": answer if isinstance(answer, str) else str(answer),
        "sources": [d.get("document_id", "") for d in docs],
    }

@mcp.tool()
async def search_knowledge_base(query: str, k: int = 5) -> list[dict[str, Any]]:
    """
    Direct semantic search against the vector store.
    Returns top-k chunks with similarity scores.
    """
    embedding = await embed_text(query) if asyncio.iscoroutinefunction(embed_text) else embed_text(query)
    return await vector_search(embedding, k=k) if asyncio.iscoroutinefunction(vector_search) else vector_search(embedding, k=k)

@mcp.resource("agent://capabilities")
async def capabilities() -> str:
    """Static description of what this agent can do."""
    return (
        "Tools: chat (RAG-backed Q&A), search_knowledge_base (raw retrieval).\\n"
        "Inference: ${aiInferenceName(cfg)}.\\n"
        "Vector store: ${vectorDBName(cfg)}.\\n"
        "Framework: ${agentFwName(cfg)}."
    )

if __name__ == "__main__":
    mcp.run()  # STDIO transport by default
`;
}

function genDirectoryReadme(cfg) {
  if (!cfg.directoryEnabled || !isAIEnabled(cfg)) return '';
  const score = computeAgenticness(cfg);
  const cat = AGENTIC_CATEGORIES.find(c => c.id === cfg.directoryCategory)?.name || cfg.directoryCategory;
  return `# Directory listing — ${cfg.projectName}-agent

Generated by STAX. Files in this directory describe how the agent presents itself
to discovery services like [agentic.ai](https://agentic.ai).

## What's here

| File | Purpose |
|------|---------|
| \`agent.json\` | Public manifest — serve at \`/.well-known/agent.json\` |
| \`submission.json\` | POST payload for \`agentic.ai/submit\` |
| \`submit-directory.sh\` | One-shot submission script (verifies manifest first) |
${cfg.directoryMcpEnabled ? '| `mcp_server.py` | MCP server adapter — exposes agent tools to MCP clients |' : ''}

## Predicted agenticness

| | |
|---|---|
| **Score** | ${score.total} / 32 |
| **Level** | **${score.level.level} · ${score.level.name}** |
| **Category** | ${cat} |
| **Autonomy** | ${AUTONOMY_LEVELS.find(a => a.id === cfg.directoryAutonomy)?.name} |

> ${score.level.desc}

### Per-dimension breakdown

${AGENTIC_DIMENSIONS.map(d => `- **${d.name}** — ${score.dimensions[d.id]} / 4 · ${score.reasons[d.id]}`).join('\n')}

> ⓘ This is the score STAX *predicts* the directory will assign based on your stack
> choices. The directory's editors re-score on submission and may disagree.

## Serving the manifest

The directory expects \`agent.json\` to be reachable at the well-known path. Add a
route to your agent service:

\`\`\`python
from fastapi.responses import FileResponse

@app.get("/.well-known/agent.json")
async def agent_manifest():
    return FileResponse("directory/agent.json", media_type="application/json")
\`\`\`

Or, if you'd rather embed it, add it to your container image at
\`/app/.well-known/agent.json\` and mount that path through your ingress.

## Submitting

\`\`\`bash
./submit-directory.sh
\`\`\`

The script:
1. Verifies your manifest URL returns HTTP 200
2. POSTs \`submission.json\` to \`agentic.ai/submit\`
3. Prints the queue position and tracking URL

You can also submit via the web form at https://agentic.ai/submit — paste the
contents of \`submission.json\` into the form fields.

${cfg.directoryMcpEnabled ? `## MCP integration

\`mcp_server.py\` exposes two tools to MCP clients:

- \`chat(question, user_id)\` — full RAG-backed answer
- \`search_knowledge_base(query, k)\` — raw retrieval

Wire it into Claude Desktop:

\`\`\`json
// ~/.config/claude/claude_desktop_config.json
{
  "mcpServers": {
    "${fmt(cfg.projectName)}-agent": {
      "command": "python",
      "args": ["/path/to/agent/mcp_server.py"]
    }
  }
}
\`\`\`

For HTTP transport (used by agentic.ai's discovery), expose the MCP server on a
separate port and add it to your ingress.
` : ''}

## Improving your score

To bump up a level:

${score.dimensions.tools < 3 ? '- **More tools** — wire in web browsing or code execution to bump the Tool use dimension.\n' : ''}${score.dimensions.autonomy < 3 ? '- **Higher autonomy** — semi or fully autonomous mode (only when you have evals + monitoring to back it).\n' : ''}${score.dimensions.adapt < 3 ? '- **Self-correction** — switch to LangGraph or add eval-driven retry loops.\n' : ''}${score.dimensions.observe < 3 ? '- **Observability** — turn on a LLM tracing tool (LangSmith / Langfuse / Phoenix).\n' : ''}${score.dimensions.memory < 3 ? '- **Memory** — wire in a vector store for retrieval-augmented context.\n' : ''}${score.total >= 24 ? '- You\'re already in L4+ territory — focus on production hardening before pursuing higher.' : ''}

---
_Re-run STAX to regenerate after config changes._
`;
}

// =============================================================
// SECURITY GENERATORS
// =============================================================
// AI/LLM/MCP security is the spine of every generated stack.
// These generators produce defense-in-depth layers, scan workflows,
// runtime detection rules, alerting, and compliance documentation.

function genGuardrailsModule(cfg) {
  if (!isAIEnabled(cfg)) return '';
  const fw = cfg.promptFirewall;
  const pii = cfg.piiDetector;

  let inputScanners = '';
  if (fw === 'lakera') {
    inputScanners = `# Lakera Guard — sub-100ms prompt-injection detection
async def _lakera_scan(text: str) -> dict:
    async with httpx.AsyncClient(timeout=2.0) as client:
        resp = await client.post(
            "https://api.lakera.ai/v2/guard",
            headers={"Authorization": f"Bearer {os.environ['LAKERA_API_KEY']}"},
            json={"messages": [{"role": "user", "content": text}]},
        )
        return resp.json()`;
  } else if (fw === 'llm-guard') {
    inputScanners = `# LLM Guard — self-hosted scanners
from llm_guard.input_scanners import PromptInjection, Anonymize, BanSubstrings, Secrets, TokenLimit
from llm_guard.vault import Vault
_vault = Vault()
_input_scanners = [
    PromptInjection(threshold=0.85),
    Anonymize(_vault, hidden_names=["company_internal"]),
    BanSubstrings(substrings=["ignore previous", "disregard", "system prompt"], match_type="word"),
    Secrets(),
    TokenLimit(limit=4096),
]`;
  } else if (fw === 'bedrock-guardrails') {
    inputScanners = `# Bedrock Guardrails — applied via Converse API guardrailConfig (already wired in main.py)
# Additional pre-flight check for high-risk patterns:
_PROMPT_ATTACK_PATTERNS = [
    re.compile(r"ignore\\s+(all\\s+)?previous\\s+(instructions|rules)", re.I),
    re.compile(r"system\\s+prompt", re.I),
    re.compile(r"</?\\s*(system|admin|developer)", re.I),
]`;
  } else if (fw === 'azure-content-safety') {
    inputScanners = `# Azure AI Content Safety — Prompt Shields
from azure.ai.contentsafety import ContentSafetyClient
from azure.ai.contentsafety.models import ShieldPromptOptions
from azure.core.credentials import AzureKeyCredential

_cs_client = ContentSafetyClient(
    endpoint=os.environ["AZURE_CONTENT_SAFETY_ENDPOINT"],
    credential=AzureKeyCredential(os.environ["AZURE_CONTENT_SAFETY_KEY"]),
)`;
  } else {
    inputScanners = `# No managed prompt firewall — basic regex-based prompt-attack detection
_PROMPT_ATTACK_PATTERNS = [
    re.compile(r"ignore\\s+(all\\s+)?previous\\s+(instructions|rules)", re.I),
    re.compile(r"system\\s+prompt", re.I),
    re.compile(r"</?\\s*(system|admin|developer)\\s*>", re.I),
    re.compile(r"jailbreak", re.I),
    re.compile(r"DAN\\s+mode", re.I),
]`;
  }

  let piiScanner = '';
  if (pii === 'presidio') {
    piiScanner = `from presidio_analyzer import AnalyzerEngine
from presidio_anonymizer import AnonymizerEngine
_pii_analyzer = AnalyzerEngine()
_pii_anonymizer = AnonymizerEngine()

def redact_pii(text: str) -> tuple[str, list[str]]:
    """Returns (redacted_text, list_of_entity_types_found)."""
    results = _pii_analyzer.analyze(text=text, language="en")
    if not results:
        return text, []
    anonymized = _pii_anonymizer.anonymize(text=text, analyzer_results=results)
    return anonymized.text, [r.entity_type for r in results]`;
  } else if (pii === 'comprehend') {
    piiScanner = `import boto3
_comprehend = boto3.client("comprehend")

def redact_pii(text: str) -> tuple[str, list[str]]:
    if not text or len(text) > 100000:
        return text, []
    resp = _comprehend.detect_pii_entities(Text=text, LanguageCode="en")
    entities = sorted(resp.get("Entities", []), key=lambda e: e["BeginOffset"], reverse=True)
    redacted = text
    types_found = []
    for e in entities:
        redacted = redacted[:e["BeginOffset"]] + f"[{e['Type']}]" + redacted[e["EndOffset"]:]
        types_found.append(e["Type"])
    return redacted, types_found`;
  } else if (pii === 'cloud-dlp') {
    piiScanner = `from google.cloud import dlp_v2
_dlp = dlp_v2.DlpServiceClient()
_DLP_PARENT = f"projects/{os.environ['GCP_PROJECT']}"

def redact_pii(text: str) -> tuple[str, list[str]]:
    item = {"value": text}
    inspect_config = {
        "info_types": [{"name": t} for t in ["EMAIL_ADDRESS","PHONE_NUMBER","CREDIT_CARD_NUMBER","US_SOCIAL_SECURITY_NUMBER","PERSON_NAME"]],
        "min_likelihood": dlp_v2.Likelihood.POSSIBLE,
        "include_quote": False,
    }
    deidentify_config = {
        "info_type_transformations": {
            "transformations": [{"primitive_transformation": {"replace_with_info_type_config": {}}}]
        }
    }
    resp = _dlp.deidentify_content(
        request={"parent": _DLP_PARENT, "deidentify_config": deidentify_config,
                 "inspect_config": inspect_config, "item": item},
    )
    return resp.item.value, []  # type list omitted for brevity`;
  } else if (pii === 'piiranha') {
    piiScanner = `from transformers import pipeline
_pii_ner = pipeline("token-classification", model="iiiorg/piiranha-v1-detect-personal-information",
                    aggregation_strategy="simple", device=-1)

def redact_pii(text: str) -> tuple[str, list[str]]:
    if not text:
        return text, []
    entities = _pii_ner(text)
    if not entities:
        return text, []
    types = list({e["entity_group"] for e in entities})
    redacted = text
    for e in sorted(entities, key=lambda x: x["start"], reverse=True):
        redacted = redacted[:e["start"]] + f"[{e['entity_group']}]" + redacted[e["end"]:]
    return redacted, types`;
  } else {
    piiScanner = `def redact_pii(text: str) -> tuple[str, list[str]]:
    """No PII detector configured — pass-through. ⚠ Set piiDetector to enable."""
    return text, []`;
  }

  return `"""
${cfg.projectName} agent · guardrails module

Defense-in-depth wrapper around LLM calls:
  1. Input PII redaction (${piiDetectorName(cfg)})
  2. Prompt-injection detection (${promptFirewallName(cfg)})
  3. Denied-topic + role-hijack pattern check
  4. LLM call (already protected by ${cfg.aiInference === 'bedrock' ? 'Bedrock Guardrails' : 'configured firewall'})
  5. Output content moderation
  6. ${cfg.llmAuditLog ? 'Append-only audit log entry' : 'Basic logging'}

Every guardrail violation is structured-logged and emitted as a counter
metric so SREs can alert on trigger-rate spikes (potential abuse signal).
"""
import os
import re
import json
import time
import hashlib
from typing import Any
from dataclasses import dataclass, asdict

import httpx
${cfg.llmAuditLog ? 'import boto3' : ''}
from prometheus_client import Counter, Histogram

# ── metrics ──────────────────────────────────────────────────────
guardrail_blocks = Counter(
    "guardrail_blocks_total",
    "LLM calls blocked by guardrails",
    ["layer", "reason"],
)
guardrail_latency = Histogram(
    "guardrail_latency_seconds",
    "Guardrail evaluation latency by layer",
    ["layer"],
)

# ── input scanners ───────────────────────────────────────────────
${inputScanners}

${piiScanner}

# ── denied topics ────────────────────────────────────────────────
${cfg.deniedTopics ? `_DENIED_TOPICS = [
    # Customise per use case. These defaults reject obvious off-topic + harmful asks.
    "build a bomb", "manufacture explosives",
    "hack into", "exfiltrate credentials",
    "evade detection", "bypass authentication",
]
def check_denied_topics(text: str) -> str | None:
    lower = text.lower()
    for topic in _DENIED_TOPICS:
        if topic in lower:
            return topic
    return None` : '# Denied-topics filter disabled — enable in security step to add'}

# ── audit log ────────────────────────────────────────────────────
${cfg.llmAuditLog ? `_audit_client = boto3.client("logs")
_AUDIT_GROUP = os.environ.get("AUDIT_LOG_GROUP", "${fmt(cfg.projectName)}-${cfg.environment}-llm-audit")
_AUDIT_STREAM = os.environ.get("HOSTNAME", "agent")

@dataclass
class AuditEntry:
    timestamp: float
    user_id: str
    session_id: str
    input_hash: str            # SHA-256 of raw input — for forensics, not regulators
    pii_types: list[str]       # what PII was redacted (no values)
    guardrail_decisions: dict  # {layer: pass|block|reason}
    model: str
    output_hash: str
    latency_ms: float
    cost_usd: float

def append_audit(entry: AuditEntry) -> None:
    """Append-only write to CloudWatch Logs (or your audit sink).
    Retention: ${cfg.environment === 'prod' ? '7 years' : '90 days'} (set via terraform).
    """
    try:
        _audit_client.put_log_events(
            logGroupName=_AUDIT_GROUP,
            logStreamName=_AUDIT_STREAM,
            logEvents=[{
                "timestamp": int(entry.timestamp * 1000),
                "message": json.dumps(asdict(entry)),
            }],
        )
    except Exception as e:
        # Never let audit failures break the request; emit metric instead.
        guardrail_blocks.labels(layer="audit", reason="audit-write-failed").inc()` : '# Audit log disabled — enable in security step for compliance frameworks'}

# ── main guarded call ────────────────────────────────────────────
class GuardrailViolation(Exception):
    def __init__(self, layer: str, reason: str):
        self.layer = layer
        self.reason = reason
        super().__init__(f"{layer}: {reason}")

async def guarded_llm(messages: list[dict], user_id: str = "anonymous", session_id: str = "") -> dict:
    """Drop-in replacement for call_llm() with full guardrail coverage."""
    from main import call_llm  # actual LLM client lives in main.py
    t0 = time.perf_counter()
    raw_input = messages[-1].get("content", "") if messages else ""
    decisions = {}

    # 1. PII redaction
    with guardrail_latency.labels(layer="pii").time():
        redacted, pii_types = redact_pii(raw_input)
        if pii_types:
            messages[-1] = {**messages[-1], "content": redacted}
            decisions["pii"] = f"redacted:{','.join(set(pii_types))}"
        else:
            decisions["pii"] = "pass"

    # 2. Prompt injection / role hijack
    with guardrail_latency.labels(layer="injection").time():
        ${fw === 'lakera' ? `scan = await _lakera_scan(redacted)
        if scan.get("flagged"):
            guardrail_blocks.labels(layer="injection", reason="lakera-flagged").inc()
            raise GuardrailViolation("injection", f"lakera: {scan.get('categories')}")` :
        fw === 'llm-guard' ? `from llm_guard import scan_prompt
        sanitised, results, scores = scan_prompt(_input_scanners, redacted)
        for scanner, ok in results.items():
            if not ok:
                guardrail_blocks.labels(layer="injection", reason=scanner).inc()
                raise GuardrailViolation("injection", f"llm-guard:{scanner}={scores[scanner]:.2f}")` :
        `for pat in _PROMPT_ATTACK_PATTERNS:
            if pat.search(redacted):
                guardrail_blocks.labels(layer="injection", reason="pattern-match").inc()
                raise GuardrailViolation("injection", f"prompt-attack pattern matched: {pat.pattern}")`}
        decisions["injection"] = "pass"

    # 3. Denied topics
    ${cfg.deniedTopics ? `topic = check_denied_topics(redacted)
    if topic:
        guardrail_blocks.labels(layer="topic", reason="denied-topic").inc()
        raise GuardrailViolation("topic", f"denied topic: {topic}")
    decisions["topic"] = "pass"` : 'decisions["topic"] = "skipped"'}

    # 4. LLM call
    output = call_llm(messages) if not _is_async(call_llm) else await call_llm(messages)
    if not isinstance(output, str):
        output = str(output)

    # 5. Output moderation
    ${cfg.outputModeration ? `with guardrail_latency.labels(layer="output").time():
        out_redacted, out_pii = redact_pii(output)
        if out_pii:
            output = out_redacted
            decisions["output"] = f"redacted:{','.join(set(out_pii))}"
        else:
            decisions["output"] = "pass"` : 'decisions["output"] = "skipped"'}

    latency_ms = (time.perf_counter() - t0) * 1000

    # 6. Audit
    ${cfg.llmAuditLog ? `append_audit(AuditEntry(
        timestamp=time.time(),
        user_id=user_id,
        session_id=session_id,
        input_hash=hashlib.sha256(raw_input.encode()).hexdigest(),
        pii_types=list(set(pii_types)),
        guardrail_decisions=decisions,
        model=os.environ.get("LLM_MODEL", "unknown"),
        output_hash=hashlib.sha256(output.encode()).hexdigest(),
        latency_ms=latency_ms,
        cost_usd=0.0,  # populated by the LLM client wrapper
    ))` : 'pass  # audit disabled'}

    return {"output": output, "decisions": decisions, "latency_ms": latency_ms}

def _is_async(fn) -> bool:
    import asyncio
    return asyncio.iscoroutinefunction(fn)
`;
}

function genMcpSecurityPolicy(cfg) {
  if (!cfg.directoryEnabled || !cfg.directoryMcpEnabled || !isAIEnabled(cfg)) return '';
  return `# ─────────────────────────────────────────────────────────────────
# MCP server security policy
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
#
# This file controls which MCP servers/tools the agent is allowed to invoke,
# and the cryptographic verification rules for each.
# ─────────────────────────────────────────────────────────────────

# Allowlist mode: explicit. Any server not listed here is REFUSED.
mode: allowlist
default_action: deny

# Per-server policies
servers:
  - name: ${fmt(cfg.projectName)}-internal
    description: First-party MCP server exposing this agent's own tools
    transport: stdio
    ${cfg.mcpSignatureVerify ? `signature_verify: required
    public_key: \${MCP_INTERNAL_PUBKEY}    # set via Secrets Manager` : 'signature_verify: optional'}
    allowed_tools:
      - chat
      - search_knowledge_base
    rate_limit:
      per_user_per_minute: 60
      per_session: 200

# Network policy for outbound MCP calls (enforced by NetworkPolicy in k8s/network-policies.yaml)
egress_allowlist:
  # Add specific MCP server hostnames here. Wildcards prohibited.
  # - api.partner-mcp.example.com:443

# Tool-level ACLs (per-user / per-role)
tool_acls:
  search_knowledge_base:
    require_role: ["user", "admin"]
    audit_every_call: true
  chat:
    require_role: ["user", "admin"]
    rate_limit_per_user_per_hour: 1000

# Anti-poisoning controls
integrity:
  # Pin tool descriptions by SHA-256 — alert on drift (rug-pull defense)
  pin_tool_schemas: ${cfg.mcpSchemaPinning ? 'true' : 'false'}
  # Re-fetch schemas at agent startup AND every 1h; alert on diff
  schema_drift_alert: true
  # Reject tools whose description was modified after schema pinning
  reject_modified_tools: ${cfg.mcpSchemaPinning ? 'true' : 'false'}

# Failure mode
on_violation:
  log: structured-error
  metric: mcp_violations_total
  alert: pagerduty   # routes via monitoring/alerts.yaml
  block_request: true
`;
}

function genSecurityScanWorkflow(cfg) {
  const scans = cfg.scanTools || [];
  if (scans.length === 0) return '';
  const isProd = cfg.environment === 'prod';

  const hasContainer = scans.some(s => ['trivy','grype'].includes(s));
  const hasIaC       = scans.some(s => ['checkov','tfsec'].includes(s));
  const hasSecret    = scans.some(s => ['gitleaks','trufflehog'].includes(s));
  const hasSbom      = scans.includes('syft');
  const hasSAST      = scans.includes('semgrep');

  return `# ─────────────────────────────────────────────────────────────────
# Security gate — every PR + nightly
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
#
# Fails the build on:
#   - HIGH/CRITICAL CVEs in container deps
#   - HIGH IaC misconfigurations
#   - Any verified secret leak
#   - SAST findings tagged security-audit / OWASP top 10
#
# Generates an SBOM (CycloneDX) attached to the workflow + container image.
# ─────────────────────────────────────────────────────────────────
name: security

on:
  pull_request:
    branches: [main]
  push:
    branches: [main]
  schedule:
    - cron: '0 3 * * *'    # nightly 03:00 UTC — catches new CVE disclosures

permissions:
  contents: read
  security-events: write    # for SARIF upload to GitHub code-scanning
  pull-requests: write

jobs:
${hasSecret ? `  secret-scan:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
        with: { fetch-depth: 0 }
${scans.includes('gitleaks') ? `      - name: Gitleaks
        uses: gitleaks/gitleaks-action@v2
        env:
          GITHUB_TOKEN: \${{ secrets.GITHUB_TOKEN }}
          GITLEAKS_ENABLE_COMMENTS: "true"
          GITLEAKS_ENABLE_UPLOAD_ARTIFACT: "true"` : ''}
${scans.includes('trufflehog') ? `      - name: TruffleHog
        uses: trufflesecurity/trufflehog@main
        with:
          path: ./
          extra_args: --only-verified --fail` : ''}
` : ''}${hasIaC ? `  iac-scan:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
${scans.includes('checkov') ? `      - name: Checkov
        uses: bridgecrewio/checkov-action@master
        with:
          directory: terraform/
          framework: terraform,kubernetes
          soft_fail: ${isProd ? 'false' : 'true'}
          output_format: sarif
          output_file_path: checkov.sarif
      - name: Upload Checkov SARIF
        if: always()
        uses: github/codeql-action/upload-sarif@v3
        with:
          sarif_file: checkov.sarif
          category: checkov` : ''}
${scans.includes('tfsec') ? `      - name: tfsec
        uses: aquasecurity/tfsec-sarif-action@master
        with:
          working_directory: terraform/
          sarif_file: tfsec.sarif
      - name: Upload tfsec SARIF
        if: always()
        uses: github/codeql-action/upload-sarif@v3
        with:
          sarif_file: tfsec.sarif
          category: tfsec` : ''}
` : ''}${hasContainer ? `  container-scan:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - name: Build image
        run: docker build -t ${fmt(cfg.projectName)}:scan .
${scans.includes('trivy') ? `      - name: Trivy
        uses: aquasecurity/trivy-action@master
        with:
          image-ref: ${fmt(cfg.projectName)}:scan
          format: sarif
          output: trivy.sarif
          severity: 'CRITICAL,HIGH'
          exit-code: ${isProd ? '1' : '0'}
      - name: Upload Trivy SARIF
        if: always()
        uses: github/codeql-action/upload-sarif@v3
        with:
          sarif_file: trivy.sarif
          category: trivy` : ''}
${scans.includes('grype') ? `      - name: Grype
        uses: anchore/scan-action@v3
        with:
          image: ${fmt(cfg.projectName)}:scan
          severity-cutoff: high
          fail-build: ${isProd ? 'true' : 'false'}` : ''}
` : ''}${hasSbom ? `  sbom:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - name: Generate SBOM (CycloneDX)
        uses: anchore/sbom-action@v0
        with:
          path: .
          format: cyclonedx-json
          output-file: sbom.cdx.json
      - name: Upload SBOM
        uses: actions/upload-artifact@v4
        with:
          name: sbom
          path: sbom.cdx.json
          retention-days: ${isProd ? 365 : 30}
` : ''}${hasSAST ? `  sast:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - name: Semgrep
        uses: returntocorp/semgrep-action@v1
        with:
          config: >-
            p/security-audit
            p/owasp-top-ten
            p/python
            p/r2c-best-practices
        env:
          SEMGREP_PR_COMMENT: "true"
` : ''}  posture-summary:
    runs-on: ubuntu-latest
    needs: [${[hasSecret && 'secret-scan', hasIaC && 'iac-scan', hasContainer && 'container-scan', hasSbom && 'sbom', hasSAST && 'sast'].filter(Boolean).join(', ')}]
    if: always()
    steps:
      - name: Post posture comment to PR
        if: github.event_name == 'pull_request'
        uses: actions/github-script@v7
        with:
          script: |
            const jobs = '\${{ toJson(needs) }}';
            const data = JSON.parse(jobs);
            const lines = Object.entries(data).map(([k,v]) => \`- \${v.result === 'success' ? '✅' : '❌'} \${k}\`);
            const body = \`### Security gate\\n\${lines.join('\\n')}\\n\\nFull SARIF: github code-scanning tab.\`;
            github.rest.issues.createComment({
              owner: context.repo.owner, repo: context.repo.repo,
              issue_number: context.issue.number, body
            });
`;
}

function genAIRedTeamWorkflow(cfg) {
  if (!isAIEnabled(cfg)) return '';
  const scans = cfg.scanTools || [];
  const aiTools = scans.filter(s => ['garak','pyrit','promptfoo','modelscan'].includes(s));
  if (aiTools.length === 0) return '';

  return `# ─────────────────────────────────────────────────────────────────
# AI red-team — adversarial probing of the agent
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
#
# Runs daily + on every change to agent/ or evals/. Probes for:
#   - Prompt injection robustness
#   - Jailbreak resistance
#   - PII / secret leakage
#   - Hallucination on adversarial inputs
#   - Tool-misuse (when MCP enabled)
#
# Posts a delta report to PR. On regression in prod, opens a security incident.
# ─────────────────────────────────────────────────────────────────
name: ai-redteam

on:
  pull_request:
    paths: ['agent/**', 'evals/**']
  schedule:
    - cron: '0 6 * * *'    # daily 06:00 UTC
  workflow_dispatch:

permissions:
  contents: read
  pull-requests: write
  issues: write

jobs:
  redteam:
    runs-on: ubuntu-latest
    timeout-minutes: 60
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-python@v5
        with: { python-version: '3.12' }

      - name: Install red-team tools
        run: |
          pip install -r agent/requirements.txt
          ${aiTools.includes('garak')     ? 'pip install garak' : ''}
          ${aiTools.includes('pyrit')     ? 'pip install pyrit' : ''}
          ${aiTools.includes('promptfoo') ? 'npm install -g promptfoo' : ''}
          ${aiTools.includes('modelscan') ? 'pip install modelscan' : ''}

      - uses: aws-actions/configure-aws-credentials@v4
        with:
          role-to-assume: \${{ secrets.AWS_ROLE_ARN }}
          aws-region: ${cfg.region}

${aiTools.includes('garak') ? `      - name: garak — LLM vulnerability probes
        run: |
          # Spin up agent locally
          docker compose -f docker-compose.test.yml up -d
          sleep 30
          # Probe via OpenAI-compatible endpoint exposed by the agent
          garak --model_type rest \\
                --model_name http://localhost:8080/chat \\
                --probes promptinject,leakreplay,malwaregen,xss,encoding \\
                --report_prefix redteam-\${{ github.run_id }}
        continue-on-error: ${cfg.environment === 'prod' ? 'false' : 'true'}

      - uses: actions/upload-artifact@v4
        if: always()
        with:
          name: garak-report
          path: redteam-*.html
` : ''}${aiTools.includes('pyrit') ? `      - name: PyRIT — multi-turn red-team
        env:
          AZURE_OPENAI_ENDPOINT: \${{ secrets.AZURE_OPENAI_ENDPOINT }}
          AZURE_OPENAI_KEY: \${{ secrets.AZURE_OPENAI_KEY }}
        run: python -m pyrit.cli.run --target ${fmt(cfg.projectName)}-agent --orchestrators prompt_sending,red_teaming
` : ''}${aiTools.includes('promptfoo') ? `      - name: Promptfoo — adversarial prompt regression
        run: promptfoo eval -c evals/promptfooconfig.yaml --output evals/redteam-results.json
        continue-on-error: ${cfg.environment === 'prod' ? 'false' : 'true'}
` : ''}${aiTools.includes('modelscan') ? `      - name: ModelScan — malicious weight detection
        run: |
          # Scan any local model weights for pickle exploits + suspicious ops
          if [ -d models/ ]; then
            modelscan -p models/ --reporting-format json --output modelscan-report.json
          fi
` : ''}      - name: Compute redteam delta vs main
        if: github.event_name == 'pull_request'
        uses: actions/github-script@v7
        with:
          script: |
            const fs = require('fs');
            const summary = "AI red-team scan complete. See artifacts for full reports.";
            github.rest.issues.createComment({
              owner: context.repo.owner, repo: context.repo.repo,
              issue_number: context.issue.number,
              body: \`### 🛡 AI red-team\\n\${summary}\`
            });

      - name: Open security incident on regression (scheduled runs only)
        if: github.event_name == 'schedule' && failure()
        uses: actions/github-script@v7
        with:
          script: |
            github.rest.issues.create({
              owner: context.repo.owner, repo: context.repo.repo,
              title: '🚨 AI red-team regression — \${{ github.run_id }}',
              labels: ['security','incident','ai-redteam'],
              body: 'Scheduled AI red-team run failed. Investigate before next deploy.',
            });
`;
}

function genFalcoRules(cfg) {
  if (!isK8s(cfg.compute) || !(cfg.scanTools || []).includes('falco')) return '';
  const ns = fmt(cfg.projectName);
  return `# ─────────────────────────────────────────────────────────────────
# Falco rules — runtime threat detection for ${cfg.projectName}
# ─────────────────────────────────────────────────────────────────
# These rules trigger on syscall-level events. Each fires an alert to the
# alert-routing pipeline. Tune severity to your noise tolerance.
- macro: agent_namespace
  condition: k8s.ns.name = "${ns}"

- macro: agent_pod
  condition: agent_namespace and k8s.pod.label.component in (ai, agent)

# ── AI-specific runtime threats ─────────────────────────────────
- rule: Agent reading sensitive files
  desc: Agent pod reading /etc/shadow, AWS creds, k8s tokens, or SSH keys
  condition: >
    agent_pod and
    open_read and
    (fd.name in (/etc/shadow, /etc/passwd-) or
     fd.name startswith /var/run/secrets/kubernetes.io/serviceaccount or
     fd.name startswith /root/.aws or
     fd.name startswith /root/.ssh)
  output: "Agent read sensitive file (file=%fd.name pod=%k8s.pod.name)"
  priority: CRITICAL
  tags: [agent, exfiltration, mitre_credential_access]

- rule: Agent spawning shell
  desc: LLM-controlled agent should never spawn /bin/sh or /bin/bash
  condition: >
    agent_pod and
    spawned_process and
    proc.name in (sh, bash, ash, dash, zsh, fish, ksh)
  output: "Agent spawned shell (cmd=%proc.cmdline pod=%k8s.pod.name)"
  priority: CRITICAL
  tags: [agent, prompt-injection, mitre_execution]

- rule: Agent unexpected egress
  desc: Outbound connection to non-allowlisted destination
  condition: >
    agent_pod and
    outbound and
    not fd.sip in (vector_db_ips, llm_provider_ips, observability_ips)
  output: "Agent unexpected egress (dest=%fd.rip:%fd.rport pod=%k8s.pod.name)"
  priority: WARNING
  tags: [agent, exfiltration, mitre_command_and_control]

- rule: Agent crypto-mining indicators
  desc: Coin-miner-like CPU patterns or known mining pools
  condition: >
    agent_pod and
    (proc.name in (xmrig, minerd, cgminer, t-rex) or
     fd.name contains stratum)
  output: "Crypto-miner indicator in agent pod (proc=%proc.name pod=%k8s.pod.name)"
  priority: CRITICAL
  tags: [agent, supply-chain, cryptomining]

- rule: Agent loading new shared library
  desc: Dynamic library load from unexpected path (typical in supply-chain attacks)
  condition: >
    agent_pod and
    open_read and
    fd.name endswith .so and
    not fd.name startswith /usr/lib and
    not fd.name startswith /lib
  output: "Agent loaded .so from non-standard path (path=%fd.name pod=%k8s.pod.name)"
  priority: WARNING
  tags: [agent, supply-chain]

# ── MCP-specific (when enabled) ─────────────────────────────────
${cfg.directoryMcpEnabled ? `- rule: MCP server schema drift
  desc: Tool definitions changed since pinned hash — possible rug-pull
  condition: >
    agent_pod and
    proc.name = "python" and
    proc.cmdline contains "mcp_server"
  output: "MCP schema drift detected — re-verify pinned hashes"
  priority: WARNING
  tags: [agent, mcp, supply-chain]` : ''}
`;
}

function genNetworkPolicies(cfg) {
  if (!isK8s(cfg.compute) || !cfg.networkPoliciesDefault) return '';
  const ns = fmt(cfg.projectName);
  return `# ─────────────────────────────────────────────────────────────────
# NetworkPolicies — default-deny + explicit allows
# ─────────────────────────────────────────────────────────────────
# Generated by STAX. The default-deny policy is the foundation; everything
# else is an explicit allow rule. If a service can't talk to something,
# add the rule here, never weaken default-deny.

# ── 1. Default deny ALL ingress + egress in the namespace ───────
apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: default-deny-all
  namespace: ${ns}
spec:
  podSelector: {}
  policyTypes: [Ingress, Egress]
---
# ── 2. Allow DNS (kube-system) for all pods ─────────────────────
apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: allow-dns
  namespace: ${ns}
spec:
  podSelector: {}
  policyTypes: [Egress]
  egress:
    - to:
        - namespaceSelector:
            matchLabels: { kubernetes.io/metadata.name: kube-system }
          podSelector:
            matchLabels: { k8s-app: kube-dns }
      ports:
        - { protocol: UDP, port: 53 }
        - { protocol: TCP, port: 53 }
---
# ── 3. Agent → vector store + LLM provider ──────────────────────
${isAIEnabled(cfg) ? `apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: agent-egress
  namespace: ${ns}
spec:
  podSelector:
    matchLabels: { app: agent }
  policyTypes: [Egress]
  egress:
    # vector DB
    ${cfg.vectorDB === 'pgvector' || cfg.vectorDB === 'qdrant' || cfg.vectorDB === 'weaviate' ? `- to:
        - podSelector:
            matchLabels: { tier: data }
      ports:
        - { protocol: TCP, port: 5432 }
        - { protocol: TCP, port: 6333 }
        - { protocol: TCP, port: 8080 }` : '# managed/SaaS vector DB — egress allowed to provider IPs via egress IPs annotation'}
    # LLM provider (AWS/Azure/GCP managed) or vLLM in-cluster
    ${isSelfHostedLLM(cfg) ? `- to:
        - podSelector:
            matchLabels: { app: vllm }
      ports:
        - { protocol: TCP, port: 8000 }` : `- to:
        - ipBlock:
            # ⚠ Replace with provider-specific CIDRs from cloud docs
            cidr: 0.0.0.0/0
            except: [10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16]
      ports:
        - { protocol: TCP, port: 443 }`}
    # observability
    - to:
        - namespaceSelector:
            matchLabels: { name: observability }
      ports:
        - { protocol: TCP, port: 4317 }
        - { protocol: TCP, port: 4318 }
---
# ── 4. Agent ingress — only from ingress controller ──────────────
apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: agent-ingress
  namespace: ${ns}
spec:
  podSelector:
    matchLabels: { app: agent }
  policyTypes: [Ingress]
  ingress:
    - from:
        - namespaceSelector:
            matchLabels: { kubernetes.io/metadata.name: ingress-nginx }
      ports:
        - { protocol: TCP, port: 8080 }
` : ''}${isSelfHostedLLM(cfg) ? `---
# ── 5. vLLM ingress — only from agent ───────────────────────────
apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: vllm-ingress
  namespace: ${ns}
spec:
  podSelector:
    matchLabels: { app: vllm }
  policyTypes: [Ingress]
  ingress:
    - from:
        - podSelector:
            matchLabels: { app: agent }
      ports:
        - { protocol: TCP, port: 8000 }
` : ''}`;
}

function genPodSecurityStandards(cfg) {
  if (!isK8s(cfg.compute) || !cfg.podSecurityRestricted) return '';
  const ns = fmt(cfg.projectName);
  return `# ─────────────────────────────────────────────────────────────────
# Pod Security Standards — restricted profile
# ─────────────────────────────────────────────────────────────────
# Apply to namespace via labels. Enforces:
#   - runAsNonRoot
#   - dropped capabilities
#   - readOnlyRootFilesystem (where possible)
#   - no privilege escalation
#   - seccomp RuntimeDefault
apiVersion: v1
kind: Namespace
metadata:
  name: ${ns}
  labels:
    pod-security.kubernetes.io/enforce: restricted
    pod-security.kubernetes.io/enforce-version: latest
    pod-security.kubernetes.io/audit: restricted
    pod-security.kubernetes.io/warn: restricted
${needsGPU(cfg) ? `---
# GPU node pool: vLLM cannot run with restricted PSS (needs hostPID + GPU device).
# Carve out a dedicated namespace for inference workloads.
apiVersion: v1
kind: Namespace
metadata:
  name: ${ns}-inference
  labels:
    pod-security.kubernetes.io/enforce: baseline
    pod-security.kubernetes.io/audit: restricted
    pod-security.kubernetes.io/warn: restricted` : ''}
`;
}

function genAlertRules(cfg) {
  if ((cfg.alertChannels || []).length === 0) return '';
  return `# ─────────────────────────────────────────────────────────────────
# Prometheus alerting rules — security + LLM signals
# ─────────────────────────────────────────────────────────────────
groups:
  - name: ai-security
    interval: 30s
    rules:
      # ── Guardrail abuse ─────────────────────────────────────
      - alert: GuardrailBlockSpike
        expr: rate(guardrail_blocks_total[5m]) > 0.5
        for: 2m
        labels: { severity: warning, team: security, category: ai }
        annotations:
          summary: "Guardrail blocks spiking ({{ $value }}/s on {{ $labels.layer }})"
          description: "Possible abuse or misconfigured prompt. Inspect audit log."
          runbook: "https://internal.docs/runbooks/guardrail-abuse"

      - alert: PromptInjectionAttempt
        expr: increase(guardrail_blocks_total{layer="injection"}[10m]) > 5
        for: 1m
        labels: { severity: critical, team: security, category: ai }
        annotations:
          summary: "≥5 prompt-injection attempts in 10m"
          description: "Inspect: kubectl logs -n ${fmt(cfg.projectName)} -l app=agent | grep guardrail"

      # ── Cost runaway ────────────────────────────────────────
      - alert: TokenSpendAnomaly
        expr: rate(llm_tokens_total[1h]) > 1.5 * avg_over_time(rate(llm_tokens_total[1h])[7d:1h])
        for: 15m
        labels: { severity: warning, team: ml, category: cost }
        annotations:
          summary: "Token spend 50% above 7-day baseline"

      # ── Eval regression ─────────────────────────────────────
      - alert: EvalScoreRegression
        expr: eval_faithfulness_score < 0.85
        for: 0m
        labels: { severity: critical, team: ml, category: quality }
        annotations:
          summary: "Faithfulness eval below threshold ({{ $value }})"
          description: "Block deploy. Investigate recent prompt or model changes."

      # ── MCP integrity ───────────────────────────────────────
${cfg.directoryMcpEnabled ? `      - alert: MCPSchemaDrift
        expr: increase(mcp_schema_drift_total[1h]) > 0
        for: 0m
        labels: { severity: critical, team: security, category: mcp }
        annotations:
          summary: "MCP tool schema changed since pin"
          description: "Possible rug-pull. Inspect tool definitions and re-pin if intentional."
` : ''}
      # ── Audit log integrity ─────────────────────────────────
      - alert: AuditLogWriteFailures
        expr: increase(guardrail_blocks_total{layer="audit",reason="audit-write-failed"}[5m]) > 0
        for: 1m
        labels: { severity: critical, team: security, category: compliance }
        annotations:
          summary: "Audit log writes failing — compliance impact"

  - name: runtime-security
    interval: 30s
    rules:
      - alert: FalcoCriticalAlert
        expr: rate(falco_events{priority="critical"}[5m]) > 0
        for: 0m
        labels: { severity: critical, team: security, category: runtime }
        annotations:
          summary: "Falco CRITICAL: {{ $labels.rule }}"
          description: "{{ $labels.output }}"

  - name: compliance-monitoring
    interval: 5m
    rules:
      - alert: SecretsRotationOverdue
        expr: (time() - aws_secretsmanager_secret_last_rotated_timestamp_seconds) > 7776000  # 90d
        for: 0m
        labels: { severity: warning, team: security, category: compliance }
        annotations:
          summary: "Secret not rotated in 90 days: {{ $labels.name }}"
`;
}

function genAlertRoutingConfig(cfg) {
  const channels = cfg.alertChannels || [];
  if (channels.length === 0) return '';
  return `# ─────────────────────────────────────────────────────────────────
# AlertManager routing — by severity + category
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
# ─────────────────────────────────────────────────────────────────
global:
  resolve_timeout: 5m

route:
  receiver: default
  group_by: [alertname, category, severity]
  group_wait: 30s
  group_interval: 5m
  repeat_interval: 4h
  routes:
    # CRITICAL → page on-call immediately
    - matchers: [severity = "critical"]
      receiver: pager
      continue: true
    # Security category → security-team channel + audit
    - matchers: [team = "security"]
      receiver: security-team
      continue: true
    # ML / quality → ml-team channel
    - matchers: [team = "ml"]
      receiver: ml-team
      continue: true

receivers:
  - name: default
    ${channels.includes('slack') ? `slack_configs:
      - api_url_file: /etc/alertmanager/secrets/slack_webhook
        channel: '#alerts-default'
        send_resolved: true` : 'webhook_configs: [{ url: "http://localhost:9999/null" }]'}

${channels.includes('pagerduty') ? `  - name: pager
    pagerduty_configs:
      - service_key_file: /etc/alertmanager/secrets/pagerduty_key
        severity: '{{ .CommonLabels.severity }}'
        details:
          firing: '{{ .Alerts.Firing | len }}'
          resolved: '{{ .Alerts.Resolved | len }}'
          summary: '{{ .CommonAnnotations.summary }}'
` : channels.includes('opsgenie') ? `  - name: pager
    opsgenie_configs:
      - api_key_file: /etc/alertmanager/secrets/opsgenie_key
        priority: 'P1'
        message: '{{ .CommonAnnotations.summary }}'
        description: '{{ .CommonAnnotations.description }}'
        responders:
          - { type: schedule, name: '${fmt(cfg.projectName)}-oncall' }
` : `  - name: pager
    webhook_configs:
      - url_file: /etc/alertmanager/secrets/pager_webhook
`}
${channels.includes('slack') ? `  - name: security-team
    slack_configs:
      - api_url_file: /etc/alertmanager/secrets/slack_webhook
        channel: '#security-alerts'
        title: '🛡 [{{ .CommonLabels.severity | toUpper }}] {{ .CommonLabels.alertname }}'
        text: |
          *Category:* {{ .CommonLabels.category }}
          *Summary:* {{ .CommonAnnotations.summary }}
          *Description:* {{ .CommonAnnotations.description }}
          {{ if .CommonAnnotations.runbook }}*Runbook:* {{ .CommonAnnotations.runbook }}{{ end }}
        send_resolved: true
` : ''}${channels.includes('slack') ? `  - name: ml-team
    slack_configs:
      - api_url_file: /etc/alertmanager/secrets/slack_webhook
        channel: '#ml-alerts'
        send_resolved: true
` : ''}${channels.includes('email') ? `  - name: email-fallback
    email_configs:
      - to: 'oncall@${fmt(cfg.projectName)}.example.com'
        from: 'alerts@${fmt(cfg.projectName)}.example.com'
        smarthost: 'smtp.gmail.com:587'
        auth_username: 'alerts@${fmt(cfg.projectName)}.example.com'
        auth_password_file: '/etc/alertmanager/secrets/smtp_password'
` : ''}${channels.includes('webhook') ? `  - name: generic-webhook
    webhook_configs:
      - url_file: /etc/alertmanager/secrets/generic_webhook
        send_resolved: true
` : ''}`;
}

function genComplianceMatrix(cfg) {
  const frameworks = cfg.complianceFrameworks || [];
  if (frameworks.length === 0) return '';
  return `# Compliance control matrix — ${cfg.projectName}

Generated by STAX. This matrix maps each control from your selected compliance
frameworks to the artifact in this repo that implements (or partially implements) it.

> ⚠ This is **technical control mapping only**. It does not replace audits,
> policy documentation, training records, or evidence collection. Use this as
> a starting point, not a certification.

## Selected frameworks

${frameworks.map(f => {
  const fw = COMPLIANCE_FRAMEWORKS.find(x => x.id === f);
  return fw ? `- **${fw.name}** — ${fw.desc}` : '';
}).filter(Boolean).join('\n')}

## Control mapping

${frameworks.map(f => {
  const fw = COMPLIANCE_FRAMEWORKS.find(x => x.id === f);
  const controls = COMPLIANCE_CONTROLS[f] || [];
  if (!fw || controls.length === 0) return '';
  return `### ${fw.name}

| Control | Description | Implementing artifact |
|---------|-------------|------------------------|
${controls.map(c => `| \`${c.id}\` | ${c.desc} | ${c.artifact} |`).join('\n')}
`;
}).filter(Boolean).join('\n')}

## Gap analysis

Controls **not** automatically satisfied by STAX-generated artifacts (you must
implement these separately):

- **Personnel security** — background checks, security training records
- **Physical security** — for self-hosted; cloud providers cover this via SOC 2
- **Vendor risk management** — third-party assessments for any external SaaS
- **Business continuity / DR** — runbooks, tested failovers, RPO/RTO targets
- **Incident response procedures** — see \`compliance/incident-response.md\` (template provided)

## Evidence collection

For an audit, you'll need to demonstrate that the controls operate as designed.
The following are auto-collectable by STAX-generated infrastructure:

- IAM access logs (CloudTrail / Audit Logs / Activity Logs) — last 90 days
- Container scan reports (.github/workflows/security.yml artifacts)
- SBOM history (Syft outputs, retained ${cfg.environment === 'prod' ? '365' : '30'} days)
- LLM audit log (CloudWatch / equivalent, retained ${cfg.environment === 'prod' ? '7 years' : '90 days'})
- AI red-team scan history (.github/workflows/ai-redteam.yml artifacts)
- Eval pass/fail history (PR comment archive)

## Re-certification cadence

Re-run STAX whenever:
- New compliance framework is added
- Control mappings update (regulatory changes)
- New AI capabilities are introduced (changes risk profile)

---
_Generated ${new Date().toISOString().slice(0,10)} · STAX v1.0.0_
`;
}

function genIncidentResponseRunbook(cfg) {
  const channels = cfg.alertChannels || [];
  if (!isAIEnabled(cfg)) return '';
  return `# Incident response runbook — ${cfg.projectName}

## Severity ladder

| Sev | Definition | Response time | Escalation |
|-----|-----------|---------------|------------|
| **SEV-1** | Active data exfiltration, public PII leak, system compromise | 15 min | Page CISO + on-call |
| **SEV-2** | Guardrail bypass, sustained injection attack, eval regression in prod | 1 hour | Security team channel |
| **SEV-3** | Single suspicious event, isolated incident | Next business day | Async ticket |

## Triage — first 15 minutes

1. **Acknowledge the page** in ${channels.includes('pagerduty') ? 'PagerDuty' : channels.includes('opsgenie') ? 'Opsgenie' : 'your alerting tool'}.
2. **Capture state** before changing anything:
   \`\`\`bash
   kubectl get pods -n ${fmt(cfg.projectName)} -o yaml > /tmp/incident-pods.yaml
   kubectl logs -n ${fmt(cfg.projectName)} -l app=agent --since=1h > /tmp/incident-logs.txt
   ${cfg.llmAuditLog ? 'aws logs filter-log-events --log-group-name ' + fmt(cfg.projectName) + '-' + cfg.environment + '-llm-audit --start-time $(date -d "1 hour ago" +%s)000 > /tmp/audit-1h.json' : ''}
   \`\`\`
3. **Decide containment**: kill switch / per-user block / rollback / WAF rule.

## Containment playbooks

### Kill switch (stop all LLM calls)
\`\`\`bash
kubectl scale deploy/agent -n ${fmt(cfg.projectName)} --replicas=0
${isSelfHostedLLM(cfg) ? `kubectl scale deploy/vllm -n ${fmt(cfg.projectName)} --replicas=0` : ''}
\`\`\`

### Block a single user
\`\`\`bash
# Add user_id to denylist ConfigMap, rolling restart
kubectl create configmap user-denylist -n ${fmt(cfg.projectName)} \\
  --from-literal=blocked='user-123,user-456' --dry-run=client -o yaml | kubectl apply -f -
kubectl rollout restart deploy/agent -n ${fmt(cfg.projectName)}
\`\`\`

### Roll back to last known good
\`\`\`bash
kubectl rollout undo deploy/agent -n ${fmt(cfg.projectName)}
\`\`\`

${cfg.waf ? `### Block source IP at WAF
\`\`\`bash
# Add IP to WAF blocklist via terraform — never directly in console
cd terraform/
echo 'blocked_ips = ["x.x.x.x/32"]' >> terraform.tfvars
terraform apply -auto-approve -target=aws_wafv2_ip_set.blocklist
\`\`\`
` : ''}

## Post-incident

1. **Snapshot** logs + metrics into incident archive (S3 \`${fmt(cfg.projectName)}-incidents/${'$INCIDENT_ID'}/\`).
2. **Write the post-mortem** within 5 business days. Template: \`compliance/postmortem-template.md\`.
3. **File a regression eval** — add the attack to \`evals/golden.jsonl\` so it's tested forever.
4. **Notify**:
   - Internal: security-team channel + leadership
   ${(cfg.complianceFrameworks || []).includes('gdpr') ? '- GDPR: DPA within 72h if personal data affected' : ''}
   ${(cfg.complianceFrameworks || []).includes('hipaa') ? '- HIPAA: HHS within 60 days if >500 individuals' : ''}
   ${(cfg.complianceFrameworks || []).includes('pci-dss') ? '- PCI-DSS: card brands + acquirer if cardholder data affected' : ''}
5. **Update this runbook** with anything that surprised you.

## Common AI-specific incidents

### Prompt injection succeeded (guardrails bypassed)
- Capture the input that bypassed (audit log → SHA → CloudTrail)
- Add to \`evals/redteam-cases.jsonl\` with expected refusal
- Re-run AI red-team workflow against current model
- If managed firewall (Lakera/Bedrock): file a regression with the vendor

### Hallucinated harmful content shipped to user
- Pull the trace from ${cfg.llmObservability !== 'none' ? LLM_OBSERVABILITY.find(o => o.id === cfg.llmObservability)?.name : 'logs'}
- Determine if grounding/RAG context was missing (more likely)
- If it was hallucination on grounded context: file model regression + tighten guardrails

### Suspected data poisoning (vector DB)
- \`SELECT count(*) FROM embeddings WHERE created_at > NOW() - INTERVAL '24 hours' GROUP BY document_id\`
- If anomalous bulk inserts: pause ingestion, rebuild affected partitions from source-of-truth
- Audit the ingestion pipeline's auth path

---
_Last reviewed: ${new Date().toISOString().slice(0,10)}_
`;
}

function genSecurityReadme(cfg) {
  const posture = computeSecurityPosture(cfg);
  const channels = cfg.alertChannels || [];
  const frameworks = cfg.complianceFrameworks || [];
  const scans = cfg.scanTools || [];
  return `# Security architecture — ${cfg.projectName}

## Posture: ${posture.grade.letter} (${posture.total}/${posture.max})

> ${posture.grade.label}

## Defense in depth

\`\`\`
   user request
        │
        ▼
   ┌────────────────────────────────────────────────────┐
   │ Edge:  ${cfg.cdn ? 'CDN' : ' — '}  ${cfg.waf ? '· WAF' : ''}                              │
   └────────────────────────────────────────────────────┘
        │
        ▼
   ┌────────────────────────────────────────────────────┐
   │ Agent service                                       │
   │  ┌─────────────────────────────────────────────┐   │
   │  │ guardrails.py                               │   │
   │  │  1. PII redaction (${piiDetectorName(cfg)})${' '.repeat(Math.max(0, 30 - piiDetectorName(cfg).length))}│   │
   │  │  2. Prompt firewall (${promptFirewallName(cfg)})${' '.repeat(Math.max(0, 28 - promptFirewallName(cfg).length))}│   │
   │  │  3. Denied-topic filter${cfg.deniedTopics ? ' ✓' : ' ✗'}                  │   │
   │  └─────────────────────────────────────────────┘   │
   │              │                                      │
   │              ▼                                      │
   │       LLM (${aiInferenceName(cfg)})${' '.repeat(Math.max(0, 35 - aiInferenceName(cfg).length))} │
   │              │                                      │
   │              ▼                                      │
   │  ┌─────────────────────────────────────────────┐   │
   │  │ Output moderation${cfg.outputModeration ? ' ✓' : ' ✗'}                          │   │
   │  │ Audit log${cfg.llmAuditLog ? ' ✓' : ' ✗'}                                  │   │
   │  └─────────────────────────────────────────────┘   │
   └────────────────────────────────────────────────────┘
        │
        ▼
   response
\`\`\`

## Continuous scanning

${scans.length === 0 ? '_No scans configured. This is a critical gap._' : `Scans run on every PR + nightly:

${scans.map(s => {
  const tool = SCAN_TOOLS.find(x => x.id === s);
  return tool ? `- **${tool.name}** _(${tool.cat})_ — ${tool.desc}` : '';
}).filter(Boolean).join('\n')}

See \`.github/workflows/security.yml\` and \`.github/workflows/ai-redteam.yml\`.`}

## Compliance

${frameworks.length === 0 ? '_No frameworks selected._' : `Selected: ${frameworks.map(f => COMPLIANCE_FRAMEWORKS.find(x => x.id === f)?.short || f).join(', ')}.

See \`compliance/control-matrix.md\` for the full mapping of controls to artifacts.`}

## Alerting

${channels.length === 0 ? '_No alert channels configured. Configure these before going to prod._' : `Routes via: ${channels.map(c => ALERT_CHANNELS.find(x => x.id === c)?.name).filter(Boolean).join(', ')}.

Alert rules live in \`monitoring/alerts.yaml\`. Routing config in \`monitoring/alertmanager.yaml\`.`}

## Incident response

See \`compliance/incident-response.md\` for the runbook.

Kill switch: \`kubectl scale deploy/agent -n ${fmt(cfg.projectName)} --replicas=0\`

## Posture breakdown

| Layer | Control | Status | Weight |
|-------|---------|--------|--------|
${posture.breakdown.map(c => `| ${c.layer} | ${c.name} | ${c.passing ? '✅' : '⬜'} | ${c.weight} |`).join('\n')}

---
_Re-run STAX to refresh after config changes._
`;
}

// =============================================================
// ROLLOUT GENERATORS
// =============================================================

function genRolloutPlanJSON(cfg) {
  if (!cfg.rolloutEnabled) return '';
  const { tickets, waves, teams } = buildRolloutPlan(cfg);
  const teamMap = Object.fromEntries(teams.map(t => [t.id,
    cfg.teamAssignments?.[t.id] || t.name
  ]));
  const out = {
    project:     cfg.projectName,
    environment: cfg.environment,
    tracker:     cfg.issueTracker,
    prefix:      (cfg.ticketPrefix || 'INFRA').toUpperCase(),
    generated:   new Date().toISOString(),
    summary: {
      total_tickets:  tickets.length,
      waves:          waves.length,
      total_effort:   tickets.reduce((s, t) => s + (t.effort || 2), 0),
      teams_involved: teams.filter(t => tickets.some(tk => tk.team === t.id)).length,
    },
    waves: waves.map((w, i) => ({
      wave:   i,
      label:  i === 0 ? 'Foundation' : i === waves.length - 1 ? 'Cutover' : `Wave ${i}`,
      count:  w.length,
      effort: w.reduce((s, t) => s + t.effort, 0),
      ticket_keys: w.map(t => t.key),
    })),
    teams: teams.filter(t => tickets.some(tk => tk.team === t.id)).map(t => ({
      id:           t.id,
      name:         teamMap[t.id],
      ticket_count: tickets.filter(tk => tk.team === t.id).length,
      total_effort: tickets.filter(tk => tk.team === t.id).reduce((s, tk) => s + tk.effort, 0),
    })),
    tickets: tickets.map(t => ({
      key:         t.key,
      summary:     t.summary,
      team:        t.team,
      assignee:    teamMap[t.team],
      wave:        t.wave,
      effort:      t.effort,
      labels:      t.labels,
      component:   t.component,
      description: t.description,
      blocked_by:  t.blocked_by,
    })),
  };
  return JSON.stringify(out, null, 2) + '\n';
}

function genJiraScript(cfg) {
  if (!cfg.rolloutEnabled || cfg.issueTracker !== 'jira') return '';
  const { tickets } = buildRolloutPlan(cfg);
  const prefix = (cfg.ticketPrefix || 'INFRA').toUpperCase();

  const ticketLines = tickets.map(t => {
    const desc = (t.description || t.summary).replace(/"/g, '\\"');
    const labels = t.labels.map(l => `"${l}"`).join(', ');
    return `# ${t.key} · ${ROLLOUT_TEAMS.find(rt => rt.id === t.team)?.name} · wave ${t.wave}
echo "  creating ${t.key}: ${t.summary.replace(/"/g,'\\"')}"
RESP=$(curl -s -X POST "$JIRA_URL/rest/api/3/issue" \\
  -H "Authorization: Basic $JIRA_AUTH" \\
  -H "Content-Type: application/json" \\
  -d '{
    "fields": {
      "project":   { "key": "${prefix}" },
      "issuetype": { "name": "Task" },
      "summary":   "${t.summary.replace(/"/g,'\\"')}",
      "description": {
        "type": "doc", "version": 1,
        "content": [{ "type": "paragraph", "content": [{ "type": "text", "text": "${desc}" }] }]
      },
      "labels":     [${labels}],
      "components": [{ "name": "${t.component}" }]
    }
  }')
KEYS["${t.key}"]=$(echo "$RESP" | jq -r '.key')
echo "    → \${KEYS["${t.key}"]}"
`;
  }).join('\n');

  const linkLines = tickets.flatMap(t =>
    t.blocked_by.map(blocker => `# ${t.key} blocked by ${blocker}
curl -s -X POST "$JIRA_URL/rest/api/3/issueLink" \\
  -H "Authorization: Basic $JIRA_AUTH" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type":         { "name": "Blocks" },
    "inwardIssue":  { "key": "'\${KEYS["${blocker}"]}'" },
    "outwardIssue": { "key": "'\${KEYS["${t.key}"]}'" }
  }' > /dev/null
echo "  \${KEYS["${blocker}"]} → blocks → \${KEYS["${t.key}"]}"`)
  ).join('\n');

  return `#!/usr/bin/env bash
# ─────────────────────────────────────────────────────────────────
# ${cfg.projectName} · ${cfg.environment} · Jira rollout creator
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
#
# Usage:
#   export JIRA_URL='https://your-org.atlassian.net'
#   export JIRA_EMAIL='you@company.com'
#   export JIRA_TOKEN='your-api-token-from-id.atlassian.com'
#   ./create-jira.sh
#
# Creates ${tickets.length} tickets across ${new Set(tickets.map(t => t.team)).size} teams
# in ${Math.max(...tickets.map(t => t.wave)) + 1} dependency waves, then links the "blocks" relations.
# ─────────────────────────────────────────────────────────────────
set -euo pipefail

: "\${JIRA_URL:?must set JIRA_URL}"
: "\${JIRA_EMAIL:?must set JIRA_EMAIL}"
: "\${JIRA_TOKEN:?must set JIRA_TOKEN}"
command -v jq >/dev/null || { echo "jq required"; exit 1; }

JIRA_AUTH=$(printf '%s' "$JIRA_EMAIL:$JIRA_TOKEN" | base64 -w0 2>/dev/null || printf '%s' "$JIRA_EMAIL:$JIRA_TOKEN" | base64)

declare -A KEYS

echo "═══ creating ${tickets.length} tickets ═══"
${ticketLines}

echo ""
echo "═══ linking dependencies ═══"
${linkLines}

echo ""
echo "✓ rollout created — ${tickets.length} tickets, ${tickets.reduce((s,t)=>s+t.blocked_by.length,0)} dependency links"
echo "  open: $JIRA_URL/projects/${prefix}/board"
`;
}

function genLinearScript(cfg) {
  if (!cfg.rolloutEnabled || cfg.issueTracker !== 'linear') return '';
  const { tickets } = buildRolloutPlan(cfg);
  return `#!/usr/bin/env bash
# ─────────────────────────────────────────────────────────────────
# ${cfg.projectName} · ${cfg.environment} · Linear rollout creator
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
# ─────────────────────────────────────────────────────────────────
set -euo pipefail
: "\${LINEAR_API_KEY:?must set LINEAR_API_KEY}"
: "\${LINEAR_TEAM_ID:?must set LINEAR_TEAM_ID}"

declare -A ISSUE_IDS

create_issue() {
  local title="$1" desc="$2" labels="$3" priority="$4"
  local query="mutation { issueCreate(input: { teamId: \\"$LINEAR_TEAM_ID\\", title: \\"$title\\", description: \\"$desc\\", priority: $priority, labelIds: [$labels] }) { issue { id identifier } } }"
  curl -s -X POST https://api.linear.app/graphql \\
    -H "Authorization: $LINEAR_API_KEY" \\
    -H "Content-Type: application/json" \\
    -d "{\\"query\\": \\"$query\\"}" | jq -r '.data.issueCreate.issue.id'
}

echo "═══ creating ${tickets.length} Linear issues ═══"
${tickets.map(t => `# ${t.key} · ${t.team} · wave ${t.wave}
ISSUE_IDS["${t.key}"]=$(create_issue "${t.summary.replace(/"/g,'\\"')}" "${(t.description || t.summary).replace(/"/g,'\\"')}" "" ${t.effort >= 4 ? 1 : t.effort >= 3 ? 2 : 3})
echo "  → ${t.key} → $\{ISSUE_IDS["${t.key}"]\}"`).join('\n')}

echo ""
echo "✓ created ${tickets.length} issues — link blockers manually in Linear or extend with issueRelationCreate."
`;
}

function genGitHubIssuesScript(cfg) {
  if (!cfg.rolloutEnabled || cfg.issueTracker !== 'github') return '';
  const { tickets } = buildRolloutPlan(cfg);
  return `#!/usr/bin/env bash
# ─────────────────────────────────────────────────────────────────
# ${cfg.projectName} · ${cfg.environment} · GitHub Issues rollout creator
# Generated by STAX · ${new Date().toISOString().slice(0,10)}
#
# Requires: gh CLI authenticated against your repo.
# Usage:    cd <repo> && ./create-github.sh
# ─────────────────────────────────────────────────────────────────
set -euo pipefail
command -v gh >/dev/null || { echo "gh CLI required"; exit 1; }

declare -A ISSUE_NUM

${tickets.map(t => {
  const labels = ['stax-generated', `wave-${t.wave}`, `team:${t.team}`, ...t.labels].join(',');
  return `# ${t.key} · ${t.team} · wave ${t.wave}
ISSUE_URL=$(gh issue create \\
  --title "${t.summary.replace(/"/g,'\\"')}" \\
  --body "**Team:** ${ROLLOUT_TEAMS.find(rt => rt.id === t.team)?.name}
**Wave:** ${t.wave}
**Effort:** ${t.effort}
**Blocked by:** ${t.blocked_by.length ? t.blocked_by.join(', ') : '(none — can start immediately)'}

${(t.description || t.summary).replace(/`/g, '\\`')}" \\
  --label "${labels}")
ISSUE_NUM["${t.key}"]=$(echo "$ISSUE_URL" | grep -oE '[0-9]+$')
echo "${t.key} → #\${ISSUE_NUM["${t.key}"]}  $ISSUE_URL"`;
}).join('\n\n')}

echo ""
echo "✓ created ${tickets.length} GitHub issues."
echo "  Use 'gh issue edit <num> --add-label blocked-by:...' to track dependencies,"
echo "  or use a project board with status columns matching waves 0–${Math.max(...tickets.map(t => t.wave))}."
`;
}

function genTicketsCSV(cfg) {
  if (!cfg.rolloutEnabled) return '';
  const { tickets } = buildRolloutPlan(cfg);
  const teamMap = Object.fromEntries(ROLLOUT_TEAMS.map(t => [t.id, cfg.teamAssignments?.[t.id] || t.name]));
  const csvCell = (s) => {
    const str = String(s ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const header = ['Key','Summary','Issue Type','Status','Wave','Team','Assignee','Component','Effort','Labels','Blocked By','Description'];
  const rows = tickets.map(t => [
    t.key,
    t.summary,
    'Task',
    'To Do',
    t.wave,
    ROLLOUT_TEAMS.find(rt => rt.id === t.team)?.name || t.team,
    teamMap[t.team],
    t.component,
    t.effort,
    t.labels.join('; '),
    t.blocked_by.join('; '),
    t.description || t.summary,
  ]);
  return [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n') + '\n';
}

function genRolloutReadme(cfg) {
  if (!cfg.rolloutEnabled) return '';
  const { tickets, waves, teams } = buildRolloutPlan(cfg);
  const usedTeams = teams.filter(t => tickets.some(tk => tk.team === t.id));
  const teamMap = Object.fromEntries(teams.map(t => [t.id, cfg.teamAssignments?.[t.id] || t.name]));
  const tracker = ISSUE_TRACKERS.find(x => x.id === cfg.issueTracker)?.name || cfg.issueTracker;

  return `# Rollout plan — ${cfg.projectName} (${cfg.environment})

Generated by STAX. ${tickets.length} tickets across ${usedTeams.length} teams in ${waves.length} dependency waves.

## How to use

1. Review \`plan.json\` — it's the source of truth for the dependency DAG.
2. Pick the script for your tracker:${cfg.issueTracker === 'jira' ? `
   \`\`\`
   export JIRA_URL='https://your-org.atlassian.net'
   export JIRA_EMAIL='you@company.com'
   export JIRA_TOKEN='your-api-token'
   ./create-jira.sh
   \`\`\`
   The script creates all ${tickets.length} issues, then walks the DAG and creates "is blocked by" links between them.` : cfg.issueTracker === 'linear' ? `
   \`\`\`
   export LINEAR_API_KEY='lin_api_xxx'
   export LINEAR_TEAM_ID='your-team-uuid'
   ./create-linear.sh
   \`\`\`` : cfg.issueTracker === 'github' ? `
   \`\`\`
   gh auth login
   ./create-github.sh
   \`\`\`` : `
   Import \`tickets.csv\` directly into your tracker (Jira, Linear, Asana, ClickUp, monday.com all support CSV import).`}

## Waves

The plan is structured in topological waves — every ticket in wave N has all its blockers in waves < N. Don't start a wave until the previous one is **done**, not "in progress".

| Wave | Label | Count | Total effort | Tickets |
|------|-------|-------|--------------|---------|
${waves.map((w, i) => {
  const label = i === 0 ? 'Foundation' : i === waves.length - 1 ? 'Cutover' : `Wave ${i}`;
  const effort = w.reduce((s, t) => s + t.effort, 0);
  return `| ${i} | ${label} | ${w.length} | ${effort} | ${w.map(t => t.key).join(', ')} |`;
}).join('\n')}

## Teams

| Team | Owner | Tickets | Effort |
|------|-------|---------|--------|
${usedTeams.map(t => {
  const ts = tickets.filter(tk => tk.team === t.id);
  const effort = ts.reduce((s, tk) => s + tk.effort, 0);
  return `| ${t.name} | ${teamMap[t.id]} | ${ts.length} | ${effort} |`;
}).join('\n')}

## Tickets (in dependency order)

${waves.map((w, i) => {
  const label = i === 0 ? 'Foundation — start here, no blockers' : i === waves.length - 1 ? 'Cutover — only after everything else is green' : `Wave ${i}`;
  return `### Wave ${i} · ${label}

${w.map(t => `- **${t.key}** · _${ROLLOUT_TEAMS.find(rt => rt.id === t.team)?.name}_ · effort ${t.effort}\n  ${t.summary}${t.blocked_by.length ? `\n  blocked by: ${t.blocked_by.join(', ')}` : ''}`).join('\n')}`;
}).join('\n\n')}

## Effort estimate

Total story points: **${tickets.reduce((s, t) => s + t.effort, 0)}**
Critical path length: **${waves.length} waves**

Effort scale: 1 = small (< 1d), 2 = medium (1–2d), 3 = large (3–5d), 5 = epic (1–2w)

---
_Regenerate by re-running STAX with updated config._
`;
}

// =============================================================
// COMPONENTS
// =============================================================

// Brand accent. Lime-500 — chosen so it works as both a button background
// AND as text on white. Light theme requires the darker shade for WCAG AA;
// the lighter lime-300 (#bef264) is reserved for soft backgrounds + glows
// via accentSoft below.
const accent = '#65a30d';     // lime-600 — text-safe on white (contrast 4.7:1)
const accentSoft = '#bef264'; // lime-300 — used for hover bg, glows, dot pulses
const accentBg = '#84cc16';   // lime-500 — primary CTA background, dark text reads on it

// ── Pricing tiers + feature gates ───────────────────────────────
// Tier strategy:
//   FREE — evaluation tier. 3 designs cap creates upgrade pressure.
//          Watermarked exports prevent commercial use.
//   PRO ($29/mo, monthly or annual) — solo professional. Unlimited
//          designs, all frameworks, cloud storage, no watermarks.
//          Sized for one person shipping production work.
//   TEAM ($4,800/yr, ANNUAL ONLY) — multi-seat, compliance-grade.
//          Adds SSO, BYOI, custom frameworks, 7yr audit retention,
//          priority support. Annual-only filters out impulse buyers
//          and matches enterprise procurement reality.
const PLAN_FEATURES = {
  free: {
    name:                 'Free',
    priceUSD:             0,
    billing:              'free',
    // Build
    designLimit:          3,
    canDownloadZip:       true,
    exportsWatermarked:   true,    // small "Generated with STAX (Free)" banner in README
    canExportPDF:         false,
    // Operate
    canCloudStore:        false,
    cloudStorageQuotaMB:  0,
    historyDays:          7,
    // Govern
    complianceCoreOnly:   true,    // SOC 2 + GDPR + ISO 27001 only
    canUseAdvancedFrameworks: false,  // HIPAA, PCI-DSS, EU AI Act, NIST AI RMF, ISO 42001, FedRAMP
    canUseCustomFrameworks: false,
    auditLogRetention:    '7 days',
    canSubmitDirectory:   false,
    // Collaborate
    canBYOI:              false,
    canSSO:               false,
    teamSeats:            1,
    // Support
    supportTier:          'community',
  },
  pro: {
    name:                 'Pro',
    priceUSD:             29,
    billing:              'monthly',  // annual at $290 (10 months effective)
    annualPriceUSD:       290,
    // Build
    designLimit:          -1,        // unlimited — designs are configs, not the value gate
    canDownloadZip:       true,
    exportsWatermarked:   false,
    canExportPDF:         true,
    // Operate
    canCloudStore:        true,
    cloudStorageQuotaMB:  100,
    historyDays:          90,
    // Govern
    complianceCoreOnly:   false,
    canUseAdvancedFrameworks: true,   // unlocks HIPAA, PCI, EU AI Act, NIST AI RMF, ISO 42001, FedRAMP
    canUseCustomFrameworks: false,    // (Team only)
    auditLogRetention:    '90 days',
    canSubmitDirectory:   true,
    // Collaborate
    canBYOI:              false,
    canSSO:               false,
    teamSeats:            1,
    // Support
    supportTier:          'email',    // 24h response
  },
  team: {
    name:                 'Team',
    priceUSD:             400,        // display price (per month equivalent of $4,800/yr)
    billing:              'annual-only',
    annualPriceUSD:       4800,
    // Build
    designLimit:          -1,
    canDownloadZip:       true,
    exportsWatermarked:   false,
    canExportPDF:         true,
    // Operate
    canCloudStore:        true,
    cloudStorageQuotaMB:  1024,       // 1 GB
    historyDays:          -1,         // unlimited
    // Govern
    complianceCoreOnly:   false,
    canUseAdvancedFrameworks: true,
    canUseCustomFrameworks: true,     // upload your own framework definitions
    auditLogRetention:    '7 years',  // SOC 2 / HIPAA / PCI-grade retention
    canSubmitDirectory:   true,
    // Collaborate
    canBYOI:              true,
    canSSO:               true,       // SAML 2.0 + OIDC (Okta, Azure AD, Auth0, Google Workspace)
    teamSeats:            10,
    // Support
    supportTier:          'priority', // 4h response + private Slack channel
  },
};

// ── Persistent storage wrapper ──────────────────────────────────
// Primary backend: localStorage (browser) — persists across tabs/sessions.
// Fallback: in-memory Map — for SSR / private mode / quota errors.
// Swap STORAGE_PREFIX to namespace if you embed STAX inside another app.
const _memStore = new Map();
const _hasLocalStorage = (() => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const probe = '__stax_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch { return false; }
})();
const STORAGE_PREFIX = 'stax:';

const Store = {
  async get(key) {
    const k = STORAGE_PREFIX + key;
    if (_hasLocalStorage) {
      try {
        const v = window.localStorage.getItem(k);
        return v === null ? null : v;
      } catch { /* fall through to mem */ }
    }
    return _memStore.has(k) ? _memStore.get(k) : null;
  },
  async set(key, value) {
    const k = STORAGE_PREFIX + key;
    const v = typeof value === 'string' ? value : JSON.stringify(value);
    if (_hasLocalStorage) {
      try { window.localStorage.setItem(k, v); return true; }
      catch { /* quota / private mode / fall through */ }
    }
    _memStore.set(k, v);
    return true;
  },
  async delete(key) {
    const k = STORAGE_PREFIX + key;
    if (_hasLocalStorage) {
      try { window.localStorage.removeItem(k); return true; }
      catch { /* fall through */ }
    }
    _memStore.delete(k);
    return true;
  },
  async list(prefix) {
    const search = STORAGE_PREFIX + (prefix || '');
    const out = [];
    if (_hasLocalStorage) {
      try {
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && k.startsWith(search)) out.push(k.slice(STORAGE_PREFIX.length));
        }
        return out;
      } catch { /* fall through */ }
    }
    for (const k of _memStore.keys()) {
      if (k.startsWith(search)) out.push(k.slice(STORAGE_PREFIX.length));
    }
    return out;
  },
};

// ─────────────────────────────────────────────────────────────────
// DesignStore — abstraction over per-user design persistence.
//
// Routes to Firestore for Google-authenticated users (cross-device,
// real cloud storage), and to localStorage for everyone else
// (email/password demo flow stays self-contained on-device).
//
// Choice is made per-call: we read user.authProvider to decide.
// Methods are async-throughout so consumers don't care which backend.
//
// Firestore schema:
//   /users/{uid}/designs/{designId}  — full design record
//
// Security rules (paste into Firebase Console → Firestore → Rules):
//   match /users/{uid}/designs/{designId} {
//     allow read, write: if request.auth != null && request.auth.uid == uid;
//   }
// ─────────────────────────────────────────────────────────────────

// Lightweight design summary for the dashboard list — avoids loading
// the full config when rendering the index.
function _summaryOf(config) {
  return {
    provider: config?.provider,
    environment: config?.environment,
    compute: config?.compute,
    database: config?.database,
    aiEnabled: !!config?.aiEnabled,
    directoryEnabled: !!config?.directoryEnabled,
    complianceFrameworks: config?.complianceFrameworks || [],
  };
}

// DesignStore — design configuration persistence (localStorage).
// NOTE: Stack zip artifacts are stored separately in Firebase Storage
// for paid users — see cloudStorage.js.
const DesignStore = {
  async list(user) {
    if (!user) return [];
    const idxRaw = await Store.get(`designs:${user.id}:_index`);
    if (!idxRaw) return [];
    try {
      const idx = JSON.parse(idxRaw);
      return Array.isArray(idx) ? idx : [];
    } catch { return []; }
  },

  async save(user, record) {
    if (!user) throw new Error('not signed in');
    await Store.set(`designs:${user.id}:${record.id}`, JSON.stringify(record));
    return record;
  },

  async load(user, id) {
    if (!user) return null;
    const raw = await Store.get(`designs:${user.id}:${id}`);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  },

  async remove(user, id) {
    if (!user) return;
    await Store.delete(`designs:${user.id}:${id}`);
  },

  async writeIndex(user, idx) {
    if (!user) return;
    await Store.set(`designs:${user.id}:_index`, JSON.stringify(idx));
  },
};

// Save status indicator (shown next to design name in StaxGenerator)
// AnimatedCounter — counts up from 0 to `to` over `dur` ms. Renders a string.
function AnimatedCounter({ to, dur = 1200 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const target = Number(to) || 0;
    if (target === 0) { setV(0); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(target * eased * 10) / 10);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, dur]);
  return Number.isInteger(to) ? String(Math.round(v)) : v.toFixed(1);
}

function SaveStatusIndicator({ status }) {
  const map = {
    idle:   { color: '#9a9890', text: '·' },
    saving: { color: '#b45309', text: 'saving…' },
    saved:  { color: '#86efac', text: 'saved' },
    error:  { color: '#be123c', text: 'save failed' },
  };
  const s = map[status] || map.idle;
  return (
    <span className="text-[10px] flex items-center gap-1.5 ml-2"
      style={{ color: s.color, fontFamily: 'IBM Plex Mono, monospace' }}>
      <span className={status === 'saving' ? 'animate-pulse' : ''} style={{ width: 4, height: 4, borderRadius: '50%', background: s.color }} />
      {s.text}
    </span>
  );
}

const Pill = ({ children, color = '#52524d', dot = true }) => (
  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] border"
    style={{ color, borderColor: '#d8d5cb', fontFamily: 'IBM Plex Mono, ui-monospace, monospace' }}>
    {dot && <span className="w-1 h-1 rounded-full" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />}
    {children}
  </span>
);

const SectionLabel = ({ children, num }) => (
  <div className="flex items-baseline gap-3 mb-5">
    {num !== undefined && (
      <span className="text-[10px] tabular-nums" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
        {String(num).padStart(2, '0')}
      </span>
    )}
    <h2 className="text-[11px] uppercase tracking-[0.22em]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
      {children}
    </h2>
    <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, #d8d5cb 0%, transparent 100%)' }} />
  </div>
);

const SelectCard = ({ option, selected, onClick, compact }) => {
  const Icon = option.icon || Box;
  return (
    <button
      onClick={onClick}
      className="group relative text-left transition-all duration-150 cursor-pointer w-full"
      style={{
        background: selected ? '#f7fbe5' : '#ffffff',
        border: `1px solid ${selected ? accent : '#d8d5cb'}`,
        padding: compact ? '12px 14px' : '18px 18px',
        boxShadow: selected ? `inset 0 0 0 1px ${accent}, 0 0 0 1px ${accent}40, 0 4px 24px ${accent}15` : 'none',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: compact ? 28 : 36, height: compact ? 28 : 36,
            background: selected ? accentBg : '#ebe9e1',
            color: selected ? '#1a1a17' : '#52524d',
            border: `1px solid ${selected ? accent : '#d8d5cb'}`,
          }}
        >
          <Icon size={compact ? 14 : 16} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-[13px]" style={{ color: '#1a1a17' }}>{option.name}</span>
            {option.tag && !compact && <Pill color="#52524d" dot={false}>{option.tag}</Pill>}
          </div>
          {option.desc && !compact && (
            <p className="text-[12px] mt-1.5 leading-relaxed" style={{ color: '#6f6e68' }}>{option.desc}</p>
          )}
        </div>
        {selected && (
          <div className="flex-shrink-0">
            <Check size={14} style={{ color: accent }} strokeWidth={3} />
          </div>
        )}
      </div>
    </button>
  );
};

const CornerMarks = () => (
  <>
    <div className="absolute top-0 left-0 w-2 h-2 border-t border-l" style={{ borderColor: accent }} />
    <div className="absolute top-0 right-0 w-2 h-2 border-t border-r" style={{ borderColor: accent }} />
    <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l" style={{ borderColor: accent }} />
    <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r" style={{ borderColor: accent }} />
  </>
);

// =============================================================
// MAIN APP
// =============================================================

function StaxGenerator({ user, currentDesign, plan, onSaveDesign, onExitToDashboard, onUpgrade }) {
  const [config, setConfig] = useState(() => currentDesign?.config || {
    projectName: 'phoenix',
    environment: 'prod',
    owner: 'platform-team',
    costCenter: 'engineering',
    provider: 'aws',
    region: 'us-east-1',
    compute: 'eks',
    database: 'rds-postgres',
    cache: 'redis',
    cicd: 'github',
    observability: 'datadog',
    cdn: true,
    waf: true,
    secretsManager: true,
    // ── AI / ML ─────────────────────────────────────────
    aiEnabled: true,
    aiInference: 'bedrock',
    vectorDB: 'pgvector',
    agentFramework: 'langgraph',
    llmObservability: 'langfuse',
    gpuType: 'g5.xlarge (A10G · 24GB)',
    enableEvals: true,
    // ── Rollout / tickets ────────────────────────────────
    rolloutEnabled: true,
    issueTracker: 'jira',
    ticketPrefix: 'PHX',
    teamAssignments: {
      'platform-net':     'platform-net@company.com',
      'security':         'security-team@company.com',
      'platform-compute': 'platform-eng@company.com',
      'data':             'data-platform@company.com',
      'ml':               'ml-platform@company.com',
      'sre':              'sre-team@company.com',
      'devops':           'devops@company.com',
      'release':          'release-eng@company.com',
    },
    // ── Directory (agentic.ai integration) ───────────────
    directoryEnabled: true,
    directoryCategory: 'enterprise-agent-platforms',
    directoryAutonomy: 'semi',
    directoryReach: 2,
    directoryPricing: 'SUBSCRIPTION',
    directoryOpenSource: false,
    directoryMcpEnabled: true,
    directoryWebTool: false,
    directoryCodeExecution: false,
    directoryTagline: '',
    directoryDescription: '',
    directoryHomepage: '',
    // ── Security & Compliance (priority spine) ──────────
    promptFirewall: 'bedrock-guardrails',
    piiDetector: 'presidio',
    outputModeration: true,
    deniedTopics: true,
    llmAuditLog: true,
    mcpAllowlist: true,
    mcpSignatureVerify: true,
    mcpSchemaPinning: true,
    networkPoliciesDefault: true,
    podSecurityRestricted: true,
    complianceFrameworks: ['soc2', 'iso42001', 'eu-ai-act'],
    scanTools: ['trivy', 'checkov', 'gitleaks', 'syft', 'semgrep', 'falco', 'garak', 'promptfoo'],
    alertChannels: ['pagerduty', 'slack'],
    // ── Data engineering (off by default — opt-in) ───────────
    deEnabled: false,
    deStreaming: 'kafka',
    deOlap: 'pinot',
    deStreamProc: 'flink',
    deWideCol: 'cassandra',
    deLakeTable: 'iceberg',
    deQueryEngine: 'trino',
    deOrchestration: 'airflow',
    deDataVolume: 'medium',  // small | medium | large — sizes the cluster footprint
  });
  const [stepIndex, setStepIndex] = useState(0);
  const [activeFile, setActiveFile] = useState('terraform/main.tf');
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState('files');
  const [designName, setDesignName] = useState(currentDesign?.name || 'untitled design');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [byoi, setByoi] = useState(null);
  // Sidebar starts expanded on desktop (≥1024px), auto-collapsed on smaller
  // viewports. User toggle is persisted across sessions.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = window.localStorage?.getItem('stax:ui:sidebarCollapsed');
    if (saved !== null) return saved === '1';
    return window.innerWidth < 1024;  // auto-collapse on small viewports first time
  });
  useEffect(() => {
    try { window.localStorage?.setItem('stax:ui:sidebarCollapsed', sidebarCollapsed ? '1' : '0'); } catch {}
  }, [sidebarCollapsed]);
  // Track viewport width for responsive grid — three columns on desktop,
  // sidebar+main on tablet (manifest stacks below), single column on mobile.
  const [viewportTier, setViewportTier] = useState(() => {
    if (typeof window === 'undefined') return 'desktop';
    if (window.innerWidth < 768) return 'mobile';
    if (window.innerWidth < 1280) return 'tablet';
    return 'desktop';
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setViewportTier(w < 768 ? 'mobile' : w < 1280 ? 'tablet' : 'desktop');
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const planFeatures = useMemo(() => PLAN_FEATURES[plan] || PLAN_FEATURES.free, [plan]);

  // ── Cloud storage state ──────────────────────────────────────
  // Eligibility: paid plan + Firebase-authenticated (Google) user.
  // localStorage-only users can't get a Firebase uid for the storage path.
  const isCloudEligible = planFeatures.canCloudStore && user?.authProvider === 'google';
  const [cloudUsage, setCloudUsage] = useState({ bytes: 0, items: [] });
  const [cloudStatus, setCloudStatus] = useState('idle'); // idle | uploading | saved | error
  const [cloudError, setCloudError] = useState('');
  const [lastCloudURL, setLastCloudURL] = useState(null);

  // Load current cloud usage when eligible. Caches in localStorage so we
  // don't re-list on every render — only refresh after a save/delete.
  const refreshCloudUsage = async () => {
    if (!isCloudEligible) return;
    try {
      const uid = firebaseAuth.currentUser?.uid;
      if (!uid) return;
      const usage = await getCloudUsage(uid);
      setCloudUsage(usage);
    } catch (err) {
      console.warn('[cloudStorage] usage refresh failed', err);
    }
  };
  useEffect(() => {
    if (isCloudEligible) refreshCloudUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloudEligible]);

  // Move between wizard steps and scroll the page to the top so the user
  // doesn't land mid-page on the new section. Smooth scroll for forward/back
  // motion; respects prefers-reduced-motion via 'auto' fallback.
  const goToStep = (next) => {
    const target = Math.max(0, Math.min(STEPS.length - 1, next));
    setStepIndex(target);
    if (typeof window !== 'undefined') {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }
  };

  // Load BYOI config when user is on Team plan
  useEffect(() => {
    if (!user || !planFeatures.canBYOI) { setByoi(null); return; }
    (async () => {
      const raw = await Store.get(`byoi:${user.id}`);
      if (raw) {
        try { setByoi(JSON.parse(raw)); } catch { setByoi(null); }
      }
    })();
  }, [user?.id, planFeatures.canBYOI]);

  // Inject BYOI into the generation config (under _byoi key — generators read from here)
  const generationConfig = useMemo(() => {
    let cfg = byoi ? { ...config, _byoi: byoi } : { ...config };
    // Defensive: if user is on free plan, strip any advanced frameworks
    // they may have selected before a plan downgrade. Otherwise the
    // generated control matrix would reference frameworks they can't use.
    if (!planFeatures.canUseAdvancedFrameworks) {
      const allowed = COMPLIANCE_FRAMEWORKS.filter(f => f.tier === 'core').map(f => f.id);
      cfg.complianceFrameworks = (cfg.complianceFrameworks || []).filter(id => allowed.includes(id));
    }
    // Watermark flag — read by genReadme. Free plan generates a small
    // "Generated with STAX (Free)" footer in the README.
    cfg._watermark = !!planFeatures.exportsWatermarked;
    return cfg;
  }, [config, byoi, planFeatures.canUseAdvancedFrameworks, planFeatures.exportsWatermarked]);

  // Hydrate from a loaded design when it changes
  useEffect(() => {
    if (currentDesign?.config) {
      setConfig(currentDesign.config);
      setDesignName(currentDesign.name);
    }
  }, [currentDesign?.id]);

  // Auto-save (debounced) — silent in background, status updates UI
  useEffect(() => {
    if (!user || !onSaveDesign) return;
    setSaveStatus('saving');
    const t = setTimeout(async () => {
      try {
        await onSaveDesign({ id: currentDesign?.id, name: designName, config });
        setSaveStatus('saved');
      } catch {
        setSaveStatus('error');
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [config, designName]);

  // sync region default when provider changes
  useEffect(() => {
    const p = PROVIDERS.find(x => x.id === config.provider);
    if (p && !p.regions.includes(config.region)) {
      setConfig(c => ({ ...c, region: p.regions[0] }));
    }
    // sync compute & database to provider-valid options
    const validCompute = COMPUTE_BY_PROVIDER[config.provider].map(x => x.id);
    if (!validCompute.includes(config.compute)) {
      setConfig(c => ({ ...c, compute: validCompute[0] }));
    }
    const validDb = DATABASE_OPTIONS[config.provider].map(x => x.id);
    if (!validDb.includes(config.database)) {
      setConfig(c => ({ ...c, database: validDb[0] }));
    }
    // sync AI inference to provider-valid options
    const validInf = AI_INFERENCE_OPTIONS[config.provider].map(x => x.id);
    if (!validInf.includes(config.aiInference)) {
      setConfig(c => ({ ...c, aiInference: validInf[0] }));
    }
    // sync GPU type to provider
    const validGpus = GPU_TYPES[config.provider];
    if (!validGpus.includes(config.gpuType)) {
      setConfig(c => ({ ...c, gpuType: validGpus[0] }));
    }
    // OpenSearch is AWS-only
    if (config.vectorDB === 'opensearch' && config.provider !== 'aws') {
      setConfig(c => ({ ...c, vectorDB: 'pgvector' }));
    }
    // Sync managed prompt firewall + PII detector to provider where applicable
    if (config.promptFirewall === 'bedrock-guardrails' && config.provider !== 'aws') {
      setConfig(c => ({ ...c, promptFirewall: config.provider === 'azure' ? 'azure-content-safety' : 'lakera' }));
    }
    if (config.promptFirewall === 'azure-content-safety' && config.provider !== 'azure') {
      setConfig(c => ({ ...c, promptFirewall: config.provider === 'aws' ? 'bedrock-guardrails' : 'lakera' }));
    }
    if (config.piiDetector === 'comprehend' && config.provider !== 'aws') {
      setConfig(c => ({ ...c, piiDetector: config.provider === 'gcp' ? 'cloud-dlp' : 'presidio' }));
    }
    if (config.piiDetector === 'cloud-dlp' && config.provider !== 'gcp') {
      setConfig(c => ({ ...c, piiDetector: config.provider === 'aws' ? 'comprehend' : 'presidio' }));
    }
  }, [config.provider]);

  const files = useMemo(() => {
    const f = {
      'terraform/main.tf':       genTerraformMain(generationConfig),
      'terraform/variables.tf':  genTerraformVariables(generationConfig),
      'terraform/networking.tf': genTerraformNetworking(generationConfig),
      'terraform/compute.tf':    genTerraformCompute(generationConfig),
      'terraform/database.tf':   genTerraformDatabase(generationConfig),
      'terraform/outputs.tf':    genTerraformOutputs(generationConfig),
      '.github/workflows/deploy.yml': genGitHubActions(generationConfig),
      'README.md':               genReadme(generationConfig),
    };
    const k8s = genK8sManifests(generationConfig);
    if (k8s) f['k8s/deployment.yaml'] = k8s;
    const docker = genDockerfile(generationConfig);
    if (docker) f['Dockerfile'] = docker;

    // ── Data Engineering files ───────────────────────────
    if (isDEOn(generationConfig)) {
      const deTf = genTerraformDataEng(generationConfig);
      if (deTf) f['terraform/data-eng.tf'] = deTf;
      const kafka = genHelmKafka(generationConfig);
      if (kafka) f['k8s/de/kafka.yaml'] = kafka;
      const redpanda = genHelmRedpanda(generationConfig);
      if (redpanda) f['k8s/de/redpanda-values.yaml'] = redpanda;
      const pinot = genHelmPinot(generationConfig);
      if (pinot) f['k8s/de/pinot-values.yaml'] = pinot;
      const pinotSchema = genPinotTableSchema(generationConfig);
      if (pinotSchema) f['k8s/de/pinot-schema.json'] = pinotSchema;
      const flink = genHelmFlink(generationConfig);
      if (flink) f['k8s/de/flink.yaml'] = flink;
      const flinkSql = genFlinkExampleJob(generationConfig);
      if (flinkSql) f['flink_jobs/events_enrich.sql'] = flinkSql;
      const cass = genHelmCassandra(generationConfig);
      if (cass) f['k8s/de/cassandra.yaml'] = cass;
      const trino = genHelmTrino(generationConfig);
      if (trino) f['k8s/de/trino-values.yaml'] = trino;
      const af = genHelmAirflow(generationConfig);
      if (af) f['k8s/de/airflow-values.yaml'] = af;
      const afDag = genAirflowExampleDag(generationConfig);
      if (afDag) f['dags/lake_to_olap.py'] = afDag;
      const deReadme = genDataEngReadme(generationConfig);
      if (deReadme) f['DATA-ENGINEERING.md'] = deReadme;
    }

    // ── AI files ─────────────────────────────────────────
    if (isAIEnabled(generationConfig)) {
      const aiTf = genTerraformAI(generationConfig);
      if (aiTf) f['terraform/ai.tf'] = aiTf;
      f['agent/main.py']         = genAgentService(generationConfig);
      f['agent/requirements.txt'] = genAgentRequirements(generationConfig);
      f['agent/Dockerfile']       = genAgentDockerfile(generationConfig);
      const agentManifest = genAgentManifest(generationConfig);
      if (agentManifest) f['k8s/agent.yaml'] = agentManifest;
      const vllm = genVllmManifest(generationConfig);
      if (vllm) f['k8s/vllm.yaml'] = vllm;
      const evals = genEvalsWorkflow(generationConfig);
      if (evals) f['.github/workflows/evals.yml'] = evals;
      const golden = genGoldenSet(generationConfig);
      if (golden) f['evals/golden.jsonl'] = golden;
    }

    // ── Rollout files ────────────────────────────────────
    if (config.rolloutEnabled) {
      f['rollout/plan.json']    = genRolloutPlanJSON(generationConfig);
      f['rollout/tickets.csv']  = genTicketsCSV(generationConfig);
      f['rollout/README.md']    = genRolloutReadme(generationConfig);
      if (config.issueTracker === 'jira')   f['rollout/create-jira.sh']   = genJiraScript(generationConfig);
      if (config.issueTracker === 'linear') f['rollout/create-linear.sh'] = genLinearScript(generationConfig);
      if (config.issueTracker === 'github') f['rollout/create-github.sh'] = genGitHubIssuesScript(generationConfig);
    }

    // ── Directory (agentic.ai integration) ──────────────
    if (config.directoryEnabled && isAIEnabled(generationConfig)) {
      f['directory/agent.json']         = genAgentDotJson(generationConfig);
      f['directory/submission.json']    = genAgenticSubmission(generationConfig);
      f['directory/submit-directory.sh'] = genDirectorySubmitScript(generationConfig);
      f['directory/README.md']          = genDirectoryReadme(generationConfig);
      const mcp = genMCPServerStub(generationConfig);
      if (mcp) f['agent/mcp_server.py'] = mcp;
    }

    // ── Security & Compliance (priority spine) ──────────
    if (isAIEnabled(generationConfig)) {
      const guardrails = genGuardrailsModule(generationConfig);
      if (guardrails) f['agent/guardrails.py'] = guardrails;
    }
    if (config.directoryEnabled && config.directoryMcpEnabled && isAIEnabled(generationConfig)) {
      const mcpPolicy = genMcpSecurityPolicy(generationConfig);
      if (mcpPolicy) f['agent/mcp_policy.yaml'] = mcpPolicy;
    }
    const secScan = genSecurityScanWorkflow(generationConfig);
    if (secScan) f['.github/workflows/security.yml'] = secScan;
    const aiRed = genAIRedTeamWorkflow(generationConfig);
    if (aiRed) f['.github/workflows/ai-redteam.yml'] = aiRed;
    const falco = genFalcoRules(generationConfig);
    if (falco) f['k8s/falco-rules.yaml'] = falco;
    const netpol = genNetworkPolicies(generationConfig);
    if (netpol) f['k8s/network-policies.yaml'] = netpol;
    const pss = genPodSecurityStandards(generationConfig);
    if (pss) f['k8s/namespace-pss.yaml'] = pss;
    const alerts = genAlertRules(generationConfig);
    if (alerts) f['monitoring/alerts.yaml'] = alerts;
    const alertRouting = genAlertRoutingConfig(generationConfig);
    if (alertRouting) f['monitoring/alertmanager.yaml'] = alertRouting;
    const compMatrix = genComplianceMatrix(generationConfig);
    if (compMatrix) f['compliance/control-matrix.md'] = compMatrix;
    const incident = genIncidentResponseRunbook(generationConfig);
    if (incident) f['compliance/incident-response.md'] = incident;
    f['SECURITY.md'] = genSecurityReadme(generationConfig);

    return f;
  }, [config, byoi]);

  const fileTree = useMemo(() => {
    const tree = {};
    Object.keys(files).forEach(path => {
      const parts = path.split('/');
      let node = tree;
      parts.forEach((p, i) => {
        if (i === parts.length - 1) node[p] = path;
        else { node[p] = node[p] || {}; node = node[p]; }
      });
    });
    return tree;
  }, [files]);

  // approximate cost estimate
  const monthlyCost = useMemo(() => {
    let c = 0;
    if (config.compute === 'eks')    c += config.environment === 'prod' ? 280 : 90;
    if (config.compute === 'gke')    c += config.environment === 'prod' ? 260 : 80;
    if (config.compute === 'aks')    c += config.environment === 'prod' ? 270 : 85;
    if (config.compute === 'ecs')    c += config.environment === 'prod' ? 140 : 40;
    if (config.compute === 'lambda') c += config.environment === 'prod' ? 60  : 12;
    if (config.compute === 'ec2')    c += config.environment === 'prod' ? 180 : 50;
    if (config.database.startsWith('rds') || config.database === 'cloudsql-pg' || config.database === 'pg-flex')
      c += config.environment === 'prod' ? 220 : 55;
    if (config.database === 'aurora') c += config.environment === 'prod' ? 320 : 90;
    if (config.database === 'dynamo' || config.database === 'cosmos') c += config.environment === 'prod' ? 80 : 15;
    if (config.cache === 'redis')    c += config.environment === 'prod' ? 110 : 25;
    if (config.cdn)                  c += 30;
    if (config.observability === 'datadog') c += config.environment === 'prod' ? 180 : 45;

    // ── AI / ML cost ────────────────────────────────────
    if (isAIEnabled(config)) {
      if (config.aiInference === 'bedrock')   c += config.environment === 'prod' ? 600 : 80;
      if (config.aiInference === 'vertex')    c += config.environment === 'prod' ? 580 : 75;
      if (config.aiInference === 'aoai')      c += config.environment === 'prod' ? 620 : 90;
      if (config.aiInference === 'sagemaker') c += config.environment === 'prod' ? 900 : 220;
      if (isSelfHostedLLM(config)) {
        const isH100 = (config.gpuType || '').includes('H100') || (config.gpuType || '').includes('p5');
        const isA100 = (config.gpuType || '').includes('A100') || (config.gpuType || '').includes('p4d');
        c += isH100 ? (config.environment === 'prod' ? 8500 : 2200)
           : isA100 ? (config.environment === 'prod' ? 3200 : 800)
           :          (config.environment === 'prod' ? 1100 : 280);
      }
      if (config.aiInference === 'external')  c += config.environment === 'prod' ? 800 : 100;
      if (config.vectorDB === 'opensearch')   c += config.environment === 'prod' ? 320 : 60;
      if (config.vectorDB === 'pinecone')     c += config.environment === 'prod' ? 90  : 20;
      if (config.vectorDB === 'qdrant' || config.vectorDB === 'weaviate')
                                              c += config.environment === 'prod' ? 180 : 45;
      if (config.llmObservability === 'langsmith') c += config.environment === 'prod' ? 150 : 30;
    }
    return c;
  }, [config]);

  const currentStep = STEPS[stepIndex];

  const updateConfig = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const copyActive = () => {
    navigator.clipboard.writeText(files[activeFile] || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const downloadActive = () => {
    const content = files[activeFile] || '';
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const filename = activeFile.split('/').pop() || 'file.txt';
    triggerDownload(blob, filename);
  };

  const downloadZip = () => {
    const fileList = Object.entries(files).map(([path, content]) => ({ path, content }));
    const blob = makeZipBlob(fileList);
    triggerDownload(blob, `${fmt(config.projectName)}-${config.environment}.zip`);
  };

  // Save the just-generated zip to the user's cloud folder.
  // Only available on paid plans + Google-authed sessions.
  const saveZipToCloud = async () => {
    setCloudError('');
    if (!isCloudEligible) {
      setCloudError(planFeatures.canCloudStore
        ? 'Sign in with Google to use cloud storage.'
        : 'Cloud storage is a paid feature. Upgrade to Pro or Team.');
      setCloudStatus('error');
      return;
    }
    const uid = firebaseAuth.currentUser?.uid;
    if (!uid) { setCloudError('Firebase session missing — sign in again.'); setCloudStatus('error'); return; }
    if (!currentDesign?.id) {
      setCloudError('Save the design first (give it a name) before storing the build.');
      setCloudStatus('error');
      return;
    }
    setCloudStatus('uploading');
    try {
      const fileList = Object.entries(files).map(([path, content]) => ({ path, content }));
      const blob = makeZipBlob(fileList);
      const result = await uploadDesignBuild({
        uid,
        designId: currentDesign.id,
        designName: designName,
        zipBlob: blob,
        manifest: {
          provider:    config.provider,
          environment: config.environment,
          compute:     config.compute,
          fileCount:   fileList.length,
          totalBytes:  fileList.reduce((a, f) => a + (f.content?.length || 0), 0),
        },
        quotaMB: planFeatures.cloudStorageQuotaMB,
      });
      setLastCloudURL(result.downloadURL);
      setCloudStatus('saved');
      await refreshCloudUsage();
      setTimeout(() => setCloudStatus(s => s === 'saved' ? 'idle' : s), 4000);
    } catch (err) {
      setCloudError(err?.message || 'Cloud save failed.');
      setCloudStatus('error');
    }
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background: '#fbfaf7',
        color: '#1a1a17',
        fontFamily: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
        backgroundImage: `linear-gradient(rgba(216,213,203,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(216,213,203,0.5) 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Serif:ital,wght@0,400;1,400&display=swap');
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #d8d5cb; border-radius: 0; }
        ::-webkit-scrollbar-thumb:hover { background: #9a9890; }
        .grid-glow { box-shadow: inset 0 0 80px rgba(190, 242, 100, 0.04); }
        @keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
        .blink { animation: blink 1s step-end infinite; }
        @keyframes pulseDot {
          0%, 100% { box-shadow: 0 0 0 0 ${accentSoft}80; }
          50% { box-shadow: 0 0 0 6px transparent; }
        }
        .pulse-dot { animation: pulseDot 2s ease-out infinite; }
        @keyframes streamIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .stream-in { animation: streamIn 0.3s ease-out; }
      `}</style>

      {/* ── Top bar ─────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{ background: 'rgba(10,10,11,0.8)', borderBottom: '1px solid #ebe9e1' }}
      >
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-6">
          <button
            onClick={() => onExitToDashboard?.()}
            className="flex items-center gap-2.5 group"
            title="Back to dashboard"
          >
            <div className="relative w-7 h-7 flex items-center justify-center transition-transform group-hover:rotate-12" style={{ background: accentBg }}>
              <Hexagon size={14} strokeWidth={2.5} style={{ color: '#1a1a17' }} fill="#1a1a17" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[15px] font-semibold tracking-[0.2em]">STAX</span>
              <span className="text-[10px] uppercase tracking-widest" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                v1.0.0
              </span>
            </div>
          </button>

          {/* Design name (editable) */}
          <div className="flex-1 hidden md:flex items-center gap-2 min-w-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            <Terminal size={12} style={{ color: '#6f6e68' }} />
            <span className="text-[11px]" style={{ color: '#9a9890' }}>~/stax/</span>
            <input
              type="text"
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              className="bg-transparent text-[11px] outline-none border-b border-transparent hover:border-[#d8d5cb] focus:border-[#bef264] transition-colors min-w-0 flex-shrink"
              style={{ color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', maxWidth: '240px' }}
              maxLength={60}
            />
            {/* Save status */}
            <SaveStatusIndicator status={saveStatus} />
          </div>

          <div className="flex items-center gap-2">
            <Pill color={config.environment === 'prod' ? '#fb7185' : config.environment === 'staging' ? '#fbbf24' : accent}>
              {config.environment}
            </Pill>
            <Pill color="#52524d">{config.provider.toUpperCase()}</Pill>
            {/* Plan badge */}
            {plan === 'free' ? (
              <button
                onClick={() => onUpgrade?.()}
                className="text-[10px] px-2 py-1 transition-colors"
                style={{
                  background: 'transparent',
                  border: `1px solid ${accent}`,
                  color: accent,
                  fontFamily: 'IBM Plex Mono, monospace',
                  letterSpacing: '0.1em',
                }}
              >
                ↑ UPGRADE
              </button>
            ) : (
              <Pill color={plan === 'team' ? '#a78bfa' : accent}>{plan.toUpperCase()}</Pill>
            )}
            {/* User avatar */}
            {user && (
              <button
                onClick={() => onExitToDashboard?.()}
                className="w-7 h-7 flex items-center justify-center text-[11px] font-semibold transition-colors"
                style={{ background: '#ebe9e1', border: '1px solid #d8d5cb', color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}
                title={user.email}
              >
                {user.email[0].toUpperCase()}
              </button>
            )}
          </div>
        </div>

        {/* BYOI active banner — only when team plan + BYOI loaded */}
        {byoi && (byoi.tfBackendBucket || byoi.registryUrl || byoi.ssoProvider !== 'none' || byoi.privateModelEndpoint) && (
          <div className="max-w-[1400px] mx-auto px-6 pb-2">
            <div className="flex items-center gap-3 px-3 py-1.5 text-[10px] flex-wrap"
              style={{ background: '#fbfaf7', border: `1px solid #a78bfa40`, fontFamily: 'IBM Plex Mono, monospace' }}>
              <span className="flex items-center gap-1.5" style={{ color: '#a78bfa' }}>
                <Lock size={10} /> BYOI ACTIVE
              </span>
              {byoi.tfBackendBucket && (
                <span style={{ color: '#6f6e68' }}>tf: <span style={{ color: '#52524d' }}>{byoi.tfBackend}</span> · <span style={{ color: '#52524d' }}>{byoi.tfBackendBucket}</span></span>
              )}
              {byoi.registryUrl && (
                <span style={{ color: '#6f6e68' }}>registry: <span style={{ color: '#52524d' }}>{byoi.registryUrl.split('/')[0]}</span></span>
              )}
              {byoi.ssoProvider && byoi.ssoProvider !== 'none' && (
                <span style={{ color: '#6f6e68' }}>sso: <span style={{ color: '#52524d' }}>{byoi.ssoProvider}</span></span>
              )}
              {byoi.privateModelEndpoint && (
                <span style={{ color: '#6f6e68' }}>llm: <span style={{ color: '#52524d' }}>private endpoint</span></span>
              )}
              <span className="ml-auto text-[9px]" style={{ color: '#9a9890' }}>
                applied to all generated files
              </span>
            </div>
          </div>
        )}

      </header>

      {/* ── Body: sidebar | main | manifest ──────────────────────── */}
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* On mobile, render a horizontal step rail above the content
            (vertical sidebar would push content below the fold). */}
        {viewportTier === 'mobile' && (
          <div className="mb-4 -mx-6 px-6">
            <div className="flex items-center gap-1 overflow-x-auto pb-2" style={{ scrollSnapType: 'x mandatory' }}>
              {STEPS.map((s, i) => {
                const done = i < stepIndex;
                const active = i === stepIndex;
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => goToStep(i)}
                    className="flex items-center gap-2 px-3 py-1.5 transition-all flex-shrink-0"
                    style={{
                      background: active ? '#ffffff' : 'transparent',
                      border: `1px solid ${active ? accent : 'transparent'}`,
                      color: active ? accent : done ? '#52524d' : '#9a9890',
                      fontFamily: 'IBM Plex Mono, monospace',
                      scrollSnapAlign: 'start',
                    }}
                  >
                    <span className="text-[10px] tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                    <Icon size={12} />
                    <span className="text-[11px] uppercase tracking-wider">{s.label}</span>
                    {done && <Check size={11} strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          className="grid gap-6"
          style={{
            gridTemplateColumns:
              viewportTier === 'mobile'
                ? '1fr'                                                       // mobile: single column, sidebar replaced by rail above
                : viewportTier === 'tablet'
                  ? (sidebarCollapsed ? '52px 1fr' : '224px 1fr')             // tablet: sidebar + main, manifest stacks below
                  : (sidebarCollapsed ? '52px 1fr 320px' : '224px 1fr 320px'),// desktop: full 3-col
          }}
        >
          {/* ── Step sidebar (hidden on mobile, replaced by rail above) ── */}
          {viewportTier !== 'mobile' && (
          <aside className="self-start sticky top-[88px]" style={{
            background: '#ffffff',
            border: '1px solid #ebe9e1',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-sm)',
            overflow: 'hidden',
          }}>
            {/* Sidebar header — toggle */}
            <div className="flex items-center justify-between border-b" style={{ borderColor: '#ebe9e1', padding: sidebarCollapsed ? '12px 8px' : '12px 14px' }}>
              {!sidebarCollapsed && (
                <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                  steps
                </span>
              )}
              <button
                onClick={() => setSidebarCollapsed(c => !c)}
                className="flex items-center justify-center w-6 h-6 transition-colors hover:bg-[#ebe9e1]"
                style={{ color: '#6f6e68' }}
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              </button>
            </div>
            {/* Step list */}
            <nav>
              {STEPS.map((s, i) => {
                const done = i < stepIndex;
                const active = i === stepIndex;
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => goToStep(i)}
                    title={sidebarCollapsed ? `${String(i + 1).padStart(2, '0')} · ${s.label}` : undefined}
                    className="w-full flex items-center gap-2 transition-all relative group"
                    style={{
                      background: active ? '#ffffff' : 'transparent',
                      borderLeft: `3px solid ${active ? accent : 'transparent'}`,
                      padding: sidebarCollapsed ? '10px 8px' : '10px 12px',
                      color: active ? accent : done ? '#52524d' : '#9a9890',
                      fontFamily: 'IBM Plex Mono, monospace',
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    }}
                  >
                    {!sidebarCollapsed && (
                      <span className="text-[10px] tabular-nums w-5 flex-shrink-0" style={{ color: active ? accent : '#9a9890' }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    )}
                    <Icon size={14} strokeWidth={active ? 2.5 : 2} className="flex-shrink-0" />
                    {!sidebarCollapsed && (
                      <span className="text-[11px] uppercase tracking-wider truncate flex-1 text-left">
                        {s.label}
                      </span>
                    )}
                    {!sidebarCollapsed && done && (
                      <Check size={11} strokeWidth={3} style={{ color: accent }} className="flex-shrink-0" />
                    )}
                    {/* Done dot when collapsed (since label is hidden) */}
                    {sidebarCollapsed && done && (
                      <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
                    )}
                  </button>
                );
              })}
            </nav>
            {/* Footer progress indicator */}
            <div className="border-t" style={{ borderColor: '#ebe9e1', padding: sidebarCollapsed ? '8px' : '10px 12px' }}>
              {sidebarCollapsed ? (
                <div className="text-[10px] tabular-nums text-center" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {stepIndex + 1}/{STEPS.length}
                </div>
              ) : (
                <>
                  <div className="text-[9px] uppercase tracking-[0.18em] mb-1.5" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                    progress
                  </div>
                  <div className="h-1 mb-1.5" style={{ background: '#ebe9e1' }}>
                    <div className="h-full transition-all" style={{
                      width: `${((stepIndex + 1) / STEPS.length) * 100}%`,
                      background: accentBg,
                    }} />
                  </div>
                  <div className="text-[10px] tabular-nums" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                    step {stepIndex + 1} of {STEPS.length}
                  </div>
                </>
              )}
            </div>
          </aside>
          )}

          {/* ── Main column ── */}
          <div className="min-w-0">
            {currentStep.id === 'project' && <ProjectStep config={config} update={updateConfig} />}
            {currentStep.id === 'cloud'   && <CloudStep   config={config} update={updateConfig} />}
            {currentStep.id === 'compute' && <ComputeStep config={config} update={updateConfig} />}
            {currentStep.id === 'data'    && <DataStep    config={config} update={updateConfig} />}
            {currentStep.id === 'data-eng' && <DataEngStep config={config} update={updateConfig} />}
            {currentStep.id === 'ai'      && <AIStep      config={config} update={updateConfig} />}
            {currentStep.id === 'security' && <SecurityStep config={config} update={updateConfig} planFeatures={planFeatures} onUpgrade={onUpgrade} />}
            {currentStep.id === 'network' && <NetworkStep config={config} update={updateConfig} />}
            {currentStep.id === 'obs'     && <ObsStep     config={config} update={updateConfig} />}
            {currentStep.id === 'cicd'    && <CicdStep    config={config} update={updateConfig} />}
            {currentStep.id === 'rollout' && <RolloutStep config={config} update={updateConfig} />}
            {currentStep.id === 'directory' && <DirectoryStep config={config} update={updateConfig} />}
            {currentStep.id === 'output'  && (
              <OutputStep
                config={config}
                files={files}
                fileTree={fileTree}
                activeFile={activeFile}
                setActiveFile={setActiveFile}
                view={view}
                setView={setView}
                copied={copied}
                onCopy={copyActive}
                onDownloadFile={downloadActive}
                onDownloadZip={downloadZip}
                onSaveToCloud={saveZipToCloud}
                cloudStatus={cloudStatus}
                cloudError={cloudError}
                cloudUsage={cloudUsage}
                lastCloudURL={lastCloudURL}
                isCloudEligible={isCloudEligible}
                cloudQuotaMB={planFeatures.cloudStorageQuotaMB}
                planName={planFeatures.name}
                onUpgrade={onUpgrade}
              />
            )}

            {/* Nav */}
            {currentStep.id !== 'output' && (
              <div className="flex items-center justify-between mt-10 pt-6 border-t" style={{ borderColor: '#ebe9e1' }}>
                <button
                  onClick={() => goToStep(stepIndex - 1)}
                  disabled={stepIndex === 0}
                  className="flex items-center gap-2 px-4 py-2.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ background: '#f4f3ee', border: '1px solid #d8d5cb', color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  <ChevronLeft size={14} /> <span className="text-[11px] uppercase tracking-wider">Back</span>
                </button>

                <div className="text-[10px] tabular-nums" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                  STEP {stepIndex + 1} / {STEPS.length}
                </div>

                <button
                  onClick={() => goToStep(stepIndex + 1)}
                  className="flex items-center gap-2 px-5 py-2.5 transition-all hover:brightness-110"
                  style={{ background: accentBg, border: `1px solid ${accent}`, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}
                >
                  <span className="text-[11px] uppercase tracking-wider">{stepIndex === STEPS.length - 2 ? 'Generate' : 'Continue'}</span>
                  <ChevronRight size={14} strokeWidth={3} />
                </button>
              </div>
            )}
          </div>

          {/* ── Stack manifest sidebar ── */}
          <aside className="lg:sticky lg:top-[120px] lg:self-start">
            <StackManifest config={config} monthlyCost={monthlyCost} />
          </aside>
        </div>
      </main>

      <footer className="max-w-[1400px] mx-auto px-6 py-8 mt-8 border-t" style={{ borderColor: '#ebe9e1' }}>
        <div className="flex flex-wrap items-center justify-between gap-4 text-[11px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
          <div className="flex items-center gap-4">
            <span>STAX · stack generator</span>
            <span style={{ color: '#d8d5cb' }}>│</span>
            <span>terraform 1.7+ · {config.provider}-provider 5.x</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: accentBg }} />
            <span>generator online</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// =============================================================
// STEP COMPONENTS
// =============================================================

function ProjectStep({ config, update }) {
  return (
    <div className="stream-in">
      <SectionLabel num={1}>Project basics</SectionLabel>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Field label="Project name" hint="Used as a prefix for every resource. Lowercase, hyphenated.">
          <input
            value={config.projectName}
            onChange={(e) => update('projectName', e.target.value)}
            className="w-full bg-transparent outline-none text-[14px] px-3 py-2.5"
            style={{ background: '#ffffff', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
          />
        </Field>
        <Field label="Owning team" hint="Tag attached to every resource for ownership.">
          <input
            value={config.owner}
            onChange={(e) => update('owner', e.target.value)}
            className="w-full bg-transparent outline-none text-[14px] px-3 py-2.5"
            style={{ background: '#ffffff', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
          />
        </Field>
      </div>

      <SectionLabel num={2}>Environment</SectionLabel>
      <p className="text-[12px] mb-4 max-w-2xl" style={{ color: '#6f6e68' }}>
        Production gets multi-AZ, encryption at rest, deletion protection, larger instance classes, longer backup retention,
        on-demand capacity. Dev is cost-optimized: spot, single-AZ, smaller footprints.
      </p>

      <div className="grid grid-cols-3 gap-3">
        {ENVIRONMENTS.map(env => (
          <button
            key={env}
            onClick={() => update('environment', env)}
            className="relative px-4 py-5 transition-all text-left"
            style={{
              background: config.environment === env ? '#f7fbe5' : '#ffffff',
              border: `1px solid ${config.environment === env ? accent : '#d8d5cb'}`,
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <CircleDot size={14} style={{ color: config.environment === env ? accent : '#9a9890' }} />
              <span className="text-[10px] tabular-nums" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                env_{ENVIRONMENTS.indexOf(env) + 1}
              </span>
            </div>
            <div className="text-[15px] font-semibold capitalize">{env}</div>
            <div className="text-[11px] mt-1" style={{ color: '#6f6e68' }}>
              {env === 'dev' ? 'Cost-optimized · spot · single-AZ' :
               env === 'staging' ? 'Prod-shape · scale-down · 2-AZ' :
               'Multi-AZ · encrypted · gated'}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CloudStep({ config, update }) {
  const provider = PROVIDERS.find(p => p.id === config.provider);
  return (
    <div className="stream-in">
      <SectionLabel num={1}>Cloud provider</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10">
        {PROVIDERS.map(p => (
          <button
            key={p.id}
            onClick={() => update('provider', p.id)}
            className="relative px-5 py-6 transition-all text-left"
            style={{
              background: config.provider === p.id ? '#f7fbe5' : '#ffffff',
              border: `1px solid ${config.provider === p.id ? accent : '#d8d5cb'}`,
            }}
          >
            {config.provider === p.id && <CornerMarks />}
            <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
              cloud · {p.id}
            </div>
            <div className="text-[18px] font-semibold mb-1">{p.short}</div>
            <div className="text-[11px]" style={{ color: '#6f6e68' }}>{p.name}</div>
          </button>
        ))}
      </div>

      <SectionLabel num={2}>Region</SectionLabel>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {provider.regions.map(r => (
          <button
            key={r}
            onClick={() => update('region', r)}
            className="px-3 py-2.5 text-[12px] transition-all"
            style={{
              background: config.region === r ? '#f7fbe5' : '#ffffff',
              border: `1px solid ${config.region === r ? accent : '#d8d5cb'}`,
              color: config.region === r ? accent : '#52524d',
              fontFamily: 'IBM Plex Mono, monospace',
            }}
          >
            {r}
          </button>
        ))}
      </div>
    </div>
  );
}

function ComputeStep({ config, update }) {
  const opts = COMPUTE_BY_PROVIDER[config.provider];
  return (
    <div className="stream-in">
      <SectionLabel num={1}>Compute runtime</SectionLabel>
      <p className="text-[12px] mb-6 max-w-2xl" style={{ color: '#6f6e68' }}>
        Pick the runtime workloads execute on. Each option generates a complete compute layer with autoscaling, IAM,
        observability hooks and a deploy target the CI pipeline can push to.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {opts.map(opt => (
          <SelectCard
            key={opt.id}
            option={opt}
            selected={config.compute === opt.id}
            onClick={() => update('compute', opt.id)}
          />
        ))}
      </div>
    </div>
  );
}

function DataStep({ config, update }) {
  const dbOpts = DATABASE_OPTIONS[config.provider];
  return (
    <div className="stream-in">
      <SectionLabel num={1}>Primary datastore</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
        {dbOpts.map(opt => (
          <SelectCard
            key={opt.id}
            option={opt}
            selected={config.database === opt.id}
            onClick={() => update('database', opt.id)}
          />
        ))}
      </div>

      <SectionLabel num={2}>Cache</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {CACHE_OPTIONS.map(opt => (
          <SelectCard
            key={opt.id}
            option={opt}
            selected={config.cache === opt.id}
            onClick={() => update('cache', opt.id)}
            compact
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================
// DATA ENGINEERING STEP
// =============================================================
// Stacks on top of the OLTP data step. Independent toggle so users can
// keep this off for simple CRUD apps. When on, generates streaming +
// OLAP + stream-processing + lake table + query engine + orchestration.
function DataEngStep({ config, update }) {
  const enabled = !!config.deEnabled;

  // Filter cloud-managed options that don't match the chosen provider
  const filterByProvider = (opts) => opts.filter(o => {
    if (o.tag !== 'managed') return true;
    if (o.id === 'kinesis' || o.id === 'redshift' || o.id === 'athena') return config.provider === 'aws';
    if (o.id === 'pubsub'  || o.id === 'bigquery') return config.provider === 'gcp';
    if (o.id === 'eventhubs' || o.id === 'synapse') return config.provider === 'azure';
    return true;
  });

  const cardGrid = (opts, key) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {filterByProvider(opts).map(opt => (
        <DETechCard
          key={opt.id}
          option={opt}
          selected={config[key] === opt.id}
          onClick={() => update(key, opt.id)}
        />
      ))}
    </div>
  );

  return (
    <div className="stream-in">
      {/* Master toggle */}
      <SectionLabel num={1}>Enable data engineering layer</SectionLabel>
      <p className="mb-4 text-[12px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
        ⓘ stacks on top of your OLTP datastore. opt in only when you have analytics, streaming, or batch workloads.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
        {[
          { v: true,  title: 'DE layer enabled',  sub: 'Streaming · OLAP · stream proc · wide-col · lake · query · orchestration' },
          { v: false, title: 'No DE layer',       sub: 'Skip — application stack only' },
        ].map(o => (
          <button
            key={String(o.v)}
            onClick={() => update('deEnabled', o.v)}
            className="text-left p-4 transition-all relative"
            style={{
              background: enabled === o.v ? '#f7fbe5' : '#ffffff',
              border: `1px solid ${enabled === o.v ? accent : '#d8d5cb'}`,
            }}
          >
            {enabled === o.v && <CornerMarks />}
            <div className="text-[13px] font-semibold mb-1" style={{ color: enabled === o.v ? '#65a30d' : '#1a1a17' }}>{o.title}</div>
            <div className="text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.sub}</div>
          </button>
        ))}
      </div>

      {!enabled ? (
        <div className="p-6" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
          <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>⏵ DE layer disabled</div>
          <p className="text-[12px]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            STAX skips Kafka, Pinot, Flink, Cassandra, Iceberg, Trino, and Airflow generation. Enable above to configure them.
          </p>
        </div>
      ) : (
        <>
          <SectionLabel num={2}>Streaming ingest</SectionLabel>
          <p className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ event log + pub/sub. landing zone for raw events from apps, CDC, IoT, clickstream.
          </p>
          <div className="mb-10">{cardGrid(DE_STREAMING, 'deStreaming')}</div>

          <SectionLabel num={3}>OLAP / serving</SectionLabel>
          <p className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ sub-second analytics on billions of rows. powers dashboards, in-product analytics, ad-hoc.
          </p>
          <div className="mb-10">{cardGrid(DE_OLAP, 'deOlap')}</div>

          <SectionLabel num={4}>Stream processing</SectionLabel>
          <p className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ enrich, join, aggregate, deduplicate streams. outputs to OLAP, lake, or operational stores.
          </p>
          <div className="mb-10">{cardGrid(DE_STREAM_PROC, 'deStreamProc')}</div>

          <SectionLabel num={5}>Wide-column / time-series</SectionLabel>
          <p className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ high-write-rate operational data — sensor data, metrics, large-fanout writes, sparse columns.
          </p>
          <div className="mb-10">{cardGrid(DE_WIDE_COL, 'deWideCol')}</div>

          <SectionLabel num={6}>Lake table format</SectionLabel>
          <p className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ ACID + schema evolution + time travel on object storage. the durable record beneath everything.
          </p>
          <div className="mb-10">{cardGrid(DE_LAKE_TABLE, 'deLakeTable')}</div>

          <SectionLabel num={7}>Federated query engine</SectionLabel>
          <p className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ join lake + OLAP + OLTP from one SQL. analyst-facing.
          </p>
          <div className="mb-10">{cardGrid(DE_QUERY_ENGINE, 'deQueryEngine')}</div>

          <SectionLabel num={8}>Workflow orchestration</SectionLabel>
          <p className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ scheduled batch jobs, backfills, data quality checks, freshness SLAs.
          </p>
          <div className="mb-10">{cardGrid(DE_ORCHESTRATION, 'deOrchestration')}</div>

          <SectionLabel num={9}>Data volume</SectionLabel>
          <p className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ sizes cluster footprint — broker count, replicas, instance classes.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: 'small',  title: 'Small',  sub: '<100 MB/s · ~10 TB/yr · single-AZ' },
              { id: 'medium', title: 'Medium', sub: '<1 GB/s · ~1 PB/yr · multi-AZ' },
              { id: 'large',  title: 'Large',  sub: '>1 GB/s · 10+ PB/yr · multi-region' },
            ].map(v => (
              <button key={v.id} onClick={() => update('deDataVolume', v.id)}
                className="text-left p-4 transition-all relative"
                style={{
                  background: config.deDataVolume === v.id ? '#f7fbe5' : '#ffffff',
                  border: `1px solid ${config.deDataVolume === v.id ? accent : '#d8d5cb'}`,
                }}>
                {config.deDataVolume === v.id && <CornerMarks />}
                <div className="text-[13px] font-semibold mb-1" style={{ color: config.deDataVolume === v.id ? '#65a30d' : '#1a1a17' }}>{v.title}</div>
                <div className="text-[10px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{v.sub}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Compact tech card with name + tag (OSS/managed) + managed-equivalent + desc
function DETechCard({ option, selected, onClick }) {
  const Icon = option.icon || Database;
  return (
    <button
      onClick={onClick}
      className="text-left p-4 transition-all relative"
      style={{
        background: selected ? '#f7fbe5' : '#ffffff',
        border: `1px solid ${selected ? accent : '#d8d5cb'}`,
      }}
    >
      {selected && <CornerMarks />}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 flex items-center justify-center flex-shrink-0"
          style={{ background: selected ? accentBg : '#ebe9e1', color: selected ? '#1a1a17' : '#52524d', border: `1px solid ${selected ? accent : '#d8d5cb'}` }}>
          <Icon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-[13px] font-semibold" style={{ color: selected ? '#65a30d' : '#1a1a17' }}>
              {option.name}
            </span>
            <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5"
              style={{ background: '#ffffff', color: option.tag === 'OSS' ? '#65a30d' : option.tag === 'managed' ? '#a78bfa' : '#9a9890', fontFamily: 'IBM Plex Mono, monospace', border: '1px solid #ebe9e1' }}>
              {option.tag}
            </span>
          </div>
          {option.managed && option.managed !== '—' && option.tag === 'OSS' && (
            <div className="text-[10px] mb-1" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
              managed: {option.managed}
            </div>
          )}
          <p className="text-[11px] leading-relaxed" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            {option.desc}
          </p>
        </div>
      </div>
    </button>
  );
}

function AIStep({ config, update }) {
  const inferenceOptions = AI_INFERENCE_OPTIONS[config.provider] || [];
  const gpuOptions = GPU_TYPES[config.provider] || [];
  const showGPU = ['vllm-eks','vllm-gke','vllm-aks'].includes(config.aiInference);
  const vectorOptions = VECTOR_DB_OPTIONS.filter(v => v.id !== 'opensearch' || config.provider === 'aws');

  return (
    <div className="stream-in">
      <SectionLabel num={1}>Enable AI / agent layer</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {[
          { v: true,  title: 'AI / agents enabled',  sub: 'Generate inference, retrieval, agent service, eval pipeline' },
          { v: false, title: 'No AI layer',          sub: 'Skip — generate plain stack only' },
        ].map(o => (
          <button
            key={String(o.v)}
            onClick={() => update('aiEnabled', o.v)}
            className="text-left p-4 transition-all relative"
            style={{
              background: config.aiEnabled === o.v ? '#f4f3ee' : 'transparent',
              border: `1px solid ${config.aiEnabled === o.v ? accent : '#d8d5cb'}`,
            }}
          >
            {config.aiEnabled === o.v && <CornerMarks />}
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={13} style={{ color: config.aiEnabled === o.v ? accent : '#9a9890' }} />
              <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace', color: config.aiEnabled === o.v ? accent : '#1a1a17' }}>
                {o.title}
              </span>
            </div>
            <p className="text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.sub}</p>
          </button>
        ))}
      </div>

      {config.aiEnabled && (
        <>
          <SectionLabel num={2}>Inference layer</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {inferenceOptions.map(o => (
              <SelectCard
                key={o.id}
                option={o}
                selected={config.aiInference === o.id}
                onClick={() => update('aiInference', o.id)}
              />
            ))}
          </div>

          {showGPU && (
            <div className="mb-8 p-4" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
              <Field label="GPU instance type" hint="Self-hosted vLLM requires GPU node group with NVIDIA device plugin">
                <select
                  value={config.gpuType}
                  onChange={(e) => update('gpuType', e.target.value)}
                  className="w-full px-3 py-2.5 text-[13px]"
                  style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {gpuOptions.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
            </div>
          )}

          <SectionLabel num={3}>Vector store · retrieval</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {vectorOptions.map(o => (
              <SelectCard
                key={o.id}
                option={o}
                selected={config.vectorDB === o.id}
                onClick={() => update('vectorDB', o.id)}
                compact
              />
            ))}
          </div>

          <SectionLabel num={4}>Agent framework</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {AGENT_FRAMEWORK_OPTIONS.map(o => (
              <SelectCard
                key={o.id}
                option={o}
                selected={config.agentFramework === o.id}
                onClick={() => update('agentFramework', o.id)}
                compact
              />
            ))}
          </div>

          <SectionLabel num={5}>LLM observability</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
            {LLM_OBSERVABILITY.map(o => (
              <button
                key={o.id}
                onClick={() => update('llmObservability', o.id)}
                className="text-left p-3 transition-all"
                style={{
                  background: config.llmObservability === o.id ? '#f4f3ee' : 'transparent',
                  border: `1px solid ${config.llmObservability === o.id ? accent : '#d8d5cb'}`,
                  color: config.llmObservability === o.id ? accent : '#52524d',
                  fontFamily: 'IBM Plex Mono, monospace',
                }}
              >
                <div className="text-[12px] font-medium mb-1">{o.name}</div>
                <div className="text-[10px]" style={{ color: '#6f6e68' }}>{o.desc}</div>
              </button>
            ))}
          </div>
          <p className="text-[10px] mt-3 mb-6" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            <span style={{ color: '#52524d' }}>i</span> generates eval workflow on PR · faithfulness · answer-relevancy · context-precision
          </p>
        </>
      )}
    </div>
  );
}

// =============================================================
// SECURITY STEP — AI/LLM/MCP guardrails + compliance + scanning
// =============================================================

function SecurityStep({ config, update, planFeatures, onUpgrade }) {
  const posture = useMemo(() => computeSecurityPosture(config), [config]);
  const aiOff = !isAIEnabled(config);
  // Default to free if not provided (e.g. preview/dev rendering)
  const features = planFeatures || PLAN_FEATURES.free;
  const canAdvanced = !!features.canUseAdvancedFrameworks;

  // Filter scan tools by category for layout
  const scansByCat = SCAN_TOOLS.reduce((acc, s) => {
    (acc[s.cat] = acc[s.cat] || []).push(s);
    return acc;
  }, {});
  const catLabels = {
    'container':  'Container CVE',
    'iac':        'IaC misconfig',
    'secrets':    'Secret scanning',
    'sbom':       'SBOM',
    'sast':       'SAST',
    'runtime':    'Runtime threat detection',
    'ai-redteam': 'AI red-team',
    'ai-eval':    'AI eval / regression',
    'ai-supply':  'AI supply chain',
  };
  const toggleScan = (id) => {
    const cur = config.scanTools || [];
    update('scanTools', cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };
  const toggleCompliance = (id) => {
    const cur = config.complianceFrameworks || [];
    update('complianceFrameworks', cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };
  const toggleAlert = (id) => {
    const cur = config.alertChannels || [];
    update('alertChannels', cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };

  // Filter prompt firewalls by provider compatibility
  const fwOptions = PROMPT_FIREWALLS.filter(o =>
    o.id !== 'bedrock-guardrails' || config.provider === 'aws'
  ).filter(o =>
    o.id !== 'azure-content-safety' || config.provider === 'azure'
  );
  const piiOptions = PII_DETECTORS.filter(o =>
    o.id !== 'comprehend' || config.provider === 'aws'
  ).filter(o =>
    o.id !== 'cloud-dlp' || config.provider === 'gcp'
  );

  return (
    <div className="stream-in">
      {/* Live posture banner */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
        <div className="p-5 text-center" style={{ background: '#ffffff', border: `1px solid ${posture.grade.color}40` }}>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            posture
          </div>
          <div className="text-[48px] font-semibold leading-none mt-2" style={{ color: posture.grade.color, fontFamily: 'IBM Plex Mono, monospace' }}>
            {posture.grade.letter}
          </div>
          <div className="text-[14px] tabular-nums mt-1" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            {posture.total}<span style={{ color: '#9a9890' }}> / {posture.max}</span>
          </div>
        </div>
        <div className="p-5" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
          <div className="text-[12px] mb-2" style={{ color: posture.grade.color, fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
            {posture.grade.label}
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {['ai','mcp','infra','scan','compliance'].map(layer => {
              const items = posture.breakdown.filter(c => c.layer === layer);
              const passing = items.filter(c => c.passing).length;
              const total = items.length;
              return (
                <div key={layer} className="px-2 py-1.5" style={{ background: '#fbfaf7', border: '1px solid #ebe9e1' }}>
                  <div className="text-[9px] uppercase tracking-[0.15em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>{layer}</div>
                  <div className="text-[14px] tabular-nums" style={{ color: passing === total ? accent : passing === 0 ? '#fb7185' : '#fbbf24', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {passing}<span style={{ color: '#9a9890' }}>/{total}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {aiOff && (
        <div className="mb-6 p-4 flex items-start gap-3" style={{ background: '#ffffff', border: '1px solid #fbbf2440' }}>
          <AlertCircle size={14} style={{ color: '#b45309', marginTop: 2 }} />
          <div className="text-[11px]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            AI / LLM-specific guardrails (sections 1–3) require the AI step to be enabled.
            Infrastructure scanning + compliance frameworks below still apply.
          </div>
        </div>
      )}

      <SectionLabel num={1}>AI / LLM guardrails</SectionLabel>
      <div className={aiOff ? 'opacity-40 pointer-events-none' : ''}>
        <div className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
          Layer 1 of defense — wraps every LLM call. PII out → firewall → topic check → LLM → output moderation → audit.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {fwOptions.map(o => (
            <SelectCard
              key={o.id}
              option={o}
              selected={config.promptFirewall === o.id}
              onClick={() => update('promptFirewall', o.id)}
            />
          ))}
        </div>
        <div className="mb-6">
          <Field label="PII detector" hint="Redacts names, emails, phones, card numbers, SSNs from inputs before they reach the LLM">
            <select
              value={config.piiDetector}
              onChange={(e) => update('piiDetector', e.target.value)}
              className="w-full px-3 py-2.5 text-[13px]"
              style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
            >
              {piiOptions.map(p => <option key={p.id} value={p.id}>{p.name} · {p.tag}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {[
            { k: 'outputModeration', label: 'Output moderation',  desc: 'Re-scan LLM outputs for PII / toxicity before sending to client.' },
            { k: 'deniedTopics',     label: 'Denied-topic filter', desc: 'Hard-reject inputs matching policy patterns (off-topic, harmful).' },
            { k: 'llmAuditLog',      label: 'LLM audit log',       desc: 'Append-only structured log per call. SOC 2 / GDPR / EU AI Act.' },
          ].map(o => {
            const on = !!config[o.k];
            return (
              <button
                key={o.k}
                onClick={() => update(o.k, !on)}
                className="text-left p-4 transition-all relative"
                style={{
                  background: on ? '#f4f3ee' : 'transparent',
                  border: `1px solid ${on ? accent : '#d8d5cb'}`,
                }}
              >
                {on && <CornerMarks />}
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck size={13} style={{ color: on ? accent : '#9a9890' }} />
                  <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace', color: on ? accent : '#1a1a17' }}>
                    {o.label}
                  </span>
                  <span className="ml-auto text-[10px]" style={{ color: on ? accent : '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {on ? 'on' : 'off'}
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <SectionLabel num={2}>MCP security</SectionLabel>
      <div className={!config.directoryMcpEnabled || aiOff ? 'opacity-40 pointer-events-none' : ''}>
        <div className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
          {config.directoryMcpEnabled ? 'Defends against tool poisoning, server impersonation, schema rug-pulls. Generates allowlist + signature config.' : 'MCP not enabled — turn it on in the Directory step to unlock these controls.'}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {[
            { k: 'mcpAllowlist',       label: 'Server allowlist',    desc: 'Default-deny. Only listed MCP servers can be invoked.' },
            { k: 'mcpSignatureVerify', label: 'Signature verification', desc: 'Verify server identity via public-key signatures.' },
            { k: 'mcpSchemaPinning',   label: 'Schema pinning',      desc: 'Pin tool definitions by SHA. Alert on drift (rug-pull defense).' },
          ].map(o => {
            const on = !!config[o.k];
            return (
              <button
                key={o.k}
                onClick={() => update(o.k, !on)}
                className="text-left p-4 transition-all relative"
                style={{
                  background: on ? '#f4f3ee' : 'transparent',
                  border: `1px solid ${on ? accent : '#d8d5cb'}`,
                }}
              >
                {on && <CornerMarks />}
                <div className="flex items-center gap-2 mb-1">
                  <Lock size={13} style={{ color: on ? accent : '#9a9890' }} />
                  <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace', color: on ? accent : '#1a1a17' }}>
                    {o.label}
                  </span>
                  <span className="ml-auto text-[10px]" style={{ color: on ? accent : '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {on ? 'on' : 'off'}
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <SectionLabel num={3}>Compliance frameworks</SectionLabel>
      <div className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
        Each selected framework generates a control-to-artifact matrix in <span style={{ color: accent }}>compliance/control-matrix.md</span>.
        {!canAdvanced && (
          <span className="ml-2" style={{ color: 'var(--warn)' }}>
            Advanced frameworks (HIPAA, PCI, EU AI Act, NIST AI RMF, ISO 42001, FedRAMP) require Pro.
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-8">
        {COMPLIANCE_FRAMEWORKS.map(fw => {
          const on = (config.complianceFrameworks || []).includes(fw.id);
          const locked = fw.tier === 'advanced' && !canAdvanced;
          return (
            <button
              key={fw.id}
              onClick={() => {
                if (locked) {
                  if (onUpgrade) onUpgrade();
                  return;
                }
                toggleCompliance(fw.id);
              }}
              className="text-left p-3 transition-all relative"
              style={{
                background: on ? '#f4f3ee' : 'transparent',
                border: `1px solid ${on ? accent : '#d8d5cb'}`,
                opacity: locked ? 0.55 : 1,
                cursor: locked ? 'pointer' : 'pointer',
              }}
              title={locked ? 'Upgrade to Pro to unlock advanced frameworks' : undefined}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-[12px] font-medium flex items-center gap-1.5" style={{ color: on ? accent : '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {fw.short}
                  {locked && <Lock size={10} style={{ color: 'var(--warn)' }} />}
                </span>
                <span className="text-[10px]" style={{ color: on ? accent : '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {locked ? 'pro+' : on ? '✓' : ''}
                </span>
              </div>
              <div className="text-[10px] leading-relaxed" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                {fw.desc}
              </div>
            </button>
          );
        })}
      </div>

      <SectionLabel num={4}>Continuous scans</SectionLabel>
      <div className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
        Every PR + nightly. AI red-team probes are run separately on agent/eval changes.
      </div>
      <div className="space-y-3 mb-8">
        {Object.entries(scansByCat).map(([cat, tools]) => (
          <div key={cat} className="p-4" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
            <div className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
              {catLabels[cat] || cat}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {tools.map(t => {
                const on = (config.scanTools || []).includes(t.id);
                return (
                  <button
                    key={t.id}
                    onClick={() => toggleScan(t.id)}
                    className="text-left p-2.5 transition-all"
                    style={{
                      background: on ? '#f4f3ee' : '#fbfaf7',
                      border: `1px solid ${on ? accent : '#ebe9e1'}`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[12px] font-medium" style={{ color: on ? accent : '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                        {t.name}
                      </span>
                      {on && <Check size={11} strokeWidth={3} style={{ color: accent }} />}
                    </div>
                    <div className="text-[10px] leading-relaxed" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {t.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <SectionLabel num={5}>Alert channels</SectionLabel>
      <div className="mb-3 text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
        CRITICAL alerts page on-call. Security-team channel for guardrail abuse, runtime threats. ML channel for eval regressions.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-8">
        {ALERT_CHANNELS.map(ch => {
          const on = (config.alertChannels || []).includes(ch.id);
          return (
            <button
              key={ch.id}
              onClick={() => toggleAlert(ch.id)}
              className="text-left p-3 transition-all"
              style={{
                background: on ? '#f4f3ee' : 'transparent',
                border: `1px solid ${on ? accent : '#d8d5cb'}`,
              }}
            >
              <div className="text-[12px] font-medium" style={{ color: on ? accent : '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                {ch.name}
              </div>
              <div className="text-[10px] mt-1" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                {ch.desc}
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
        <span style={{ color: '#52524d' }}>i</span> view the full security architecture in the Output step under "Security"
      </p>
    </div>
  );
}

function NetworkStep({ config, update }) {
  const Toggle = ({ k, label, desc, icon: Icon }) => (
    <button
      onClick={() => update(k, !config[k])}
      className="text-left px-4 py-4 transition-all w-full"
      style={{
        background: config[k] ? '#f7fbe5' : '#ffffff',
        border: `1px solid ${config[k] ? accent : '#d8d5cb'}`,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: 34, height: 34,
            background: config[k] ? accent : '#ebe9e1',
            color: config[k] ? '#fbfaf7' : '#52524d',
            border: `1px solid ${config[k] ? accent : '#d8d5cb'}`,
          }}
        >
          <Icon size={15} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium">{label}</span>
            <span
              className="ml-auto text-[10px] px-2 py-0.5 uppercase tracking-wider"
              style={{
                color: config[k] ? '#fbfaf7' : '#9a9890',
                background: config[k] ? accent : 'transparent',
                border: `1px solid ${config[k] ? accent : '#d8d5cb'}`,
                fontFamily: 'IBM Plex Mono, monospace',
              }}
            >
              {config[k] ? 'on' : 'off'}
            </span>
          </div>
          <p className="text-[11px] mt-1" style={{ color: '#6f6e68' }}>{desc}</p>
        </div>
      </div>
    </button>
  );

  return (
    <div className="stream-in">
      <SectionLabel num={1}>Networking</SectionLabel>
      <p className="text-[12px] mb-6 max-w-2xl" style={{ color: '#6f6e68' }}>
        VPC topology is generated automatically with public/private subnets across the AZ count appropriate for
        your environment, plus NAT, flow logs and least-privilege security groups. Configure edge services below.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
        <Toggle k="cdn"            icon={Globe}       label="CDN"               desc={config.provider === 'aws' ? 'CloudFront distribution with OAC and managed cache policies.' : 'Provider-native CDN with HTTPS termination at the edge.'} />
        <Toggle k="waf"            icon={Shield}      label="Web Application Firewall" desc="OWASP managed rule set, rate limiting, geo blocking." />
        <Toggle k="secretsManager" icon={KeyRound}    label="Secrets Manager"   desc="KMS-encrypted secrets with rotation Lambdas wired in." />
      </div>

      <SectionLabel num={2}>Topology preview</SectionLabel>
      <NetworkTopologySVG config={config} />
    </div>
  );
}

function ObsStep({ config, update }) {
  return (
    <div className="stream-in">
      <SectionLabel num={1}>Observability</SectionLabel>
      <p className="text-[12px] mb-6 max-w-2xl" style={{ color: '#6f6e68' }}>
        Logs, metrics, traces. The selected stack will be wired into your compute layer
        — agent DaemonSets for K8s, sidecars for ECS, layers for Lambda — and the admin dashboard generated
        in the final step links to it directly.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {OBSERVABILITY_OPTIONS.map(opt => (
          <SelectCard
            key={opt.id}
            option={opt}
            selected={config.observability === opt.id}
            onClick={() => update('observability', opt.id)}
          />
        ))}
      </div>
    </div>
  );
}

function CicdStep({ config, update }) {
  return (
    <div className="stream-in">
      <SectionLabel num={1}>CI/CD platform</SectionLabel>
      <p className="text-[12px] mb-6 max-w-2xl" style={{ color: '#6f6e68' }}>
        All generated pipelines use OIDC federation — no static cloud credentials in CI. Production deploys are gated
        on environment protection rules with required reviewers.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {CICD_OPTIONS.map(opt => (
          <SelectCard
            key={opt.id}
            option={opt}
            selected={config.cicd === opt.id}
            onClick={() => update('cicd', opt.id)}
          />
        ))}
      </div>

      <div className="mt-10">
        <SectionLabel num={2}>Pipeline shape</SectionLabel>
        <PipelineDiagram config={config} />
      </div>
    </div>
  );
}

// =============================================================
// ROLLOUT STEP — issue tracker config + team mapping
// =============================================================

function RolloutStep({ config, update }) {
  const plan = useMemo(() => buildRolloutPlan(config), [config]);
  const usedTeams = ROLLOUT_TEAMS.filter(t => plan.tickets.some(tk => tk.team === t.id));

  return (
    <div className="stream-in">
      <SectionLabel num={1}>Generate rollout tickets</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {[
          { v: true,  title: 'Generate ticket plan',  sub: 'Build dependency-ordered tickets per team for issue tracker import' },
          { v: false, title: 'Skip rollout',          sub: 'No tickets — just generate code' },
        ].map(o => (
          <button
            key={String(o.v)}
            onClick={() => update('rolloutEnabled', o.v)}
            className="text-left p-4 transition-all relative"
            style={{
              background: config.rolloutEnabled === o.v ? '#f4f3ee' : 'transparent',
              border: `1px solid ${config.rolloutEnabled === o.v ? accent : '#d8d5cb'}`,
            }}
          >
            {config.rolloutEnabled === o.v && <CornerMarks />}
            <div className="flex items-center gap-2 mb-1">
              <GitBranch size={13} style={{ color: config.rolloutEnabled === o.v ? accent : '#9a9890' }} />
              <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace', color: config.rolloutEnabled === o.v ? accent : '#1a1a17' }}>
                {o.title}
              </span>
            </div>
            <p className="text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.sub}</p>
          </button>
        ))}
      </div>

      {config.rolloutEnabled && (
        <>
          <SectionLabel num={2}>Issue tracker</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {ISSUE_TRACKERS.map(o => (
              <button
                key={o.id}
                onClick={() => update('issueTracker', o.id)}
                className="text-left p-4 transition-all relative"
                style={{
                  background: config.issueTracker === o.id ? '#f4f3ee' : 'transparent',
                  border: `1px solid ${config.issueTracker === o.id ? accent : '#d8d5cb'}`,
                }}
              >
                {config.issueTracker === o.id && <CornerMarks />}
                <div className="text-[13px] font-medium mb-1" style={{ color: config.issueTracker === o.id ? accent : '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {o.name}
                </div>
                <div className="text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.desc}</div>
              </button>
            ))}
          </div>

          <SectionLabel num={3}>Project key</SectionLabel>
          <div className="mb-8 p-4" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
            <Field label="Ticket prefix" hint="Will produce keys like PHX-1, PHX-2, … (Jira project key or Linear team prefix)">
              <input
                type="text"
                value={config.ticketPrefix}
                onChange={(e) => update('ticketPrefix', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                maxLength={10}
                className="w-full px-3 py-2.5 text-[13px]"
                style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
              />
            </Field>
          </div>

          <SectionLabel num={4}>Team assignments</SectionLabel>
          <div className="space-y-2 mb-8">
            {usedTeams.map(team => {
              const Icon = team.icon;
              const ticketCount = plan.tickets.filter(t => t.team === team.id).length;
              const totalEffort = plan.tickets.filter(t => t.team === team.id).reduce((s, t) => s + t.effort, 0);
              return (
                <div
                  key={team.id}
                  className="grid grid-cols-[180px_1fr_140px] gap-3 items-center px-4 py-3"
                  style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-1 h-8" style={{ background: team.color }} />
                    <Icon size={14} style={{ color: team.color }} />
                    <span className="text-[12px] truncate" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#1a1a17' }}>
                      {team.name}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={config.teamAssignments?.[team.id] || ''}
                    onChange={(e) => update('teamAssignments', { ...config.teamAssignments, [team.id]: e.target.value })}
                    placeholder="email or username"
                    className="px-3 py-1.5 text-[12px]"
                    style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
                  />
                  <div className="text-right text-[10px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                    <span style={{ color: team.color }}>{ticketCount}</span> tickets · <span style={{ color: team.color }}>{totalEffort}</span> pts
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-3 gap-3 mb-2">
            <div className="px-4 py-3" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>tickets</div>
              <div className="text-[20px] font-semibold tabular-nums mt-1" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                {plan.tickets.length}
              </div>
            </div>
            <div className="px-4 py-3" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>waves</div>
              <div className="text-[20px] font-semibold tabular-nums mt-1" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                {plan.waves.length}
              </div>
            </div>
            <div className="px-4 py-3" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
              <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>total effort</div>
              <div className="text-[20px] font-semibold tabular-nums mt-1" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                {plan.tickets.reduce((s, t) => s + t.effort, 0)} <span className="text-[11px]" style={{ color: '#6f6e68' }}>pts</span>
              </div>
            </div>
          </div>
          <p className="text-[10px] mt-3" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            <span style={{ color: '#52524d' }}>i</span> see the full DAG visualization in the Output step under "Rollout"
          </p>
        </>
      )}
    </div>
  );
}

// =============================================================
// DIRECTORY STEP — agentic.ai integration config
// =============================================================

function DirectoryStep({ config, update }) {
  const score = useMemo(() => computeAgenticness(config), [config]);
  const aiOff = !isAIEnabled(config);

  if (aiOff) {
    return (
      <div className="stream-in">
        <SectionLabel num={1}>Directory integration</SectionLabel>
        <div className="p-6" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
          <div className="flex items-start gap-3">
            <AlertCircle size={16} style={{ color: '#b45309', marginTop: 2 }} />
            <div className="text-[12px]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
              Directory integration requires the AI / agent layer to be enabled.
              Go back to the <span style={{ color: accent }}>AI / Agents</span> step and turn it on first.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stream-in">
      <SectionLabel num={1}>Generate directory listing</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {[
          { v: true,  title: 'Enable directory integration', sub: 'Generate agent.json manifest, submission payload, MCP stub' },
          { v: false, title: 'Skip directory',               sub: 'No public manifest, no submission' },
        ].map(o => (
          <button
            key={String(o.v)}
            onClick={() => update('directoryEnabled', o.v)}
            className="text-left p-4 transition-all relative"
            style={{
              background: config.directoryEnabled === o.v ? '#f4f3ee' : 'transparent',
              border: `1px solid ${config.directoryEnabled === o.v ? accent : '#d8d5cb'}`,
            }}
          >
            {config.directoryEnabled === o.v && <CornerMarks />}
            <div className="flex items-center gap-2 mb-1">
              <Globe size={13} style={{ color: config.directoryEnabled === o.v ? accent : '#9a9890' }} />
              <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace', color: config.directoryEnabled === o.v ? accent : '#1a1a17' }}>
                {o.title}
              </span>
            </div>
            <p className="text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.sub}</p>
          </button>
        ))}
      </div>

      {config.directoryEnabled && (
        <>
          {/* Live agenticness preview */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-3">
            <div className="p-5" style={{ background: '#ffffff', border: `1px solid ${accent}40` }}>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                predicted agenticness
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-[36px] font-semibold tabular-nums" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                  {score.total}
                </span>
                <span className="text-[14px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>/ 32</span>
              </div>
              <div className="mt-3 px-3 py-2" style={{ background: '#fbfaf7', border: '1px solid #d8d5cb' }}>
                <div className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>level</div>
                <div className="text-[14px] font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                  {score.level.level} · {score.level.name}
                </div>
              </div>
              <div className="text-[10px] mt-2 leading-relaxed" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                {score.level.desc}
              </div>
            </div>
            <div className="p-5" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
              <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                dimension breakdown
              </div>
              <div className="space-y-1.5">
                {AGENTIC_DIMENSIONS.map(d => {
                  const v = score.dimensions[d.id] || 0;
                  return (
                    <div key={d.id} className="grid grid-cols-[100px_1fr_24px] gap-3 items-center text-[11px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      <span style={{ color: '#52524d' }}>{d.name.toLowerCase()}</span>
                      <div className="flex gap-1 h-2">
                        {[0,1,2,3].map(i => (
                          <div key={i} className="flex-1" style={{ background: i < v ? accent : '#ebe9e1' }} />
                        ))}
                      </div>
                      <span className="text-right tabular-nums" style={{ color: v > 0 ? accent : '#9a9890' }}>{v}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <SectionLabel num={2}>Category</SectionLabel>
          <div className="mb-8 p-4" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
            <Field label="agentic.ai category" hint="Determines where your agent appears in the directory taxonomy">
              <select
                value={config.directoryCategory}
                onChange={(e) => update('directoryCategory', e.target.value)}
                className="w-full px-3 py-2.5 text-[13px]"
                style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
              >
                {AGENTIC_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>

          <SectionLabel num={3}>Autonomy level</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            {AUTONOMY_LEVELS.map(o => (
              <button
                key={o.id}
                onClick={() => update('directoryAutonomy', o.id)}
                className="text-left p-4 transition-all relative"
                style={{
                  background: config.directoryAutonomy === o.id ? '#f4f3ee' : 'transparent',
                  border: `1px solid ${config.directoryAutonomy === o.id ? accent : '#d8d5cb'}`,
                }}
              >
                {config.directoryAutonomy === o.id && <CornerMarks />}
                <div className="text-[12px] font-medium mb-1" style={{ color: config.directoryAutonomy === o.id ? accent : '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {o.name}
                </div>
                <div className="text-[10px] mb-2" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>{o.tag}</div>
                <div className="text-[11px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.desc}</div>
              </button>
            ))}
          </div>

          <SectionLabel num={4}>Domain reach</SectionLabel>
          <div className="mb-8 p-5" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {[
                { v: 1, label: 'Narrow', sub: 'single task' },
                { v: 2, label: 'Specialised', sub: 'one domain' },
                { v: 3, label: 'Multi-skill', sub: 'within domain' },
                { v: 4, label: 'Generalist', sub: 'cross-domain' },
              ].map(o => (
                <button
                  key={o.v}
                  onClick={() => update('directoryReach', o.v)}
                  className="text-left p-3 transition-all"
                  style={{
                    background: config.directoryReach === o.v ? '#f4f3ee' : '#fbfaf7',
                    border: `1px solid ${config.directoryReach === o.v ? accent : '#ebe9e1'}`,
                  }}
                >
                  <div className="text-[12px] font-medium" style={{ color: config.directoryReach === o.v ? accent : '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {o.label}
                  </div>
                  <div className="text-[10px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.sub}</div>
                </button>
              ))}
            </div>
          </div>

          <SectionLabel num={5}>Capability building blocks</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {[
              { k: 'directoryMcpEnabled',    label: 'MCP server',         desc: 'Expose agent tools via Model Context Protocol — discoverable by Claude Desktop, Cursor, agentic.ai.', icon: Boxes },
              { k: 'directoryWebTool',       label: 'Web browsing tool',  desc: 'Add a web-fetch / browser tool. Bumps Tool use dimension; requires WAF allowlist for outbound.',     icon: Globe },
              { k: 'directoryCodeExecution', label: 'Code execution',     desc: 'Sandboxed Python/shell exec tool. High agenticness boost; requires sandboxed runtime.',              icon: Terminal },
              { k: 'directoryOpenSource',    label: 'Open source listing', desc: 'Mark this agent as open-source in the directory. Affects pricing tag + discoverability.',           icon: GitBranch },
            ].map(o => {
              const Icon = o.icon;
              const on = !!config[o.k];
              return (
                <button
                  key={o.k}
                  onClick={() => update(o.k, !on)}
                  className="text-left p-4 transition-all relative"
                  style={{
                    background: on ? '#f4f3ee' : 'transparent',
                    border: `1px solid ${on ? accent : '#d8d5cb'}`,
                  }}
                >
                  {on && <CornerMarks />}
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={13} style={{ color: on ? accent : '#9a9890' }} />
                    <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace', color: on ? accent : '#1a1a17' }}>
                      {o.label}
                    </span>
                    <span className="ml-auto text-[10px]" style={{ color: on ? accent : '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {on ? 'on' : 'off'}
                    </span>
                  </div>
                  <p className="text-[11px] leading-relaxed" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{o.desc}</p>
                </button>
              );
            })}
          </div>

          <SectionLabel num={6}>Listing copy</SectionLabel>
          <div className="grid grid-cols-1 gap-3 mb-8 p-4" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
            <Field label="Tagline (one line)" hint="Shown in directory cards">
              <input
                type="text"
                value={config.directoryTagline}
                onChange={(e) => update('directoryTagline', e.target.value)}
                placeholder={`${agentFwName(config)} + ${vectorDBName(config)} retrieval agent`}
                maxLength={120}
                className="w-full px-3 py-2.5 text-[13px]"
                style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
              />
            </Field>
            <Field label="Description" hint="Full listing description">
              <textarea
                value={config.directoryDescription}
                onChange={(e) => update('directoryDescription', e.target.value)}
                placeholder={`Internal agent built with STAX. Inference via ${aiInferenceName(config)}, retrieval-augmented with ${vectorDBName(config)}.`}
                rows={3}
                className="w-full px-3 py-2.5 text-[12px] resize-y"
                style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Homepage URL" hint="Where the manifest is served">
                <input
                  type="text"
                  value={config.directoryHomepage}
                  onChange={(e) => update('directoryHomepage', e.target.value)}
                  placeholder={`https://${fmt(config.projectName)}.example.com`}
                  className="w-full px-3 py-2.5 text-[12px]"
                  style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
                />
              </Field>
              <Field label="Pricing model">
                <select
                  value={config.directoryPricing}
                  onChange={(e) => update('directoryPricing', e.target.value)}
                  className="w-full px-3 py-2.5 text-[12px]"
                  style={{ background: '#fbfaf7', border: '1px solid #d8d5cb', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {PRICING_MODELS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <p className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            <span style={{ color: '#52524d' }}>i</span> see the full agenticness radar in the Output step under "Agenticness"
          </p>
        </>
      )}
    </div>
  );
}

// =============================================================
// OUTPUT STEP — file viewer / arch / admin UI
// =============================================================

function OutputStep({
  config, files, fileTree, activeFile, setActiveFile, view, setView,
  copied, onCopy, onDownloadFile, onDownloadZip,
  onSaveToCloud, cloudStatus, cloudError, cloudUsage, lastCloudURL,
  isCloudEligible, cloudQuotaMB, planName, onUpgrade,
}) {
  const usagePct = cloudQuotaMB ? Math.min(100, (cloudUsage.bytes / (cloudQuotaMB * 1024 * 1024)) * 100) : 0;
  return (
    <div className="stream-in">
      <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
        <div>
          <SectionLabel num={1}>Generated stack</SectionLabel>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {[
            { id: 'files',       label: 'Code', icon: FileCode2 },
            { id: 'security',    label: 'Security', icon: ShieldCheck },
            { id: 'arch',        label: 'Architecture', icon: MapIcon },
            { id: 'admin',       label: 'Ops dashboard', icon: GaugeCircle },
            ...(config.rolloutEnabled ? [{ id: 'rollout', label: 'Rollout DAG', icon: GitBranch }] : []),
            ...(config.directoryEnabled && isAIEnabled(config) ? [{ id: 'agenticness', label: 'Agenticness', icon: Layers }] : []),
          ].map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setView(t.id)}
                className="flex items-center gap-2 px-3 py-1.5 transition-all"
                style={{
                  background: view === t.id ? '#f4f3ee' : 'transparent',
                  border: `1px solid ${view === t.id ? accent : '#d8d5cb'}`,
                  color: view === t.id ? accent : '#52524d',
                  fontFamily: 'IBM Plex Mono, monospace',
                }}
              >
                <Icon size={12} />
                <span className="text-[11px] uppercase tracking-wider">{t.label}</span>
              </button>
            );
          })}
          <div className="w-2" />

          {/* Cloud save — only for eligible (paid + Google) users */}
          {isCloudEligible ? (
            <button
              onClick={onSaveToCloud}
              disabled={cloudStatus === 'uploading'}
              className="flex items-center gap-2 px-3 py-1.5 transition-all hover:brightness-95 disabled:opacity-60"
              style={{ background: 'transparent', border: `1px solid ${accent}`, color: accent, fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}
              title={`Save build to your cloud folder · ${(cloudUsage.bytes / (1024*1024)).toFixed(1)} / ${cloudQuotaMB} MB used`}
            >
              <Cloud size={12} strokeWidth={2.5} />
              <span className="text-[11px] uppercase tracking-wider">
                {cloudStatus === 'uploading' ? 'saving…' :
                 cloudStatus === 'saved'     ? 'saved ✓'  :
                 cloudStatus === 'error'     ? 'retry'    : 'save to cloud'}
              </span>
            </button>
          ) : (
            <button
              onClick={() => onUpgrade && onUpgrade()}
              className="flex items-center gap-2 px-3 py-1.5 transition-all hover:brightness-95"
              style={{ background: 'transparent', border: '1px solid #d8d5cb', color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}
              title="Cloud storage is a paid feature (100 MB included on Pro & Team)"
            >
              <Lock size={12} />
              <span className="text-[11px] uppercase tracking-wider">cloud · pro+</span>
            </button>
          )}

          <button
            onClick={onDownloadZip}
            className="flex items-center gap-2 px-3 py-1.5 transition-all hover:brightness-110"
            style={{ background: accentBg, border: `1px solid ${accent}`, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}
            title="Download all generated files as a ZIP archive"
          >
            <Download size={12} strokeWidth={2.5} />
            <span className="text-[11px] uppercase tracking-wider">Download .zip</span>
          </button>
        </div>
      </div>

      {/* Cloud status row — quota meter + error / success line */}
      {isCloudEligible && (
        <div className="mb-6 px-4 py-2.5" style={{ background: '#ffffff', border: '1px solid #ebe9e1' }}>
          <div className="flex items-center gap-3 flex-wrap text-[11px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            <span className="flex items-center gap-1.5" style={{ color: accent }}>
              <Cloud size={11} /> CLOUD STORAGE
            </span>
            <span style={{ color: '#6f6e68' }}>
              {(cloudUsage.bytes / (1024*1024)).toFixed(1)} / {cloudQuotaMB} MB used · {planName} plan
            </span>
            <div className="flex-1 min-w-[120px] h-1.5" style={{ background: '#ebe9e1' }}>
              <div className="h-full transition-all" style={{
                width: `${usagePct}%`,
                background: usagePct > 90 ? '#dc2626' : usagePct > 70 ? '#d97706' : accentBg,
              }} />
            </div>
            {cloudStatus === 'saved' && lastCloudURL && (
              <a href={lastCloudURL} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 hover:underline" style={{ color: accent }}>
                <Download size={10} /> re-download
              </a>
            )}
          </div>
          {cloudStatus === 'error' && cloudError && (
            <div className="mt-2 text-[11px]" style={{ color: '#be123c', fontFamily: 'IBM Plex Mono, monospace' }}>
              ⚠ {cloudError}
            </div>
          )}
        </div>
      )}

      {view === 'files' && (
        <FileExplorer
          files={files}
          fileTree={fileTree}
          activeFile={activeFile}
          setActiveFile={setActiveFile}
          copied={copied}
          onCopy={onCopy}
          onDownloadFile={onDownloadFile}
        />
      )}
      {view === 'arch'        && <ArchitectureView config={config} />}
      {view === 'admin'       && <AdminDashboard config={config} />}
      {view === 'rollout'     && <RolloutPlanView config={config} />}
      {view === 'agenticness' && <AgenticnessView config={config} />}
      {view === 'security'    && <SecurityPostureView config={config} />}
    </div>
  );
}

function FileExplorer({ files, fileTree, activeFile, setActiveFile, copied, onCopy, onDownloadFile }) {
  const renderTree = (node, prefix = '', depth = 0) => {
    return Object.entries(node).map(([key, val]) => {
      const isFile = typeof val === 'string';
      const path = isFile ? val : prefix + key + '/';
      return (
        <div key={path}>
          {isFile ? (
            <button
              onClick={() => setActiveFile(val)}
              className="w-full flex items-center gap-2 px-2 py-1 text-[12px] transition-colors text-left"
              style={{
                color: activeFile === val ? accent : '#52524d',
                background: activeFile === val ? '#f4f3ee' : 'transparent',
                fontFamily: 'IBM Plex Mono, monospace',
                paddingLeft: 8 + depth * 14,
              }}
            >
              <File size={11} style={{ color: activeFile === val ? accent : '#9a9890' }} />
              <span className="truncate">{key}</span>
            </button>
          ) : (
            <>
              <div
                className="flex items-center gap-2 px-2 py-1 text-[11px] uppercase tracking-wider"
                style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace', paddingLeft: 8 + depth * 14 }}
              >
                <Folder size={11} />
                <span>{key}</span>
              </div>
              {renderTree(val, prefix + key + '/', depth + 1)}
            </>
          )}
        </div>
      );
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-0 border" style={{ borderColor: '#d8d5cb' }}>
      {/* Tree */}
      <div className="md:border-r py-3" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
        <div className="text-[10px] uppercase tracking-[0.2em] px-3 mb-2" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
          <FolderTree size={11} className="inline mr-1" /> files · {Object.keys(files).length}
        </div>
        {renderTree(fileTree)}
      </div>

      {/* Code */}
      <div className="min-w-0">
        <div
          className="flex items-center justify-between px-4 py-2 border-b"
          style={{ borderColor: '#d8d5cb', background: '#ffffff' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: accentBg }} />
            <span className="text-[11px] truncate" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
              {activeFile}
            </span>
            <span className="text-[10px] ml-2" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
              {(files[activeFile] || '').split('\n').length} lines · {Math.ceil((files[activeFile] || '').length / 1024)} kb
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={onDownloadFile}
              className="flex items-center gap-1.5 px-2.5 py-1 transition-all"
              style={{ background: '#f4f3ee', border: '1px solid #d8d5cb', color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}
              title="Download this file"
            >
              <Download size={11} />
              <span className="text-[10px] uppercase tracking-wider">file</span>
            </button>
            <button
              onClick={onCopy}
              className="flex items-center gap-1.5 px-2.5 py-1 transition-all"
              style={{ background: '#f4f3ee', border: '1px solid #d8d5cb', color: copied ? accent : '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}
            >
              {copied ? <Check size={11} strokeWidth={3} /> : <Copy size={11} />}
              <span className="text-[10px] uppercase tracking-wider">{copied ? 'copied' : 'copy'}</span>
            </button>
          </div>
        </div>
        <pre
          className="p-4 overflow-auto text-[12px] leading-[1.65] max-h-[640px]"
          style={{ background: '#fbfaf7', color: '#d8d5cb', fontFamily: 'IBM Plex Mono, monospace' }}
        >
          <CodeBlock content={files[activeFile] || ''} filename={activeFile} />
        </pre>
      </div>
    </div>
  );
}

// minimal syntax highlighter
function CodeBlock({ content, filename }) {
  const lines = content.split('\n');
  const lang =
    filename.endsWith('.tf') ? 'hcl' :
    filename.endsWith('.yml') || filename.endsWith('.yaml') ? 'yaml' :
    filename.endsWith('.md') ? 'md' :
    filename === 'Dockerfile' ? 'docker' :
    'txt';

  const colorize = (line) => {
    if (!line.trim()) return line;

    // comments
    if (lang === 'hcl' && /^\s*#/.test(line))      return <span style={{ color: '#9a9890', fontStyle: 'italic' }}>{line}</span>;
    if (lang === 'yaml' && /^\s*#/.test(line))     return <span style={{ color: '#9a9890', fontStyle: 'italic' }}>{line}</span>;
    if (lang === 'docker' && /^\s*#/.test(line))   return <span style={{ color: '#9a9890', fontStyle: 'italic' }}>{line}</span>;
    if (lang === 'md' && /^#+\s/.test(line))       return <span style={{ color: accent, fontWeight: 600 }}>{line}</span>;

    // tokens
    const parts = [];
    let rest = line;

    if (lang === 'hcl') {
      const m = line.match(/^(\s*)(resource|data|module|variable|output|provider|terraform|locals|backend)(\s+)/);
      if (m) {
        parts.push(m[1]);
        parts.push(<span key="kw" style={{ color: '#be123c' }}>{m[2]}</span>);
        parts.push(m[3]);
        rest = line.slice(m[0].length);
      }
    }
    if (lang === 'yaml') {
      const m = line.match(/^(\s*-?\s*)([A-Za-z_][\w-]*)(:)/);
      if (m) {
        parts.push(m[1]);
        parts.push(<span key="k" style={{ color: '#60a5fa' }}>{m[2]}</span>);
        parts.push(<span key="c" style={{ color: '#6f6e68' }}>{m[3]}</span>);
        rest = line.slice(m[0].length);
      }
    }
    if (lang === 'docker') {
      const m = line.match(/^(FROM|RUN|COPY|WORKDIR|EXPOSE|CMD|ENV|USER|ENTRYPOINT|ARG|LABEL)\s/);
      if (m) {
        parts.push(<span key="kw" style={{ color: '#be123c' }}>{m[1]}</span>);
        rest = line.slice(m[1].length);
      }
    }

    // strings + numbers in remainder
    const tokenized = rest.split(/("(?:[^"\\]|\\.)*")/g).flatMap((seg, i) => {
      if (i % 2 === 1) return <span key={'s' + i} style={{ color: accent }}>{seg}</span>;
      return seg.split(/(\b\d+(?:\.\d+)?\b)/g).map((s, j) =>
        /^\d/.test(s) ? <span key={i + 'n' + j} style={{ color: '#b45309' }}>{s}</span> : s
      );
    });

    return <>{parts}{tokenized}</>;
  };

  return (
    <code>
      {lines.map((line, i) => (
        <div key={i} className="flex">
          <span
            className="flex-shrink-0 text-right pr-4 select-none"
            style={{ color: '#9a9890', minWidth: 36 }}
          >
            {i + 1}
          </span>
          <span className="flex-1 whitespace-pre">{colorize(line) || ' '}</span>
        </div>
      ))}
    </code>
  );
}

// =============================================================
// SECURITY POSTURE VIEW — defense-in-depth + control coverage
// =============================================================

function SecurityPostureView({ config }) {
  const posture = useMemo(() => computeSecurityPosture(config), [config]);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);
  const layers = ['ai', 'mcp', 'infra', 'scan', 'compliance'];
  const layerLabel = {
    ai:         'AI / LLM defense',
    mcp:        'MCP integrity',
    infra:      'Infra hardening',
    scan:       'Continuous scans',
    compliance: 'Compliance + alerting',
  };
  const layerColor = {
    ai:         '#f0abfc',
    mcp:        '#a78bfa',
    infra:      '#60a5fa',
    scan:       '#86efac',
    compliance: '#fbbf24',
  };

  const frameworks = (config.complianceFrameworks || [])
    .map(f => COMPLIANCE_FRAMEWORKS.find(x => x.id === f))
    .filter(Boolean);
  const scans = (config.scanTools || [])
    .map(s => SCAN_TOOLS.find(x => x.id === s))
    .filter(Boolean);

  return (
    <div className="border" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b flex-wrap gap-3" style={{ borderColor: '#d8d5cb' }}>
        <div className="flex items-center gap-3">
          <ShieldCheck size={14} style={{ color: posture.grade.color }} />
          <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            security posture
          </span>
          <span className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            defense-in-depth · <AnimatedCounter to={posture.total} />/{posture.max} pts
          </span>
        </div>
        <div className="flex items-baseline gap-3">
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>grade</span>
          <span className="text-[24px] font-semibold leading-none" style={{ color: posture.grade.color, fontFamily: 'IBM Plex Mono, monospace' }}>
            {posture.grade.letter}
          </span>
        </div>
      </div>

      {/* Defense in depth diagram */}
      <div className="p-6 border-b" style={{ borderColor: '#d8d5cb' }}>
        <div className="text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
          defense in depth · LLM call path
        </div>
        <DefenseInDepthDiagram config={config} />
      </div>

      {/* Layer breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-5 border-b" style={{ borderColor: '#d8d5cb' }}>
        {layers.map(layer => {
          const items = posture.breakdown.filter(c => c.layer === layer);
          const earned = items.filter(c => c.passing).reduce((s, c) => s + c.weight, 0);
          const total = items.reduce((s, c) => s + c.weight, 0);
          const pct = total === 0 ? 0 : (earned / total) * 100;
          return (
            <div key={layer} className="px-5 py-4 border-r" style={{ borderColor: '#d8d5cb' }}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                <span className="w-2 h-2" style={{ background: layerColor[layer] }} />
                {layerLabel[layer]}
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-[20px] font-semibold tabular-nums" style={{ color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                  <AnimatedCounter to={earned} />
                </span>
                <span className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>/ {total}</span>
              </div>
              <div className="h-1 mt-2 overflow-hidden" style={{ background: '#ebe9e1' }}>
                <div className="h-full" style={{
                  background: layerColor[layer],
                  width: mounted ? `${pct}%` : '0%',
                  transition: 'width 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: pct > 0 ? `0 0 8px ${layerColor[layer]}80` : 'none',
                }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-control list */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="p-5 border-r" style={{ borderColor: '#d8d5cb' }}>
          <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            controls · {posture.breakdown.filter(c => c.passing).length} of {posture.breakdown.length} passing
          </div>
          <div className="space-y-1">
            {posture.breakdown.map((c, idx) => (
              <div key={c.id}
                className="grid grid-cols-[16px_1fr_60px_24px] gap-3 items-center py-1.5 border-b text-[11px] stream-in transition-colors hover:bg-[#f4f3ee]"
                style={{
                  borderColor: '#ebe9e1',
                  fontFamily: 'IBM Plex Mono, monospace',
                  animationDelay: `${idx * 25}ms`,
                  animationFillMode: 'backwards',
                }}>
                {c.passing
                  ? <Check size={12} strokeWidth={3} style={{ color: accent }} />
                  : <span className="block w-3 h-3 rounded-full" style={{ border: '1px solid #9a9890' }} />}
                <span style={{ color: c.passing ? '#1a1a17' : '#6f6e68' }}>{c.name}</span>
                <span className="text-right text-[10px]" style={{ color: layerColor[c.layer], fontFamily: 'IBM Plex Mono, monospace' }}>{c.layer}</span>
                <span className="text-right tabular-nums text-[10px]" style={{ color: c.passing ? accent : '#9a9890' }}>+{c.weight}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-5">
          {/* Compliance frameworks */}
          <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            compliance frameworks · {frameworks.length}
          </div>
          {frameworks.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6">
              {frameworks.map(fw => {
                const controls = COMPLIANCE_CONTROLS[fw.id] || [];
                return (
                  <div key={fw.id} className="px-3 py-2" style={{ background: '#fbfaf7', border: '1px solid #ebe9e1' }}>
                    <div className="text-[12px] font-medium" style={{ color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {fw.short}
                    </div>
                    <div className="text-[10px] mt-0.5" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {controls.length} mapped controls
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[11px] mb-6" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
              No frameworks selected.
            </div>
          )}

          {/* Active scans */}
          <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            active scans · {scans.length}
          </div>
          {scans.length > 0 ? (
            <div className="space-y-1">
              {scans.map(s => (
                <div key={s.id} className="flex items-center gap-3 px-3 py-1.5 text-[11px]"
                  style={{ background: '#fbfaf7', border: '1px solid #ebe9e1', fontFamily: 'IBM Plex Mono, monospace' }}>
                  <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: accentBg }} />
                  <span style={{ color: '#1a1a17' }}>{s.name}</span>
                  <span style={{ color: '#9a9890' }}>·</span>
                  <span style={{ color: '#6f6e68' }}>{s.cat}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px]" style={{ color: '#be123c', fontFamily: 'IBM Plex Mono, monospace' }}>
              ⚠ No scans configured. Critical gap.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DefenseInDepthDiagram({ config }) {
  const aiOn = isAIEnabled(config);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1500);
    return () => clearInterval(id);
  }, []);

  const W = 920, H = 320;
  const lanes = [
    { x: 40,  w: 130, label: 'Edge',     color: '#52524d', items: [
      config.cdn ? 'CDN' : null,
      config.waf ? 'WAF' : null,
      'TLS 1.3',
    ].filter(Boolean) },
    { x: 190, w: 180, label: 'Input scan', color: '#f0abfc', items: aiOn ? [
      `PII · ${piiDetectorName(config)}`,
      `Firewall · ${promptFirewallName(config)}`,
      config.deniedTopics ? 'Topic filter' : null,
    ].filter(Boolean) : ['(AI disabled)'] },
    { x: 390, w: 160, label: 'Inference', color: '#bef264', items: aiOn ? [
      aiInferenceName(config),
      isSelfHostedLLM(config) ? 'GPU node' : 'managed',
    ] : ['(AI disabled)'] },
    { x: 570, w: 160, label: 'Output scan', color: '#f0abfc', items: aiOn ? [
      config.outputModeration ? 'Moderation' : null,
      config.llmAuditLog ? 'Audit log' : null,
    ].filter(Boolean) : ['(AI disabled)'] },
    { x: 750, w: 130, label: 'Egress', color: '#52524d', items: [
      'TLS 1.3',
      isK8s(config.compute) && config.networkPoliciesDefault ? 'NetPol' : null,
    ].filter(Boolean) },
  ];

  const yLane = 80;
  const laneH = 130;

  // Build path through lane centers for animateMotion
  const flowPath = `M ${lanes.map(l => `${l.x + l.w / 2},${yLane + laneH / 2}`).join(' L ')}`;

  // Live synthetic stats (deterministic, look real)
  const stats = {
    rps: ((tick % 100) * 0.4 + 8.2).toFixed(1),
    blocks: ((tick * 7) % 233),
    pii: ((tick * 3) % 89),
    audit: ((tick * 11) % 9999).toLocaleString(),
  };

  // Lane that's currently "active" (pulses)
  const activeIdx = tick % lanes.length;

  return (
    <div>
      {/* Live header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: accentBg }} />
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
            live · request flow through your guardrails
          </span>
        </div>
        <div className="flex items-center gap-4 text-[10px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          <span style={{ color: '#9a9890' }}>req/s <span className="tabular-nums" style={{ color: accent }}>{stats.rps}</span></span>
          <span style={{ color: '#9a9890' }}>blocks <span className="tabular-nums" style={{ color: '#be123c' }}>{stats.blocks}</span></span>
          <span style={{ color: '#9a9890' }}>pii <span className="tabular-nums" style={{ color: '#f0abfc' }}>{stats.pii}</span></span>
          <span style={{ color: '#9a9890' }}>audit <span className="tabular-nums" style={{ color: '#86efac' }}>{stats.audit}</span></span>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="flowArrow" markerWidth="8" markerHeight="8" refX="7" refY="2.5" orient="auto">
            <path d="M0,0 L0,5 L7,2.5 z" fill="#9a9890" />
          </marker>
          <linearGradient id="flowGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor={accent} stopOpacity="0" />
            <stop offset="50%"  stopColor={accent} stopOpacity="0.8" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>

        <text x={W/2} y={28} fontSize="11" fill="#52524d" fontFamily="IBM Plex Mono, monospace" textAnchor="middle" letterSpacing="2">
          REQUEST → RESPONSE PATH
        </text>

        {/* Direction arrow */}
        <line x1={20} y1={50} x2={W - 20} y2={50} stroke="#d8d5cb" strokeWidth={1} strokeDasharray="4 3" markerEnd="url(#flowArrow)" />
        <text x={W - 30} y={45} fontSize="9" fill="#9a9890" fontFamily="IBM Plex Mono, monospace" textAnchor="end">flow</text>

        {/* Animated baseline path (subtle gradient) */}
        <path d={flowPath} fill="none" stroke="url(#flowGrad)" strokeWidth={1.2} />

        {/* Lanes */}
        {lanes.map((lane, i) => {
          const disabled = lane.items.length > 0 && lane.items[0] === '(AI disabled)';
          const isActive = i === activeIdx && !disabled;
          return (
            <g key={lane.label}>
              <rect
                x={lane.x} y={yLane} width={lane.w} height={laneH}
                fill="#fbfaf7"
                stroke={lane.color}
                strokeWidth={isActive ? 1.5 : 1}
                style={{
                  filter: disabled
                    ? 'none'
                    : isActive
                      ? `drop-shadow(0 0 16px ${lane.color}90)`
                      : `drop-shadow(0 0 6px ${lane.color}30)`,
                  opacity: disabled ? 0.4 : 1,
                  transition: 'filter 0.5s ease',
                }}
              />
              <text x={lane.x + 10} y={yLane + 18} fontSize="10" fill={isActive ? lane.color : '#9a9890'} fontFamily="IBM Plex Mono, monospace" letterSpacing="2">
                {lane.label.toUpperCase()}
              </text>
              {lane.items.map((item, idx) => (
                <g key={idx}>
                  <rect
                    x={lane.x + 10}
                    y={yLane + 32 + idx * 26}
                    width={lane.w - 20}
                    height={20}
                    fill={lane.color + '15'}
                    stroke={lane.color + '60'}
                    strokeWidth={0.5}
                  />
                  <text
                    x={lane.x + 16}
                    y={yLane + 46 + idx * 26}
                    fontSize="10"
                    fill="#1a1a17"
                    fontFamily="IBM Plex Mono, monospace"
                  >
                    {item}
                  </text>
                </g>
              ))}
              {/* arrow to next lane */}
              {i < lanes.length - 1 && (
                <line
                  x1={lane.x + lane.w}
                  y1={yLane + laneH / 2}
                  x2={lanes[i+1].x - 2}
                  y2={yLane + laneH / 2}
                  stroke="#9a9890"
                  strokeWidth={1.5}
                  markerEnd="url(#flowArrow)"
                />
              )}
            </g>
          );
        })}

        {/* Animated request dots traveling along the path */}
        {!aiOn ? null : [0, 1, 2].map(i => (
          <circle key={i} r="4.5" fill={accent}
            style={{ filter: `drop-shadow(0 0 6px ${accentSoft})` }}>
            <animateMotion dur="4.5s" repeatCount="indefinite" begin={`${i * 1.5}s`} path={flowPath} />
          </circle>
        ))}

        {/* Below lanes — runtime + audit context */}
        <g>
          <text x={20} y={H - 28} fontSize="9" fill="#9a9890" fontFamily="IBM Plex Mono, monospace" letterSpacing="2">
            RUNTIME
          </text>
          <text x={20} y={H - 12} fontSize="11" fill="#52524d" fontFamily="IBM Plex Mono, monospace">
            {[
              isK8s(config.compute) && (config.scanTools || []).includes('falco') ? 'Falco' : null,
              isK8s(config.compute) && config.podSecurityRestricted ? 'PSS:restricted' : null,
              isK8s(config.compute) && config.networkPoliciesDefault ? 'NetPol:default-deny' : null,
              'KMS',
            ].filter(Boolean).join(' · ') || '— minimal runtime hardening —'}
          </text>
        </g>
      </svg>
    </div>
  );
}

// =============================================================
// AGENTICNESS VIEW — radar chart + dimension breakdown
// =============================================================

function AgenticnessView({ config }) {
  const score = useMemo(() => computeAgenticness(config), [config]);
  const W = 460, cx = W / 2, cy = W / 2, R = 170;
  const N = AGENTIC_DIMENSIONS.length;
  const [hoveredDim, setHoveredDim] = useState(null);

  // Polygon points for the score
  const angle = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / N;
  const point = (i, v) => {
    const r = (v / 4) * R;
    return [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  };
  const labelPoint = (i) => {
    const r = R + 28;
    return [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  };

  const scorePolygon = AGENTIC_DIMENSIONS.map((d, i) => point(i, score.dimensions[d.id] || 0)).map(p => p.join(',')).join(' ');
  const ringPolygon = (v) => AGENTIC_DIMENSIONS.map((_, i) => point(i, v)).map(p => p.join(',')).join(' ');

  // Reference: agentic.ai top tools (illustrative, derived from llms.txt cohorts)
  const benchmarks = [
    { name: 'Hermes Agent',   level: 'L3', score: 21, color: '#a78bfa' },
    { name: 'Claude Code',    level: 'L3', score: 20, color: '#60a5fa' },
    { name: 'Perplexity Computer', level: 'L2', score: 16, color: '#b45309' },
    { name: 'GitHub Copilot', level: 'L2', score: 14, color: '#52524d' },
  ];

  const aiOff = !isAIEnabled(config);
  if (aiOff) {
    return (
      <div className="border p-10 text-center" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
        <Sparkles size={24} style={{ color: '#9a9890', margin: '0 auto 12px' }} />
        <div className="text-[12px]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
          Agenticness scoring requires the AI / agent layer.
        </div>
      </div>
    );
  }

  return (
    <div className="border" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b flex-wrap gap-3" style={{ borderColor: '#d8d5cb' }}>
        <div className="flex items-center gap-3">
          <Layers size={14} style={{ color: accent }} />
          <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            agenticness profile
          </span>
          <span className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            agentic.ai 32-point scale · self-reported
          </span>
        </div>
        <a
          href="https://agentic.ai/agenticness"
          target="_blank"
          rel="noreferrer"
          className="text-[10px] hover:underline"
          style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}
        >
          framework spec ↗
        </a>
      </div>

      {/* Main grid: radar + level card + dimensions */}
      <div className="grid grid-cols-1 lg:grid-cols-[480px_1fr]">
        {/* Radar */}
        <div className="p-6 border-r" style={{ borderColor: '#d8d5cb' }}>
          <svg viewBox={`0 0 ${W} ${W}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
            {/* concentric rings */}
            {[1, 2, 3, 4].map(v => (
              <polygon
                key={v}
                points={ringPolygon(v)}
                fill="none"
                stroke="#d8d5cb"
                strokeWidth={v === 4 ? 1 : 0.5}
                strokeDasharray={v === 4 ? '' : '2 3'}
              />
            ))}
            {/* spokes */}
            {AGENTIC_DIMENSIONS.map((d, i) => {
              const [x, y] = point(i, 4);
              const isActive = hoveredDim === d.id;
              return <line key={d.id} x1={cx} y1={cy} x2={x} y2={y}
                stroke={isActive ? accent : '#d8d5cb'}
                strokeWidth={isActive ? 1.5 : 0.5}
                style={{ transition: 'all 0.2s ease' }} />;
            })}
            {/* score polygon — breathing animation */}
            <polygon
              points={scorePolygon}
              fill={accent}
              fillOpacity="0.18"
              stroke={accent}
              strokeWidth="1.5"
              style={{ filter: `drop-shadow(0 0 6px ${accentSoft}50)`, transformOrigin: `${cx}px ${cy}px` }}
            >
              <animate attributeName="fill-opacity" values="0.18;0.28;0.18" dur="3s" repeatCount="indefinite" />
              <animate attributeName="stroke-width" values="1.5;2;1.5" dur="3s" repeatCount="indefinite" />
            </polygon>
            {/* score points — staggered pulse */}
            {AGENTIC_DIMENSIONS.map((d, i) => {
              const [x, y] = point(i, score.dimensions[d.id] || 0);
              return (
                <circle key={d.id} cx={x} cy={y} r="3.5" fill={accent}
                  style={{ filter: `drop-shadow(0 0 4px ${accentSoft})` }}>
                  <animate attributeName="r" values="3.5;5;3.5" dur="2s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
                </circle>
              );
            })}
            {/* axis labels — hover to inspect */}
            {AGENTIC_DIMENSIONS.map((d, i) => {
              const [x, y] = labelPoint(i);
              const a = angle(i);
              const anchor = Math.cos(a) > 0.3 ? 'start' : Math.cos(a) < -0.3 ? 'end' : 'middle';
              const isActive = hoveredDim === d.id;
              return (
                <g key={d.id}
                  onMouseEnter={() => setHoveredDim(d.id)}
                  onMouseLeave={() => setHoveredDim(null)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* invisible hit area for easier hovering */}
                  <rect
                    x={x - 50} y={y - 16} width={100} height={36}
                    fill="transparent"
                  />
                  <text
                    x={x} y={y}
                    fontSize={isActive ? "12" : "11"}
                    fill={isActive ? accent : '#52524d'}
                    fontFamily="IBM Plex Mono, monospace"
                    textAnchor={anchor}
                    dominantBaseline="middle"
                    fontWeight={isActive ? 600 : 400}
                    style={{ transition: 'all 0.2s ease' }}
                  >
                    {d.name.toLowerCase()}
                  </text>
                  <text
                    x={x} y={y + 14}
                    fontSize="10"
                    fill={accent}
                    fontFamily="IBM Plex Mono, monospace"
                    textAnchor={anchor}
                    dominantBaseline="middle"
                  >
                    {score.dimensions[d.id] || 0}/4
                  </text>
                </g>
              );
            })}
            {/* center score — counts up on mount */}
            <circle cx={cx} cy={cy} r="32" fill="#fbfaf7" stroke={accent} strokeWidth="1">
              <animate attributeName="stroke-width" values="1;2;1" dur="3s" repeatCount="indefinite" />
            </circle>
            <text x={cx} y={cy - 2} fontSize="20" fill={accent} fontFamily="IBM Plex Mono, monospace" fontWeight="600" textAnchor="middle">
              <AnimatedCounter to={score.total} />
            </text>
            <text x={cx} y={cy + 14} fontSize="9" fill="#6f6e68" fontFamily="IBM Plex Mono, monospace" textAnchor="middle">
              / 32
            </text>
          </svg>
          {/* Hover-detail panel — shows when an axis label is hovered */}
          <div className="mt-2 px-4 py-2.5 min-h-[56px] flex flex-col justify-center" style={{ background: '#fbfaf7', border: '1px solid #ebe9e1' }}>
            {hoveredDim ? (() => {
              const d = AGENTIC_DIMENSIONS.find(x => x.id === hoveredDim);
              const v = score.dimensions[hoveredDim] || 0;
              return (
                <div className="stream-in">
                  <div className="flex items-baseline gap-3 mb-1">
                    <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>inspect</span>
                    <span className="text-[12px] font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                      {d.name.toLowerCase()}
                    </span>
                    <span className="text-[10px] tabular-nums" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {v}/4
                    </span>
                  </div>
                  <p className="text-[11px] leading-relaxed" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {d.desc}
                  </p>
                </div>
              );
            })() : (
              <span className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                ⏵ hover an axis to inspect a dimension
              </span>
            )}
          </div>
        </div>

        {/* Right rail: level + dimensions list */}
        <div className="p-6">
          {/* Level badge */}
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
              level
            </div>
            <div className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
              {AGENTIC_LEVELS.find(l => l.level === score.level.level)?.min}–{AGENTIC_LEVELS.find(l => l.level === score.level.level)?.max} pts
            </div>
          </div>
          <div className="px-4 py-3" style={{ background: '#fbfaf7', border: `1px solid ${accent}40` }}>
            <div className="flex items-center gap-3">
              <span className="text-[24px] font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                {score.level.level}
              </span>
              <span className="text-[16px] font-medium" style={{ color: '#1a1a17' }}>
                {score.level.name}
              </span>
            </div>
            <div className="text-[11px] mt-2 leading-relaxed" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
              {score.level.desc}
            </div>
          </div>

          {/* Level progression bar */}
          <div className="mt-4 mb-6">
            <div className="grid grid-cols-6 gap-1">
              {AGENTIC_LEVELS.map(l => {
                const active = l.level === score.level.level;
                const reached = score.total >= l.min;
                return (
                  <div key={l.level}
                    className="px-2 py-1.5 text-center"
                    style={{
                      background: active ? '#f4f3ee' : 'transparent',
                      border: `1px solid ${active ? accent : reached ? '#9a9890' : '#ebe9e1'}`,
                      color: active ? accent : reached ? '#52524d' : '#9a9890',
                      fontFamily: 'IBM Plex Mono, monospace',
                    }}
                  >
                    <div className="text-[10px] font-semibold">{l.level}</div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-6 gap-1 mt-1">
              {AGENTIC_LEVELS.map(l => (
                <div key={l.level} className="text-[8px] text-center" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {l.min}–{l.max}
                </div>
              ))}
            </div>
          </div>

          {/* Dimension breakdown */}
          <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            dimension reasoning
          </div>
          <div className="space-y-1.5">
            {AGENTIC_DIMENSIONS.map(d => {
              const v = score.dimensions[d.id] || 0;
              return (
                <div key={d.id} className="grid grid-cols-[100px_60px_1fr] gap-3 items-start py-1.5 border-b" style={{ borderColor: '#ebe9e1', fontFamily: 'IBM Plex Mono, monospace' }}>
                  <span className="text-[11px]" style={{ color: '#52524d' }}>{d.name.toLowerCase()}</span>
                  <div className="flex gap-0.5 h-2 mt-1">
                    {[0,1,2,3].map(i => (
                      <div key={i} className="flex-1" style={{ background: i < v ? accent : '#ebe9e1' }} />
                    ))}
                  </div>
                  <span className="text-[10px] leading-relaxed" style={{ color: '#6f6e68' }}>{score.reasons[d.id]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Benchmarks footer */}
      <div className="border-t px-5 py-4" style={{ borderColor: '#d8d5cb', background: '#fbfaf7' }}>
        <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
          benchmarks · top tools on agentic.ai
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          {benchmarks.map(b => (
            <div key={b.name} className="px-3 py-2" style={{ background: '#ffffff', border: '1px solid #ebe9e1' }}>
              <div className="flex items-baseline justify-between">
                <span className="text-[11px]" style={{ color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>{b.name}</span>
                <span className="text-[10px] font-semibold" style={{ color: b.color, fontFamily: 'IBM Plex Mono, monospace' }}>{b.level}</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 h-1" style={{ background: '#ebe9e1' }}>
                  <div className="h-full" style={{ background: b.color, width: `${(b.score / 32) * 100}%` }} />
                </div>
                <span className="text-[10px] tabular-nums" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{b.score}</span>
              </div>
            </div>
          ))}
          <div className="px-3 py-2" style={{ background: '#ffffff', border: `1px solid ${accent}` }}>
            <div className="flex items-baseline justify-between">
              <span className="text-[11px]" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>your agent</span>
              <span className="text-[10px] font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>{score.level.level}</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 h-1" style={{ background: '#ebe9e1' }}>
                <div className="h-full" style={{ background: accentBg, width: `${(score.total / 32) * 100}%` }} />
              </div>
              <span className="text-[10px] tabular-nums" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>{score.total}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// ROLLOUT PLAN VIEW — wave-layered DAG of tickets
// =============================================================

function RolloutPlanView({ config }) {
  const plan = useMemo(() => buildRolloutPlan(config), [config]);
  const [hoveredTicket, setHoveredTicket] = useState(null);
  const [activeWave, setActiveWave] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const usedTeams = ROLLOUT_TEAMS.filter(t => plan.tickets.some(tk => tk.team === t.id));

  // Cycle the "currently deploying" wave (gated on isPlaying)
  useEffect(() => {
    if (plan.waves.length === 0 || !isPlaying) return;
    const id = setInterval(() => {
      setActiveWave(w => (w + 1) % plan.waves.length);
    }, 2500);
    return () => clearInterval(id);
  }, [plan.waves.length, isPlaying]);

  // Layout: each wave is a column, tickets stacked vertically within
  const cardW = 220, cardH = 70, gapX = 60, gapY = 14, padX = 80, padY = 80;
  const W = padX * 2 + plan.waves.length * (cardW + gapX) - gapX;
  const maxRows = Math.max(...plan.waves.map(w => w.length), 1);
  const H = padY * 2 + maxRows * (cardH + gapY) - gapY + 60;

  // Position lookup: { ticketKey: { x, y, cx, cy } }
  const pos = {};
  plan.waves.forEach((wave, wIdx) => {
    wave.forEach((t, idx) => {
      const x = padX + wIdx * (cardW + gapX);
      const y = padY + idx * (cardH + gapY) + 20;
      pos[t.key] = { x, y, cx: x + cardW, cy: y + cardH / 2, lx: x, ly: y + cardH / 2 };
    });
  });

  const teamColor = (id) => ROLLOUT_TEAMS.find(t => t.id === id)?.color || '#52524d';
  const isHighlighted = (key) => {
    if (!hoveredTicket) return false;
    if (hoveredTicket === key) return true;
    const t = plan.tickets.find(x => x.key === hoveredTicket);
    if (t?.blocked_by.includes(key)) return true;
    const downstream = plan.tickets.find(x => x.key === key);
    if (downstream?.blocked_by.includes(hoveredTicket)) return true;
    return false;
  };

  return (
    <div className="border" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
      {/* header */}
      <div className="flex items-center justify-between px-5 py-3 border-b flex-wrap gap-3" style={{ borderColor: '#d8d5cb' }}>
        <div className="flex items-center gap-3">
          <GitBranch size={14} style={{ color: accent }} />
          <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            rollout DAG · {ISSUE_TRACKERS.find(x => x.id === config.issueTracker)?.name}
          </span>
          <span className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            {plan.tickets.length} tickets · {plan.waves.length} waves · {plan.tickets.reduce((s, t) => s + t.effort, 0)} pts
          </span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Play/pause + wave scrubber */}
          <div className="flex items-center gap-2 px-2 py-1" style={{ background: '#fbfaf7', border: '1px solid #ebe9e1' }}>
            <button
              onClick={() => setIsPlaying(p => !p)}
              className="w-5 h-5 flex items-center justify-center transition-colors hover:bg-[#f4f3ee]"
              title={isPlaying ? 'pause' : 'play'}
              style={{ color: isPlaying ? accent : '#52524d' }}
            >
              {isPlaying ? (
                <svg width="9" height="10" viewBox="0 0 9 10" fill="currentColor"><rect x="0" y="0" width="3" height="10" /><rect x="6" y="0" width="3" height="10" /></svg>
              ) : (
                <svg width="9" height="10" viewBox="0 0 9 10" fill="currentColor"><polygon points="0,0 9,5 0,10" /></svg>
              )}
            </button>
            <div className="flex items-center gap-0.5">
              {plan.waves.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setIsPlaying(false); setActiveWave(i); }}
                  className="transition-all"
                  style={{
                    width: i === activeWave ? 16 : 6,
                    height: 6,
                    background: i === activeWave ? accent : '#9a9890',
                  }}
                  title={`wave ${i + 1}`}
                />
              ))}
            </div>
            <span className="text-[10px] tabular-nums px-1" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
              w{activeWave + 1}/{plan.waves.length}
            </span>
          </div>
          {usedTeams.map(t => (
            <div key={t.id} className="flex items-center gap-1.5 text-[10px]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
              <span className="w-2.5 h-2.5" style={{ background: t.color }} />
              <span>{t.name.split(' · ').pop().toLowerCase()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* DAG */}
      <div className="overflow-auto p-6" style={{ maxHeight: 720 }}>
        <svg width={W} height={H} style={{ minWidth: W, fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <defs>
            <pattern id="dag-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ebe9e1" strokeWidth="0.5" />
            </pattern>
            <marker id="arrowDag" markerWidth="8" markerHeight="8" refX="7" refY="2.5" orient="auto">
              <path d="M0,0 L0,5 L7,2.5 z" fill="#9a9890" />
            </marker>
            <marker id="arrowDagHi" markerWidth="8" markerHeight="8" refX="7" refY="2.5" orient="auto">
              <path d="M0,0 L0,5 L7,2.5 z" fill={accent} />
            </marker>
          </defs>
          <rect width={W} height={H} fill="url(#dag-grid)" />

          {/* wave headers */}
          {plan.waves.map((w, i) => {
            const x = padX + i * (cardW + gapX);
            const label = i === 0 ? 'WAVE 0 · FOUNDATION' : i === plan.waves.length - 1 ? `WAVE ${i} · CUTOVER` : `WAVE ${i}`;
            const isActive = i === activeWave;
            const isPast = i < activeWave;
            return (
              <g key={i}>
                <text x={x} y={50} fontSize="10" fill={isActive ? accent : isPast ? '#86efac' : '#6f6e68'} fontFamily="IBM Plex Mono, monospace" letterSpacing="2"
                  style={{ transition: 'fill 0.4s ease' }}>
                  {label}
                  {isActive && <tspan dx="6" fill={accent}>● deploying</tspan>}
                  {isPast && <tspan dx="6" fill="#86efac">✓</tspan>}
                </text>
                <text x={x} y={64} fontSize="9" fill="#9a9890" fontFamily="IBM Plex Mono, monospace">
                  {w.length} ticket{w.length !== 1 ? 's' : ''} · {w.reduce((s, t) => s + t.effort, 0)} pts
                </text>
                <line x1={x} y1={70} x2={x + cardW} y2={70}
                  stroke={isActive ? accent : isPast ? '#86efac' : '#d8d5cb'}
                  strokeWidth={isActive ? 2 : isPast ? 1 : 0.5}
                  style={{
                    filter: isActive ? `drop-shadow(0 0 6px ${accentSoft})` : 'none',
                    transition: 'stroke 0.4s ease, stroke-width 0.4s ease',
                  }} />
                {isActive && (
                  <circle cx={x} cy={70} r="3" fill={accent} style={{ filter: `drop-shadow(0 0 4px ${accentSoft})` }}>
                    <animate attributeName="cx" from={x} to={x + cardW} dur="2.5s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            );
          })}

          {/* dependency arrows (drawn first, behind cards) */}
          {plan.tickets.flatMap(t => t.blocked_by.map(b => {
            const from = pos[b];
            const to = pos[t.key];
            if (!from || !to) return null;
            const hi = isHighlighted(t.key) && isHighlighted(b);
            // Bezier curve from right of `from` to left of `to`
            const dx = (to.lx - from.cx) * 0.4;
            const path = `M ${from.cx} ${from.cy} C ${from.cx + dx} ${from.cy}, ${to.lx - dx} ${to.ly}, ${to.lx - 4} ${to.ly}`;
            return (
              <path
                key={`${b}->${t.key}`}
                d={path}
                fill="none"
                stroke={hi ? accent : '#d8d5cb'}
                strokeWidth={hi ? 1.5 : 1}
                strokeOpacity={hoveredTicket && !hi ? 0.2 : 1}
                markerEnd={hi ? 'url(#arrowDagHi)' : 'url(#arrowDag)'}
              />
            );
          }))}

          {/* ticket cards */}
          {plan.tickets.map(t => {
            const p = pos[t.key];
            if (!p) return null;
            const hi = isHighlighted(t.key);
            const dim = hoveredTicket && !hi;
            const color = teamColor(t.team);
            const inActiveWave = t.wave === activeWave;
            return (
              <g
                key={t.key}
                onMouseEnter={() => setHoveredTicket(t.key)}
                onMouseLeave={() => setHoveredTicket(null)}
                onClick={() => setSelectedTicket(t.key)}
                style={{ cursor: 'pointer', opacity: dim ? 0.35 : 1 }}
              >
                {/* card background */}
                <rect
                  x={p.x} y={p.y} width={cardW} height={cardH}
                  fill="#fbfaf7"
                  stroke={hi ? accent : inActiveWave ? accent : color}
                  strokeWidth={hi ? 1.5 : inActiveWave ? 1.4 : 1}
                  style={{
                    filter: hi ? `drop-shadow(0 0 8px ${accentSoft}40)` : inActiveWave ? `drop-shadow(0 0 8px ${accentSoft}50)` : 'none',
                    transition: 'stroke 0.4s ease, filter 0.4s ease',
                  }}
                />
                {/* deploying badge for active-wave cards */}
                {inActiveWave && !hoveredTicket && (
                  <g>
                    <circle cx={p.x + cardW - 10} cy={p.y + 10} r="3" fill={accent}>
                      <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
                    </circle>
                  </g>
                )}
                {/* team color stripe */}
                <rect x={p.x} y={p.y} width={3} height={cardH} fill={color} />
                {/* key + effort */}
                <text x={p.x + 12} y={p.y + 18} fontSize="10" fill="#6f6e68" fontFamily="IBM Plex Mono, monospace" letterSpacing="1.5">
                  {t.key}
                </text>
                <text x={p.x + cardW - 10} y={p.y + 18} fontSize="10" fill={color} fontFamily="IBM Plex Mono, monospace" textAnchor="end">
                  {t.effort} pt{t.effort !== 1 ? 's' : ''}
                </text>
                {/* summary (truncated) */}
                <foreignObject x={p.x + 12} y={p.y + 24} width={cardW - 22} height={cardH - 28}>
                  <div
                    style={{
                      fontSize: '11px',
                      lineHeight: '1.35',
                      color: '#1a1a17',
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {t.summary}
                  </div>
                  <div style={{ fontSize: '9px', color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace', marginTop: 2 }}>
                    {ROLLOUT_TEAMS.find(rt => rt.id === t.team)?.name.split(' · ').pop().toLowerCase()}
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </div>

      {/* hover detail bar */}
      <div className="border-t px-5 py-3" style={{ borderColor: '#d8d5cb', background: '#fbfaf7', minHeight: 64 }}>
        {hoveredTicket ? (() => {
          const t = plan.tickets.find(x => x.key === hoveredTicket);
          const team = ROLLOUT_TEAMS.find(rt => rt.id === t.team);
          return (
            <div className="text-[11px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              <div className="flex items-center gap-3 mb-1">
                <span style={{ color: team.color }}>{t.key}</span>
                <span style={{ color: '#52524d' }}>{t.summary}</span>
              </div>
              <div className="text-[10px]" style={{ color: '#9a9890' }}>
                team: <span style={{ color: team.color }}>{team.name}</span> ·
                wave {t.wave} ·
                effort {t.effort} pts ·
                blocked by: <span style={{ color: '#52524d' }}>{t.blocked_by.length ? t.blocked_by.join(', ') : '— (can start immediately)'}</span>
              </div>
            </div>
          );
        })() : (
          <div className="text-[11px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            hover any ticket to highlight blockers · click to pin details
          </div>
        )}
      </div>

      {/* per-team summary */}
      <div className="border-t grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8" style={{ borderColor: '#d8d5cb' }}>
        {usedTeams.map(t => {
          const ts = plan.tickets.filter(x => x.team === t.id);
          const Icon = t.icon;
          return (
            <div key={t.id} className="px-4 py-3 border-r" style={{ borderColor: '#d8d5cb' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon size={11} style={{ color: t.color }} />
                <span className="text-[10px] uppercase tracking-[0.15em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {t.name.split(' · ').pop().toLowerCase()}
                </span>
              </div>
              <div className="text-[16px] font-semibold tabular-nums" style={{ color: t.color, fontFamily: 'IBM Plex Mono, monospace' }}>
                {ts.length}
              </div>
              <div className="text-[10px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                {ts.reduce((s, x) => s + x.effort, 0)} pts
              </div>
            </div>
          );
        })}
      </div>

      {/* Click-to-pin ticket detail modal */}
      {selectedTicket && (() => {
        const t = plan.tickets.find(x => x.key === selectedTicket);
        if (!t) return null;
        const team = ROLLOUT_TEAMS.find(rt => rt.id === t.team);
        const blockedBy = t.blocked_by.map(k => plan.tickets.find(x => x.key === k)).filter(Boolean);
        const blocks = plan.tickets.filter(x => x.blocked_by.includes(t.key));
        return (
          <Modal onClose={() => setSelectedTicket(null)}>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                ticket
              </span>
              <span className="px-2 py-0.5 text-[11px]" style={{ background: team.color + '20', color: team.color, fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
                {t.key}
              </span>
              <span className="ml-auto text-[10px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                wave {t.wave + 1} · {t.effort}pt
              </span>
            </div>
            <h3 className="text-[16px] font-semibold mb-3" style={{ color: '#1a1a17' }}>{t.summary}</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                  team
                </div>
                <div className="text-[12px]" style={{ color: team.color, fontFamily: 'IBM Plex Mono, monospace' }}>
                  {team.name}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                  assignee
                </div>
                <div className="text-[12px] truncate" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {config.teamAssignments?.[t.team] || '—'}
                </div>
              </div>
            </div>
            {blockedBy.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                  blocked by · {blockedBy.length}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {blockedBy.map(b => (
                    <button key={b.key} onClick={() => setSelectedTicket(b.key)}
                      className="px-2 py-1 text-[11px] hover:bg-[#f4f3ee] transition-colors"
                      style={{ background: '#fbfaf7', border: `1px solid ${teamColor(b.team)}40`, color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
                      <span style={{ color: teamColor(b.team) }}>{b.key}</span> · {b.summary.slice(0, 40)}{b.summary.length > 40 ? '…' : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {blocks.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                  blocks · {blocks.length}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {blocks.slice(0, 6).map(b => (
                    <button key={b.key} onClick={() => setSelectedTicket(b.key)}
                      className="px-2 py-1 text-[11px] hover:bg-[#f4f3ee] transition-colors"
                      style={{ background: '#fbfaf7', border: `1px solid ${teamColor(b.team)}40`, color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
                      <span style={{ color: teamColor(b.team) }}>{b.key}</span> · {b.summary.slice(0, 40)}{b.summary.length > 40 ? '…' : ''}
                    </button>
                  ))}
                  {blocks.length > 6 && (
                    <span className="px-2 py-1 text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                      +{blocks.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            )}
            {blockedBy.length === 0 && blocks.length === 0 && (
              <div className="text-[11px] mb-3" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                ⏵ standalone ticket · no DAG dependencies
              </div>
            )}
            <div className="flex justify-end pt-3 border-t" style={{ borderColor: '#ebe9e1' }}>
              <button onClick={() => setSelectedTicket(null)}
                className="px-3 py-1.5 text-[11px]"
                style={{ background: 'transparent', border: '1px solid #d8d5cb', color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
                close
              </button>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

// =============================================================
// ARCHITECTURE DIAGRAM
// =============================================================

function ArchitectureView({ config }) {
  return (
    <div>
      <div className="border p-6 md:p-10" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
        <ArchitectureSVG config={config} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <Stat label="Resources" value={estimateResources(config)} unit="defs" />
        <Stat label="Lines of HCL" value={Math.round(estimateLines(config))} unit="loc" />
        <Stat label="Est. monthly" value={`$${estimateCost(config)}`} unit={config.environment} />
      </div>
    </div>
  );
}

const Stat = ({ label, value, unit }) => (
  <div className="border p-4" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
    <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>{label}</div>
    <div className="flex items-baseline gap-2 mt-2">
      <span className="text-[24px] font-semibold tabular-nums" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>{value}</span>
      <span className="text-[10px] uppercase tracking-wider" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{unit}</span>
    </div>
  </div>
);

const estimateResources = (cfg) => {
  let n = 12; // base networking + iam
  if (cfg.compute === 'eks') n += 14;
  if (cfg.compute === 'ecs') n += 8;
  if (cfg.compute === 'lambda') n += 9;
  if (cfg.database !== 'none') n += 8;
  if (cfg.cache === 'redis') n += 4;
  if (cfg.cdn) n += 3;
  if (cfg.waf) n += 2;
  return n;
};
const estimateLines = (cfg) => 280 + estimateResources(cfg) * 18;
const estimateCost = (cfg) => {
  let c = 0;
  if (cfg.compute === 'eks')    c += cfg.environment === 'prod' ? 280 : 90;
  if (cfg.compute === 'ecs')    c += cfg.environment === 'prod' ? 140 : 40;
  if (cfg.compute === 'lambda') c += cfg.environment === 'prod' ? 60  : 12;
  if (cfg.compute === 'ec2')    c += cfg.environment === 'prod' ? 180 : 50;
  if (cfg.database !== 'none')  c += cfg.environment === 'prod' ? 220 : 55;
  if (cfg.cache === 'redis')    c += cfg.environment === 'prod' ? 110 : 25;
  return c;
};

function ArchitectureSVG({ config }) {
  // Build a layered diagram: edge -> LB -> compute -> data -> ai (-> de)
  const aiOn = isAIEnabled(config);
  const deOn = isDEOn(config);
  const W = 920;
  const H = (aiOn ? 720 : 540) + (deOn ? 220 : 0);
  const k8s = isK8s(config.compute);
  const fn = isFunctions(config.compute);
  const computeLabel = computeName(config);
  const dbLabel = dbName(config);
  const deTierY = (aiOn ? 720 : 540) + 20; // top of DE tier when enabled

  // ── interactivity: hover state + live ticker ─────────────────
  const [hoveredNode, setHoveredNode] = useState(null);
  const [tickRate, setTickRate] = useState(12.4);
  useEffect(() => {
    const id = setInterval(() => {
      setTickRate(prev => {
        const drift = (Math.random() - 0.5) * 1.5;
        return Math.max(8, Math.min(45, prev + drift));
      });
    }, 1200);
    return () => clearInterval(id);
  }, []);

  // Node detail map — shown in tooltip on hover
  const nodeDetails = {
    cdn:       { title: 'CDN',           detail: `${config.provider === 'aws' ? 'CloudFront' : config.provider === 'gcp' ? 'Cloud CDN' : 'Azure CDN'} · 50+ PoPs · TLS 1.3 · HTTP/3` },
    waf:       { title: 'WAF',           detail: `OWASP Top-10 ruleset · rate limit ${config.environment === 'prod' ? '1000/s/IP' : '100/s/IP'} · bot detection` },
    lb:        { title: 'Load Balancer', detail: `Application LB · TLS termination · health checks · sticky sessions` },
    compute:   { title: computeLabel,    detail: `${k8s ? 'kubernetes · HPA · PDB · 3 replicas min' : fn ? 'serverless · concurrency=200 · cold start <1s' : 'fargate tasks · 0.5 vCPU / 1 GB'}` },
    db:        { title: dbLabel,         detail: `${config.environment === 'prod' ? 'multi-AZ · 7d backups' : 'single-AZ · 1d backups'} · KMS-encrypted at rest · TLS in transit` },
    cache:     { title: 'Redis',         detail: `t3.medium · 6 GB · in-memory · LRU eviction · single-AZ replica` },
    agent:     { title: 'Agent service', detail: `${agentFwName(config)} · FastAPI · /chat /healthz /metrics · 6-layer guardrails wrapper` },
    vector:    { title: vectorDBName(config),    detail: `dim 1536 · k-NN · HNSW index · ${config.vectorDB === 'pgvector' ? 'shares RDS' : 'dedicated cluster'}` },
    llm:       { title: aiInferenceName(config), detail: `${isSelfHostedLLM(config) ? 'vLLM · paged attention · KV cache' : 'managed · IRSA · pay-per-token'} · ${config.environment === 'prod' ? 'reserved capacity' : 'on-demand'}` },
    gpu:       { title: 'GPU node group', detail: `${(config.gpuType || '').split(' ')[0]} · taint nvidia.com/gpu · cluster autoscaler` },
    obs:       { title: OBSERVABILITY_OPTIONS.find(x=>x.id===config.observability)?.name || 'Observability', detail: 'metrics · logs · traces · alert rules' },
    secrets:   { title: 'Secrets Manager', detail: 'KMS-encrypted · 90d auto-rotation · IAM-scoped access' },
    state:     { title: 'Terraform state', detail: 'S3 + DynamoDB lock · versioned · encrypted' },
    nat:       { title: 'NAT Gateway',   detail: 'egress for private subnets · per-AZ HA' },
    llmObs:    { title: LLM_OBSERVABILITY.find(o => o.id === config.llmObservability)?.name || 'LLM tracing', detail: 'prompt traces · token cost · feedback loop · OTLP export' },
    'de-stream': { title: deStreamingName(config),    detail: `streaming ingest · ${cfg => deSize(config).kafka.brokers}× brokers · ${deSize(config).kafka.storage} per broker`.replace('cfg => deSize(config).kafka.brokers', deSize(config).kafka.brokers) },
    'de-proc':   { title: deStreamProcName(config),   detail: `exactly-once · ${deSize(config).flink.tm}× task managers · ${deSize(config).flink.mem} mem each · checkpointing to lake` },
    'de-olap':   { title: deOlapName(config),         detail: `${deSize(config).pinot.servers}× servers · ${deSize(config).pinot.brokers}× brokers · sub-second on billions of rows` },
    'de-lake':   { title: deLakeTableName(config),    detail: 'ACID · schema evolution · time travel · partitioned by day · KMS encrypted' },
    'de-query':  { title: deQueryEngineName(config),  detail: `${deSize(config).trino.workers}× workers · ${deSize(config).trino.mem} per worker · federates lake + olap + oltp + cassandra` },
  };

  const Box = ({ x, y, w, h, label, sub, color = '#d8d5cb', glow = false, dashed = false, nodeId }) => {
    const isHovered = hoveredNode === nodeId;
    const isOther = hoveredNode && hoveredNode !== nodeId;
    // Compact boxes (h ≤ 50) get tighter vertical spacing so labels stay inside.
    // Taller boxes get the original spacing for breathing room.
    const compact = h <= 50;
    const subY   = compact ? y + 15 : y + 22;
    const labelY = compact ? y + 32 : y + 44;
    return (
      <g
        onMouseEnter={() => nodeId && setHoveredNode(nodeId)}
        onMouseLeave={() => nodeId && setHoveredNode(null)}
        style={{ cursor: nodeId ? 'pointer' : 'default', opacity: isOther ? 0.4 : 1, transition: 'opacity 0.2s ease' }}
      >
        <rect
          x={x} y={y} width={w} height={h}
          fill={isHovered ? '#f4f3ee' : '#fbfaf7'}
          stroke={color}
          strokeWidth={isHovered ? 2 : 1}
          strokeDasharray={dashed ? '4 3' : undefined}
          style={(glow || isHovered) ? { filter: `drop-shadow(0 0 ${isHovered ? 12 : 8}px ${color}${isHovered ? 'cc' : '80'})`, transition: 'all 0.2s ease' } : { transition: 'all 0.2s ease' }}
        />
        {sub && <text x={x + 10} y={subY} fontSize="9" fill="#9a9890" fontFamily="IBM Plex Mono, monospace" textTransform="uppercase" letterSpacing="2">{sub}</text>}
        <text x={x + 10} y={labelY} fontSize={compact ? 12 : 13} fill="#1a1a17" fontFamily="IBM Plex Sans, sans-serif" fontWeight="600">{label}</text>
      </g>
    );
  };

  const aiAccent = '#f0abfc'; // pink-300 for AI tier
  const aiTierY = 470; // top of AI tier when enabled

  return (
    <div className="relative">
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ebe9e1" strokeWidth="0.5" />
        </pattern>
        <marker id="arrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill={accent} />
        </marker>
        <marker id="arrowAI" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill={aiAccent} />
        </marker>
      </defs>

      <rect width={W} height={H} fill="url(#grid)" />

      {/* Cloud envelope */}
      <rect x={20} y={20} width={W - 40} height={H - 40} fill="none" stroke="#d8d5cb" strokeWidth={1} strokeDasharray="6 4" />
      <text x={32} y={42} fontSize="9" fill="#6f6e68" fontFamily="IBM Plex Mono, monospace" letterSpacing="2">
        {config.provider.toUpperCase()} · {config.region}
      </text>
      <text x={W - 32} y={42} fontSize="9" fill="#6f6e68" fontFamily="IBM Plex Mono, monospace" letterSpacing="2" textAnchor="end">
        env: {config.environment}
      </text>

      {/* Edge */}
      {config.cdn && (
        <>
          <Box x={60}  y={70}  w={200} h={56}  label="CDN" sub="edge · global" color={accent} glow nodeId="cdn" />
        </>
      )}
      {config.waf && (
        <Box x={280} y={70}  w={200} h={56}  label="WAF" sub="rate limit · owasp" color={accent} glow nodeId="waf" />
      )}
      <Box x={500} y={70}  w={W - 520} h={56} label={config.cdn ? 'TLS termination + LB' : 'Application Load Balancer'} sub="layer 7 · tls" color="#52524d" nodeId="lb" />

      {/* VPC */}
      <rect x={60} y={160} width={W - 120} height={(aiOn ? H - 220 : H - 200)} fill="none" stroke="#9a9890" strokeWidth={1} />
      <text x={72} y={178} fontSize="9" fill="#6f6e68" fontFamily="IBM Plex Mono, monospace" letterSpacing="2">VPC · 10.0.0.0/16</text>

      {/* public subnet */}
      <rect x={80} y={194} width={W - 160} height={56} fill="none" stroke="#d8d5cb" strokeDasharray="3 2" />
      <text x={90} y={210} fontSize="8" fill="#9a9890" fontFamily="IBM Plex Mono, monospace" letterSpacing="1.5">PUBLIC SUBNET</text>
      <Box x={W - 280} y={216} w={180} h={28} label="NAT Gateway" sub="" color="#9a9890" nodeId="nat" />

      {/* private subnet */}
      <rect x={80} y={264} width={W - 160} height={(aiOn ? H - 320 : H - 290)} fill="none" stroke="#d8d5cb" strokeDasharray="3 2" />
      <text x={90} y={280} fontSize="8" fill="#9a9890" fontFamily="IBM Plex Mono, monospace" letterSpacing="1.5">PRIVATE SUBNETS · multi-AZ</text>

      {/* Compute */}
      <g>
        <Box x={120} y={300} w={260} h={90} label={computeLabel} sub={k8s ? 'kubernetes · autoscaled' : fn ? 'functions · concurrency' : 'tasks · fargate'} color={accent} glow nodeId="compute" />
        {k8s && (
          <>
            <circle cx={150} cy={370} r={6} fill={accent} />
            <circle cx={170} cy={370} r={6} fill={accent} />
            <circle cx={190} cy={370} r={6} fill={accent} />
            <circle cx={210} cy={370} r={6} fill={accent} fillOpacity="0.4" />
            <circle cx={230} cy={370} r={6} fill={accent} fillOpacity="0.4" />
            <text x={250} y={374} fontSize="9" fill="#6f6e68" fontFamily="IBM Plex Mono, monospace">pods · hpa</text>
          </>
        )}
      </g>

      {/* Data */}
      {config.database !== 'none' && (
        <Box x={420} y={300} w={240} h={90} label={dbLabel} sub={config.environment === 'prod' ? 'multi-az · encrypted' : 'single-az · encrypted'} color="#60a5fa" glow nodeId="db" />
      )}
      {config.cache === 'redis' && (
        <Box x={690} y={300} w={170} h={42} label="Redis" sub="cache · in-memory" color="#fbbf24" nodeId="cache" />
      )}

      {/* AI tier (only if enabled) */}
      {aiOn && (
        <>
          {/* AI tier label band */}
          <rect x={100} y={aiTierY - 18} width={W - 200} height={1} fill={aiAccent} fillOpacity="0.3" />
          <text x={110} y={aiTierY - 6} fontSize="9" fill={aiAccent} fontFamily="IBM Plex Mono, monospace" letterSpacing="2">AI / AGENT TIER</text>

          {/* Agent service */}
          <Box
            x={120} y={aiTierY} w={220} h={70}
            label={`Agent · ${agentFwName(config)}`}
            sub={`fastapi · ${k8s ? 'pod' : 'service'}`}
            color={aiAccent}
            glow
            nodeId="agent"
          />

          {/* Vector DB */}
          {config.vectorDB !== 'none' && (
            <Box
              x={380} y={aiTierY} w={200} h={70}
              label={vectorDBName(config)}
              sub="vector store · k-NN"
              color={aiAccent}
              nodeId="vector"
            />
          )}

          {/* Inference (managed or self-hosted) */}
          <Box
            x={620} y={aiTierY} w={W - 720} h={70}
            label={aiInferenceName(config)}
            sub={isSelfHostedLLM(config) ? 'gpu · vllm · paged-attn' : 'managed · iam'}
            color={aiAccent}
            glow
            nodeId="llm"
          />

          {/* GPU node group indicator */}
          {needsGPU(config) && (
            <>
              <rect x={620} y={aiTierY + 78} width={W - 720} height={32} fill="#fbfaf7" stroke={aiAccent} strokeWidth={1} strokeDasharray="3 2" />
              <text x={630} y={aiTierY + 92} fontSize="9" fill={aiAccent} fontFamily="IBM Plex Mono, monospace" letterSpacing="1.5">GPU NODE GROUP</text>
              <text x={630} y={aiTierY + 105} fontSize="8" fill="#52524d" fontFamily="IBM Plex Mono, monospace">{(config.gpuType || '').split(' ')[0]} · taint nvidia.com/gpu</text>
            </>
          )}

          {/* LLM observability */}
          {config.llmObservability !== 'none' && (
            <Box
              x={380} y={aiTierY + 80} w={200} h={32}
              label={LLM_OBSERVABILITY.find(o => o.id === config.llmObservability)?.name}
              sub="llm tracing"
              color="#a78bfa"
              nodeId="llmObs"
            />
          )}

          {/* Arrows: compute -> agent, agent -> vector, agent -> llm */}
          <line x1={250} y1={390} x2={250} y2={aiTierY} stroke={aiAccent} strokeWidth="1.5" markerEnd="url(#arrowAI)" />
          {/* animated packet on compute → agent */}
          <circle r="3" fill={accent} style={{ filter: `drop-shadow(0 0 4px ${accentSoft})` }}>
            <animateMotion dur="2.5s" repeatCount="indefinite"
              path={`M 250 390 L 250 ${aiTierY}`} />
          </circle>
          {config.vectorDB !== 'none' && (
            <>
              <line x1={340} y1={aiTierY + 35} x2={380} y2={aiTierY + 35} stroke={aiAccent} strokeWidth="1.5" markerEnd="url(#arrowAI)" />
              <circle r="2.5" fill={aiAccent} style={{ filter: `drop-shadow(0 0 4px ${aiAccent})` }}>
                <animateMotion dur="1.8s" repeatCount="indefinite" begin="0.6s"
                  path={`M 340 ${aiTierY + 35} L 380 ${aiTierY + 35}`} />
              </circle>
            </>
          )}
          <line x1={config.vectorDB !== 'none' ? 580 : 340} y1={aiTierY + 35} x2={620} y2={aiTierY + 35} stroke={aiAccent} strokeWidth="1.5" markerEnd="url(#arrowAI)" />
          <circle r="3" fill={aiAccent} style={{ filter: `drop-shadow(0 0 5px ${aiAccent})` }}>
            <animateMotion dur="2.2s" repeatCount="indefinite" begin="1.1s"
              path={`M ${config.vectorDB !== 'none' ? 580 : 340} ${aiTierY + 35} L 620 ${aiTierY + 35}`} />
          </circle>

          {/* Pulsing aura around the agent service */}
          <rect x={116} y={aiTierY - 4} width={228} height={78} fill="none" stroke={aiAccent} strokeWidth={1} opacity="0.4">
            <animate attributeName="opacity" values="0.4;0.05;0.4" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="x" values="116;112;116" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="y" values={`${aiTierY - 4};${aiTierY - 8};${aiTierY - 4}`} dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="width" values="228;236;228" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="height" values="78;86;78" dur="2.4s" repeatCount="indefinite" />
          </rect>

          {/* AI -> data link if pgvector */}
          {config.vectorDB === 'pgvector' && config.database !== 'none' && (
            <line x1={480} y1={aiTierY} x2={540} y2={395} stroke="#60a5fa" strokeWidth="1" strokeDasharray="2 3" opacity="0.6" />
          )}
        </>
      )}

      {/* Observability + secrets row */}
      <Box x={420} y={aiOn ? H - 90 : 410} w={200} h={42} label={OBSERVABILITY_OPTIONS.find(x=>x.id===config.observability)?.name} sub="metrics · logs · traces" color="#a78bfa" nodeId="obs" />
      {config.secretsManager && (
        <Box x={640} y={aiOn ? H - 90 : 410} w={140} h={42} label="Secrets" sub="kms · rotation" color="#fb7185" nodeId="secrets" />
      )}
      <Box x={120} y={aiOn ? H - 90 : 410} w={280} h={42} label="State · S3 + DynamoDB lock" sub="terraform backend" color="#9a9890" dashed nodeId="state" />

      {/* arrows */}
      <line x1={250} y1={126} x2={250} y2={300} stroke={accent} strokeWidth="1.5" markerEnd="url(#arrow)" strokeDasharray="3 3" />
      {/* animated request packet entering the system */}
      <circle r="3.5" fill={accent} style={{ filter: `drop-shadow(0 0 5px ${accentSoft})` }}>
        <animateMotion dur="2s" repeatCount="indefinite" path="M 250 126 L 250 300" />
      </circle>
      {config.database !== 'none' && (
        <>
          <line x1={380} y1={345} x2={420} y2={345} stroke={accent} strokeWidth="1.5" markerEnd="url(#arrow)" />
          <circle r="2.5" fill={accent} style={{ filter: `drop-shadow(0 0 4px ${accentSoft})` }}>
            <animateMotion dur="1.6s" repeatCount="indefinite" begin="0.4s" path="M 380 345 L 420 345" />
          </circle>
        </>
      )}
      {config.cache === 'redis' && (
        <>
          <line x1={380} y1={355} x2={690} y2={320} stroke="#fbbf24" strokeWidth="1" strokeDasharray="2 3" />
          <circle r="2" fill="#fbbf24" style={{ filter: 'drop-shadow(0 0 3px #fbbf24)' }}>
            <animateMotion dur="2s" repeatCount="indefinite" begin="0.9s" path="M 380 355 L 690 320" />
          </circle>
        </>
      )}
      {/* AI tier — agent → llm-obs flow when both enabled */}
      {aiOn && config.llmObservability !== 'none' && (
        <>
          <line x1={340} y1={aiTierY + 60} x2={380} y2={aiTierY + 90} stroke="#a78bfa" strokeWidth="1" strokeDasharray="2 3" opacity="0.6" />
          <circle r="2" fill="#a78bfa" style={{ filter: 'drop-shadow(0 0 3px #a78bfa)' }}>
            <animateMotion dur="2.4s" repeatCount="indefinite" begin="1.6s" path={`M 340 ${aiTierY + 60} L 380 ${aiTierY + 90}`} />
          </circle>
        </>
      )}

      {/* ── DE tier ─────────────────────────────────────────── */}
      {deOn && (() => {
        const deBoxes = [
          { id: 'de-stream', x: 40,  label: deStreamingName(config),   sub: 'streaming · ingest', color: '#fbbf24' },
          { id: 'de-proc',   x: 220, label: deStreamProcName(config),  sub: 'stream · processing', color: '#fb923c' },
          { id: 'de-olap',   x: 400, label: deOlapName(config),         sub: 'olap · sub-second',  color: '#60a5fa' },
          { id: 'de-lake',   x: 580, label: deLakeTableName(config),    sub: 'lake · iceberg',     color: '#a78bfa' },
          { id: 'de-query',  x: 760, label: deQueryEngineName(config),  sub: 'federated · sql',    color: '#86efac' },
        ];
        // Filter out 'none' selections — they break the visual story
        const active = deBoxes.filter((b, i) => {
          if (i === 0) return config.deStreaming   !== 'none';
          if (i === 1) return config.deStreamProc  !== 'none';
          if (i === 2) return config.deOlap        !== 'none';
          if (i === 3) return config.deLakeTable   !== 'none';
          if (i === 4) return config.deQueryEngine !== 'none';
          return true;
        });
        return (
          <g>
            {/* Tier label */}
            <text x={20} y={deTierY - 4} fontSize="10" fill="#6f6e68" fontFamily="IBM Plex Mono, monospace" letterSpacing="2">
              ── DATA ENGINEERING ──
            </text>
            {/* Tier band background */}
            <rect x={20} y={deTierY} width={W - 40} height={170} fill="none" stroke="#fbbf24" strokeWidth="1" strokeDasharray="6 4" opacity="0.4" />
            {/* Compute -> streaming arrow (apps publish events) */}
            <line x1={250} y1={390} x2={130} y2={deTierY + 30} stroke="#fbbf24" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
            <circle r="2.5" fill="#fbbf24" style={{ filter: 'drop-shadow(0 0 3px #fbbf24)' }}>
              <animateMotion dur="3s" repeatCount="indefinite" path={`M 250 390 L 130 ${deTierY + 30}`} />
            </circle>
            {/* Boxes for each DE component */}
            {active.map(b => (
              <g key={b.id}
                onMouseEnter={() => setHoveredNode(b.id)}
                onMouseLeave={() => setHoveredNode(null)}
                style={{ cursor: 'pointer' }}>
                <rect x={b.x} y={deTierY + 30} width={150} height={80} fill="#ffffff" stroke={b.color} strokeWidth={hoveredNode === b.id ? 2 : 1}
                  style={{ filter: `drop-shadow(0 0 ${hoveredNode === b.id ? 12 : 6}px ${b.color}${hoveredNode === b.id ? 'cc' : '60'})`, transition: 'all 0.2s ease' }} />
                <text x={b.x + 12} y={deTierY + 50} fontSize="9" fill="#9a9890" fontFamily="IBM Plex Mono, monospace" textTransform="uppercase" letterSpacing="2">
                  {b.sub}
                </text>
                <text x={b.x + 12} y={deTierY + 72} fontSize="13" fill="#1a1a17" fontFamily="IBM Plex Sans, sans-serif" fontWeight="600">
                  {b.label}
                </text>
              </g>
            ))}
            {/* Connecting arrows + flow dots between boxes */}
            {active.slice(0, -1).map((b, i) => {
              const next = active[i + 1];
              const x1 = b.x + 150, x2 = next.x;
              return (
                <g key={`flow-${i}`}>
                  <line x1={x1} y1={deTierY + 70} x2={x2} y2={deTierY + 70} stroke="#6f6e68" strokeWidth="1" markerEnd="url(#arrow)" />
                  <circle r="3" fill={b.color} style={{ filter: `drop-shadow(0 0 4px ${b.color})` }}>
                    <animateMotion dur={`${2.2 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} path={`M ${x1} ${deTierY + 70} L ${x2} ${deTierY + 70}`} />
                  </circle>
                </g>
              );
            })}
            {/* Wide-column DB callout below (operational store, off the main flow) */}
            {config.deWideCol !== 'none' && (
              <g>
                <rect x={W / 2 - 75} y={deTierY + 130} width={150} height={32} fill="#ffffff" stroke="#a78bfa" strokeWidth="1" opacity="0.9" />
                <text x={W / 2 - 65} y={deTierY + 150} fontSize="11" fill="#1a1a17" fontFamily="IBM Plex Mono, monospace">
                  {deWideColName(config)} · ops
                </text>
                <line x1={active[1]?.x + 75 || 295} y1={deTierY + 110} x2={W / 2} y2={deTierY + 130} stroke="#a78bfa" strokeWidth="1" strokeDasharray="2 3" />
              </g>
            )}
          </g>
        );
      })()}
    </svg>
    {/* ── Live request-rate badge overlay ──────────────────── */}
    <div className="absolute top-2 right-2 flex items-center gap-2 px-3 py-1.5 pointer-events-none"
      style={{ background: 'rgba(251,250,247,0.88)', border: '1px solid #d8d5cb', backdropFilter: 'blur(4px)' }}>
      <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: accentBg }} />
      <span className="text-[9px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>req/s</span>
      <span className="text-[12px] tabular-nums font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
        {tickRate.toFixed(1)}
      </span>
    </div>
    {/* ── Hover-to-inspect tooltip ─────────────────────────── */}
    {hoveredNode && nodeDetails[hoveredNode] && (
      <div className="absolute bottom-2 left-2 right-2 px-4 py-2.5 pointer-events-none stream-in"
        style={{ background: 'rgba(251,250,247,0.95)', border: '1px solid #d8d5cb', backdropFilter: 'blur(6px)' }}>
        <div className="flex items-baseline gap-3">
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            inspect:
          </span>
          <span className="text-[12px] font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
            {nodeDetails[hoveredNode].title}
          </span>
          <span className="text-[11px]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            {nodeDetails[hoveredNode].detail}
          </span>
        </div>
      </div>
    )}
    </div>
  );
}

function NetworkTopologySVG({ config }) {
  const W = 760, H = 200;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto border" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
      <defs>
        <pattern id="grid2" width="16" height="16" patternUnits="userSpaceOnUse">
          <path d="M 16 0 L 0 0 0 16" fill="none" stroke="#ebe9e1" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width={W} height={H} fill="url(#grid2)" />

      <rect x={20} y={30} width={W - 40} height={H - 60} fill="none" stroke="#9a9890" />
      <text x={32} y={48} fontSize="9" fill="#6f6e68" fontFamily="IBM Plex Mono, monospace" letterSpacing="1.5">VPC · 10.0.0.0/16</text>

      {[0, 1, 2].slice(0, config.environment === 'prod' ? 3 : 2).map((i, idx, arr) => {
        const w = (W - 80) / arr.length - 16;
        const x = 40 + idx * (w + 16);
        return (
          <g key={i}>
            <text x={x} y={66} fontSize="8" fill="#9a9890" fontFamily="IBM Plex Mono, monospace" letterSpacing="1.5">AZ-{i + 1}</text>
            <rect x={x} y={72} width={w} height={28} fill="#fbfaf7" stroke="#d8d5cb" strokeDasharray="2 2" />
            <text x={x + 8} y={90} fontSize="9" fill="#52524d" fontFamily="IBM Plex Mono, monospace">public · 10.0.{i}.0/24</text>

            <rect x={x} y={108} width={w} height={32} fill="#fbfaf7" stroke={accent} />
            <text x={x + 8} y={128} fontSize="9" fill="#1a1a17" fontFamily="IBM Plex Mono, monospace">private · 10.0.{i + 8}.0/24</text>
          </g>
        );
      })}

      <text x={32} y={H - 14} fontSize="9" fill="#9a9890" fontFamily="IBM Plex Mono, monospace">
        flow logs → cloudwatch · {config.environment === 'prod' ? '90' : '14'}-day retention
      </text>
    </svg>
  );
}

function PipelineDiagram({ config }) {
  const stages = [
    { label: 'lint',     desc: 'fmt · validate · tflint · trivy' },
    isContainer(config.compute) && { label: 'build', desc: 'docker · scan · push to ECR' },
    { label: 'plan',     desc: 'terraform plan → artifact' },
    { label: 'gate',     desc: config.environment === 'prod' ? 'manual approval' : 'auto' },
    { label: 'apply',    desc: 'terraform apply tfplan' },
    isK8s(config.compute) && { label: 'deploy',   desc: 'kubectl rollout · status' },
    { label: 'smoke',    desc: '/healthz · slo check' },
  ].filter(Boolean);

  return (
    <div className="border p-5 overflow-x-auto" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
      <div className="flex items-stretch gap-2 min-w-max">
        {stages.map((s, i) => (
          <React.Fragment key={s.label}>
            <div
              className="px-4 py-3 min-w-[140px]"
              style={{ background: '#fbfaf7', border: `1px solid ${i === 3 && config.environment === 'prod' ? '#fbbf24' : '#d8d5cb'}` }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: i === 3 && config.environment === 'prod' ? '#fbbf24' : accent }} />
                <span className="text-[11px] uppercase tracking-wider" style={{ color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {s.label}
                </span>
              </div>
              <div className="text-[10px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{s.desc}</div>
            </div>
            {i < stages.length - 1 && (
              <div className="flex items-center" style={{ color: '#d8d5cb' }}>
                <ChevronRight size={14} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// =============================================================
// ADMIN DASHBOARD MOCKUP
// =============================================================

function AdminDashboard({ config }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1500);
    return () => clearInterval(id);
  }, []);

  const aiOn = isAIEnabled(config);
  const baseServices = [
    { name: 'web',     replicas: config.environment === 'prod' ? '6/6' : '2/2', status: 'healthy', latency: 42 + (tick % 5) },
    { name: 'api',     replicas: config.environment === 'prod' ? '4/4' : '2/2', status: 'healthy', latency: 89 + (tick % 7) },
    { name: 'worker',  replicas: config.environment === 'prod' ? '3/3' : '1/1', status: 'healthy', latency: null },
    { name: 'scheduler', replicas: '1/1', status: 'degraded', latency: null },
  ];
  const aiServices = aiOn ? [
    { name: 'agent',   replicas: config.environment === 'prod' ? '3/3' : '1/1', status: 'healthy', latency: 312 + (tick % 18) },
    ...(isSelfHostedLLM(config) ? [{ name: 'vllm',    replicas: config.environment === 'prod' ? '2/2' : '1/1', status: 'healthy', latency: 1840 + (tick % 90) }] : []),
  ] : [];
  const services = [...baseServices, ...aiServices];

  return (
    <div className="border" style={{ borderColor: '#d8d5cb', background: '#ffffff' }}>
      {/* Dashboard header */}
      <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#d8d5cb' }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full pulse-dot" style={{ background: accentBg }} />
            <span className="text-[12px] font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{config.projectName}</span>
            <span className="text-[10px]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>· {config.environment} · {config.region}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px]" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
          <span>refreshed {tick}s ago</span>
          <RefreshCw size={11} className="animate-spin" style={{ animationDuration: '4s' }} />
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-b" style={{ borderColor: '#d8d5cb' }}>
        {[
          { label: 'requests / min', value: (12_400 + tick * 7).toLocaleString(), trend: '+2.4%' },
          { label: 'p99 latency',    value: `${184 + (tick % 12)}ms`, trend: '−6.8%' },
          { label: 'error rate',     value: '0.014%', trend: '−0.002' },
          { label: 'monthly burn',   value: `$${estimateCost(config).toLocaleString()}`, trend: '~' },
        ].map(s => (
          <div key={s.label} className="px-5 py-4 border-r last:border-r-0" style={{ borderColor: '#d8d5cb' }}>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>{s.label}</div>
            <div className="flex items-baseline gap-2 mt-1.5">
              <span className="text-[20px] font-semibold tabular-nums" style={{ color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>{s.value}</span>
              <span className="text-[10px]" style={{ color: s.trend.startsWith('+') ? accent : s.trend.startsWith('−') ? '#fb7185' : '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                {s.trend}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* AI/LLM stats row (only when AI enabled) */}
      {aiOn && (
        <div className="grid grid-cols-2 md:grid-cols-4 border-b" style={{ borderColor: '#d8d5cb', background: '#fbfaf7' }}>
          {[
            { label: 'tokens / sec',     value: (4_280 + tick * 11).toLocaleString(), trend: '+5.1%', accent: '#f0abfc' },
            { label: '$ / 1k tokens',    value: isSelfHostedLLM(config) ? '$0.0008' : config.aiInference === 'bedrock' ? '$0.0030' : config.aiInference === 'aoai' ? '$0.0050' : '$0.0040', trend: '~', accent: '#f0abfc' },
            { label: 'eval pass rate',   value: `${(91.2 + (tick % 4) * 0.3).toFixed(1)}%`, trend: '+0.8%', accent: '#f0abfc' },
            { label: 'cache hit rate',   value: `${(34 + (tick % 8)).toFixed(0)}%`, trend: '+2.1%', accent: '#f0abfc' },
          ].map(s => (
            <div key={s.label} className="px-5 py-3 border-r last:border-r-0" style={{ borderColor: '#d8d5cb' }}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
                <Sparkles size={9} style={{ color: s.accent }} />
                <span>{s.label}</span>
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-[16px] font-semibold tabular-nums" style={{ color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>{s.value}</span>
                <span className="text-[10px]" style={{ color: s.trend.startsWith('+') ? accent : s.trend.startsWith('−') ? '#fb7185' : '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {s.trend}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Services + chart */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px]">
        <div className="p-5 border-r" style={{ borderColor: '#d8d5cb' }}>
          <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            services
          </div>
          <div className="space-y-1">
            {services.map(s => (
              <div key={s.name} className="grid grid-cols-[1fr_60px_80px_60px] gap-2 items-center px-3 py-2.5"
                style={{ background: '#fbfaf7', border: '1px solid #ebe9e1' }}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: s.status === 'healthy' ? accent : '#fbbf24' }}
                  />
                  <span className="text-[12px] truncate" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{s.name}</span>
                </div>
                <span className="text-[10px] tabular-nums" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>{s.replicas}</span>
                <Pill color={s.status === 'healthy' ? accent : '#fbbf24'}>{s.status}</Pill>
                <span className="text-[10px] text-right tabular-nums" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {s.latency ? `${s.latency}ms` : '—'}
                </span>
              </div>
            ))}
          </div>

          {/* Logs */}
          <div className="text-[10px] uppercase tracking-[0.2em] mt-6 mb-3" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
            recent events · {OBSERVABILITY_OPTIONS.find(x=>x.id===config.observability)?.name}
          </div>
          <div className="text-[11px] leading-[1.85] p-3" style={{ background: '#fbfaf7', border: '1px solid #ebe9e1', fontFamily: 'IBM Plex Mono, monospace' }}>
            {[
              { t: '11:42:01', lvl: 'info',  msg: `Deployment ${fmt(config.projectName)}-app rolled out · 6/6 ready` },
              ...(aiOn ? [
                { t: '11:41:50', lvl: 'info',  msg: `agent · /chat p95 ${312 + (tick % 18)}ms · ${aiInferenceName(config).toLowerCase()}` },
              ] : []),
              { t: '11:41:34', lvl: 'info',  msg: 'Pulled image sha256:8a1c…3d9e' },
              ...(aiOn && config.llmObservability !== 'none' ? [
                { t: '11:40:12', lvl: 'info',  msg: `evals · faithfulness 0.93 · answer-rel 0.87 · pass` },
              ] : []),
              { t: '11:38:12', lvl: 'warn',  msg: 'scheduler · liveness probe slow (2.4s)' },
              { t: '11:32:07', lvl: 'info',  msg: 'HPA scaled api 3→4 (CPU 78%)' },
              ...(isSelfHostedLLM(config) ? [
                { t: '11:30:18', lvl: 'info',  msg: `vllm · loaded ${OPEN_MODELS[0].split('/')[1]} · gpu_util 87%` },
              ] : []),
              { t: '11:28:55', lvl: 'info',  msg: `terraform apply complete · ${estimateResources(config)} resources` },
            ].map((e, i) => (
              <div key={i} className="grid grid-cols-[60px_50px_1fr] gap-3">
                <span style={{ color: '#9a9890' }}>{e.t}</span>
                <span style={{ color: e.lvl === 'warn' ? '#fbbf24' : e.lvl === 'error' ? '#fb7185' : accent }}>{e.lvl}</span>
                <span style={{ color: '#52524d' }}>{e.msg}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right rail: tiny sparkline + actions */}
        <div className="p-5 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
              traffic · 1h
            </div>
            <Sparkline tick={tick} />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
              actions
            </div>
            <div className="space-y-1.5">
              {[
                { icon: Rocket, label: 'Deploy from main' },
                { icon: RefreshCw, label: 'Rollback last' },
                { icon: ShieldCheck, label: 'Run policy scan' },
                { icon: Radio, label: 'Open runbook' },
              ].map(a => {
                const Icon = a.icon;
                return (
                  <button key={a.label}
                    className="w-full flex items-center gap-2.5 px-3 py-2 transition-all hover:bg-[#f4f3ee]"
                    style={{ background: '#fbfaf7', border: '1px solid #ebe9e1', fontFamily: 'IBM Plex Mono, monospace' }}>
                    <Icon size={12} style={{ color: '#52524d' }} />
                    <span className="text-[11px]" style={{ color: '#d8d5cb' }}>{a.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Sparkline({ tick }) {
  const W = 240, H = 80;
  const points = [];
  for (let i = 0; i < 40; i++) {
    const v = 0.4 + 0.4 * Math.sin((i + tick) * 0.4) + 0.2 * Math.sin((i + tick) * 0.13);
    points.push([i / 39 * W, H - v * (H - 8) - 4]);
  }
  const path = points.map(([x, y], i) => (i === 0 ? 'M' : 'L') + x.toFixed(1) + ',' + y.toFixed(1)).join(' ');
  const fill = path + ` L ${W} ${H} L 0 ${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ background: '#fbfaf7', border: '1px solid #ebe9e1' }}>
      <path d={fill} fill={accent} fillOpacity="0.08" />
      <path d={path} fill="none" stroke={accent} strokeWidth="1.5" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="3" fill={accent}>
        <animate attributeName="r" values="3;5;3" dur="1.5s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// =============================================================
// STACK MANIFEST sidebar
// =============================================================

function StackManifest({ config, monthlyCost }) {
  const rolloutPlan = config.rolloutEnabled ? buildRolloutPlan(config) : null;
  const posture = computeSecurityPosture(config);
  const rows = [
    ['posture',   `${posture.grade.letter} · ${posture.total}/${posture.max}`],
    ['provider',  config.provider.toUpperCase()],
    ['region',    config.region],
    ['env',       config.environment],
    ['compute',   computeName(config)],
    ['data',      config.database === 'none' ? '—' : dbName(config)],
    ['cache',     config.cache === 'none' ? '—' : config.cache],
    ['cdn',       config.cdn ? 'enabled' : 'off'],
    ['waf',       config.waf ? 'enabled' : 'off'],
    ['secrets',   config.secretsManager ? 'enabled' : 'off'],
    ['observ.',   OBSERVABILITY_OPTIONS.find(x=>x.id===config.observability)?.name],
    ['ci/cd',     CICD_OPTIONS.find(x=>x.id===config.cicd)?.name],
    ...(isAIEnabled(config) ? [
      ['─── ai ───', ''],
      ['ai infer',  aiInferenceName(config)],
      ['vector',    vectorDBName(config)],
      ['agents',    agentFwName(config)],
      ['llm obs',   LLM_OBSERVABILITY.find(x=>x.id===config.llmObservability)?.name],
      ...(needsGPU(config) ? [['gpu', (config.gpuType || '').split(' ')[0]]] : []),
    ] : []),
    ...(rolloutPlan ? [
      ['─── rollout ───', ''],
      ['tracker',  ISSUE_TRACKERS.find(x=>x.id===config.issueTracker)?.name],
      ['tickets',  String(rolloutPlan.tickets.length)],
      ['waves',    String(rolloutPlan.waves.length)],
      ['effort',   `${rolloutPlan.tickets.reduce((s, t) => s + t.effort, 0)} pts`],
    ] : []),
    ...(config.directoryEnabled && isAIEnabled(config) ? (() => {
      const score = computeAgenticness(config);
      return [
        ['─── directory ───', ''],
        ['category',  AGENTIC_CATEGORIES.find(c => c.id === config.directoryCategory)?.name],
        ['autonomy',  AUTONOMY_LEVELS.find(a => a.id === config.directoryAutonomy)?.tag],
        ['mcp',       config.directoryMcpEnabled ? 'enabled' : 'off'],
        ['agentic.',  `${score.total}/32 · ${score.level.level}`],
      ];
    })() : []),
    ...(isDEOn(config) ? [
      ['─── data eng ───', ''],
      ['streaming', deStreamingName(config)],
      ['olap',      deOlapName(config)],
      ['stream proc', deStreamProcName(config)],
      ['wide-col',  deWideColName(config)],
      ['lake',      deLakeTableName(config)],
      ['query',     deQueryEngineName(config)],
      ['orchestr.', deOrchestrationName(config)],
      ['volume',    config.deDataVolume],
    ] : []),
    ['─── security ───', ''],
    ['firewall',  isAIEnabled(config) ? promptFirewallName(config) : '—'],
    ['pii',       isAIEnabled(config) ? piiDetectorName(config) : '—'],
    ['scans',     `${(config.scanTools || []).length} tools`],
    ['frameworks',`${(config.complianceFrameworks || []).length} selected`],
    ['alerts',    `${(config.alertChannels || []).length} channels`],
  ];

  return (
    <div className="grid-glow" style={{ background: '#ffffff', border: '1px solid #d8d5cb' }}>
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#d8d5cb' }}>
        <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
          stack manifest
        </span>
        <span className="text-[10px] tabular-nums" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
          {Object.keys(rows).length} keys
        </span>
      </div>
      <div className="p-4 space-y-1.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[80px_1fr] gap-3 text-[11px] py-1 border-b last:border-b-0" style={{ borderColor: '#ebe9e1' }}>
            <span style={{ color: '#9a9890' }}>{k}</span>
            <span className="truncate" style={{ color: '#1a1a17' }}>{v}</span>
          </div>
        ))}
      </div>
      <div className="px-4 py-3 border-t" style={{ borderColor: '#d8d5cb', background: '#fbfaf7' }}>
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>
            est. monthly
          </span>
          <span className="text-[18px] font-semibold tabular-nums" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
            ${monthlyCost.toLocaleString()}
          </span>
        </div>
        <div className="text-[10px] mt-1" style={{ color: '#6f6e68', fontFamily: 'IBM Plex Mono, monospace' }}>
          on-demand pricing · excludes egress
        </div>
      </div>
    </div>
  );
}

// =============================================================
// utility
// =============================================================

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#52524d', fontFamily: 'IBM Plex Mono, monospace' }}>
          {label}
        </label>
      </div>
      {children}
      {hint && <p className="text-[10px] mt-1.5" style={{ color: '#9a9890', fontFamily: 'IBM Plex Mono, monospace' }}>{hint}</p>}
    </div>
  );
}

// =============================================================
// =============================================================
// NEW TOP-LEVEL APP — hosted product shell
// Routes between landing → auth → dashboard → generator
// =============================================================
// =============================================================

export default function App() {
  const [route, setRoute] = useState('landing');
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [currentDesign, setCurrentDesign] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    // Bootstrap: check localStorage first (covers email/password sessions
    // AND persisted Google sessions where we cached the user record).
    (async () => {
      const raw = await Store.get('auth:current_user');
      if (raw) {
        try {
          const u = JSON.parse(raw);
          setUser(u);
          setRoute('dashboard');
          await refreshDesigns(u);
        } catch {}
      }
      setAuthChecking(false);
    })();
  }, []);

  // Firebase auth state listener — fires on Google sign-in/out and on
  // page load if the user already has a valid Firebase session.
  // Plan is now sourced from Firestore (users/{uid}.plan) instead of
  // localStorage. localStorage stays as a cache so the UI doesn't flash
  // a stale plan on first paint, but Firestore always wins.
  useEffect(() => {
    let planSnapshotUnsub = null;

    const authUnsub = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      // Tear down any existing plan snapshot listener
      if (planSnapshotUnsub) { planSnapshotUnsub(); planSnapshotUnsub = null; }

      if (!fbUser) return;  // Sign-out path is handled by handleSignOut

      // Read or create the user's Firestore profile doc.
      const userRef = doc(firestoreDb, 'users', fbUser.uid);
      let plan = 'free';
      let createdAt = new Date().toISOString();
      try {
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          plan = data.plan || 'free';
          createdAt = data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt || createdAt;
        } else {
          // First-time sign-in — create the profile at plan=free.
          // The rules enforce: plan must be "free" and planSource must be
          // "signup-default" on initial create. Anything else gets rejected.
          await setDoc(userRef, {
            plan:          'free',
            planSource:    'signup-default',
            planUpdatedAt: serverTimestamp(),
            email:         fbUser.email,
            displayName:   fbUser.displayName || fbUser.email?.split('@')[0] || 'user',
            createdAt:     serverTimestamp(),
          });
        }
      } catch (err) {
        // Firestore unavailable / rules misconfigured — fall back to local
        // cache so the user can still use the app, just without the live
        // plan source. We log so the issue is visible to whoever's deploying.
        console.warn('[stax] Firestore profile read failed, falling back to localStorage:', err?.code || err?.message);
        const existing = await Store.get(`users:${fbUser.email}`);
        if (existing) {
          try {
            const stored = JSON.parse(existing);
            plan = stored.plan || 'free';
            createdAt = stored.createdAt || createdAt;
          } catch {}
        }
      }

      const id = `user_google_${fbUser.uid}`;
      const buildUser = (planValue) => ({
        id,
        email: fbUser.email,
        name: fbUser.displayName || fbUser.email?.split('@')[0] || 'user',
        plan: planValue,
        createdAt,
        authProvider: 'google',
        photoURL: fbUser.photoURL || null,
      });

      const initialUser = buildUser(plan);
      // Cache for next reload so we don't flash a stale plan
      await Store.set(`users:${fbUser.email}`, JSON.stringify(initialUser));
      await Store.set('auth:current_user', JSON.stringify(initialUser));
      setUser(initialUser);
      setRoute(prev => prev === 'signin' || prev === 'signup' || prev === 'landing' ? 'dashboard' : prev);
      await refreshDesigns(initialUser);

      // Live listener: if an admin flips plan in Firebase Console, the UI
      // updates without a refresh. Same listener covers Stripe webhook
      // updates when that lands in Stage 2.
      try {
        planSnapshotUnsub = onSnapshot(userRef, (snap) => {
          if (!snap.exists()) return;
          const newPlan = snap.data()?.plan || 'free';
          setUser(prev => {
            if (!prev || prev.plan === newPlan) return prev;
            const updated = { ...prev, plan: newPlan };
            // Update cache too so a quick refresh doesn't show stale plan
            Store.set('auth:current_user', JSON.stringify(updated)).catch(() => {});
            Store.set(`users:${fbUser.email}`, JSON.stringify(updated)).catch(() => {});
            return updated;
          });
        }, (err) => {
          console.warn('[stax] Firestore plan listener error:', err?.code || err?.message);
        });
      } catch (err) {
        console.warn('[stax] Could not subscribe to plan changes:', err?.code || err?.message);
      }
    });

    return () => {
      authUnsub();
      if (planSnapshotUnsub) planSnapshotUnsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshDesigns(u) {
    if (!u) return;
    const idx = await DesignStore.list(u);
    setDesigns(idx);
  }

  async function handleSignUp({ email, password, name }) {
    setAuthError('');
    if (!email || !password) { setAuthError('Email and password required'); return; }
    if (password.length < 8) { setAuthError('Password must be ≥ 8 characters'); return; }
    const existing = await Store.get(`users:${email}`);
    if (existing) { setAuthError('Account already exists. Sign in instead.'); return; }
    const id = `user_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const u = { id, email, name: name || email.split('@')[0], plan: 'free', createdAt: new Date().toISOString() };
    await Store.set(`users:${email}`, JSON.stringify({ ...u, _passhint: btoa(password).slice(0, 16) }));
    await Store.set('auth:current_user', JSON.stringify(u));
    setUser(u);
    setRoute('dashboard');
    await refreshDesigns(u);
  }

  async function handleSignIn({ email, password }) {
    setAuthError('');
    const raw = await Store.get(`users:${email}`);
    if (!raw) { setAuthError('No account found for that email'); return; }
    try {
      const stored = JSON.parse(raw);
      if (stored._passhint !== btoa(password || '').slice(0, 16)) {
        setAuthError('Incorrect password'); return;
      }
      const u = { id: stored.id, email: stored.email, name: stored.name, plan: stored.plan, createdAt: stored.createdAt };
      await Store.set('auth:current_user', JSON.stringify(u));
      setUser(u);
      setRoute('dashboard');
      await refreshDesigns(u);
    } catch { setAuthError('Account data corrupted'); }
  }

  async function handleGoogleSignIn() {
    setAuthError('');
    try {
      // The popup result + the onAuthStateChanged listener above both fire;
      // the listener handles user object construction so we just await the
      // popup so we can surface errors here.
      await signInWithPopup(firebaseAuth, googleProvider);
      // The listener will route to dashboard.
    } catch (err) {
      // Friendly errors for the most common Firebase Auth misconfigurations.
      const code = err?.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        // User dismissed the popup — silent, not an error.
        return;
      }
      if (code === 'auth/unauthorized-domain') {
        setAuthError('This domain is not authorised in Firebase Console → Auth → Settings → Authorized domains. Add it and try again.');
        return;
      }
      if (code === 'auth/operation-not-allowed') {
        setAuthError('Google sign-in is not enabled in Firebase Console → Auth → Sign-in method. Enable Google and try again.');
        return;
      }
      if (code === 'auth/popup-blocked') {
        setAuthError('Browser blocked the popup. Allow popups for this site and try again.');
        return;
      }
      setAuthError(err?.message || 'Google sign-in failed');
    }
  }

  async function handleSignOut() {
    // Sign out of Firebase too if the current session was Google-backed.
    // Safe to call unconditionally — silently no-ops if no Firebase session.
    try { await firebaseSignOut(firebaseAuth); } catch {}
    await Store.delete('auth:current_user');
    setUser(null);
    setCurrentDesign(null);
    setDesigns([]);
    setRoute('landing');
  }

  async function handleUpgrade(targetPlan) {
    if (!user) return;

    // Plan tracking lives in Firestore, which requires a Firebase Auth
    // session (Google sign-in). localStorage-only email/password users
    // can't have a server-tracked plan — ask them to switch.
    if (user.authProvider !== 'google' && targetPlan !== 'free') {
      const ok = window.confirm(
        'Paid plans require signing in with Google.\n\n' +
        'The email/password option is for local demos only — your plan\n' +
        'cannot be tracked across devices or linked to a payment.\n\n' +
        'Sign out and sign back in with Google?'
      );
      if (ok) {
        await handleSignOut();
        setRoute('signin');
      }
      return;
    }

    // Free → downgrade is the only client-controllable transition.
    // Anything else requires payment (Pro) or contract (Team), and
    // both are gated server-side by Firestore rules now.
    if (targetPlan === 'free') {
      // No-op for now — Firestore rules don't allow client-side plan
      // changes regardless. Future Stripe webhook will set this on
      // subscription cancellation.
      return;
    }

    // Team — annual-only / contract-based. Route to sales.
    if (targetPlan === 'team') {
      const subject = encodeURIComponent('STAX Team plan inquiry');
      const body = encodeURIComponent(
        `Hi,\n\nI'd like to talk about the STAX Team plan ($4,800/yr · annual).\n\n` +
        `Current account: ${user.email}\n` +
        `Team size:\n` +
        `Compliance frameworks of interest:\n` +
        `Use case:\n\nThanks.`
      );
      window.location.href = `mailto:sales@stax.dev?subject=${subject}&body=${body}`;
      return;
    }

    // Pro — Stripe Checkout will handle this in Stage 2. For now,
    // route to a manual upgrade request so we can flip the plan in
    // Firebase Console while the payment flow is being built.
    if (targetPlan === 'pro') {
      const subject = encodeURIComponent('STAX Pro plan upgrade');
      const body = encodeURIComponent(
        `Hi,\n\nI'd like to upgrade to STAX Pro ($29/mo).\n\n` +
        `Current account: ${user.email}\n\n` +
        `(Stripe self-serve checkout is being wired — for now this is\n` +
        `a manual flow. We'll send a payment link by reply.)\n\nThanks.`
      );
      window.location.href = `mailto:sales@stax.dev?subject=${subject}&body=${body}`;
      return;
    }
  }

  async function handleSaveDesign(design) {
    if (!user) return;
    const id = design.id || `design_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const record = {
      id,
      name: design.name || 'untitled design',
      config: design.config,
      updatedAt: new Date().toISOString(),
      createdAt: design.createdAt || new Date().toISOString(),
    };
    const plan = PLAN_FEATURES[user.plan] || PLAN_FEATURES.free;
    const existingIdx = designs.findIndex(d => d.id === id);
    if (existingIdx === -1 && plan.designLimit !== -1 && designs.length >= plan.designLimit) {
      throw new Error(`design limit reached (${plan.designLimit}). upgrade to save more.`);
    }
    // Persist (DesignStore picks Firestore for Google users, localStorage otherwise)
    const saved = await DesignStore.save(user, record);
    // Mini-summary kept in index — used for filtering/sorting without loading full config
    const idxEntry = {
      id: saved.id,
      name: saved.name,
      updatedAt: saved.updatedAt,
      createdAt: saved.createdAt,
      config: _summaryOf(saved.config),
    };
    const newIdx = existingIdx >= 0
      ? designs.map(d => d.id === id ? idxEntry : d)
      : [...designs, idxEntry];
    await DesignStore.writeIndex(user, newIdx);
    setDesigns(newIdx);
    if (!currentDesign || currentDesign.id !== id) {
      setCurrentDesign(saved);
    }
    return saved;
  }

  async function handleLoadDesign(id) {
    if (!user) return;
    const record = await DesignStore.load(user, id);
    if (!record) return;
    setCurrentDesign(record);
    setRoute('app');
  }

  async function handleDeleteDesign(id) {
    if (!user) return;
    await DesignStore.remove(user, id);
    const newIdx = designs.filter(d => d.id !== id);
    await DesignStore.writeIndex(user, newIdx);
    setDesigns(newIdx);
    if (currentDesign?.id === id) setCurrentDesign(null);
  }

  async function handleDuplicateDesign(id) {
    if (!user) return;
    const planF = PLAN_FEATURES[user.plan] || PLAN_FEATURES.free;
    if (planF.designLimit !== -1 && designs.length >= planF.designLimit) {
      // Hit limit — bounce to pricing
      setRoute('pricing');
      return;
    }
    const orig = await DesignStore.load(user, id);
    if (!orig) return;
    const newId = `design_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const copy = {
      id: newId,
      name: `${orig.name} (copy)`,
      config: orig.config,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };
    const saved = await DesignStore.save(user, copy);
    const idxEntry = {
      id: saved.id,
      name: saved.name,
      updatedAt: saved.updatedAt,
      createdAt: saved.createdAt,
      config: _summaryOf(saved.config),
    };
    const newIdx = [...designs, idxEntry];
    await DesignStore.writeIndex(user, newIdx);
    setDesigns(newIdx);
  }

  function handleNewDesign() {
    setCurrentDesign(null);
    setRoute('app');
  }

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#fbfaf7' }}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 flex items-center justify-center animate-pulse" style={{ background: accentBg }}>
            <Hexagon size={14} strokeWidth={2.5} style={{ color: '#1a1a17' }} fill="#1a1a17" />
          </div>
          <span className="text-[15px] tracking-[0.2em]" style={{ color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace' }}>STAX</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <GlobalStyles />
      {route === 'landing' && (
        <Landing
          onGetStarted={() => setRoute(user ? 'dashboard' : 'signup')}
          onSignIn={() => setRoute('signin')}
          onPricing={() => setRoute('pricing')}
          user={user}
          nav={setRoute}
        />
      )}
      {route === 'signin' && (
        <AuthScreen mode="signin" error={authError} onSubmit={handleSignIn}
          onGoogleSignIn={handleGoogleSignIn}
          onSwitchMode={() => { setAuthError(''); setRoute('signup'); }}
          onBack={() => { setAuthError(''); setRoute('landing'); }} />
      )}
      {route === 'signup' && (
        <AuthScreen mode="signup" error={authError} onSubmit={handleSignUp}
          onGoogleSignIn={handleGoogleSignIn}
          onSwitchMode={() => { setAuthError(''); setRoute('signin'); }}
          onBack={() => { setAuthError(''); setRoute('landing'); }} />
      )}
      {route === 'dashboard' && user && (
        <Dashboard user={user} designs={designs} plan={user.plan}
          onOpenDesign={handleLoadDesign} onDeleteDesign={handleDeleteDesign}
          onDuplicateDesign={handleDuplicateDesign}
          onNewDesign={handleNewDesign} onSignOut={handleSignOut}
          onUpgrade={() => setRoute('pricing')} onAccount={() => setRoute('account')}
          onBYOI={() => setRoute('byoi')} />
      )}
      {route === 'pricing' && (
        <PricingPage currentPlan={user?.plan || null}
          onSelectPlan={(p) => user ? handleUpgrade(p) : (setRoute('signup'))}
          onBack={() => setRoute(user ? 'dashboard' : 'landing')} />
      )}
      {route === 'byoi' && user && (
        <BYOIConfig user={user} plan={user.plan}
          onUpgrade={() => setRoute('pricing')} onBack={() => setRoute('dashboard')}
          onSave={async (cfg) => { await Store.set(`byoi:${user.id}`, JSON.stringify(cfg)); setRoute('dashboard'); }} />
      )}
      {route === 'account' && user && (
        <AccountPage user={user} designs={designs}
          onBack={() => setRoute('dashboard')} onUpgrade={() => setRoute('pricing')}
          onSignOut={handleSignOut} />
      )}
      {route === 'app' && user && (
        <StaxGenerator user={user} plan={user.plan} currentDesign={currentDesign}
          onSaveDesign={handleSaveDesign} onExitToDashboard={() => setRoute('dashboard')}
          onUpgrade={() => setRoute('pricing')} />
      )}
      {/* ── Marketing pages ─────────────────────────────────── */}
      {route === 'features'   && <FeaturesPage   nav={setRoute} user={user} />}
      {route === 'byoi-info'  && <BYOIInfoPage   nav={setRoute} user={user} />}
      {route === 'changelog'  && <ChangelogPage  nav={setRoute} user={user} />}
      {route === 'quickstart' && <QuickstartPage nav={setRoute} user={user} />}
      {route === 'examples'   && <ExamplesPage   nav={setRoute} user={user} />}
      {route === 'compliance-guide' && <ComplianceGuidePage nav={setRoute} user={user} />}
      {route === 'about'      && <AboutPage      nav={setRoute} user={user} />}
      {route === 'security'   && <SecurityPage   nav={setRoute} user={user} />}
    </>
  );
}

function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Serif:wght@400;500;600;700&display=swap');

      :root {
        /* ── Surface tones (warm off-white system) ─────────── */
        --bg:           #fbfaf7;   /* warm off-white page background */
        --bg-elev:      #ffffff;   /* cards float white above the bg */
        --bg-card:      #f4f3ee;   /* secondary card surface */
        --bg-sunken:    #f1f0eb;   /* recessed inputs, code blocks */

        /* ── Borders + dividers ────────────────────────────── */
        --border:        #ebe9e1;  /* default divider — soft warm */
        --border-strong: #d8d5cb;  /* selected / focused */
        --border-faint:  #f0eee6;  /* barely-there */

        /* ── Ink (text) ────────────────────────────────────── */
        --text:         #1a1a17;   /* near-black with warm cast — easier on eyes than pure #000 */
        --text-dim:     #52524d;   /* secondary copy */
        --text-mute:    #6f6e68;   /* tertiary, captions */
        --text-faint:   #9a9890;   /* metadata, timestamps */

        /* ── Accent system (lime preserved, semantic colors warmed) ── */
        --accent:       #65a30d;   /* lime-600 — readable on white */
        --accent-bg:    #84cc16;   /* lime-500 — CTA backgrounds */
        --accent-soft:  #ecfccb;   /* lime-100 — subtle highlights */
        --accent-glow:  #bef264;   /* lime-300 — used in glows + animations */
        --warn:         #b45309;   /* amber-700 */
        --warn-soft:    #fef3c7;   /* amber-100 */
        --crit:         #b91c1c;   /* red-700 — slightly less harsh than rose */
        --crit-soft:    #fee2e2;   /* red-100 */
        --info:         #1d4ed8;   /* blue-700 */
        --info-soft:    #dbeafe;   /* blue-100 */

        /* ── Elevation system (shadows do the work borders did) ── */
        --shadow-xs: 0 1px 2px rgba(20, 18, 12, 0.04);
        --shadow-sm: 0 1px 2px rgba(20, 18, 12, 0.04), 0 1px 3px rgba(20, 18, 12, 0.06);
        --shadow-md: 0 2px 4px rgba(20, 18, 12, 0.04), 0 4px 12px rgba(20, 18, 12, 0.06);
        --shadow-lg: 0 4px 8px rgba(20, 18, 12, 0.05), 0 12px 24px rgba(20, 18, 12, 0.08);
        --shadow-xl: 0 8px 16px rgba(20, 18, 12, 0.06), 0 24px 48px rgba(20, 18, 12, 0.10);
        --shadow-accent: 0 4px 12px rgba(132, 204, 22, 0.18), 0 1px 3px rgba(132, 204, 22, 0.12);

        /* ── Radii — gentle rounding adds modernity without losing geometry ── */
        --radius-xs: 4px;
        --radius-sm: 6px;
        --radius-md: 10px;
        --radius-lg: 14px;
      }

      html { background: var(--bg); }
      body {
        background: var(--bg);
        color: var(--text);
        font-family: 'IBM Plex Sans', system-ui, sans-serif;
        font-feature-settings: 'cv05', 'cv11', 'ss01';   /* IBM Plex stylistic sets */
        text-rendering: optimizeLegibility;
        -webkit-font-smoothing: antialiased;
      }

      /* Smoother heading defaults — modern feel without changing the typeface */
      h1, h2, h3, h4 { letter-spacing: -0.015em; }

      @keyframes blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
      .blink { animation: blink 1s steps(1) infinite; }

      @keyframes streamIn {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .stream-in { animation: streamIn 0.4s cubic-bezier(0.16, 1, 0.3, 1); }

      @keyframes fadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      .fade-in { animation: fadeIn 0.5s ease-out; }

      @keyframes pulseDot {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%      { opacity: 0.5; transform: scale(1.2); }
      }
      .pulse-dot { animation: pulseDot 2s ease-in-out infinite; }

      @keyframes scanline { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
      .scanline-anim::before {
        content: ''; position: absolute; inset: 0;
        background: linear-gradient(90deg, transparent 0%, rgba(190, 242, 100, 0.18) 50%, transparent 100%);
        animation: scanline 4s linear infinite;
        pointer-events: none;
      }

      @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
      .ticker-track { animation: ticker 40s linear infinite; }
      .ticker-track:hover { animation-play-state: paused; }

      .lattice {
        background-image:
          linear-gradient(to right, rgba(216, 213, 203, 0.5) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(216, 213, 203, 0.5) 1px, transparent 1px);
        background-size: 48px 48px;
      }
      .lattice-fade {
        mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 80%);
        -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 80%);
      }

      /* Soft global rounding — turns brutalist hard corners into a subtler
         modern look without changing any component logic. Inputs, buttons,
         and form controls inherit; cards opt in via inline style. */
      button:not([class*="rounded"]) { border-radius: var(--radius-sm); }
      input:not([class*="rounded"]),
      textarea:not([class*="rounded"]),
      select:not([class*="rounded"]) { border-radius: var(--radius-sm); }

      /* Reusable card surfaces — replaces the ad-hoc inline border patterns */
      .card {
        background: var(--bg-elev);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-sm);
      }
      .card-elev {
        background: var(--bg-elev);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-md);
      }
      .card-hover { transition: transform 0.2s ease, box-shadow 0.2s ease; }
      .card-hover:hover { transform: translateY(-1px); box-shadow: var(--shadow-md); }

      /* Modernized button defaults — apply to any <button> via the matching class */
      .btn { border-radius: var(--radius-sm); transition: all 0.15s ease; }
      .btn-primary {
        background: var(--accent-bg);
        color: var(--text);
        border: 1px solid var(--accent);
        border-radius: var(--radius-sm);
        box-shadow: var(--shadow-sm);
        transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
      }
      .btn-primary:hover { transform: translateY(-1px); box-shadow: var(--shadow-accent); filter: brightness(1.04); }
      .btn-primary:active { transform: translateY(0); box-shadow: var(--shadow-xs); }
      .btn-secondary {
        background: var(--bg-elev);
        color: var(--text-dim);
        border: 1px solid var(--border-strong);
        border-radius: var(--radius-sm);
        transition: all 0.15s ease;
      }
      .btn-secondary:hover { background: var(--bg-card); color: var(--text); border-color: var(--text-faint); }

      /* Modernized scrollbar — thinner, subtler */
      ::-webkit-scrollbar { width: 10px; height: 10px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: 5px; border: 2px solid var(--bg); }
      ::-webkit-scrollbar-thumb:hover { background: var(--text-faint); }

      /* Selection */
      ::selection { background: var(--accent-soft); color: var(--text); }

      /* Text-only buttons / interactive */
      .link-hover { transition: color 0.15s ease; }
      .link-hover:hover { color: var(--text); }
    `}</style>
  );
}

// =============================================================
// LANDING PAGE — hero · ticker · features · live diagrams · CTA
// =============================================================

function Landing({ onGetStarted, onSignIn, onPricing, user, nav }) {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="absolute inset-0 lattice lattice-fade pointer-events-none" />
      <div className="absolute top-[-20%] left-[20%] w-[60%] h-[60%] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}10 0%, transparent 60%)` }} />
      {/* Second softer orb on the right — adds depth + visual movement */}
      <div className="absolute top-[10%] right-[-10%] w-[50%] h-[50%] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, rgba(132, 204, 22, 0.06) 0%, transparent 65%)` }} />
      {/* Vertical gradient — page edges fade slightly warmer/cooler */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, transparent 0%, transparent 60%, rgba(244, 243, 238, 0.5) 100%)' }} />

      <LandingNav onSignIn={onSignIn} onGetStarted={onGetStarted} onPricing={onPricing} user={user} />
      <LiveStatusTicker />

      {/* Hero */}
      <section className="relative max-w-[1400px] mx-auto px-6 pt-20 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-16 items-center">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full pulse-dot" style={{ background: accentBg }} />
              <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                production-grade · multi-cloud · agent-native
              </span>
            </div>
            <h1 className="text-[56px] md:text-[72px] leading-[0.95] font-semibold mb-6"
              style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
              Ship agents,<br />
              not <span style={{ fontStyle: 'italic', color: accent }}>infrastructure</span><br />
              tickets.
            </h1>
            <p className="text-[18px] leading-relaxed mb-8 max-w-[540px]" style={{ color: 'var(--text-dim)' }}>
              STAX generates a complete production stack — Terraform, K8s, agent runtime, guardrails,
              CI/CD, compliance matrix — from a five-minute wizard. Every output is auditable code.
              Nothing proprietary. Nothing locked in.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-12">
              <button onClick={onGetStarted}
                className="group relative px-6 py-3 transition-all hover:-translate-y-0.5"
                style={{
                  background: accentBg,
                  color: '#1a1a17',
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  fontSize: 13,
                  borderRadius: 'var(--radius-sm)',
                  boxShadow: 'var(--shadow-md), var(--shadow-accent)',
                }}>
                <span className="flex items-center gap-2">
                  {user ? '→ open dashboard' : '→ start building free'}
                  <ChevronRight size={14} strokeWidth={2.5} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </button>
              <button onClick={onPricing}
                className="px-6 py-3 transition-colors hover:bg-[#f4f3ee]"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-strong)',
                  color: 'var(--text-dim)',
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontWeight: 500,
                  letterSpacing: '0.05em',
                  fontSize: 13,
                  borderRadius: 'var(--radius-sm)',
                }}>
                view pricing
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              <span><span style={{ color: accent }}>✓</span> AWS · GCP · Azure</span>
              <span><span style={{ color: accent }}>✓</span> SOC 2 · HIPAA · EU AI Act</span>
              <span><span style={{ color: accent }}>✓</span> MCP-native</span>
              <span><span style={{ color: accent }}>✓</span> No vendor lock-in</span>
            </div>
          </div>
          <div className="relative">
            <LiveOutputPreview />
          </div>
        </div>
      </section>

      <FeaturesSection />
      <DiagramShowcase />
      <PricingTeaser onPricing={onPricing} />
      <ComplianceStrip />

      <section className="relative py-32 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-[42px] leading-tight font-semibold mb-6" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
            From idea to <span style={{ fontStyle: 'italic', color: accent }}>deployed agent</span> in one afternoon.
          </h2>
          <p className="text-[16px] mb-8" style={{ color: 'var(--text-dim)' }}>
            Pick your cloud, your model, your guardrails. STAX generates the rest.
          </p>
          <button onClick={onGetStarted}
            className="px-8 py-4 transition-all"
            style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, letterSpacing: '0.05em', fontSize: 13 }}>
            → start building free
          </button>
        </div>
      </section>
      <LandingFooter nav={nav} />
    </div>
  );
}

function LandingNav({ onSignIn, onGetStarted, onPricing, user }) {
  return (
    <nav className="relative z-10 max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 flex items-center justify-center" style={{ background: accentBg }}>
          <Hexagon size={14} strokeWidth={2.5} style={{ color: '#1a1a17' }} fill="#1a1a17" />
        </div>
        <span className="text-[15px] tracking-[0.2em] font-semibold">STAX</span>
        <span className="hidden sm:inline text-[10px] uppercase tracking-widest ml-1" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
          v1.0
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onPricing} className="px-3 py-2 text-[12px] hover:text-white transition-colors"
          style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
          pricing
        </button>
        <a href="#features" className="hidden md:inline-block px-3 py-2 text-[12px] hover:text-white transition-colors"
          style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
          features
        </a>
        <a href="#diagrams" className="hidden md:inline-block px-3 py-2 text-[12px] hover:text-white transition-colors"
          style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
          live demo
        </a>
        {user ? (
          <button onClick={onGetStarted} className="ml-2 px-4 py-2 transition-all"
            style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600 }}>
            dashboard →
          </button>
        ) : (
          <>
            <button onClick={onSignIn} className="px-3 py-2 text-[12px] hover:text-white transition-colors"
              style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              sign in
            </button>
            <button onClick={onGetStarted} className="ml-1 px-4 py-2 transition-all"
              style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600 }}>
              get started
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

function LiveStatusTicker() {
  const items = [
    { label: 'STACKS GENERATED', value: '14,283', delta: '+127 today' },
    { label: 'AVG POSTURE GRADE', value: 'A-', delta: '92.1/100' },
    { label: 'SCANS / DAY', value: '8,491', delta: 'all green' },
    { label: 'CLOUDS SUPPORTED', value: '3', delta: 'aws · gcp · azure' },
    { label: 'COMPLIANCE FRAMEWORKS', value: '9', delta: 'soc2 · iso · gdpr · hipaa' },
    { label: 'AVG TIME TO PROD', value: '14 min', delta: '-94% vs manual' },
    { label: 'AGENTS INDEXED', value: '1,847', delta: 'agentic.ai directory' },
    { label: 'CVE BLOCKED', value: '47k', delta: 'this month' },
  ];
  const looped = [...items, ...items];
  return (
    <div className="relative border-y overflow-hidden" style={{ borderColor: 'var(--border)' }}>
      <div className="ticker-track flex" style={{ width: 'max-content' }}>
        {looped.map((it, i) => (
          <div key={i} className="flex items-center gap-3 px-8 py-3 border-r" style={{ borderColor: 'var(--border)', minWidth: 280 }}>
            <span className="w-1 h-1 rounded-full pulse-dot" style={{ background: accentBg }} />
            <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{it.label}</span>
            <span className="text-[14px] font-semibold tabular-nums" style={{ color: 'var(--text)', fontFamily: 'IBM Plex Mono, monospace' }}>{it.value}</span>
            <span className="text-[10px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>{it.delta}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveOutputPreview() {
  const lines = [
    { t: 0,    text: '$ stax generate --provider aws --ai bedrock', cls: 'white' },
    { t: 600,  text: '→ analyzing requirements...',                  cls: 'mute' },
    { t: 1100, text: '✓ terraform/main.tf',                          cls: 'accent' },
    { t: 1300, text: '✓ terraform/networking.tf',                    cls: 'accent' },
    { t: 1500, text: '✓ terraform/ai.tf',                            cls: 'accent' },
    { t: 1700, text: '✓ k8s/deployment.yaml',                        cls: 'accent' },
    { t: 1900, text: '✓ agent/main.py',                              cls: 'accent' },
    { t: 2100, text: '✓ agent/guardrails.py    [12 layers]',         cls: 'accent' },
    { t: 2300, text: '✓ .github/workflows/security.yml',             cls: 'accent' },
    { t: 2500, text: '✓ .github/workflows/ai-redteam.yml',           cls: 'accent' },
    { t: 2700, text: '✓ compliance/control-matrix.md',               cls: 'accent' },
    { t: 2900, text: '✓ monitoring/alerts.yaml',                     cls: 'accent' },
    { t: 3100, text: '✓ SECURITY.md',                                cls: 'accent' },
    { t: 3400, text: '',                                             cls: '' },
    { t: 3500, text: '52 files · 8,432 LOC · posture: A',            cls: 'warn' },
    { t: 3700, text: 'monthly cost: $1,847 · grade: A · L3 agent',   cls: 'mute' },
  ];
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const timers = lines.map((l, i) => setTimeout(() => setShown(s => Math.max(s, i + 1)), l.t));
    const restart = setTimeout(() => setShown(0), 6000);
    return () => { timers.forEach(clearTimeout); clearTimeout(restart); };
  }, [shown === 0 ? Math.floor(Date.now() / 6000) : null]);
  const colorMap = { white: '#1a1a17', mute: '#6f6e68', accent, warn: '#b45309', '': '#9a9890' };
  return (
    <div className="relative scanline-anim" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
      <div className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#9a9890' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#9a9890' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: accentBg }} />
        </div>
        <span className="text-[10px] ml-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
          stax · phoenix-agent · live
        </span>
        <span className="ml-auto text-[10px] flex items-center gap-1.5" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
          <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: accentBg }} />
          generating
        </span>
      </div>
      <div className="p-5 text-[12px] leading-relaxed min-h-[420px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
        {lines.slice(0, shown).map((l, i) => (
          <div key={i} className="stream-in" style={{ color: colorMap[l.cls] }}>
            {l.text || '\u00A0'}
          </div>
        ))}
        {shown < lines.length && <span className="blink" style={{ color: accent }}>▍</span>}
      </div>
    </div>
  );
}

function FeaturesSection() {
  const features = [
    { n: '01', title: 'Multi-cloud, no lock-in', desc: 'Generate identical-shape stacks for AWS, GCP, or Azure. Switch providers without rewriting your agent.', icon: Cloud },
    { n: '02', title: 'AI security as the spine', desc: 'Every LLM call is wrapped in a 6-layer guardrail: PII redaction, prompt firewall, denied-topic filter, audit log.', icon: ShieldCheck },
    { n: '03', title: 'Compliance by construction', desc: 'Pick your frameworks (SOC 2, HIPAA, EU AI Act, ISO 42001) — get a control-to-artifact matrix. Auditors love it.', icon: FileCode2 },
    { n: '04', title: 'Continuous AI red-teaming', desc: 'garak + PyRIT + Promptfoo run nightly. Find prompt-injection regressions before your users do.', icon: Activity },
    { n: '05', title: 'MCP-native', desc: 'Every agent ships with an MCP server stub. Claude Desktop, Cursor, agentic.ai discover it instantly.', icon: Boxes },
    { n: '06', title: 'Rollout DAG, not a pile of tickets', desc: 'STAX builds a wave-layered deployment graph with team assignments and dependency-correct sequencing.', icon: GitBranch },
  ];
  return (
    <section id="features" className="relative py-32 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-baseline justify-between mb-16 flex-wrap gap-4">
          <h2 className="text-[36px] font-semibold leading-tight" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
            What you actually <span style={{ fontStyle: 'italic', color: accent }}>get</span>.
          </h2>
          <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            06 · capabilities
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: 'var(--border)' }}>
          {features.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.n} className="p-8 transition-colors hover:bg-[#f4f3ee]" style={{ background: 'var(--bg)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{f.n}</span>
                  <Icon size={14} style={{ color: accent }} />
                </div>
                <h3 className="text-[18px] font-semibold mb-2">{f.title}</h3>
                <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-dim)' }}>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DiagramShowcase() {
  const [tab, setTab] = useState('flow');
  return (
    <section id="diagrams" className="relative py-32 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12">
          <span className="text-[10px] uppercase tracking-[0.25em] mb-4 block" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ live demo
          </span>
          <h2 className="text-[36px] font-semibold leading-tight max-w-[700px]" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
            Watch a request flow through <span style={{ fontStyle: 'italic', color: accent }}>defense-in-depth</span>.
          </h2>
          <p className="text-[14px] mt-4 max-w-[600px]" style={{ color: 'var(--text-dim)' }}>
            These aren't mockups. Every diagram in STAX is generated from your config and animated with real packet flow. Hover anything to inspect.
          </p>
        </div>
        <div className="flex items-center gap-1 mb-4 flex-wrap">
          {[
            { id: 'flow', label: 'Request flow' },
            { id: 'arch', label: 'Architecture' },
            { id: 'agentic', label: 'Agenticness' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="px-4 py-2 text-[11px] transition-all"
              style={{
                background: tab === t.id ? 'var(--bg-card)' : 'transparent',
                border: `1px solid ${tab === t.id ? accent : 'var(--border-strong)'}`,
                color: tab === t.id ? accent : 'var(--text-dim)',
                fontFamily: 'IBM Plex Mono, monospace',
              }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
          {tab === 'flow' && <AnimatedDefenseInDepth />}
          {tab === 'arch' && <AnimatedArchitecture />}
          {tab === 'agentic' && <AnimatedAgenticnessRadar />}
        </div>
      </div>
    </section>
  );
}

function AnimatedDefenseInDepth() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1800);
    return () => clearInterval(id);
  }, []);
  const W = 1100, H = 320;
  const lanes = [
    { x: 60, w: 160, label: 'Edge', items: ['CDN', 'WAF', 'TLS 1.3'] },
    { x: 240, w: 200, label: 'Input scan', items: ['PII · Presidio', 'Firewall · Bedrock', 'Topic filter'] },
    { x: 460, w: 180, label: 'Inference', items: ['Bedrock', 'Claude 3.5 Sonnet'] },
    { x: 660, w: 200, label: 'Output scan', items: ['Moderation', 'Audit log'] },
    { x: 880, w: 160, label: 'Egress', items: ['TLS 1.3', 'NetPol'] },
  ];
  const yLane = 100;
  const laneH = 130;
  const pathPoints = lanes.map(l => `${l.x + l.w / 2},${yLane + laneH / 2}`).join(' L ');
  const flowPath = `M ${pathPoints}`;
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: accentBg }} />
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
            live · simulated traffic flowing through guardrails
          </span>
        </div>
        <span className="text-[10px]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
          requests/s: <span style={{ color: accent }} className="tabular-nums">{((tick % 100) * 0.3 + 12).toFixed(1)}</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="arr-flow" markerWidth="8" markerHeight="8" refX="7" refY="2.5" orient="auto">
            <path d="M0,0 L0,5 L7,2.5 z" fill="var(--text-faint)" />
          </marker>
          <linearGradient id="grad-flow" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={accent} stopOpacity="0" />
            <stop offset="50%" stopColor={accent} stopOpacity="0.8" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        <text x={W/2} y={28} fontSize="11" fill="var(--text-mute)" fontFamily="IBM Plex Mono, monospace" textAnchor="middle" letterSpacing="3">
          ⎯⎯⎯  REQUEST   ⎯⎯⎯  RESPONSE PATH  ⎯⎯⎯
        </text>
        <path id="flowPath" d={flowPath} fill="none" stroke="url(#grad-flow)" strokeWidth={1.5} />
        {lanes.map((lane, i) => {
          const isActive = (tick % lanes.length) === i;
          const color = i === 0 || i === 4 ? '#52524d' : i === 2 ? accent : '#f0abfc';
          return (
            <g key={lane.label}>
              <rect x={lane.x} y={yLane} width={lane.w} height={laneH} fill="var(--bg)"
                stroke={color} strokeWidth={isActive ? 1.5 : 1}
                style={{ filter: isActive ? `drop-shadow(0 0 12px ${color}80)` : `drop-shadow(0 0 4px ${color}30)`, transition: 'filter 0.6s ease' }} />
              <text x={lane.x + 12} y={yLane + 18} fontSize="9" fill="var(--text-faint)" fontFamily="IBM Plex Mono, monospace" letterSpacing="2">
                {lane.label.toUpperCase()}
              </text>
              {lane.items.map((item, idx) => (
                <g key={idx}>
                  <rect x={lane.x + 12} y={yLane + 30 + idx * 26} width={lane.w - 24} height={20}
                    fill={`${color}15`} stroke={`${color}50`} strokeWidth={0.5} />
                  <text x={lane.x + 18} y={yLane + 44 + idx * 26} fontSize="10" fill="var(--text)" fontFamily="IBM Plex Mono, monospace">
                    {item}
                  </text>
                </g>
              ))}
              {i < lanes.length - 1 && (
                <line x1={lane.x + lane.w} y1={yLane + laneH / 2} x2={lanes[i+1].x - 2} y2={yLane + laneH / 2}
                  stroke="var(--border-strong)" strokeWidth={1.5} markerEnd="url(#arr-flow)" />
              )}
            </g>
          );
        })}
        {[0, 1, 2].map(i => (
          <circle key={`dot-${i}`} r="5" fill={accent} style={{ filter: `drop-shadow(0 0 6px ${accentSoft})` }}>
            <animateMotion dur="5s" repeatCount="indefinite" begin={`${i * 1.6}s`} path={flowPath} />
          </circle>
        ))}
      </svg>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        {[
          { label: 'p99 latency', value: '847ms' },
          { label: 'guardrail blocks', value: ((tick * 7) % 233).toString() },
          { label: 'pii redactions', value: ((tick * 3) % 89).toString() },
          { label: 'audit entries', value: ((tick * 11) % 9999).toString() },
          { label: 'posture grade', value: 'A' },
        ].map(s => (
          <div key={s.label} className="px-3 py-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{s.label}</div>
            <div className="text-[14px] tabular-nums font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnimatedArchitecture() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT(x => x + 1), 600);
    return () => clearInterval(id);
  }, []);
  const W = 1100, H = 480;
  const nodes = [
    { id: 'user', x: 70, y: 240, w: 90, h: 50, label: 'user', tier: 'edge', color: '#52524d' },
    { id: 'cdn', x: 200, y: 180, w: 120, h: 50, label: 'CDN', tier: 'edge', color: '#52524d' },
    { id: 'waf', x: 200, y: 300, w: 120, h: 50, label: 'WAF', tier: 'edge', color: '#fb923c' },
    { id: 'lb', x: 360, y: 240, w: 120, h: 50, label: 'load balancer', tier: 'edge', color: '#52524d' },
    { id: 'agent', x: 530, y: 240, w: 130, h: 60, label: 'agent svc', tier: 'app', color: accent },
    { id: 'guardrails', x: 530, y: 130, w: 130, h: 50, label: 'guardrails', tier: 'app', color: '#f0abfc' },
    { id: 'rds', x: 720, y: 180, w: 120, h: 50, label: 'RDS · pgvector', tier: 'data', color: '#60a5fa' },
    { id: 'redis', x: 720, y: 260, w: 120, h: 50, label: 'Redis cache', tier: 'data', color: '#60a5fa' },
    { id: 'bedrock', x: 880, y: 130, w: 160, h: 50, label: 'Bedrock LLM', tier: 'ai', color: accent },
    { id: 's3', x: 880, y: 320, w: 160, h: 50, label: 'S3 · KB', tier: 'ai', color: '#a78bfa' },
    { id: 'obs', x: 530, y: 380, w: 130, h: 50, label: 'Datadog', tier: 'obs', color: '#86efac' },
  ];
  const edges = [
    { from: 'user', to: 'cdn', animated: true },
    { from: 'user', to: 'waf', animated: true },
    { from: 'cdn', to: 'lb', animated: true },
    { from: 'waf', to: 'lb', animated: true },
    { from: 'lb', to: 'agent', animated: true, primary: true },
    { from: 'agent', to: 'guardrails', bidi: true },
    { from: 'guardrails', to: 'bedrock', animated: true },
    { from: 'agent', to: 'rds', animated: true },
    { from: 'agent', to: 'redis', animated: true },
    { from: 'agent', to: 's3', animated: true },
    { from: 'agent', to: 'obs' },
  ];
  function nodeAt(id) { return nodes.find(n => n.id === id); }
  function edgePath(e) {
    const a = nodeAt(e.from), b = nodeAt(e.to);
    if (!a || !b) return '';
    const ax = a.x + a.w / 2, ay = a.y + a.h / 2;
    const bx = b.x + b.w / 2, by = b.y + b.h / 2;
    const mx = (ax + bx) / 2, my = (ay + by) / 2;
    const cx = mx + (by - ay) * 0.12;
    const cy = my - (bx - ax) * 0.12;
    const sx = ax + (bx - ax) * 0.07;
    const sy = ay + (by - ay) * 0.07;
    const ex = bx - (bx - ax) * 0.07;
    const ey = by - (by - ay) * 0.07;
    return `M ${sx} ${sy} Q ${cx} ${cy} ${ex} ${ey}`;
  }
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: accentBg }} />
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
            live · architecture · packet flow visualised
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
          {[['edge', '#52524d'], ['app', accent], ['data', '#60a5fa'], ['ai', '#a78bfa']].map(([l, c]) => (
            <span key={l} className="flex items-center gap-1.5">
              <span className="w-2 h-2" style={{ background: c }} /> {l}
            </span>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="arr-arch" markerWidth="6" markerHeight="6" refX="5" refY="2" orient="auto">
            <path d="M0,0 L0,4 L5,2 z" fill="var(--text-mute)" />
          </marker>
        </defs>
        {edges.map((e, i) => {
          const path = edgePath(e);
          return (
            <g key={i}>
              <path d={path} fill="none" stroke="var(--border-strong)" strokeWidth={1} markerEnd="url(#arr-arch)" />
              {e.animated && (
                <circle r="3.5" fill={e.primary ? accent : '#86efac'}
                  style={{ filter: `drop-shadow(0 0 4px ${e.primary ? accent : '#86efac'})` }}>
                  <animateMotion dur={`${2.5 + (i % 3) * 0.5}s`} repeatCount="indefinite" begin={`${i * 0.3}s`} path={path} />
                </circle>
              )}
            </g>
          );
        })}
        {nodes.map(n => (
          <g key={n.id}>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} fill="var(--bg-elev)" stroke={n.color} strokeWidth={1.5}
              style={{ filter: `drop-shadow(0 0 8px ${n.color}40)` }} />
            <text x={n.x + n.w / 2} y={n.y + n.h / 2 + 4} fontSize="11" fill="var(--text)" fontFamily="IBM Plex Mono, monospace" textAnchor="middle">
              {n.label}
            </text>
            {n.id === 'agent' && (
              <rect x={n.x - 4} y={n.y - 4} width={n.w + 8} height={n.h + 8} fill="none" stroke={accent} strokeWidth={1} opacity="0.4">
                <animate attributeName="opacity" values="0.4;0.05;0.4" dur="2s" repeatCount="indefinite" />
                <animate attributeName="x" values={`${n.x - 4};${n.x - 8};${n.x - 4}`} dur="2s" repeatCount="indefinite" />
                <animate attributeName="y" values={`${n.y - 4};${n.y - 8};${n.y - 4}`} dur="2s" repeatCount="indefinite" />
                <animate attributeName="width" values={`${n.w + 8};${n.w + 16};${n.w + 8}`} dur="2s" repeatCount="indefinite" />
                <animate attributeName="height" values={`${n.h + 8};${n.h + 16};${n.h + 8}`} dur="2s" repeatCount="indefinite" />
              </rect>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

function AnimatedAgenticnessRadar() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT(x => x + 1), 100);
    return () => clearInterval(id);
  }, []);
  const dims = [
    { name: 'goal', base: 4 },
    { name: 'tools', base: 4 },
    { name: 'planning', base: 4 },
    { name: 'memory', base: 3 },
    { name: 'autonomy', base: 3 },
    { name: 'observe', base: 4 },
    { name: 'adapt', base: 3 },
    { name: 'reach', base: 3 },
  ];
  const W = 540, cx = W / 2, cy = W / 2 - 40, R = 180;
  const N = dims.length;
  const angle = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / N;
  const point = (i, v) => {
    const r = (v / 4) * R;
    return [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  };
  const liveDims = dims.map((d, i) => ({
    ...d,
    v: Math.max(2, Math.min(4, d.base + Math.sin((t / 20) + i) * 0.3)),
  }));
  const scorePolygon = liveDims.map((d, i) => point(i, d.v)).map(p => p.join(',')).join(' ');
  const total = liveDims.reduce((s, d) => s + d.v, 0);
  return (
    <div className="p-8 grid grid-cols-1 md:grid-cols-[540px_1fr] gap-6 items-center">
      <svg viewBox={`0 0 ${W} ${W - 80}`} className="w-full h-auto">
        {[1, 2, 3, 4].map(v => (
          <polygon key={v}
            points={dims.map((_, i) => point(i, v)).map(p => p.join(',')).join(' ')}
            fill="none" stroke="var(--border)" strokeWidth={v === 4 ? 1 : 0.5}
            strokeDasharray={v === 4 ? '' : '2 3'} />
        ))}
        {dims.map((_, i) => {
          const [x, y] = point(i, 4);
          return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--border)" strokeWidth="0.5" />;
        })}
        <polygon points={scorePolygon} fill={accent} fillOpacity="0.15" stroke={accent} strokeWidth="2"
          style={{ filter: `drop-shadow(0 0 12px ${accentSoft}50)`, transition: 'all 0.3s ease' }} />
        {liveDims.map((d, i) => {
          const [x, y] = point(i, d.v);
          return <circle key={i} cx={x} cy={y} r="4" fill={accent}
            style={{ filter: `drop-shadow(0 0 6px ${accentSoft})`, transition: 'all 0.3s ease' }} />;
        })}
        {dims.map((d, i) => {
          const r = R + 24;
          const [x, y] = [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
          const a = angle(i);
          const anchor = Math.cos(a) > 0.3 ? 'start' : Math.cos(a) < -0.3 ? 'end' : 'middle';
          return (
            <text key={i} x={x} y={y} fontSize="11" fill="var(--text-dim)"
              fontFamily="IBM Plex Mono, monospace" textAnchor={anchor} dominantBaseline="middle">
              {d.name}
            </text>
          );
        })}
        <circle cx={cx} cy={cy} r="36" fill="var(--bg-elev)" stroke={accent} strokeWidth="1.5" />
        <text x={cx} y={cy - 2} fontSize="22" fill={accent} fontFamily="IBM Plex Mono, monospace" fontWeight="600" textAnchor="middle">
          {total.toFixed(1)}
        </text>
        <text x={cx} y={cy + 14} fontSize="9" fill="var(--text-mute)" fontFamily="IBM Plex Mono, monospace" textAnchor="middle">
          / 32
        </text>
      </svg>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
          live · agenticness telemetry
        </div>
        <div className="text-[28px] font-semibold mb-3" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
          <span style={{ color: accent }}>L4</span> · Autonomous Operator
        </div>
        <p className="text-[13px] mb-6" style={{ color: 'var(--text-dim)' }}>
          STAX scores every generated agent across 8 dimensions on the agentic.ai scale, then publishes the manifest at <code className="px-1.5 py-0.5 text-[12px]" style={{ background: 'var(--bg-card)', color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>/.well-known/agent.json</code>.
        </p>
        <div className="space-y-1">
          {liveDims.map((d) => (
            <div key={d.name} className="grid grid-cols-[80px_1fr_30px] gap-3 items-center text-[11px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              <span style={{ color: 'var(--text-mute)' }}>{d.name}</span>
              <div className="flex gap-1 h-1.5">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="flex-1 transition-all" style={{ background: i < Math.floor(d.v) ? accent : 'var(--border)' }} />
                ))}
              </div>
              <span className="text-right tabular-nums" style={{ color: accent }}>{d.v.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PricingTeaser({ onPricing }) {
  const tiers = [
    { plan: 'free', name: 'Free', price: '$0', desc: 'For exploring + side projects' },
    { plan: 'pro', name: 'Pro', price: '$29', desc: 'For builders shipping to prod', featured: true },
    { plan: 'team', name: 'Team', price: '$199', desc: 'For platform teams + BYOI' },
  ];
  return (
    <section className="relative py-24 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-baseline justify-between mb-12 flex-wrap gap-4">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] mb-3 block" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              ⏵ pricing
            </span>
            <h2 className="text-[36px] font-semibold leading-tight" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
              Three tiers. <span style={{ fontStyle: 'italic', color: accent }}>No traps.</span>
            </h2>
          </div>
          <button onClick={onPricing} className="text-[12px] flex items-center gap-1.5 hover:gap-2.5 transition-all"
            style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
            full comparison <ChevronRight size={12} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: 'var(--border)' }}>
          {tiers.map(t => (
            <div key={t.plan} className="p-8 relative" style={{ background: t.featured ? 'var(--bg-card)' : 'var(--bg)' }}>
              {t.featured && (
                <span className="absolute top-4 right-4 text-[9px] px-2 py-0.5"
                  style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.1em', fontWeight: 600 }}>
                  POPULAR
                </span>
              )}
              <div className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{t.plan}</div>
              <div className="text-[42px] font-semibold leading-none mb-2" style={{ fontFamily: 'IBM Plex Serif, serif', color: t.featured ? accent : 'var(--text)' }}>
                {t.price}
              </div>
              <div className="text-[12px] mb-6" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>per month</div>
              <p className="text-[13px]" style={{ color: 'var(--text-dim)' }}>{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComplianceStrip() {
  const items = ['SOC 2', 'ISO 27001', 'ISO 42001', 'GDPR', 'HIPAA', 'PCI-DSS', 'EU AI Act', 'NIST AI RMF', 'FedRAMP'];
  return (
    <section className="relative py-12 px-6 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-[1400px] mx-auto">
        <div className="text-[10px] uppercase tracking-[0.25em] mb-6 text-center" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
          ⏵ compliance frameworks · auto-mapped to artifacts
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {items.map(it => (
            <span key={it} className="px-3 py-1.5 text-[11px] border"
              style={{ borderColor: 'var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em' }}>
              {it}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function LandingFooter({ nav }) {
  const cols = [
    { title: 'product', items: [
      { label: 'features',  to: 'features' },
      { label: 'pricing',   to: 'pricing' },
      { label: 'BYOI',      to: 'byoi-info' },
      { label: 'changelog', to: 'changelog' },
    ]},
    { title: 'docs', items: [
      { label: 'quickstart',       to: 'quickstart' },
      { label: 'examples',         to: 'examples' },
      { label: 'compliance guide', to: 'compliance-guide' },
    ]},
    { title: 'company', items: [
      { label: 'about',    to: 'about' },
      { label: 'security', to: 'security' },
    ]},
  ];
  return (
    <footer className="relative border-t py-12 px-6" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center" style={{ background: accentBg }}>
              <Hexagon size={10} strokeWidth={2.5} style={{ color: '#1a1a17' }} fill="#1a1a17" />
            </div>
            <span className="text-[11px] tracking-[0.2em] font-semibold">STAX</span>
          </div>
          <p className="text-[11px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
            production stack generator for agent platforms.
          </p>
        </div>
        {cols.map(col => (
          <div key={col.title}>
            <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{col.title}</div>
            <ul className="space-y-2">
              {col.items.map(it => (
                <li key={it.label}>
                  <button
                    onClick={() => { nav?.(it.to); window.scrollTo({ top: 0 }); }}
                    className="text-[11px] hover:text-black transition-colors text-left"
                    style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {it.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t pt-6 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: 'var(--border)' }}>
        <span className="text-[10px]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
          © {new Date().getFullYear()} stax · made with terminals + caffeine
        </span>
        <span className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
          <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: accentBg }} />
          all systems operational
        </span>
      </div>
    </footer>
  );
}

// =============================================================
// AUTH SCREEN — sign in / sign up
// =============================================================

function AuthScreen({ mode, error, onSubmit, onGoogleSignIn, onSwitchMode, onBack }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const isSignup = mode === 'signup';
  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="absolute top-6 left-6">
        <button onClick={onBack} className="flex items-center gap-2 text-[11px] hover:text-white transition-colors"
          style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
          <ChevronLeft size={12} /> back
        </button>
      </div>
      <div className="hidden lg:flex flex-col justify-between w-[44%] p-12 relative overflow-hidden border-r" style={{ borderColor: 'var(--border)' }}>
        <div className="absolute inset-0 lattice opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: accentBg }}>
              <Hexagon size={14} strokeWidth={2.5} style={{ color: '#1a1a17' }} fill="#1a1a17" />
            </div>
            <span className="text-[15px] tracking-[0.2em] font-semibold">STAX</span>
          </div>
          <h1 className="text-[44px] leading-tight font-semibold mb-4" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
            {isSignup ? 'Start building.' : 'Welcome back.'}
          </h1>
          <p className="text-[14px] max-w-[400px]" style={{ color: 'var(--text-dim)' }}>
            {isSignup
              ? 'No credit card. Three saved designs free. Upgrade only when you need to ship.'
              : 'Pick up where you left off. Your designs are persisted across sessions.'}
          </p>
        </div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ trusted by
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: '14k+', label: 'stacks generated' },
              { value: 'A-', label: 'avg posture grade' },
              { value: '52', label: 'files / stack' },
              { value: '14m', label: 'avg time to prod' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-[20px] font-semibold tabular-nums" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>{s.value}</div>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-12">
        <div className="w-full max-w-[400px]">
          <div className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            {isSignup ? '01 / new account' : '01 / sign in'}
          </div>
          <h2 className="text-[28px] font-semibold mb-8" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
            {isSignup ? 'Create your account' : 'Access your dashboard'}
          </h2>

          {/* Google sign-in — primary path. Real Firebase auth. */}
          {onGoogleSignIn && (
            <>
              <button
                type="button"
                onClick={onGoogleSignIn}
                className="w-full px-4 py-3 mb-4 flex items-center justify-center gap-3 transition-all hover:brightness-95"
                style={{ background: '#ffffff', border: '1px solid var(--border-strong)', color: '#1a1a17', fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 13, fontWeight: 500 }}
              >
                {/* Inline Google "G" mark — no extra dependency */}
                <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.20455c0-.63864-.0573-1.25182-.1636-1.84091H9v3.48136h4.8436c-.2086 1.125-.8427 2.07818-1.7959 2.71636v2.25818h2.9087c1.7018-1.56682 2.6836-3.87409 2.6836-6.61500z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.18l-2.9087-2.25818c-.806.54-1.83709.85909-3.04773.85909-2.34409 0-4.32818-1.58318-5.03591-3.71091H.957275v2.33182C2.43818 15.9832 5.48182 18 9 18z" fill="#34A853"/>
                  <path d="M3.96409 10.71c-.18-.54-.28227-1.11682-.28227-1.71s.10227-1.17.28227-1.71V4.95818H.957273C.347727 6.17318 0 7.5475 0 9s.347727 2.82682.957273 4.04182l3.00682-2.33182z" fill="#FBBC05"/>
                  <path d="M9 3.57955c1.32136 0 2.50773.45409 3.44045 1.34591l2.58136-2.58136C13.4632.891818 11.4259 0 9 0 5.48182 0 2.43818 2.01682.957275 4.95818l3.00682 2.33182C4.67182 5.16273 6.65591 3.57955 9 3.57955z" fill="#EA4335"/>
                </svg>
                <span>Continue with Google</span>
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px" style={{ background: 'var(--border-strong)' }} />
                <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>or</span>
                <div className="flex-1 h-px" style={{ background: 'var(--border-strong)' }} />
              </div>
            </>
          )}

          <form onSubmit={(e) => { e.preventDefault(); onSubmit({ email, password, name }); }} className="space-y-4">
            {isSignup && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] block mb-1.5" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                  name (optional)
                </label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2.5 outline-none text-[13px] focus:border-[#bef264] transition-colors"
                  style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)', color: 'var(--text)', fontFamily: 'IBM Plex Mono, monospace' }}
                  placeholder="ada lovelace" />
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] block mb-1.5" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                email
              </label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus
                className="w-full px-3 py-2.5 outline-none text-[13px] focus:border-[#bef264] transition-colors"
                style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)', color: 'var(--text)', fontFamily: 'IBM Plex Mono, monospace' }}
                placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] block mb-1.5" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                password
              </label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 outline-none text-[13px] focus:border-[#bef264] transition-colors"
                style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)', color: 'var(--text)', fontFamily: 'IBM Plex Mono, monospace' }}
                placeholder={isSignup ? '≥ 8 characters' : ''} />
            </div>
            {error && (
              <div className="px-3 py-2 text-[11px] flex items-start gap-2" style={{ background: '#fb71851a', border: '1px solid #fb718540', color: '#be123c', fontFamily: 'IBM Plex Mono, monospace' }}>
                <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button type="submit" className="w-full px-4 py-3 transition-all"
              style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, letterSpacing: '0.05em', fontSize: 12 }}>
              {isSignup ? '→ create account' : '→ sign in'}
            </button>
          </form>
          <div className="mt-8 pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
            <button onClick={onSwitchMode} className="text-[12px] hover:text-white transition-colors"
              style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              {isSignup ? '← already have an account? sign in' : 'no account yet? create one →'}
            </button>
          </div>
          <div className="mt-8 text-[10px] leading-relaxed" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⓘ <strong style={{ color: 'var(--text-mute)' }}>Google sign-in is real</strong> (Firebase Auth). Email/password below is demo only — credentials persist via localStorage on this device. Use Google for a real account.
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// DASHBOARD
// =============================================================

function Dashboard({ user, designs, plan, onOpenDesign, onDeleteDesign, onNewDesign, onSignOut, onUpgrade, onAccount, onBYOI, onDuplicateDesign }) {
  const [hovered, setHovered] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('updated'); // updated | created | name
  const [filterProvider, setFilterProvider] = useState('all'); // all | aws | gcp | azure
  const planFeatures = PLAN_FEATURES[plan] || PLAN_FEATURES.free;
  const atLimit = planFeatures.designLimit !== -1 && designs.length >= planFeatures.designLimit;

  // ── Cloud builds ──────────────────────────────────────────────
  const isCloudEligible = planFeatures.canCloudStore && user?.authProvider === 'google';
  const [cloudBuilds, setCloudBuilds] = useState([]);
  const [cloudUsage, setCloudUsage] = useState({ bytes: 0 });
  const [loadingCloud, setLoadingCloud] = useState(false);

  const refreshCloud = async () => {
    if (!isCloudEligible) return;
    const uid = firebaseAuth.currentUser?.uid;
    if (!uid) return;
    setLoadingCloud(true);
    try {
      const [builds, usage] = await Promise.all([
        listStoredDesigns(uid),
        getCloudUsage(uid),
      ]);
      setCloudBuilds(builds);
      setCloudUsage(usage);
    } catch (err) {
      console.warn('[dashboard] cloud refresh failed', err);
    } finally {
      setLoadingCloud(false);
    }
  };
  useEffect(() => {
    if (isCloudEligible) refreshCloud();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloudEligible]);

  const removeCloudBuild = async (designId) => {
    const uid = firebaseAuth.currentUser?.uid;
    if (!uid) return;
    await deleteStoredDesign(uid, designId);
    refreshCloud();
  };

  // Compute filtered + sorted designs
  const sortedDesigns = useMemo(() => {
    let result = [...designs];
    // search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(d => (d.name || '').toLowerCase().includes(q));
    }
    // provider filter — need to peek at config which isn't in index; load on demand
    if (filterProvider !== 'all') {
      result = result.filter(d => (d.config?.provider || 'aws') === filterProvider);
    }
    // sort
    result.sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'created') return (b.createdAt || '').localeCompare(a.createdAt || '');
      return (b.updatedAt || '').localeCompare(a.updatedAt || '');
    });
    return result;
  }, [designs, search, sortBy, filterProvider]);
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <header className="sticky top-0 z-30 backdrop-blur-md border-b" style={{ background: 'rgba(251,250,247,0.88)', borderColor: 'var(--border)' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: accentBg }}>
              <Hexagon size={14} strokeWidth={2.5} style={{ color: '#1a1a17' }} fill="#1a1a17" />
            </div>
            <span className="text-[15px] tracking-[0.2em] font-semibold">STAX</span>
            <span className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              dashboard
            </span>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <button onClick={onAccount} className="px-3 py-1.5 text-[11px] hover:bg-[#f4f3ee] transition-colors"
              style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              account
            </button>
            <button onClick={onUpgrade} className="px-3 py-1.5 text-[11px] hover:bg-[#f4f3ee] transition-colors"
              style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              pricing
            </button>
            {planFeatures.canBYOI && (
              <button onClick={onBYOI} className="px-3 py-1.5 text-[11px] hover:bg-[#f4f3ee] transition-colors"
                style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
                BYOI
              </button>
            )}
            <button onClick={onSignOut} className="px-3 py-1.5 text-[11px] hover:bg-[#f4f3ee] transition-colors"
              style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              sign out
            </button>
            <Pill color={plan === 'free' ? '#52524d' : plan === 'team' ? '#a78bfa' : accent}>{plan}</Pill>
            <div className="w-7 h-7 flex items-center justify-center text-[11px] font-semibold"
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              {user.email[0].toUpperCase()}
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="flex items-baseline justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              ⏵ welcome back, {user.name}
            </div>
            <h1 className="text-[36px] font-semibold leading-tight" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
              Your <span style={{ fontStyle: 'italic', color: accent }}>designs</span>.
            </h1>
            <p className="text-[12px] mt-2" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
              {designs.length} {designs.length === 1 ? 'design' : 'designs'}
              {planFeatures.designLimit !== -1 && (
                <> · {Math.max(0, planFeatures.designLimit - designs.length)} remaining on {plan} plan</>
              )}
            </p>
          </div>
          <button onClick={atLimit ? onUpgrade : onNewDesign}
            className="px-5 py-3 transition-all flex items-center gap-2"
            style={{
              background: atLimit ? 'transparent' : accent,
              border: atLimit ? `1px solid ${accent}` : 'none',
              color: atLimit ? accent : '#fbfaf7',
              fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, letterSpacing: '0.05em', fontSize: 12,
            }}>
            {atLimit ? '↑ upgrade to create more' : '+ new design'}
          </button>
        </div>
        {designs.length === 0 ? (
          <EmptyDashboard onNewDesign={onNewDesign} />
        ) : (
          <>
            {/* Search + filter + sort toolbar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-[360px]">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="search designs..."
                  className="w-full px-3 py-2 text-[12px] outline-none focus:border-[#bef264] transition-colors"
                  style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)', color: 'var(--text)', fontFamily: 'IBM Plex Mono, monospace' }}
                />
                {search && (
                  <button onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-[#d8d5cb]"
                    style={{ color: 'var(--text-mute)' }}>
                    <X size={11} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              {/* Provider filter */}
              <div className="flex" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
                {['all', 'aws', 'gcp', 'azure'].map(p => (
                  <button key={p} onClick={() => setFilterProvider(p)}
                    className="px-3 py-2 text-[11px] transition-colors"
                    style={{
                      background: filterProvider === p ? '#f4f3ee' : 'transparent',
                      color: filterProvider === p ? accent : 'var(--text-dim)',
                      fontFamily: 'IBM Plex Mono, monospace',
                      borderRight: p !== 'azure' ? '1px solid var(--border)' : 'none',
                    }}>
                    {p}
                  </button>
                ))}
              </div>
              {/* Sort */}
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 text-[11px] outline-none cursor-pointer"
                style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
                <option value="updated">↓ recently updated</option>
                <option value="created">↓ recently created</option>
                <option value="name">A → Z by name</option>
              </select>
              {(search || filterProvider !== 'all') && (
                <span className="text-[10px] ml-auto" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {sortedDesigns.length} of {designs.length}
                </span>
              )}
            </div>
            {sortedDesigns.length === 0 ? (
              <div className="text-center py-16" style={{ background: 'var(--bg-elev)', border: '1px dashed var(--border-strong)' }}>
                <span className="text-[12px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                  no designs match. <button onClick={() => { setSearch(''); setFilterProvider('all'); }}
                    className="underline" style={{ color: accent }}>clear filters</button>
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedDesigns.map(d => (
                  <DesignCard key={d.id} design={d} user={user}
                    onOpen={() => onOpenDesign(d.id)}
                    onDelete={() => setConfirmDelete(d.id)}
                    onDuplicate={onDuplicateDesign && !atLimit ? () => onDuplicateDesign(d.id) : null}
                    hovered={hovered === d.id}
                    onHover={(h) => setHovered(h ? d.id : null)} />
                ))}
              </div>
            )}
          </>
        )}
        {plan === 'free' && designs.length >= 2 && (
          <div className="mt-12 p-6 relative overflow-hidden" style={{ background: 'var(--bg-elev)', border: `1px solid ${accent}40` }}>
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                  ⏵ approaching limit
                </div>
                <h3 className="text-[18px] font-semibold mb-1">
                  Save up to <span style={{ color: accent }}>50 designs</span> with Pro.
                </h3>
                <p className="text-[12px]" style={{ color: 'var(--text-dim)' }}>
                  Plus PDF export, directory submission, and 90-day history. $29/mo, cancel anytime.
                </p>
              </div>
              <button onClick={onUpgrade} className="px-5 py-2.5"
                style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, fontSize: 12 }}>
                upgrade →
              </button>
            </div>
          </div>
        )}
      </main>
      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)}>
          <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#be123c', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ confirm delete
          </div>
          <h3 className="text-[20px] font-semibold mb-2">Delete this design?</h3>
          <p className="text-[12px] mb-6" style={{ color: 'var(--text-dim)' }}>
            This cannot be undone. The design and all its config will be removed permanently.
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-[12px]"
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              cancel
            </button>
            <button onClick={() => { onDeleteDesign(confirmDelete); setConfirmDelete(null); }}
              className="px-4 py-2 text-[12px]"
              style={{ background: '#fb7185', color: '#fbfaf7', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
              delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function EmptyDashboard({ onNewDesign }) {
  return (
    <div className="text-center py-24 px-6" style={{ background: 'var(--bg-elev)', border: '1px dashed var(--border-strong)' }}>
      <div className="inline-flex w-12 h-12 items-center justify-center mb-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)' }}>
        <Sparkles size={20} style={{ color: accent }} />
      </div>
      <h3 className="text-[18px] font-semibold mb-2">No designs yet.</h3>
      <p className="text-[12px] mb-6 max-w-[400px] mx-auto" style={{ color: 'var(--text-mute)' }}>
        Generate your first stack — pick a cloud, add an agent, ship to prod.
      </p>
      <button onClick={onNewDesign} className="px-5 py-2.5"
        style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, fontSize: 12 }}>
        + create your first design
      </button>
    </div>
  );
}

function DesignCard({ design, onOpen, onDelete, onDuplicate, hovered, onHover }) {
  const [posture, agenticness] = useMemo(() => {
    if (!design.config) return [null, null];
    try {
      return [computeSecurityPosture(design.config), computeAgenticness(design.config)];
    } catch { return [null, null]; }
  }, [design]);
  const cfg = design.config || {};
  const updated = design.updatedAt ? new Date(design.updatedAt) : null;
  const ago = updated ? timeAgo(updated) : 'never';
  return (
    <div className="relative cursor-pointer"
      style={{
        background: 'var(--bg-elev)',
        border: `1px solid ${hovered ? accent + '60' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)',
        boxShadow: hovered ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease, border-color 0.15s ease',
        overflow: 'hidden',
      }}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onClick={onOpen}>
      <div className="p-5 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold truncate">{design.name}</h3>
            <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
              updated {ago}
            </div>
          </div>
          {posture && (
            <div className="px-2 py-1 text-center min-w-[40px]" style={{ background: 'var(--bg)', border: `1px solid ${posture.grade.color}40` }}>
              <div className="text-[16px] font-semibold leading-none" style={{ color: posture.grade.color, fontFamily: 'IBM Plex Mono, monospace' }}>
                {posture.grade.letter}
              </div>
              <div className="text-[8px] uppercase tracking-[0.15em] mt-0.5" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
                posture
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Pill color="#52524d" dot={false}>{(cfg.provider || 'aws').toUpperCase()}</Pill>
          <Pill color={cfg.environment === 'prod' ? '#fb7185' : cfg.environment === 'staging' ? '#fbbf24' : accent} dot={false}>
            {cfg.environment || 'dev'}
          </Pill>
          {cfg.aiEnabled && <Pill color="#a78bfa" dot={false}>AI</Pill>}
          {cfg.directoryEnabled && agenticness && <Pill color={accent} dot={false}>{agenticness.level.level}</Pill>}
        </div>
      </div>
      <div className="grid grid-cols-3 divide-x" style={{ background: 'var(--bg)' }}>
        {[
          { label: 'compute', value: cfg.compute || '—' },
          { label: 'database', value: (cfg.database || '—').split('-')[0] },
          { label: 'frameworks', value: (cfg.complianceFrameworks || []).length },
        ].map(s => (
          <div key={s.label} className="px-3 py-2.5 text-center" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              {s.label}
            </div>
            <div className="text-[12px] font-medium tabular-nums truncate" style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>
      {hovered && (
        <div className="absolute top-3 right-3 flex gap-1.5">
          {onDuplicate && (
            <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              className="p-1.5 transition-colors hover:bg-[#bef26420]"
              style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', color: accent }}
              title="Duplicate design">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="9" y="9" width="13" height="13" rx="0" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 transition-colors hover:bg-[#fb71851a]"
            style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', color: '#be123c' }}
            title="Delete design">
            <X size={12} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
}

function timeAgo(date) {
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return date.toISOString().slice(0, 10);
}

function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(26,26,23,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div className="max-w-md w-full p-6 stream-in"
        style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// =============================================================
// PRICING PAGE
// =============================================================

function PricingPage({ currentPlan, onSelectPlan, onBack }) {
  // Pricing toggle: monthly vs annual. Team is annual-only so the toggle
  // only affects the Pro card. Free is free.
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'

  // Feature matrix grouped into four sections — Build / Operate / Govern / Collaborate.
  // Each row: { label, free, pro, team } where values can be:
  //   - true / false → check / em-dash
  //   - string → rendered as-is (e.g. "100 MB", "unlimited")
  const featureGroups = [
    {
      title: 'Build',
      sub:   'What you can produce and how long the result sticks around.',
      rows: [
        { label: 'Saved designs',                free: '3',         pro: 'unlimited', team: 'unlimited' },
        { label: 'Full generator (Terraform + K8s + agent)', free: true, pro: true, team: true },
        { label: 'Watermark on generated README',free: true,        pro: false,        team: false },
        { label: 'PDF / DOCX export',            free: false,       pro: true,         team: true },
        { label: 'agentic.ai directory submission', free: false,    pro: true,         team: true },
      ],
    },
    {
      title: 'Operate',
      sub:   'Where your generated builds and history live.',
      rows: [
        { label: 'Cloud storage of generated zips', free: false,   pro: '100 MB',     team: '1 GB' },
        { label: 'History retention',            free: '7 days',    pro: '90 days',    team: 'unlimited' },
        { label: 'Re-download previous builds',  free: false,       pro: true,         team: true },
      ],
    },
    {
      title: 'Govern',
      sub:   'Compliance frameworks STAX maps controls into. Auditors get matrices.',
      rows: [
        { label: 'Core frameworks (SOC 2 · GDPR · ISO 27001)', free: true,  pro: true, team: true },
        { label: 'Advanced frameworks (HIPAA · PCI-DSS · EU AI Act · NIST AI RMF · ISO 42001 · FedRAMP)', free: false, pro: true, team: true },
        { label: 'Custom frameworks (upload your own)', free: false, pro: false,        team: true },
        { label: 'Audit log retention',          free: '7 days',    pro: '90 days',    team: '7 years' },
      ],
    },
    {
      title: 'Collaborate',
      sub:   'Multi-seat, identity provider integration, and bring-your-own infrastructure.',
      rows: [
        { label: 'Team seats',                   free: '1',         pro: '1',          team: '10' },
        { label: 'SSO (SAML 2.0 + OIDC)',        free: false,       pro: false,        team: true },
        { label: 'BYOI (own backends + registry)', free: false,     pro: false,        team: true },
        { label: 'Support',                      free: 'community', pro: 'email · 24h',team: 'priority · 4h + Slack' },
      ],
    },
  ];

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      tagline: 'See what STAX does.',
      sub: 'Personal projects + evaluation. Three saved designs. All exports include a watermark.',
      monthly: 0,
      annual:  0,
      cta: 'start free',
      pitch: [
        'Full multi-cloud generator',
        'Core compliance frameworks',
        'MIT-licensed output, yours forever',
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      tagline: 'Ship production.',
      sub: 'Solo professional toolkit. Everything in Free, no watermarks, all frameworks, cloud storage.',
      monthly: 29,
      annual:  290,  // 10× = ~17% off
      cta: 'choose Pro',
      featured: true,
      pitch: [
        'Unlimited designs',
        'All 9 compliance frameworks unlocked',
        '100 MB cloud storage of generated zips',
        'PDF/DOCX export, no watermark',
        'agentic.ai directory submission',
        'Email support · 24h response',
      ],
    },
    {
      id: 'team',
      name: 'Team',
      tagline: 'For real businesses.',
      sub: 'Multi-seat, BYOI, SSO, audit-grade retention, custom frameworks. Annual contract.',
      monthly: null,       // not available monthly
      annual:  4800,
      cta: 'contact sales',  // Team is annual-only — bias toward conversation, not self-serve
      pitch: [
        'Up to 10 seats with role separation',
        'SSO (SAML 2.0 + OIDC)',
        'BYOI: your backends, registry, audit sink',
        'Custom compliance frameworks',
        '7-year audit log retention (HIPAA / PCI-grade)',
        '1 GB cloud storage',
        'Priority support · private Slack channel',
        'Annual invoicing, MSA available',
      ],
    },
  ];

  const priceFor = (t) => {
    if (t.id === 'free') return { big: '$0', sub: 'forever' };
    if (t.id === 'team') return { big: '$4,800', sub: '/ year · annual only' };
    if (billing === 'annual') return { big: `$${Math.round(t.annual / 12)}`, sub: `/ mo · billed $${t.annual}/yr` };
    return { big: `$${t.monthly}`, sub: '/ mo' };
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <button onClick={onBack} className="flex items-center gap-2 text-[11px] hover:text-black transition-colors"
          style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
          <ChevronLeft size={12} /> back
        </button>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ pricing
          </span>
          <h1 className="text-[52px] font-semibold leading-tight mt-4 mb-3" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
            Three plans. <span style={{ fontStyle: 'italic', color: accent }}>Honest about who they're for.</span>
          </h1>
          <p className="text-[14px] max-w-[640px] mx-auto" style={{ color: 'var(--text-dim)' }}>
            Free is for evaluation. Pro is for solo professionals shipping production. Team is for companies that need seats, compliance evidence, and bring-your-own-infrastructure.
          </p>
        </div>

        {/* Billing toggle (only affects Pro — Team is annual-only, Free is free) */}
        <div className="flex items-center justify-center gap-2 mb-12">
          <button
            onClick={() => setBilling('monthly')}
            className="px-4 py-2 text-[11px] transition-colors"
            style={{
              background: billing === 'monthly' ? 'var(--bg-card)' : 'transparent',
              color: billing === 'monthly' ? accent : 'var(--text-mute)',
              border: `1px solid ${billing === 'monthly' ? accent : 'var(--border-strong)'}`,
              fontFamily: 'IBM Plex Mono, monospace',
            }}>
            monthly
          </button>
          <button
            onClick={() => setBilling('annual')}
            className="px-4 py-2 text-[11px] transition-colors flex items-center gap-2"
            style={{
              background: billing === 'annual' ? 'var(--bg-card)' : 'transparent',
              color: billing === 'annual' ? accent : 'var(--text-mute)',
              border: `1px solid ${billing === 'annual' ? accent : 'var(--border-strong)'}`,
              fontFamily: 'IBM Plex Mono, monospace',
            }}>
            annual
            <span className="text-[9px] px-1.5 py-0.5"
              style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
              SAVE 17%
            </span>
          </button>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-20">
          {tiers.map(t => {
            const isCurrent = currentPlan === t.id;
            const price = priceFor(t);
            return (
              <div key={t.id} className="relative p-8 transition-all flex flex-col"
                style={{
                  background: t.featured ? 'var(--bg-elev)' : 'var(--bg-elev)',
                  border: `1px solid ${t.featured ? accent : 'var(--border)'}`,
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: t.featured ? 'var(--shadow-lg), var(--shadow-accent)' : 'var(--shadow-sm)',
                  transform: t.featured ? 'translateY(-4px)' : 'none',
                }}>
                {t.featured && (
                  <span className="absolute -top-3 left-6 text-[9px] px-2.5 py-1"
                    style={{
                      background: accentBg,
                      color: '#1a1a17',
                      fontFamily: 'IBM Plex Mono, monospace',
                      letterSpacing: '0.1em',
                      fontWeight: 600,
                      borderRadius: 'var(--radius-sm)',
                      boxShadow: 'var(--shadow-sm)',
                    }}>
                    MOST POPULAR
                  </span>
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {t.name}
                </div>
                <div className="text-[20px] font-semibold mb-2" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                  {t.tagline}
                </div>
                <p className="text-[12px] mb-6 min-h-[36px]" style={{ color: 'var(--text-dim)' }}>{t.sub}</p>

                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-[42px] font-semibold leading-none tabular-nums"
                    style={{ fontFamily: 'IBM Plex Serif, serif', color: t.featured ? accent : 'var(--text)' }}>
                    {price.big}
                  </span>
                  <span className="text-[11px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                    {price.sub}
                  </span>
                </div>
                {t.id === 'team' && (
                  <div className="text-[10px] mb-4" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
                    ≈ ${Math.round(t.annual / 12)}/mo · invoiced annually
                  </div>
                )}
                {t.id === 'pro' && billing === 'annual' && (
                  <div className="text-[10px] mb-4" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                    save $58/yr vs monthly
                  </div>
                )}

                <ul className="space-y-2 mb-8 flex-1">
                  {t.pitch.map(p => (
                    <li key={p} className="flex gap-2 text-[12px] leading-relaxed" style={{ color: 'var(--text-dim)' }}>
                      <Check size={12} strokeWidth={3} style={{ color: accent, marginTop: 4, flexShrink: 0 }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <button onClick={() => onSelectPlan(t.id)} disabled={isCurrent}
                  className="w-full px-4 py-3 transition-all hover:brightness-95"
                  style={{
                    background: isCurrent ? 'transparent' : (t.featured ? accentBg : 'var(--bg)'),
                    border: `1px solid ${isCurrent ? 'var(--border-strong)' : (t.featured ? accent : 'var(--border-strong)')}`,
                    color: isCurrent ? 'var(--text-faint)' : (t.featured ? '#1a1a17' : 'var(--text)'),
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontWeight: 600, fontSize: 12, letterSpacing: '0.05em',
                    cursor: isCurrent ? 'not-allowed' : 'pointer',
                  }}>
                  {isCurrent ? '✓ current plan' : `→ ${t.cta}`}
                </button>
              </div>
            );
          })}
        </div>

        {/* Grouped feature comparison */}
        <div className="mb-16">
          <div className="text-[10px] uppercase tracking-[0.25em] mb-6" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ feature comparison
          </div>
          <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
            {/* Header row — sticky-feeling, slightly heavier */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr] border-b" style={{ borderColor: 'var(--border-strong)', background: 'var(--bg-card)' }}>
              <div className="px-5 py-3 text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
                feature
              </div>
              {['Free', 'Pro', 'Team'].map(p => (
                <div key={p} className="px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-center" style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {p}
                </div>
              ))}
            </div>

            {featureGroups.map((g, gi) => (
              <div key={g.title}>
                {/* Group header — full-width band */}
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr] border-b" style={{ borderColor: 'var(--border-strong)', background: 'var(--bg)' }}>
                  <div className="px-5 py-2.5 col-span-4">
                    <div className="text-[10px] uppercase tracking-[0.22em] mb-0.5" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                      {String(gi + 1).padStart(2, '0')} · {g.title}
                    </div>
                    <div className="text-[11px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {g.sub}
                    </div>
                  </div>
                </div>
                {/* Group rows */}
                {g.rows.map(f => (
                  <div key={f.label} className="grid grid-cols-[2fr_1fr_1fr_1fr] border-b items-center" style={{ borderColor: 'var(--border)' }}>
                    <div className="px-5 py-3 text-[12px]" style={{ color: 'var(--text)', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {f.label}
                    </div>
                    {['free', 'pro', 'team'].map(p => (
                      <div key={p} className="px-5 py-3 text-center text-[12px] flex items-center justify-center" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {f[p] === true && <Check size={14} strokeWidth={2.5} style={{ color: accent }} />}
                        {f[p] === false && <span style={{ color: 'var(--text-faint)' }}>—</span>}
                        {typeof f[p] === 'string' && <span style={{ color: 'var(--text-dim)' }}>{f[p]}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ frequently asked
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: 'var(--border)' }}>
            {[
              {
                q: 'Why is Team annual-only?',
                a: 'Team is built for companies, not individuals. Annual contracts match how procurement actually works at the kind of org that needs SSO, BYOI, and 7-year audit retention. If you want to use STAX as one person, Pro is the right plan — Team would be over-spending.',
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Pro: yes, cancel any time, your plan stays active through the billing period then drops to Free. Saved designs stay accessible (read-only on Free if over the 3-design cap). Team: annual contracts run their term; you can choose not to renew.',
              },
              {
                q: 'What\'s the difference between Pro and Team for compliance?',
                a: 'Both unlock all 9 frameworks (SOC 2, HIPAA, PCI, GDPR, EU AI Act, etc.). Team adds: custom framework definitions (upload your own), 7-year audit log retention (vs 90 days on Pro), and the SSO + multi-seat infrastructure auditors expect for an enterprise STAX deployment.',
              },
              {
                q: 'Do I own the generated code?',
                a: 'Yes. Everything STAX generates is yours, MIT-licensed. Take it, modify it, run it on whatever you want. No phone-home, no vendor lock-in. The Free tier adds a small "Generated with STAX (Free)" line in the README — Pro and Team strip that.',
              },
              {
                q: 'What is BYOI?',
                a: 'Bring Your Own Infrastructure. Team users configure their own Terraform backend, container registry, secrets manager, audit sink, and SSO/SAML provider. Generated code uses your bucket, your registry, your IdP — STAX configures around you instead of asking you to migrate.',
              },
              {
                q: 'Is the auth real?',
                a: 'Google sign-in uses real Firebase Authentication — your account persists across devices, password is properly hashed by Google. The email/password option below is demo-only (localStorage on the current device, not properly hashed). Use Google for a real account.',
              },
            ].map(f => (
              <div key={f.q} className="p-6" style={{ background: 'var(--bg-elev)' }}>
                <h3 className="text-[14px] font-semibold mb-2">{f.q}</h3>
                <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-dim)' }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// BYOI CONFIG — bring your own infrastructure (Team plan)
// =============================================================

function BYOIConfig({ user, plan, onUpgrade, onBack, onSave }) {
  const planFeatures = PLAN_FEATURES[plan];
  const [cfg, setCfg] = useState({
    tfBackend: 's3',
    tfBackendBucket: '',
    tfBackendRegion: 'us-east-1',
    registry: 'ecr',
    registryUrl: '',
    secretsManager: 'aws-sm',
    ssoProvider: 'none',
    ssoMetadataUrl: '',
    ssoEntityId: '',
    privateModelEndpoint: '',
    privateVectorEndpoint: '',
    customAlertWebhook: '',
    auditLogSink: 'cloudwatch',
    auditLogRetentionDays: 2555,
  });
  if (!planFeatures.canBYOI) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
        <div className="max-w-[480px] text-center">
          <div className="inline-flex w-12 h-12 items-center justify-center mb-4" style={{ background: 'var(--bg-card)', border: `1px solid ${accent}` }}>
            <Lock size={20} style={{ color: accent }} />
          </div>
          <h1 className="text-[28px] font-semibold mb-3" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
            BYOI is a <span style={{ color: accent, fontStyle: 'italic' }}>Team</span> feature
          </h1>
          <p className="text-[13px] mb-6" style={{ color: 'var(--text-dim)' }}>
            Bring your own Terraform backend, registry, secrets manager, SSO provider, and private model endpoints — when you are on the Team plan.
          </p>
          <div className="flex gap-2 justify-center">
            <button onClick={onBack} className="px-4 py-2 text-[12px]"
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
              back
            </button>
            <button onClick={onUpgrade} className="px-4 py-2 text-[12px]"
              style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
              → upgrade to Team
            </button>
          </div>
        </div>
      </div>
    );
  }
  const update = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="max-w-[1100px] mx-auto px-6 py-6">
        <button onClick={onBack} className="flex items-center gap-2 text-[11px] hover:text-white transition-colors mb-8"
          style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
          <ChevronLeft size={12} /> back to dashboard
        </button>
        <div className="mb-12">
          <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ team plan · BYOI configuration
          </span>
          <h1 className="text-[36px] font-semibold leading-tight mt-3 mb-2" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
            Bring your own <span style={{ fontStyle: 'italic', color: accent }}>infrastructure</span>.
          </h1>
          <p className="text-[13px] max-w-[680px]" style={{ color: 'var(--text-dim)' }}>
            STAX will use these endpoints when generating Terraform / Helm / CI configs.
            Nothing is sent anywhere — these settings just shape the generated code.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px" style={{ background: 'var(--border)' }}>
          <BYOISection title="Terraform backend" icon={Database}>
            <BYOIField label="backend type">
              <select value={cfg.tfBackend} onChange={(e) => update('tfBackend', e.target.value)} className="byoi-input">
                <option value="s3">S3 + DynamoDB lock</option>
                <option value="gcs">GCS</option>
                <option value="azurerm">Azure Storage</option>
                <option value="terraform-cloud">Terraform Cloud</option>
                <option value="atlantis">Atlantis (self-hosted)</option>
              </select>
            </BYOIField>
            <BYOIField label="backend bucket / workspace">
              <input value={cfg.tfBackendBucket} onChange={(e) => update('tfBackendBucket', e.target.value)}
                placeholder="my-team-tfstate" className="byoi-input" />
            </BYOIField>
            <BYOIField label="backend region">
              <input value={cfg.tfBackendRegion} onChange={(e) => update('tfBackendRegion', e.target.value)}
                placeholder="us-east-1" className="byoi-input" />
            </BYOIField>
          </BYOISection>
          <BYOISection title="Container registry" icon={Boxes}>
            <BYOIField label="registry">
              <select value={cfg.registry} onChange={(e) => update('registry', e.target.value)} className="byoi-input">
                <option value="ecr">AWS ECR</option>
                <option value="gcr">Google Artifact Registry</option>
                <option value="acr">Azure Container Registry</option>
                <option value="ghcr">GitHub Container Registry</option>
                <option value="harbor">Harbor (self-hosted)</option>
                <option value="quay">Quay.io</option>
              </select>
            </BYOIField>
            <BYOIField label="registry URL">
              <input value={cfg.registryUrl} onChange={(e) => update('registryUrl', e.target.value)}
                placeholder="123456.dkr.ecr.us-east-1.amazonaws.com" className="byoi-input" />
            </BYOIField>
          </BYOISection>
          <BYOISection title="SSO / SAML provider" icon={Lock}>
            <BYOIField label="provider">
              <select value={cfg.ssoProvider} onChange={(e) => update('ssoProvider', e.target.value)} className="byoi-input">
                <option value="none">None (basic auth)</option>
                <option value="okta">Okta</option>
                <option value="azure-ad">Azure AD / Entra ID</option>
                <option value="auth0">Auth0</option>
                <option value="google-workspace">Google Workspace</option>
                <option value="onelogin">OneLogin</option>
                <option value="custom-saml">Custom SAML 2.0</option>
              </select>
            </BYOIField>
            {cfg.ssoProvider !== 'none' && (
              <>
                <BYOIField label="metadata URL">
                  <input value={cfg.ssoMetadataUrl} onChange={(e) => update('ssoMetadataUrl', e.target.value)}
                    placeholder="https://idp.example.com/metadata" className="byoi-input" />
                </BYOIField>
                <BYOIField label="entity ID">
                  <input value={cfg.ssoEntityId} onChange={(e) => update('ssoEntityId', e.target.value)}
                    placeholder="urn:idp:example" className="byoi-input" />
                </BYOIField>
              </>
            )}
          </BYOISection>
          <BYOISection title="Audit log sink" icon={ShieldCheck}>
            <BYOIField label="sink">
              <select value={cfg.auditLogSink} onChange={(e) => update('auditLogSink', e.target.value)} className="byoi-input">
                <option value="cloudwatch">CloudWatch Logs</option>
                <option value="datadog">Datadog</option>
                <option value="splunk">Splunk HEC</option>
                <option value="elastic">Elasticsearch</option>
                <option value="s3-immutable">S3 with Object Lock (immutable)</option>
                <option value="custom-webhook">Custom webhook</option>
              </select>
            </BYOIField>
            <BYOIField label="retention (days)" hint="default 2555 (7 years) for HIPAA / PCI">
              <input type="number" value={cfg.auditLogRetentionDays} onChange={(e) => update('auditLogRetentionDays', parseInt(e.target.value) || 0)}
                className="byoi-input" />
            </BYOIField>
          </BYOISection>
          <BYOISection title="Private model endpoint" icon={Sparkles}>
            <BYOIField label="endpoint URL" hint="Override the generated provider — e.g. own vLLM cluster">
              <input value={cfg.privateModelEndpoint} onChange={(e) => update('privateModelEndpoint', e.target.value)}
                placeholder="https://llm.internal.company.com/v1" className="byoi-input" />
            </BYOIField>
          </BYOISection>
          <BYOISection title="Private vector store" icon={Database}>
            <BYOIField label="endpoint URL" hint="Use existing pgvector / OpenSearch / Pinecone">
              <input value={cfg.privateVectorEndpoint} onChange={(e) => update('privateVectorEndpoint', e.target.value)}
                placeholder="https://vectors.internal.company.com" className="byoi-input" />
            </BYOIField>
          </BYOISection>
        </div>
        <div className="mt-8 flex justify-end gap-2">
          <button onClick={onBack} className="px-4 py-2.5 text-[12px]"
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
            cancel
          </button>
          <button onClick={() => onSave(cfg)} className="px-5 py-2.5 text-[12px]"
            style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
            → save BYOI config
          </button>
        </div>
        <style>{`
          .byoi-input {
            background: var(--bg);
            border: 1px solid var(--border-strong);
            color: var(--text);
            font-family: 'IBM Plex Mono', monospace;
            font-size: 12px;
            padding: 8px 12px;
            width: 100%;
            outline: none;
          }
          .byoi-input:focus { border-color: var(--accent); }
        `}</style>
      </div>
    </div>
  );
}

function BYOISection({ title, icon: Icon, children }) {
  return (
    <div className="p-6" style={{ background: 'var(--bg-elev)' }}>
      <div className="flex items-center gap-2 mb-5">
        <Icon size={13} style={{ color: accent }} />
        <h3 className="text-[12px] uppercase tracking-[0.18em]" style={{ color: 'var(--text)', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
          {title}
        </h3>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function BYOIField({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.18em] block mb-1.5" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
        {label}
      </label>
      {children}
      {hint && <p className="text-[10px] mt-1" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{hint}</p>}
    </div>
  );
}

// =============================================================
// ACCOUNT PAGE
// =============================================================

function AccountPage({ user, designs, onBack, onUpgrade, onSignOut }) {
  const planFeatures = PLAN_FEATURES[user.plan] || PLAN_FEATURES.free;
  const usage = {
    designs: designs.length,
    designLimit: planFeatures.designLimit,
    usedPct: planFeatures.designLimit === -1 ? 0 : (designs.length / planFeatures.designLimit) * 100,
  };
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="max-w-[900px] mx-auto px-6 py-6">
        <button onClick={onBack} className="flex items-center gap-2 text-[11px] hover:text-white transition-colors mb-8"
          style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
          <ChevronLeft size={12} /> back to dashboard
        </button>
        <h1 className="text-[36px] font-semibold leading-tight mb-12" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
          Account
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mb-8" style={{ background: 'var(--border)' }}>
          <div className="p-6" style={{ background: 'var(--bg-elev)' }}>
            <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              profile
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>name</div>
                <div className="text-[14px] mt-0.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{user.name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>email</div>
                <div className="text-[14px] mt-0.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{user.email}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>since</div>
                <div className="text-[14px] mt-0.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {new Date(user.createdAt).toISOString().slice(0, 10)}
                </div>
              </div>
            </div>
          </div>
          <div className="p-6" style={{ background: 'var(--bg-elev)' }}>
            <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              plan & usage
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-[28px] font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                {planFeatures.name}
              </span>
              <span className="text-[12px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                ${planFeatures.priceUSD}/mo
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
              designs used
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-[18px] tabular-nums" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {usage.designs}
              </span>
              <span className="text-[11px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                / {planFeatures.designLimit === -1 ? '∞' : planFeatures.designLimit}
              </span>
            </div>
            <div className="h-1.5" style={{ background: 'var(--border)' }}>
              <div className="h-full transition-all" style={{ background: accentBg, width: `${Math.min(100, usage.usedPct)}%` }} />
            </div>
            <div className="mt-4 flex gap-2">
              {user.plan !== 'team' && (
                <button onClick={onUpgrade} className="px-3 py-1.5 text-[11px]"
                  style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
                  upgrade →
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="p-6" style={{ background: 'var(--bg-elev)', border: '1px solid #fb718540' }}>
          <div className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: '#be123c', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ session
          </div>
          <h3 className="text-[15px] font-semibold mb-1">Sign out of STAX</h3>
          <p className="text-[12px] mb-4" style={{ color: 'var(--text-dim)' }}>
            Your saved designs stay encrypted. Sign in again to retrieve them.
          </p>
          <button onClick={onSignOut} className="px-4 py-2 text-[12px]"
            style={{ background: 'transparent', border: '1px solid #fb7185', color: '#be123c', fontFamily: 'IBM Plex Mono, monospace' }}>
            sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// MARKETING PAGES
// Shared shell (top nav + footer) + 11 standalone content pages.
// All accept { nav, user } where nav is setRoute from App.
// =============================================================

function MarketingShell({ nav, user, eyebrow, title, subtitle, children }) {
  return (
    <div className="min-h-screen relative" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <MarketingNav nav={nav} user={user} />
      <header className="relative max-w-[1400px] mx-auto px-6 pt-16 pb-12 border-b" style={{ borderColor: 'var(--border)' }}>
        {eyebrow && (
          <div className="text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
            ⏵ {eyebrow}
          </div>
        )}
        <h1 className="text-[44px] md:text-[56px] leading-[1] font-semibold max-w-[900px]"
          style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.02em' }}>
          {title}
        </h1>
        {subtitle && (
          <p className="mt-4 text-[16px] leading-relaxed max-w-[640px]" style={{ color: 'var(--text-dim)' }}>
            {subtitle}
          </p>
        )}
      </header>
      <main className="relative max-w-[1400px] mx-auto px-6 py-16">
        {children}
      </main>
      <LandingFooter nav={nav} />
    </div>
  );
}

function MarketingNav({ nav, user }) {
  return (
    <nav className="relative z-10 max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between border-b" style={{ borderColor: 'var(--border)' }}>
      <button onClick={() => nav('landing')} className="flex items-center gap-2.5 group">
        <div className="w-7 h-7 flex items-center justify-center transition-transform group-hover:rotate-12" style={{ background: accentBg }}>
          <Hexagon size={14} strokeWidth={2.5} style={{ color: '#1a1a17' }} fill="#1a1a17" />
        </div>
        <span className="text-[15px] tracking-[0.2em] font-semibold">STAX</span>
      </button>
      <div className="flex items-center gap-1">
        <button onClick={() => nav('features')} className="px-3 py-2 text-[12px] hover:text-black transition-colors"
          style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>features</button>
        <button onClick={() => nav('pricing')} className="px-3 py-2 text-[12px] hover:text-black transition-colors"
          style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>pricing</button>
        <button onClick={() => nav('quickstart')} className="hidden md:inline-block px-3 py-2 text-[12px] hover:text-black transition-colors"
          style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>docs</button>
        {user ? (
          <button onClick={() => nav('dashboard')} className="ml-2 px-4 py-2"
            style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600 }}>
            dashboard →
          </button>
        ) : (
          <>
            <button onClick={() => nav('signin')} className="px-3 py-2 text-[12px] hover:text-black transition-colors"
              style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>sign in</button>
            <button onClick={() => nav('signup')} className="ml-1 px-4 py-2"
              style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600 }}>
              get started
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

// ── Reusable layout primitives for marketing copy ──────────────
const SectionHead = ({ num, title, sub }) => (
  <div className="mb-8">
    {num && (
      <div className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
        ⏵ {num}
      </div>
    )}
    <h2 className="text-[28px] md:text-[32px] font-semibold leading-tight" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.01em' }}>
      {title}
    </h2>
    {sub && <p className="mt-2 text-[14px] max-w-[640px]" style={{ color: 'var(--text-dim)' }}>{sub}</p>}
  </div>
);

const Card = ({ children, accent: a }) => (
  <div className="p-6" style={{ background: 'var(--bg-elev)', border: `1px solid ${a ? accent : 'var(--border-strong)'}` }}>
    {children}
  </div>
);

const InlineCode = ({ children }) => (
  <code className="px-1.5 py-0.5 text-[12px]"
    style={{ background: 'var(--bg-card)', color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', border: '1px solid var(--border-strong)' }}>
    {children}
  </code>
);

const TerminalBlock = ({ lines }) => (
  <pre className="p-4 overflow-x-auto text-[12px] leading-relaxed"
    style={{ background: '#1a1a17', color: '#fbfaf7', fontFamily: 'IBM Plex Mono, monospace', border: '1px solid var(--border-strong)' }}>
    {lines.map((l, i) => {
      if (l.startsWith('$')) return <div key={i}><span style={{ color: accentSoft }}>{l[0]}</span><span style={{ color: '#fbfaf7' }}>{l.slice(1)}</span></div>;
      if (l.startsWith('#')) return <div key={i} style={{ color: '#6f6e68' }}>{l}</div>;
      if (l.startsWith('✓') || l.startsWith('→')) return <div key={i} style={{ color: accentSoft }}>{l}</div>;
      return <div key={i} style={{ color: '#9a9890' }}>{l || '\u00A0'}</div>;
    })}
  </pre>
);

// =============================================================
// FEATURES PAGE
// =============================================================

function FeaturesPage({ nav, user }) {
  const capabilities = [
    {
      n: '01', name: 'Multi-cloud, no lock-in', icon: Cloud,
      tagline: 'Same agent, three clouds. Switch in a config flip.',
      bullets: [
        'Identical-shape Terraform for AWS, GCP, and Azure — VPCs, subnets, IAM, KMS, secrets, all named consistently.',
        'Compute primitives mapped per provider: EKS / GKE / AKS for Kubernetes; Fargate / Cloud Run / ACI for serverless containers; Lambda / Cloud Functions / Azure Functions for FaaS.',
        'Provider-aware AI inference: Bedrock on AWS, Vertex on GCP, Azure OpenAI on Azure — or self-hosted vLLM on a GPU node group on any of them.',
        'No proprietary CRDs, no vendor SDKs in the agent code. Standard FastAPI + the LLM provider of your choice.',
      ],
    },
    {
      n: '02', name: 'AI security as the spine', icon: ShieldCheck,
      tagline: 'Six layers between every user prompt and your model.',
      bullets: [
        'PII redaction (Presidio, Comprehend, Cloud DLP, or Piiranha) runs before the LLM ever sees the input.',
        'Prompt firewall (Bedrock Guardrails, Azure Content Safety, Lakera, LLM Guard, or Prompt Armor) catches injection, jailbreaks, and policy violations.',
        'Denied-topic filter rejects off-policy requests with explicit rules instead of model heuristics.',
        'Output moderation re-scans LLM responses for PII, toxicity, and brand safety before sending to the client.',
        'Append-only audit log captures every call (input hash, decisions, latency, cost) — SOC 2, GDPR, EU AI Act compliant.',
        'Continuous AI red-teaming with garak, PyRIT, and Promptfoo runs nightly on every deployed agent.',
      ],
    },
    {
      n: '03', name: 'Compliance by construction', icon: FileCode2,
      tagline: 'Pick your frameworks. Get a control-to-artifact matrix.',
      bullets: [
        'Nine frameworks supported: SOC 2 Type II, ISO 27001, ISO 42001, GDPR, HIPAA, PCI-DSS, EU AI Act, NIST AI RMF, FedRAMP Moderate.',
        'Every selected framework generates a markdown matrix mapping individual controls (e.g. SOC 2 CC6.1, EU AI Act Art. 12) to the specific file in your repo that implements it.',
        'Auditors get a one-page deliverable. Engineers get a checklist tied to actual code.',
        'Re-run STAX after a regulatory change and the matrix updates automatically.',
      ],
    },
    {
      n: '04', name: 'Continuous AI red-teaming', icon: Activity,
      tagline: 'Adversarial probing, not vibes-based testing.',
      bullets: [
        'GitHub Action runs nightly: garak (50+ probe categories), PyRIT for multi-turn attacks, Promptfoo for prompt regression suites.',
        'Findings flow into your existing alerting (PagerDuty / Slack / Opsgenie) — same channels as a Falco runtime alert.',
        'Eval scores tracked over time so you can see when a model upgrade or prompt change degrades robustness.',
        'Pre-built attack libraries for prompt injection, data exfiltration, function-call abuse, and tool poisoning.',
      ],
    },
    {
      n: '05', name: 'MCP-native', icon: Boxes,
      tagline: 'Discoverable by Claude Desktop, Cursor, and the agentic.ai directory the day you ship.',
      bullets: [
        'Every generated agent ships with an MCP server stub exposing your tools as standard MCP capabilities.',
        'Claude Desktop and Cursor pick up the manifest at /.well-known/mcp.json automatically.',
        'agentic.ai directory submission is a single CI step. Auto-generates the agent.json and submission.json.',
        'Self-reported agenticness scoring (L0-L5) across 8 dimensions (goal, tools, planning, memory, autonomy, observe, adapt, reach).',
      ],
    },
    {
      n: '06', name: 'Rollout DAG, not a pile of tickets', icon: GitBranch,
      tagline: 'Deployment plans that respect dependencies and team boundaries.',
      bullets: [
        'Each generated stack ships with a wave-layered deployment plan (Jira, Linear, or GitHub Issues).',
        'Dependency-correct sequencing: networking before compute, compute before data, data before agents.',
        'Per-team assignments derived from your component ownership map.',
        'Critical path computed automatically, with effort estimates so PMs can timeline.',
      ],
    },
  ];

  return (
    <MarketingShell nav={nav} user={user}
      eyebrow="features"
      title={<>What you actually <em style={{ color: accent, fontStyle: 'italic' }}>get</em>.</>}
      subtitle="STAX generates a complete production stack in 14 minutes — not a starter template, not a boilerplate, not a tutorial. The full thing: Terraform, K8s, agent runtime, guardrails, CI/CD, compliance matrix.">

      <div className="space-y-px" style={{ background: 'var(--border)' }}>
        {capabilities.map(cap => {
          const Icon = cap.icon;
          return (
            <div key={cap.n} className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-6 p-8" style={{ background: 'var(--bg)' }}>
              <div>
                <div className="text-[10px] tabular-nums mb-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {cap.n}
                </div>
                <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)' }}>
                  <Icon size={18} style={{ color: accent }} />
                </div>
              </div>
              <div>
                <h3 className="text-[22px] font-semibold leading-tight" style={{ fontFamily: 'IBM Plex Sans' }}>{cap.name}</h3>
                <p className="text-[14px] mt-1 mb-4" style={{ color: 'var(--text-dim)' }}>{cap.tagline}</p>
                <ul className="space-y-2">
                  {cap.bullets.map((b, i) => (
                    <li key={i} className="flex gap-3 text-[13px] leading-relaxed">
                      <span className="flex-shrink-0 w-4 h-4 mt-0.5 flex items-center justify-center" style={{ background: 'var(--bg-card)' }}>
                        <Check size={10} strokeWidth={3} style={{ color: accent }} />
                      </span>
                      <span style={{ color: 'var(--text-dim)' }}>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="mt-16 p-12 text-center" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
        <h3 className="text-[28px] font-semibold mb-3" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
          Generate your first stack in <em style={{ color: accent, fontStyle: 'italic' }}>14 minutes</em>.
        </h3>
        <p className="text-[14px] mb-6 max-w-[480px] mx-auto" style={{ color: 'var(--text-dim)' }}>
          Free plan. No credit card. Three saved designs. Real generated code, MIT-licensed, all yours.
        </p>
        <button onClick={() => nav(user ? 'dashboard' : 'signup')} className="px-6 py-3"
          style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, fontSize: 13, letterSpacing: '0.05em' }}>
          → start building free
        </button>
      </div>
    </MarketingShell>
  );
}

// =============================================================
// BYOI INFO PAGE (public marketing — different from BYOIConfig)
// =============================================================

function BYOIInfoPage({ nav, user }) {
  const integrations = [
    { cat: 'Terraform backend', items: ['S3 + DynamoDB lock', 'Google Cloud Storage', 'Azure Storage', 'Terraform Cloud workspaces', 'Atlantis (self-hosted)'] },
    { cat: 'Container registry', items: ['AWS ECR', 'Google Artifact Registry', 'Azure Container Registry', 'GitHub Container Registry', 'Harbor (self-hosted)', 'Quay.io'] },
    { cat: 'SSO / SAML provider', items: ['Okta', 'Microsoft Entra ID (Azure AD)', 'Auth0', 'Google Workspace', 'OneLogin', 'Custom SAML 2.0'] },
    { cat: 'Audit log sink', items: ['CloudWatch Logs', 'Datadog', 'Splunk HEC', 'Elasticsearch', 'S3 with Object Lock (immutable)', 'Custom webhook'] },
    { cat: 'Private model endpoints', items: ['Self-hosted vLLM', 'TGI on bare metal', 'On-prem Llama 3.3 / Mistral', 'OpenAI-compatible internal proxies', 'Air-gapped inference'] },
    { cat: 'Private vector store', items: ['Self-hosted pgvector', 'Internal OpenSearch', 'On-prem Qdrant', 'Weaviate cluster', 'Pinecone enterprise tenant'] },
  ];

  return (
    <MarketingShell nav={nav} user={user}
      eyebrow="bring your own infrastructure"
      title={<>Your stack. Your <em style={{ color: accent, fontStyle: 'italic' }}>backends</em>.</>}
      subtitle="STAX generates the code. Where it deploys, where state lives, who can sign in, how audit logs are stored — all yours. BYOI is included on the Team plan.">

      <SectionHead num="01" title="Why BYOI?" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {[
          { t: 'No data residency surprises', d: 'You decide which region holds the Terraform state, which cluster runs the agent, where audit logs land. STAX never touches it.' },
          { t: 'Compliance-friendly', d: 'Auditors want to see the data plane on infrastructure you control. BYOI hands them exactly that.' },
          { t: 'Use what you already have', d: "Already have Okta? An immutable S3 bucket for audit logs? A vLLM cluster on H100s? STAX configures around them rather than asking you to migrate." },
        ].map(b => (
          <Card key={b.t}>
            <h4 className="text-[15px] font-semibold mb-2">{b.t}</h4>
            <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-dim)' }}>{b.d}</p>
          </Card>
        ))}
      </div>

      <SectionHead num="02" title="What you can bring" sub="Six categories of backends, dozens of supported providers across each. Mix-and-match — bring some, let STAX provision the rest." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px mb-16" style={{ background: 'var(--border)' }}>
        {integrations.map(g => (
          <div key={g.cat} className="p-6" style={{ background: 'var(--bg-elev)' }}>
            <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              {g.cat}
            </div>
            <ul className="space-y-1.5">
              {g.items.map(i => (
                <li key={i} className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
                  <Check size={11} strokeWidth={3} style={{ color: accent }} />
                  {i}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <SectionHead num="03" title="How it flows through" sub="BYOI values shape every generator output. They are not stored on STAX servers — they live only in your local config and the generated repo." />
      <div className="mb-16">
        <TerminalBlock lines={[
          '# 1. Configure BYOI from the dashboard (Team plan)',
          '$ stax → settings → BYOI',
          '',
          '# 2. Open your design',
          '$ stax → my-rag-agent → generate',
          '',
          '# 3. Generated terraform/main.tf — your bucket, your region',
          'terraform {',
          '  backend "s3" {',
          '    bucket = "acme-platform-tfstate"   # ← from BYOI',
          '    key    = "prod/terraform.tfstate"',
          '    region = "eu-west-1"               # ← from BYOI',
          '  }',
          '}',
          '',
          '# 4. Generated CI pushes to your registry',
          '✓ docker push ghcr.io/acme/agent:${SHA}',
          '',
          '# 5. Generated agent code calls your private LLM',
          '✓ LLM_BASE_URL=https://llm.acme.internal/v1',
        ]} />
      </div>

      <SectionHead num="04" title="Pricing" sub="BYOI is a Team-plan feature. $199/month, 10 seats, unlimited designs." />
      <div className="flex items-center gap-3">
        <button onClick={() => nav('pricing')} className="px-5 py-2.5"
          style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600, fontSize: 12 }}>
          → see pricing
        </button>
        <button onClick={() => nav('quickstart')} className="px-5 py-2.5"
          style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12 }}>
          quickstart docs
        </button>
      </div>
    </MarketingShell>
  );
}

// =============================================================
// CHANGELOG PAGE
// =============================================================

function ChangelogPage({ nav, user }) {
  const releases = [
    {
      version: 'v1.0.0', date: '2026-05-01', tag: 'STABLE',
      title: 'General availability',
      items: [
        { type: 'feat', txt: 'Multi-tenancy on Team plan — 10 seats per workspace, per-design ACLs.' },
        { type: 'feat', txt: 'BYOI now supports Atlantis backends and custom SAML 2.0 providers.' },
        { type: 'feat', txt: 'New compliance framework: ISO/IEC 42001 (AI management system).' },
        { type: 'fix',  txt: 'Bedrock Guardrails: corrected JSON shape for v2 of the Converse API.' },
        { type: 'fix',  txt: 'Rollout DAG: critical-path now honors blocked-by + blocks-merge labels correctly.' },
      ],
    },
    {
      version: 'v0.9.4', date: '2026-04-12', tag: 'BETA',
      title: 'AI red-team workflow',
      items: [
        { type: 'feat', txt: 'New GitHub workflow: ai-redteam.yml runs garak + PyRIT + Promptfoo nightly.' },
        { type: 'feat', txt: 'Defense-in-depth diagram now animates packet flow live in the output view.' },
        { type: 'feat', txt: 'Posture grade (A-F) shown on every design card in the dashboard.' },
        { type: 'fix',  txt: 'GCP Workload Identity binding: corrected the trust principal format.' },
      ],
    },
    {
      version: 'v0.9.0', date: '2026-03-20', tag: 'BETA',
      title: 'Security spine reframe',
      items: [
        { type: 'feat', txt: 'New top-level Security step in the wizard, between AI and Networking.' },
        { type: 'feat', txt: 'Continuous scan tools: Trivy, Checkov, Gitleaks, Syft, Semgrep, Falco.' },
        { type: 'feat', txt: 'Compliance matrix auto-generated for SOC 2, ISO 27001, GDPR, HIPAA, PCI-DSS, EU AI Act, NIST AI RMF, FedRAMP.' },
        { type: 'feat', txt: 'Alert rules + AlertManager routing config (PagerDuty, Opsgenie, Slack, webhook, email).' },
        { type: 'breaking', txt: 'Renamed config.guardrails to config.promptFirewall to disambiguate from output moderation.' },
      ],
    },
    {
      version: 'v0.8.0', date: '2026-02-14', tag: 'BETA',
      title: 'Agentic.ai directory integration',
      items: [
        { type: 'feat', txt: 'Generate agent.json + submission.json + MCP server stub in directory/ folder.' },
        { type: 'feat', txt: 'Self-reported agenticness scoring across 8 dimensions, L0-L5.' },
        { type: 'feat', txt: 'submit-directory.sh script for one-shot submission to agentic.ai.' },
      ],
    },
    {
      version: 'v0.7.0', date: '2026-01-22', tag: 'BETA',
      title: 'Rollout DAG generator',
      items: [
        { type: 'feat', txt: 'New Rollout step generates Jira, Linear, or GitHub Issues from your stack.' },
        { type: 'feat', txt: 'Wave-layered deployment plan with dependency-correct sequencing.' },
        { type: 'feat', txt: 'Effort estimates per ticket; total points and critical path computed.' },
        { type: 'feat', txt: 'Per-team assignment based on component ownership.' },
      ],
    },
    {
      version: 'v0.6.0', date: '2025-12-08', tag: 'BETA',
      title: 'AI step',
      items: [
        { type: 'feat', txt: 'AI inference: Bedrock, SageMaker, vLLM (self-hosted), Vertex AI, Azure OpenAI.' },
        { type: 'feat', txt: 'Vector stores: pgvector, OpenSearch, Qdrant, Pinecone, Weaviate.' },
        { type: 'feat', txt: 'Agent frameworks: LangGraph, LlamaIndex, CrewAI, PydanticAI.' },
        { type: 'feat', txt: 'LLM observability: LangSmith, Langfuse, Phoenix.' },
      ],
    },
  ];

  const tagColor = (t) =>
    t === 'STABLE' ? accent : t === 'BETA' ? '#b45309' : '#9a9890';
  const itemColor = (t) =>
    t === 'feat' ? accent : t === 'fix' ? '#52524d' : t === 'breaking' ? '#be123c' : '#6f6e68';

  return (
    <MarketingShell nav={nav} user={user}
      eyebrow="changelog"
      title={<>Every shipped change, in <em style={{ color: accent, fontStyle: 'italic' }}>reverse order</em>.</>}
      subtitle="Versioned releases. No silent updates. Breaking changes called out explicitly. Subscribe via RSS at /changelog/feed.xml or watch the GitHub repo.">

      <div className="space-y-px" style={{ background: 'var(--border)' }}>
        {releases.map(r => (
          <article key={r.version} className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 p-8" style={{ background: 'var(--bg)' }}>
            <div>
              <div className="text-[20px] font-semibold tabular-nums" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {r.version}
              </div>
              <div className="text-[11px] mt-1" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
                {r.date}
              </div>
              <div className="mt-2 inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em]"
                style={{ background: 'var(--bg-card)', color: tagColor(r.tag), border: `1px solid ${tagColor(r.tag)}40`, fontFamily: 'IBM Plex Mono, monospace' }}>
                {r.tag}
              </div>
            </div>
            <div>
              <h3 className="text-[18px] font-semibold mb-4">{r.title}</h3>
              <ul className="space-y-2">
                {r.items.map((it, i) => (
                  <li key={i} className="flex gap-3 text-[13px] leading-relaxed">
                    <span className="text-[10px] uppercase tracking-[0.15em] w-16 flex-shrink-0 mt-0.5 tabular-nums"
                      style={{ color: itemColor(it.type), fontFamily: 'IBM Plex Mono, monospace' }}>
                      {it.type}
                    </span>
                    <span style={{ color: 'var(--text-dim)' }}>{it.txt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-12 p-6 flex items-center justify-between flex-wrap gap-3" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
        <span className="text-[13px]" style={{ color: 'var(--text-dim)' }}>
          Subscribe to release announcements via RSS or follow on GitHub.
        </span>
        <div className="flex gap-2">
          <button className="px-4 py-2 text-[12px]"
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
            RSS feed
          </button>
          <button className="px-4 py-2 text-[12px]"
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
            github releases
          </button>
        </div>
      </div>
    </MarketingShell>
  );
}

// =============================================================
// QUICKSTART PAGE
// =============================================================

function QuickstartPage({ nav, user }) {
  const steps = [
    {
      n: '01',
      title: 'Sign up + create your first design',
      detail: 'No CLI install needed. Everything runs in the browser. Designs are auto-saved to your account.',
      terminal: [
        '# Browser-based — no install',
        '$ open https://stax.dev',
        '→ click "start building free"',
        '→ enter email, choose password',
        '✓ account created · free plan · 3 designs',
      ],
    },
    {
      n: '02',
      title: 'Walk the 12-step wizard',
      detail: 'Every step has sensible defaults. Click through fast for a first stack, or stop and configure deeply. Auto-saves every 1.2s.',
      terminal: [
        '# Step rail',
        '01 project   → name, environment (dev/staging/prod)',
        '02 cloud     → AWS · GCP · Azure',
        '03 compute   → EKS / Fargate / Lambda / etc.',
        '04 data      → RDS / DynamoDB / Redis',
        '05 ai        → Bedrock / Vertex / vLLM + vector store',
        '06 security  → guardrails, scans, compliance',
        '07 networking',
        '08 observability',
        '09 ci/cd',
        '10 rollout   → Jira / Linear / GitHub Issues',
        '11 directory → agentic.ai submission',
        '12 generate  → 52 files · 8,432 LOC',
      ],
    },
    {
      n: '03',
      title: 'Generate + download',
      detail: 'STAX produces a complete repo. Terraform, K8s manifests, agent code, CI/CD, compliance docs. MIT-licensed, all yours.',
      terminal: [
        '$ stax generate',
        '✓ terraform/main.tf',
        '✓ terraform/networking.tf',
        '✓ terraform/ai.tf',
        '✓ k8s/deployment.yaml',
        '✓ agent/main.py',
        '✓ agent/guardrails.py',
        '✓ .github/workflows/deploy.yml',
        '✓ .github/workflows/security.yml',
        '✓ .github/workflows/ai-redteam.yml',
        '✓ compliance/control-matrix.md',
        '✓ SECURITY.md · README.md',
        '',
        '52 files · posture A · L3 agent · ~$1,847/mo',
        '$ download stax.zip',
      ],
    },
    {
      n: '04',
      title: 'Deploy',
      detail: 'STAX outputs are standard. No proprietary CLI, no phone-home, no hidden runtime. Run terraform apply when you\'re ready.',
      terminal: [
        '$ unzip stax.zip && cd phoenix-agent',
        '$ terraform -chdir=terraform init',
        '$ terraform -chdir=terraform plan',
        '$ terraform -chdir=terraform apply',
        '',
        '# CI takes over from here',
        '$ git init && git remote add origin git@github.com:acme/phoenix.git',
        '$ git push origin main',
        '✓ .github/workflows/deploy.yml triggered',
        '✓ image built · scanned · deployed',
        '✓ live at https://phoenix.acme.com',
      ],
    },
  ];

  return (
    <MarketingShell nav={nav} user={user}
      eyebrow="quickstart"
      title={<>Stack to <em style={{ color: accent, fontStyle: 'italic' }}>production</em> in one afternoon.</>}
      subtitle="Four steps. No installation. The first design is free. Total time, signup to deployed: about 90 minutes including the terraform apply.">

      <div className="space-y-12">
        {steps.map(s => (
          <div key={s.n} className="grid grid-cols-1 md:grid-cols-[80px_1fr_1.2fr] gap-6 items-start">
            <div className="text-[32px] tabular-nums font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
              {s.n}
            </div>
            <div>
              <h3 className="text-[20px] font-semibold leading-tight mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>{s.title}</h3>
              <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-dim)' }}>{s.detail}</p>
            </div>
            <TerminalBlock lines={s.terminal} />
          </div>
        ))}
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: 'var(--border)' }}>
        {[
          { t: 'Examples', d: 'Six real stack configs to riff on.', to: 'examples' },
          { t: 'Quickstart', d: 'Generate your first stack in five minutes.', to: 'quickstart' },
          { t: 'Compliance guide', d: 'Map controls to artifacts.', to: 'compliance-guide' },
        ].map(c => (
          <button key={c.t} onClick={() => nav(c.to)}
            className="text-left p-6 hover:bg-[#ffffff] transition-colors"
            style={{ background: 'var(--bg-elev)' }}>
            <div className="flex items-center gap-2 text-[15px] font-semibold mb-2">
              {c.t} <ChevronRight size={14} style={{ color: accent }} />
            </div>
            <p className="text-[12px]" style={{ color: 'var(--text-dim)' }}>{c.d}</p>
          </button>
        ))}
      </div>
    </MarketingShell>
  );
}

// =============================================================
// EXAMPLES PAGE
// =============================================================

function ExamplesPage({ nav, user }) {
  const examples = [
    {
      slug: 'rag-support-agent',
      name: 'RAG support agent',
      tagline: 'Customer support over a knowledge base, with citations + handoff.',
      stack: ['AWS', 'EKS', 'RDS PostgreSQL + pgvector', 'Bedrock (Claude 3.5 Sonnet)', 'LangGraph', 'Langfuse'],
      posture: 'A · 100/100',
      agentic: 'L3 · Goal-directed Worker',
      effort: '38 pts · 4 waves',
      cost: '~$1,840/mo',
      desc: 'A help-desk agent that answers customer questions over your support docs, escalates to a human when uncertain, and logs every interaction for review.',
    },
    {
      slug: 'code-review-agent',
      name: 'Code review agent',
      tagline: 'PR review that runs on every push, comments inline, suggests diffs.',
      stack: ['GCP', 'Cloud Run', 'Cloud SQL + pgvector', 'Vertex AI (Gemini 2.5 Pro)', 'PydanticAI', 'Phoenix'],
      posture: 'A · 96/100',
      agentic: 'L3 · Goal-directed Worker',
      effort: '32 pts · 3 waves',
      cost: '~$640/mo',
      desc: 'GitHub App that reviews pull requests against your style guide + previous-PR feedback. Suggests inline diffs the author can apply with one click.',
    },
    {
      slug: 'compliance-bot',
      name: 'Compliance audit bot',
      tagline: 'Reads your repo + cloud config and flags SOC 2 / HIPAA gaps.',
      stack: ['Azure', 'AKS', 'Cosmos DB + Pinecone', 'Azure OpenAI (GPT-5)', 'CrewAI', 'LangSmith'],
      posture: 'A · 100/100',
      agentic: 'L4 · Autonomous Operator',
      effort: '54 pts · 5 waves',
      cost: '~$2,920/mo',
      desc: 'A multi-agent crew that crawls your IaC + audit logs, maps findings to your selected compliance frameworks, and drafts remediation tickets.',
    },
    {
      slug: 'sales-research',
      name: 'Sales research agent',
      tagline: 'Researches prospects from public sources, drafts personalised intros.',
      stack: ['AWS', 'Lambda', 'DynamoDB + OpenSearch', 'Bedrock (Llama 3.3 70B)', 'LlamaIndex', 'Langfuse'],
      posture: 'B · 78/100',
      agentic: 'L2 · Reactive Assistant',
      effort: '24 pts · 3 waves',
      cost: '~$420/mo',
      desc: 'Triggered by CRM events, gathers company info from public APIs and the web, drafts a personalised outreach email for the rep to review and send.',
    },
    {
      slug: 'incident-triage',
      name: 'Incident triage agent',
      tagline: 'Pages on alerts, gathers context from logs/metrics, drafts the postmortem.',
      stack: ['GCP', 'GKE', 'AlloyDB + Qdrant', 'Vertex AI (Gemini 2.5 Pro)', 'LangGraph', 'Phoenix'],
      posture: 'A · 96/100',
      agentic: 'L4 · Autonomous Operator',
      effort: '46 pts · 4 waves',
      cost: '~$2,160/mo',
      desc: 'When PagerDuty fires, the agent pulls the last 30m of metrics + relevant logs, summarises the timeline in #incidents, and drafts a first-cut postmortem.',
    },
    {
      slug: 'doc-writer',
      name: 'Internal doc writer',
      tagline: 'Watches your codebase and keeps engineering docs current.',
      stack: ['AWS', 'Fargate', 'RDS PostgreSQL + Weaviate', 'Bedrock (Claude 3.5 Sonnet)', 'LangGraph', 'Langfuse'],
      posture: 'A · 92/100',
      agentic: 'L3 · Goal-directed Worker',
      effort: '36 pts · 3 waves',
      cost: '~$890/mo',
      desc: 'Subscribes to merged PRs in selected repos, identifies the modules affected, and updates the corresponding doc page in your wiki — with the engineer as reviewer.',
    },
  ];

  return (
    <MarketingShell nav={nav} user={user}
      eyebrow="examples"
      title={<>Six real <em style={{ color: accent, fontStyle: 'italic' }}>stacks</em>. Take one. Modify it. Ship.</>}
      subtitle="Each example below is a full STAX configuration that generates a working agent. They're starting points — clone one, adjust the model or compliance frameworks, regenerate.">

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {examples.map(ex => (
          <div key={ex.slug} className="p-6" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
              <h3 className="text-[18px] font-semibold leading-tight">{ex.name}</h3>
              <span className="text-[10px] tabular-nums" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>
                {ex.posture}
              </span>
            </div>
            <p className="text-[13px] mb-4" style={{ color: 'var(--text-dim)' }}>{ex.tagline}</p>
            <p className="text-[12px] leading-relaxed mb-4" style={{ color: 'var(--text-mute)' }}>{ex.desc}</p>

            <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              stack
            </div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {ex.stack.map(s => (
                <span key={s} className="px-2 py-0.5 text-[10px]" style={{ background: 'var(--bg-card)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace', border: '1px solid var(--border)' }}>
                  {s}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-3 divide-x text-center" style={{ borderColor: 'var(--border)' }}>
              {[
                { l: 'agentic', v: ex.agentic.split(' · ')[0] },
                { l: 'effort',  v: ex.effort },
                { l: 'cost',    v: ex.cost },
              ].map(s => (
                <div key={s.l} className="px-2 py-2" style={{ borderColor: 'var(--border)' }}>
                  <div className="text-[9px] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{s.l}</div>
                  <div className="text-[11px] font-semibold mt-0.5 tabular-nums" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{s.v}</div>
                </div>
              ))}
            </div>

            <button onClick={() => nav(user ? 'app' : 'signup')}
              className="w-full mt-4 px-4 py-2 text-[11px]"
              style={{ background: 'transparent', border: `1px solid ${accent}`, color: accent, fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
              → start from this example
            </button>
          </div>
        ))}
      </div>
    </MarketingShell>
  );
}

// =============================================================
// COMPLIANCE GUIDE PAGE
// =============================================================

function ComplianceGuidePage({ nav, user }) {
  const frameworks = [
    {
      id: 'soc2', name: 'SOC 2 Type II', short: 'SOC 2',
      desc: 'Security · Availability · Confidentiality controls. Most common framework for B2B SaaS.',
      controls: [
        { id: 'CC6.1', d: 'Logical access controls', a: 'IAM least-privilege + IRSA / Workload Identity (terraform/main.tf)' },
        { id: 'CC6.6', d: 'Encryption in transit + at rest', a: 'KMS keys (terraform/ai.tf, terraform/database.tf), TLS 1.3' },
        { id: 'CC7.1', d: 'Vulnerability monitoring', a: 'Trivy + Grype CI scan (.github/workflows/security.yml)' },
        { id: 'CC7.2', d: 'Detection of anomalies', a: 'Falco + alert rules (k8s/falco-rules.yaml, monitoring/alerts.yaml)' },
        { id: 'CC8.1', d: 'Change management', a: 'CI/CD env promotion + approval gate' },
      ],
    },
    {
      id: 'eu-ai-act', name: 'EU AI Act', short: 'EU AI',
      desc: 'High-risk AI system requirements: risk assessment, transparency, human oversight, post-market monitoring.',
      controls: [
        { id: 'Art.9',  d: 'Risk management system',                  a: 'compliance/ai-impact-assessment.md' },
        { id: 'Art.10', d: 'Data + data governance',                  a: 'PII detection + retention policies + lineage' },
        { id: 'Art.12', d: 'Logging — automatic recording of events', a: 'agent/audit_log.py + structured tracing' },
        { id: 'Art.13', d: 'Transparency — instructions for use',     a: 'directory/agent.json + README' },
        { id: 'Art.14', d: 'Human oversight',                         a: 'COPILOT/SEMI autonomy + escalation paths' },
        { id: 'Art.15', d: 'Accuracy + robustness + cybersecurity',   a: 'evals + AI red-team workflow + guardrails' },
        { id: 'Art.61', d: 'Post-market monitoring',                  a: 'LLM observability + alert rules + drift detection' },
      ],
    },
    {
      id: 'iso42001', name: 'ISO/IEC 42001', short: 'ISO 42k',
      desc: 'AI management system. New 2023 standard for AI governance, complementary to ISO 27001.',
      controls: [
        { id: 'A.6.2.6', d: 'AI system impact assessment',             a: 'compliance/ai-impact-assessment.md' },
        { id: 'A.7.4',   d: 'Resources for AI system development',     a: 'agent/ + agent/guardrails.py + evals/' },
        { id: 'A.8.2',   d: 'AI risk management throughout lifecycle', a: '.github/workflows/ai-redteam.yml + evals.yml' },
        { id: 'A.8.4',   d: 'Verification & validation of AI',         a: 'evals/golden.jsonl + promptfoo regression' },
      ],
    },
    {
      id: 'gdpr', name: 'GDPR', short: 'GDPR',
      desc: 'EU data protection regulation. Right to erasure, lawful basis for processing, DPO when required.',
      controls: [
        { id: 'Art.5',  d: 'Data minimisation + purpose limitation', a: 'agent/guardrails.py PII scrubber pre-LLM' },
        { id: 'Art.17', d: 'Right to erasure',                       a: 'compliance/data-subject-procedures.md' },
        { id: 'Art.25', d: 'Privacy by design + default',            a: 'PII redaction enabled by default in guardrails' },
        { id: 'Art.32', d: 'Security of processing',                 a: 'KMS, TLS, NetworkPolicies, audit logs' },
        { id: 'Art.35', d: 'DPIA for high-risk processing',          a: 'compliance/dpia-template.md' },
      ],
    },
    {
      id: 'hipaa', name: 'HIPAA', short: 'HIPAA',
      desc: 'US healthcare PHI handling. Requires a Business Associate Agreement with your cloud provider.',
      controls: [
        { id: '164.308', d: 'Administrative safeguards',                 a: 'IAM RBAC + audit log retention 6yr' },
        { id: '164.310', d: 'Physical safeguards',                       a: 'Cloud provider BAA (AWS / GCP / Azure)' },
        { id: '164.312', d: 'Technical safeguards — encryption + audit', a: 'KMS encryption, CloudTrail / Audit logs' },
        { id: '164.502', d: 'Minimum necessary',                         a: 'PII redaction + denied-topics in guardrails' },
      ],
    },
  ];

  return (
    <MarketingShell nav={nav} user={user}
      eyebrow="compliance guide"
      title={<>Map every <em style={{ color: accent, fontStyle: 'italic' }}>control</em> to a real artifact in your repo.</>}
      subtitle="For each framework you select in the wizard, STAX generates a control-to-artifact matrix. This page shows what's mapped — auditors get one-page deliverables, engineers get checklists tied to actual code paths.">

      <div className="mb-12 p-6" style={{ background: 'var(--bg-elev)', border: `1px solid ${accent}40` }}>
        <div className="flex items-start gap-3">
          <ShieldCheck size={16} style={{ color: accent, marginTop: 2 }} />
          <div>
            <h3 className="text-[14px] font-semibold mb-1">Technical control mapping only</h3>
            <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-dim)' }}>
              STAX maps controls to <strong style={{ color: 'var(--text)' }}>generated code artifacts</strong>. It does not replace audits, policy documentation, training records, or evidence collection. You still need: personnel security (background checks, training), physical security (your cloud provider covers this via SOC 2), vendor risk management for any external SaaS, business continuity / DR plans, and incident response procedures.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-12">
        {frameworks.map(fw => (
          <section key={fw.id}>
            <div className="flex items-baseline gap-3 mb-4 flex-wrap">
              <h2 className="text-[24px] font-semibold" style={{ fontFamily: 'IBM Plex Serif, serif' }}>{fw.name}</h2>
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5"
                style={{ background: 'var(--bg-card)', color: accent, fontFamily: 'IBM Plex Mono, monospace', border: `1px solid ${accent}40` }}>
                {fw.short}
              </span>
            </div>
            <p className="text-[14px] mb-4" style={{ color: 'var(--text-dim)' }}>{fw.desc}</p>
            <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
              <div className="grid grid-cols-[100px_1fr_1.5fr] border-b" style={{ borderColor: 'var(--border-strong)' }}>
                <div className="px-4 py-2 text-[9px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>control</div>
                <div className="px-4 py-2 text-[9px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>requirement</div>
                <div className="px-4 py-2 text-[9px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>generated artifact</div>
              </div>
              {fw.controls.map(c => (
                <div key={c.id} className="grid grid-cols-[100px_1fr_1.5fr] border-b" style={{ borderColor: 'var(--border)' }}>
                  <div className="px-4 py-2.5 text-[12px]" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>{c.id}</div>
                  <div className="px-4 py-2.5 text-[12px]" style={{ color: 'var(--text)' }}>{c.d}</div>
                  <div className="px-4 py-2.5 text-[11px]" style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>{c.a}</div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-12 p-6" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
        <h3 className="text-[16px] font-semibold mb-2">Other supported frameworks</h3>
        <p className="text-[13px] leading-relaxed mb-3" style={{ color: 'var(--text-dim)' }}>
          The same mapping pattern applies to: <strong style={{ color: 'var(--text)' }}>ISO 27001</strong> (information security management), <strong style={{ color: 'var(--text)' }}>PCI-DSS</strong> (payment cards), <strong style={{ color: 'var(--text)' }}>NIST AI RMF</strong> (US voluntary AI risk framework), and <strong style={{ color: 'var(--text)' }}>FedRAMP Moderate</strong> (US federal cloud authorization).
        </p>
        <button onClick={() => nav(user ? 'app' : 'signup')}
          className="px-4 py-2 text-[12px]"
          style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
          → generate a stack with these frameworks
        </button>
      </div>
    </MarketingShell>
  );
}

// =============================================================
// ABOUT PAGE
// =============================================================

function AboutPage({ nav, user }) {
  const principles = [
    { n: '01', t: 'Generated code is auditable code', d: 'Nothing STAX produces is opaque. No SDK, no agent, no proprietary runtime. Read it, modify it, run it on whatever you want.' },
    { n: '02', t: 'Security is the spine, not a checkbox', d: 'Guardrails ship before the model is wired in. Audit logs ship before the first user signs up. The compliance matrix is generated alongside the code.' },
    { n: '03', t: 'Multi-cloud is one config flag', d: "If your stack only runs on one provider, you don't have a stack — you have lock-in." },
    { n: '04', t: 'No phone-home, no telemetry from generated code', d: 'STAX itself runs in your browser. The code it produces is yours, MIT-licensed, with no callbacks to us.' },
    { n: '05', t: 'Documentation is a deliverable', d: "Compliance matrices, incident response runbooks, READMEs. If it's not written down, it doesn't exist." },
    { n: '06', t: 'Defaults should be safe by default', d: 'Production environments get multi-AZ, encryption, deletion protection, restricted PSS, default-deny network policies. Dev gets cost-optimized — but never less secure.' },
  ];

  return (
    <MarketingShell nav={nav} user={user}
      eyebrow="about"
      title={<>We build the <em style={{ color: accent, fontStyle: 'italic' }}>boring infrastructure</em> so you can build the agents.</>}
      subtitle="STAX started as an internal tool. The same engineers were generating the same Terraform, the same K8s manifests, the same audit-log boilerplate every time we shipped a new agent. We turned the tool into a product.">

      {/* Mission */}
      <section className="mb-20">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              ⏵ mission
            </div>
            <h2 className="text-[28px] font-semibold leading-tight" style={{ fontFamily: 'IBM Plex Serif, serif', letterSpacing: '-0.01em' }}>
              Compress weeks of <em style={{ color: accent, fontStyle: 'italic' }}>infrastructure setup</em> into minutes — without skipping a single security or compliance step.
            </h2>
          </div>
          <div className="space-y-4 text-[14px] leading-relaxed" style={{ color: 'var(--text-dim)' }}>
            <p>
              In 2025 the cost of building an AI agent collapsed. Frameworks like LangGraph, Bedrock, and pgvector turned 6 weeks of plumbing into 6 hours of code.
            </p>
            <p>
              The cost of <em>shipping</em> that agent to production didn't move. You still spent two weeks on Terraform, a week on CI/CD, three days on guardrails, two days on the compliance matrix. Same tickets, same Slack threads, same boilerplate, every single time.
            </p>
            <p>
              STAX is the answer to "why are we doing this every time?" Twelve steps, fourteen minutes, fifty-two files. Production-grade output. No proprietary runtime. The code is yours.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="mb-20">
        <SectionHead num="principles" title="Six things we won't compromise on." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: 'var(--border)' }}>
          {principles.map(p => (
            <div key={p.n} className="p-6" style={{ background: 'var(--bg)' }}>
              <div className="text-[10px] tabular-nums mb-3" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{p.n}</div>
              <h4 className="text-[15px] font-semibold mb-2 leading-tight">{p.t}</h4>
              <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-dim)' }}>{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="mb-20">
        <SectionHead num="trajectory" title="One year in." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: 'var(--border)' }}>
          {[
            { v: '14,283', l: 'stacks generated' },
            { v: '1,847',  l: 'agents indexed' },
            { v: '47k',    l: 'CVEs blocked' },
            { v: '92.1',   l: 'avg posture grade' },
          ].map(s => (
            <div key={s.l} className="p-6" style={{ background: 'var(--bg-elev)' }}>
              <div className="text-[32px] font-semibold leading-none tabular-nums" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>{s.v}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] mt-2" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Backers / quiet */}
      <section className="mb-20">
        <SectionHead num="company" title="Small. Independent. Distributed." sub="Eleven engineers across five timezones. No outside investors. Bootstrapped from day one — every dollar of ARR pays salaries and runs the infrastructure." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { t: 'Founded', v: '2025' },
            { t: 'Headcount', v: '11 engineers' },
            { t: 'Funding', v: 'Bootstrapped' },
          ].map(s => (
            <div key={s.t} className="p-5" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
              <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>{s.t}</div>
              <div className="text-[15px] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => nav('security')} className="px-4 py-2 text-[12px]"
          style={{ background: accentBg, color: '#1a1a17', fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>
          → how we handle security
        </button>
        <button onClick={() => nav('quickstart')} className="px-4 py-2 text-[12px]"
          style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
          quickstart
        </button>
      </div>
    </MarketingShell>
  );
}

// =============================================================
// SECURITY PAGE
// =============================================================

function SecurityPage({ nav, user }) {
  const certifications = [
    { name: 'SOC 2 Type II', status: 'Audited annually', body: 'Drata' },
    { name: 'ISO/IEC 27001', status: 'In progress (Q3 2026)', body: 'BSI' },
    { name: 'GDPR', status: 'Compliant', body: 'Self-attested' },
    { name: 'EU AI Act', status: 'Risk assessment complete', body: 'Self-attested' },
  ];

  return (
    <MarketingShell nav={nav} user={user}
      eyebrow="security"
      title={<>How we protect your <em style={{ color: accent, fontStyle: 'italic' }}>designs and data</em>.</>}
      subtitle="STAX is the company's own product applied to itself. Everything below is implemented, monitored, and audited. Vulnerability disclosure policy at the bottom.">

      <SectionHead num="01" title="What we store" sub="STAX runs in the browser. The list of things on our servers is short on purpose." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
        <Card>
          <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>we store</div>
          <ul className="space-y-1.5 text-[12px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            <li>· email + hashed password (argon2id)</li>
            <li>· workspace name + plan</li>
            <li>· design configurations (your wizard inputs)</li>
            <li>· BYOI configuration (Team plan)</li>
            <li>· billing identifiers (no card data — Stripe holds those)</li>
          </ul>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: '#be123c', fontFamily: 'IBM Plex Mono, monospace' }}>we don't store</div>
          <ul className="space-y-1.5 text-[12px]" style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono, monospace' }}>
            <li>· generated source code (it streams to your browser)</li>
            <li>· cloud credentials (we never ask for them)</li>
            <li>· LLM API keys</li>
            <li>· usage telemetry from generated agents</li>
            <li>· anything from your deployed stacks at all</li>
          </ul>
        </Card>
      </div>

      <SectionHead num="02" title="Encryption + key management" />
      <div className="space-y-2 mb-12">
        {[
          { k: 'In transit', v: 'TLS 1.3 only. HSTS preload. No fallback to TLS 1.0/1.1/1.2.' },
          { k: 'At rest', v: 'AES-256 via AWS KMS. Per-workspace data keys. CMK rotation every 90 days.' },
          { k: 'Backups', v: 'Encrypted, immutable, retained 30 days for free/pro, 7 years for team plans on request.' },
          { k: 'Secrets', v: 'AWS Secrets Manager. No secrets in environment variables, no secrets in container images, no secrets in code.' },
        ].map(r => (
          <div key={r.k} className="grid grid-cols-[140px_1fr] gap-4 p-4" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)' }}>
            <div className="text-[12px] font-semibold" style={{ color: accent, fontFamily: 'IBM Plex Mono, monospace' }}>{r.k}</div>
            <div className="text-[12px]" style={{ color: 'var(--text-dim)' }}>{r.v}</div>
          </div>
        ))}
      </div>

      <SectionHead num="03" title="Access controls" />
      <ul className="space-y-2 mb-12">
        {[
          'Single sign-on (SAML 2.0) on the Team plan — Okta, Entra ID, Auth0, Google Workspace, OneLogin all supported.',
          'Per-workspace IAM. Designs are isolated to the workspace; cross-workspace access is impossible by data model.',
          'All employee access to production requires hardware MFA (YubiKey).',
          'Production access logs retained 7 years. SSH access by named user only — no shared accounts.',
          'Background checks for all employees with production access.',
        ].map((it, i) => (
          <li key={i} className="flex gap-3 text-[13px] leading-relaxed">
            <Check size={14} strokeWidth={3} style={{ color: accent, marginTop: 2, flexShrink: 0 }} />
            <span style={{ color: 'var(--text-dim)' }}>{it}</span>
          </li>
        ))}
      </ul>

      <SectionHead num="04" title="Certifications + frameworks" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
        {certifications.map(c => (
          <div key={c.name} className="p-4 flex items-center gap-4" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
            <ShieldCheck size={20} style={{ color: accent }} />
            <div className="flex-1">
              <div className="text-[14px] font-semibold">{c.name}</div>
              <div className="text-[11px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>{c.status} · {c.body}</div>
            </div>
          </div>
        ))}
      </div>

      <SectionHead num="05" title="Vulnerability disclosure" sub="Found something? We pay bounties and we don't sue researchers. Coordinated disclosure please." />
      <div className="mb-12 p-6" style={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              report to
            </div>
            <div className="text-[14px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>security@stax.dev</div>
            <div className="text-[11px] mt-1" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
              PGP key: 0xA3F9B2C1D8E7…
            </div>
            <div className="text-[11px]" style={{ color: 'var(--text-mute)', fontFamily: 'IBM Plex Mono, monospace' }}>
              SLA: acknowledge within 24h, triage within 72h
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-faint)', fontFamily: 'IBM Plex Mono, monospace' }}>
              bounty ranges
            </div>
            <ul className="space-y-1 text-[12px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              <li>Critical (RCE / data exfil): <span style={{ color: accent }}>$5,000 — $25,000</span></li>
              <li>High (auth bypass / IDOR): <span style={{ color: accent }}>$1,500 — $5,000</span></li>
              <li>Medium (XSS / CSRF): <span style={{ color: accent }}>$300 — $1,500</span></li>
              <li>Low (info disclosure): <span style={{ color: accent }}>$100 — $300</span></li>
            </ul>
          </div>
        </div>
      </div>

      <SectionHead num="06" title="Incident response" />
      <p className="text-[13px] mb-3" style={{ color: 'var(--text-dim)' }}>
        Our incident response runbook is the same one STAX generates for customers. Severity ladder, kill-switch commands, regulator-notification timeline based on the framework affected (GDPR 72h, HIPAA 60d, etc.).
      </p>
      <p className="text-[13px]" style={{ color: 'var(--text-dim)' }}>
        Past incidents and postmortems are published at <InlineCode>status.stax.dev</InlineCode>. We commit to a public postmortem within 5 business days of any SEV-1 or SEV-2.
      </p>
    </MarketingShell>
  );
}
